import React, { useState, useEffect, useMemo, useRef, useCallback, memo, Suspense, lazy } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Linkify from 'react-linkify';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { API, getAuthToken, removeAuthToken, updateAppBadge } from '../App';
import { useActivityTracking } from '../hooks/useActivityTracking';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Bell, Plus, PlusCircle, LogOut, Calendar, AlertCircle, AlertTriangle, CheckCircle2, Clock, Phone, Users as UsersIcon, Shield, Briefcase, Gift, X, Download, Key, FileText, Edit, Edit3, Trash2, Search, Building2, Menu, ClipboardList, ClipboardCheck, Mail, BarChart3, TrendingUp, PieChart, MessageCircle, Send, ArrowLeft, Image as LucideImage, Video, Link2, UserPlus, UserMinus, MapPin, DollarSign, ChevronRight, GraduationCap, Home, Building, Laptop, Eye, EyeOff, Upload, Copy, GripVertical, Type, ImageIcon, Minus, Award, User, Globe, Settings, PhoneOff, PhoneMissed, PhoneCall, PhoneIncoming, PhoneOutgoing, MessageSquare, Star, Link as LinkIcon, UserCog, Ban, ToggleLeft, ToggleRight, RotateCcw, Activity, ChevronDown, XCircle, ChevronUp, ChevronLeft, HelpCircle, BookOpen, Tag, Check, CheckCheck, PanelLeftClose, PanelLeft, LayoutDashboard, Smile, Camera, Loader2, Archive, Pencil, MoreVertical, Lock, Unlock, Bold, Italic, Strikethrough, Maximize2, Minimize2, CornerDownLeft, Inbox, Sparkles, Languages, Wand2, Headphones, IndianRupee, Share2, ExternalLink, Linkedin, Target, Route, Brain, Zap, FileSignature, FileSpreadsheet, RefreshCw, Monitor, Smartphone, Tablet } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { format } from 'date-fns';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { PieChart as RechartsPieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { MASCOT_OPTIONS, getMascotById } from '../components/mascots/MascotCollection';
import LazyTabContent from './dashboard-tabs/LazyTabContent';
import { useSectionData } from './dashboard-tabs/useSectionData';
import EmojiPicker from '../components/EmojiPicker';
import LinkifyText from '../components/LinkifyText';
import ProfilePhotoUpload from '../components/ProfilePhotoUpload';
import MessagingPanel from '../components/dashboard/MessagingPanel';
import PartnerDashboard from '../components/dashboard/PartnerDashboard';
import BannerManagement from '../components/dashboard/BannerManagement';
import FinanceModule from '../components/dashboard/FinanceModule';
import IntegrationSettingsPanel from '../components/admin/IntegrationSettingsPanel';
import CandidateActivityFeed from '../components/activity/CandidateActivityFeed';
import GlobalActivityFeed from '../components/activity/GlobalActivityFeed';
import RabbitSignIntegration from '../components/integrations/RabbitSignIntegration';
const CandidateSpreadsheet = lazy(() => import('./CandidateSpreadsheet'));
import { NotificationBell } from '../components/notifications/NotificationCenter';
import ApplicationHub from './ApplicationHub';
import { RichTextEditor } from '../components/ui/rich-text-editor';
import { CallProvider, useCall } from '../contexts/CallContext';
import CallUI from '../components/CallUI';
import AIAssistant from '../components/AIAssistant';
import AIImageGenerator from '../components/AIImageGenerator';
import TeamActivityReports from '../components/TeamActivityReports';
import QuestionBankManager from '../components/QuestionBankManager';
import TelephonicScreeningDialog from '../components/TelephonicScreeningDialog';
import { ScreeningHistoryDialog } from '../components/ScreeningHistoryDialog';
import { ClipboardCheck as ScreeningHistoryIcon } from 'lucide-react';
import { HRAssessmentConfigPanel, AddPerformanceDialog } from '../components/HRPerformanceModule';
const InterviewScheduler = lazy(() => import('../components/InterviewScheduler'));
import { EmailSupport } from '../components/support/EmailSupport';
import { SupportFeedbackPanel } from '../components/support/SupportFeedbackPanel';
import { archiveApi, archiveConfig, ARCHIVE_API, downloadArchive, archiveError } from '../components/backup/archiveApi';
import SurveyResponseCard, { SessionComparisonView } from '../components/SurveyResponseCard';
import { HRSessionEditDialog } from '../components/HRSessionEditor';
import { MailboxManager } from '../components/email/MailboxManager';
import { MailboxInbox } from '../components/email/MailboxInbox';
import { OfficiumPages } from '../components/kb/OfficiumPages';
import { BriefingSessionSummary, DemoRoundSummary } from '../components/HRSessionSummaryCards';

// IST (Indian Standard Time) formatting utility - Kolkata/Chennai timezone
// Using 12-hour format with AM/PM
const formatIST = (dateString, formatStr = 'MMM dd, h:mm a') => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    // Format in IST timezone (Asia/Kolkata)
    const options = { timeZone: 'Asia/Kolkata' };
    
    // Get IST date components
    const istDate = new Date(date.toLocaleString('en-US', options));
    
    // Use date-fns format with IST adjusted date
    return format(istDate, formatStr);
  } catch (e) {
    console.error('Date format error:', e);
    return dateString;
  }
};

// Short time format for messages (h:mm a - 12 hour with AM/PM)
const formatISTTime = (dateString) => formatIST(dateString, 'h:mm a');

// Full date format (MMM dd, yyyy h:mm a - 12 hour with AM/PM)
const formatISTFull = (dateString) => formatIST(dateString, 'MMM dd, yyyy h:mm a');

// Date only format (MMM dd, yyyy)
const formatISTDate = (dateString) => formatIST(dateString, 'MMM dd, yyyy');

// Debounce utility for performance
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

// Helper function to get proper image URL
const getImageUrl = (imagePath, apiBase) => {
  if (!imagePath || imagePath === '' || imagePath === 'null' || imagePath === 'undefined') return null;
  // Already a full URL or base64 data
  if (imagePath.startsWith('data:') || imagePath.startsWith('http')) {
    return imagePath;
  }
  // Path starting with /api - remove /api from base and add path
  if (imagePath.startsWith('/api/')) {
    return `${apiBase.replace('/api', '')}${imagePath}`;
  }
  // Path starting with /uploads
  if (imagePath.startsWith('/uploads')) {
    return `${apiBase.replace('/api', '')}${imagePath}`;
  }
  // Relative path - add to API base
  return `${apiBase}/${imagePath}`;
};

// Helper function to render formatted message content (bold, italic, strikethrough)
const formatMessageContent = (content) => {
  if (!content) return content;
  
  // If content already contains HTML tags (from rich text editor), process links within it
  let formatted = content;
  
  // URL regex - matches http, https, www links
  const urlRegex = /(?<!href=["'])(?<!src=["'])(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+)/gi;
  
  // Email regex
  const emailRegex = /(?<!mailto:)([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/gi;
  
  // Phone regex - matches various formats like +91 1234567890, (123) 456-7890, 123-456-7890, 1234567890
  const phoneRegex = /(?<![\d-])(\+?\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}(?![\d-])/g;
  
  // Only process if not already containing anchor tags for that content
  if (!/<a[^>]*>/.test(formatted)) {
    // Replace URLs with clickable links
    formatted = formatted.replace(urlRegex, (match) => {
      const href = match.startsWith('www.') ? `https://${match}` : match;
      return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline break-all">${match}</a>`;
    });
    
    // Replace emails with mailto links
    formatted = formatted.replace(emailRegex, (match) => {
      return `<a href="mailto:${match}" class="text-blue-600 hover:text-blue-800 underline">${match}</a>`;
    });
    
    // Replace phone numbers with tel links
    formatted = formatted.replace(phoneRegex, (match) => {
      const cleanNumber = match.replace(/[\s()-]/g, '');
      return `<a href="tel:${cleanNumber}" class="text-blue-600 hover:text-blue-800 underline">${match}</a>`;
    });
  }
  
  // If content doesn't contain HTML tags, also process markdown-style formatting
  if (!/<[^>]+>/.test(content)) {
    // Replace *text* with bold (for backward compatibility with old messages)
    formatted = formatted.replace(/\*([^*]+)\*/g, '<strong>$1</strong>');
    // Replace _text_ with italic
    formatted = formatted.replace(/_([^_]+)_/g, '<em>$1</em>');
    // Replace ~text~ with strikethrough
    formatted = formatted.replace(/~([^~]+)~/g, '<del>$1</del>');
  }
  
  return formatted;
};

// Define all available tabs for permission control
const ALL_TABS = [
  { id: 'tasks', label: 'Tasks', icon: 'ClipboardList' },
  { id: 'messages', label: 'Messages', icon: 'MessageSquare' },
  { id: 'grammar', label: 'Grammar', icon: 'SpellCheck' },
  { id: 'candidates', label: 'Candidates', icon: 'Users' },
  { id: 'hr-rounds', label: 'HR', icon: 'Calendar' },
  { id: 'hr-config', label: 'HR Config', icon: 'Settings' },
  { id: 'interviews', label: 'Interviews', icon: 'Calendar' },
  { id: 'team-management', label: 'Team Management', icon: 'Users' },
  { id: 'job-updates', label: 'Jobs', icon: 'Briefcase' },
  { id: 'all-applications', label: 'Applications', icon: 'FileText' },
  { id: 'app-hub', label: 'App Hub', icon: 'Zap' },
  { id: 'activity-feed', label: 'Activity Feed', icon: 'Activity' },
  { id: 'google-sheets', label: 'Sheets', icon: 'FileSpreadsheet' },
  { id: 'blacklist-restrictions', label: 'Restrictions', icon: 'Ban' },
  { id: 'companies', label: 'Companies', icon: 'Building2' },
  { id: 'officium-pages', label: 'Officium Pages', icon: 'BookOpen' },
  { id: 'question-banks', label: 'Screening Banks', icon: 'Phone' },
  { id: 'user-management', label: 'Users', icon: 'UserCog' },
  { id: 'team-reports', label: 'Reports', icon: 'ClipboardCheck' },
  { id: 'analytics', label: 'Analytics', icon: 'BarChart3' },
  { id: 'support', label: 'Support', icon: 'HeadphonesIcon' },
  { id: 'forms', label: 'Forms', icon: 'FileInput' },
  { id: 'skill-assessments', label: 'Assessments', icon: 'Target' },
  { id: 'career-paths', label: 'Careers', icon: 'Route' },
  { id: 'all-referrals', label: 'Referrals', icon: 'Gift' },
  { id: 'banners', label: 'Banners', icon: 'Image' },
  { id: 'emails', label: 'Emails', icon: 'Mail' },
  { id: 'popup-announcements', label: 'Popups', icon: 'Bell' },
  { id: 'pending-employers', label: 'Employers', icon: 'Building' },
  { id: 'courses', label: 'Courses', icon: 'GraduationCap' },
  { id: 'position-requests', label: 'Requests', icon: 'FileQuestion' },
  { id: 'finance', label: 'Finance', icon: 'IndianRupee' }
];

// Helper function to check if user has access to a specific tab
const hasTabAccess = (user, tabId) => {
  // If no user, deny access
  if (!user) return false;
  
  // Admins have access to everything
  if (user?.is_admin === true) return true;
  
  // Employers and admins who access /dashboard should have full access (admin portal users)
  // Check multiple possible field names for compatibility
  const userRole = user?.role?.toLowerCase?.() || '';
  const userType = user?.user_type?.toLowerCase?.() || '';
  
  if (userRole === 'admin' || userType === 'admin' || 
      userRole === 'employer' || userType === 'employer') {
    return true;
  }
  
  // If user has explicit tab_permissions array, check it
  if (user?.tab_permissions && user.tab_permissions.length > 0) {
    return user.tab_permissions.includes(tabId);
  }
  
  // Backward compatibility: check questions_tab_access for interview-questions tab
  if (tabId === 'officium-pages' && user?.questions_tab_access) {
    return true;
  }
  
  // Default role-based access for users without explicit permissions
  const defaultAccess = {
    lead_specialist: ['messages', 'all-referrals', 'team-reports', 'officium-pages'],
    crm_user: ['tasks', 'messages', 'candidates', 'hr-rounds', 'interviews', 'job-updates', 'all-applications', 'companies', 'support', 'skill-assessments', 'career-paths', 'team-reports', 'officium-pages'],
    telesales: ['messages', 'candidates', 'team-reports', 'officium-pages'],
    user: ['tasks', 'messages', 'support', 'analytics', 'candidates', 'hr-rounds', 'interviews', 'job-updates', 'all-applications', 'companies', 'officium-pages', 'grammar', 'forms', 'emails', 'skill-assessments', 'career-paths', 'team-reports']
  };
  
  const roleAccess = defaultAccess[userRole] || defaultAccess[userType] || [];
  return roleAccess.includes(tabId);
};

// Call Button component for chat headers
const CallButton = ({ userEmail, userName, userAvatar }) => {
  const { initiateCall, callState } = useCall();
  const [canCall, setCanCall] = useState(true);

  // Check if user can receive calls (not a candidate)
  useEffect(() => {
    const checkCanCall = async () => {
      try {
        const token = getAuthToken();
        const res = await axios.get(`${API}/calls/user-status/${userEmail}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCanCall(res.data.can_call !== false);
      } catch (error) {
        setCanCall(true); // Allow call attempt on error
      }
    };
    checkCanCall();
  }, [userEmail]);

  const handleCall = async () => {
    if (callState.isInCall) {
      toast.error('Already in a call');
      return;
    }
    if (!canCall) {
      toast.error('Cannot call this user');
      return;
    }
    await initiateCall(userEmail, userName, userAvatar);
  };

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={handleCall}
      disabled={callState.isInCall || !canCall}
      className="h-9 w-9 rounded-full text-emerald-600 hover:bg-emerald-50"
      title="Start audio call"
      data-testid="call-button"
    >
      <Phone className="h-5 w-5" />
    </Button>
  );
};

// Callback button for call history
const CallbackButton = ({ userEmail, userName }) => {
  const { initiateCall, callState } = useCall();

  const handleCallback = async (e) => {
    e.stopPropagation();
    if (callState.isInCall) {
      toast.error('Already in a call');
      return;
    }
    await initiateCall(userEmail, userName, null);
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleCallback}
      disabled={callState.isInCall}
      className="h-7 w-7 p-0 text-emerald-600 hover:bg-emerald-50 rounded-full mt-1"
      title="Call back"
      data-testid="callback-button"
    >
      <PhoneCall className="h-4 w-4" />
    </Button>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  
  // Activity tracking hook
  const { logActivity, endSession } = useActivityTracking();
  
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [announcements, setAnnouncements] = useState([]);
  const [dismissedAnnouncements, setDismissedAnnouncements] = useState([]);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstall, setShowInstall] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);
  const [profileData, setProfileData] = useState({ name: '', profile_picture: '' });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [availabilityStatus, setAvailabilityStatus] = useState('available');
  
  // Messaging state
  const [isMessagingOpen, setIsMessagingOpen] = useState(false);
  const [isAIAssistantOpen, setIsAIAssistantOpen] = useState(false); // AI Assistant state
  const [isImageGeneratorOpen, setIsImageGeneratorOpen] = useState(false); // AI Image Generator state
  const [isFullscreenMessage, setIsFullscreenMessage] = useState(null); // For fullscreen message view
  const [isMobileFullscreenChat, setIsMobileFullscreenChat] = useState(false); // Mobile fullscreen conversation
  const [isMessagesFullscreen, setIsMessagesFullscreen] = useState(false); // Full page messages view
  const [previousTabBeforeChat, setPreviousTabBeforeChat] = useState(null); // Track previous tab for mobile back
  const [conversations, setConversations] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [replyToMessage, setReplyToMessage] = useState(null); // For reply feature
  const [editingMessage, setEditingMessage] = useState(null); // For edit feature
  const [selectedMessageId, setSelectedMessageId] = useState(null); // For mobile tap menu
  const [textFormatting, setTextFormatting] = useState({ bold: false, italic: false, underline: false }); // Text formatting
  const [messagingActiveTab, setMessagingActiveTab] = useState('chats'); // chats or groups
  const [messageSearchQuery, setMessageSearchQuery] = useState(''); // Search query for messages
  const messageCacheRef = useRef({}); // Cache messages per conversation (useRef for stable polling)
  const messageCacheTimestampsRef = useRef({}); // Track when cache was updated
  const lastMessageInfoRef = useRef({}); // Track last message count and timestamp per conversation
  const [messagesLoading, setMessagesLoading] = useState(false); // Loading state for messages
  const [typingUsers, setTypingUsers] = useState([]); // Users currently typing
  const typingTimeoutRef = useRef(null);
  const lastTypingRef = useRef(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const prevUnreadCountRef = useRef(0); // Use ref for stable polling
  const messagesEndRef = useRef(null);
  const selectedChatRef = useRef(null); // Ref for stable polling access
  const isMessagingOpenRef = useRef(false); // Ref for stable polling access
  const isMessagesFullscreenRef = useRef(false); // Ref for fullscreen messages polling
  const [isCreateGroupOpen, setIsCreateGroupOpen] = useState(false);
  const [isEditGroupOpen, setIsEditGroupOpen] = useState(false);  // Edit group dialog
  const [editGroupName, setEditGroupName] = useState('');  // Edit group name
  const [newGroupName, setNewGroupName] = useState('');
  const [selectedGroupMembers, setSelectedGroupMembers] = useState([]);
  
  // Call history state
  const [callHistory, setCallHistory] = useState([]);
  const [missedCallCount, setMissedCallCount] = useState(0);
  const [groups, setGroups] = useState([]);
  const [messageType, setMessageType] = useState('text');
  const [imagePreview, setImagePreview] = useState(null);
  const imageInputRef = useRef(null);
  const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);
  const [memberToAdd, setMemberToAdd] = useState('');
  const [selectedMembersToAdd, setSelectedMembersToAdd] = useState([]);  // For multi-select members
  const [isGroupImageUploading, setIsGroupImageUploading] = useState(false);
  const groupImageInputRef = useRef(null);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [isAdminViewOpen, setIsAdminViewOpen] = useState(false);
  const [adminConversations, setAdminConversations] = useState([]);
  const [adminViewChat, setAdminViewChat] = useState(null);
  const [adminChatMessages, setAdminChatMessages] = useState([]);
  
  // Task Reply state
  const [selectedTaskForReply, setSelectedTaskForReply] = useState(null);
  const [isReplyDialogOpen, setIsReplyDialogOpen] = useState(false);
  const [taskReplies, setTaskReplies] = useState([]);
  const [newReply, setNewReply] = useState('');
  const [replyImageFile, setReplyImageFile] = useState(null);
  const [replyImagePreview, setReplyImagePreview] = useState(null);
  const [uploadingReplyImage, setUploadingReplyImage] = useState(false);
  const replyImageInputRef = useRef(null);
  
  // Notification dropdown state
  const [isNotificationDropdownOpen, setIsNotificationDropdownOpen] = useState(false);
  
  // Job Updates State
  const [jobUpdates, setJobUpdates] = useState([]);
  const [isJobPanelOpen, setIsJobPanelOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobApplications, setJobApplications] = useState([]);
  const [allApplications, setAllApplications] = useState([]);
  const [loadingApplications, setLoadingApplications] = useState(false);
  const [showArchivedApplications, setShowArchivedApplications] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    const requested = new URLSearchParams(window.location.search).get('tab');
    return ['interviews', 'google-sheets'].includes(requested) ? requested : 'tasks';
  });
  const [isJobFormOpen, setIsJobFormOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true); // Always start collapsed, toggle via hamburger
  
  // Universal Search State
  const [universalSearch, setUniversalSearch] = useState('');
  const [searchResults, setSearchResults] = useState({ candidates: [], jobs: [], applications: [], companies: [], users: [], tasks: [] });
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  
  // Application Filter State
  const [applicationFilter, setApplicationFilter] = useState('all');
  
  // Bulk Email State
  const [selectedForBulkEmail, setSelectedForBulkEmail] = useState([]);
  const [isBulkEmailMode, setIsBulkEmailMode] = useState(false);
  const [isBulkEmailDialogOpen, setIsBulkEmailDialogOpen] = useState(false);
  const [bulkEmailData, setBulkEmailData] = useState({
    subject: '',
    message: '',
    useTemplate: false,
    templateId: ''
  });
  const [sendingBulkEmail, setSendingBulkEmail] = useState(false);
  
  // Grammar Check State
  const [grammarText, setGrammarText] = useState('');
  const [grammarMode, setGrammarMode] = useState('basic');
  const [grammarResult, setGrammarResult] = useState(null);
  const [grammarLoading, setGrammarLoading] = useState(false);
  
  // Database Backup State
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const importFileInputRef = useRef(null);
  
  // Popup Announcements State
  const [popupAnnouncements, setPopupAnnouncements] = useState([]);
  const [isPopupAnnouncementDialogOpen, setIsPopupAnnouncementDialogOpen] = useState(false);
  const [newPopupAnnouncement, setNewPopupAnnouncement] = useState({
    title: '',
    content: '',
    image_url: '',
    button_text: '',
    button_link: '',
    priority: 'normal',
    is_active: true,
    start_date: '',
    end_date: '',
    mascot_type: 'robot'
  });
  const [popupImagePreview, setPopupImagePreview] = useState(null);
  const [uploadingPopupImage, setUploadingPopupImage] = useState(false);
  
  // Manual Email Dialog State
  const [isManualEmailDialogOpen, setIsManualEmailDialogOpen] = useState(false);
  const [manualEmailData, setManualEmailData] = useState({
    to: '',
    subject: '',
    body: '',
    applicantName: '',
    isHtml: false
  });
  const [sendingManualEmail, setSendingManualEmail] = useState(false);
  const [emailActiveSection, setEmailActiveSection] = useState('templates'); // 'compose', 'inbox', 'mailboxes', 'templates', 'sent', 'settings'
  // Connected professional mailboxes (additive - default no-reply sender is unchanged)
  const [emailSenders, setEmailSenders] = useState([]);
  const [selectedEmailSender, setSelectedEmailSender] = useState('default');

  const loadEmailSenders = useCallback(async () => {
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API}/mailboxes/senders`, { headers: { Authorization: `Bearer ${token}` } });
      setEmailSenders(res.data || []);
    } catch (error) {
      setEmailSenders([]);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'emails') loadEmailSenders();
  }, [activeTab, loadEmailSenders]);
  
  // Feedback Dialog State
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false);
  const [feedbackData, setFeedbackData] = useState({
    applicationId: '',
    applicantName: '',
    feedback: '',
    rating: 0,
    feedback_by: '',
    feedback_at: ''
  });
  const [savingFeedback, setSavingFeedback] = useState(false);
  
  // Edit Application State (Admin Only)
  const [isEditApplicationDialogOpen, setIsEditApplicationDialogOpen] = useState(false);
  const [editingApplication, setEditingApplication] = useState(null);
  const [editApplicationForm, setEditApplicationForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    actual_contact_number: '',
    current_city_state: '',
    aadhar_address: '',
    education: '',
    education_other: '',
    university_name: '',
    college_name: '',
    total_experience: '',
    previous_company_name: '',
    previous_lpa: '',
    expected_salary: '',
    hear_about_us: '',
    skills: '',
    current_company: '',
    current_position: '',
    notes: ''
  });
  const [savingApplicationEdit, setSavingApplicationEdit] = useState(false);
  
  // Pending Employers State
  const [pendingEmployers, setPendingEmployers] = useState([]);
  
  // Pending Applications Count (for notifications)
  const [pendingApplicationsCount, setPendingApplicationsCount] = useState(0);
  
  // Support Tickets State
  const [supportTickets, setSupportTickets] = useState([]);
  const [selectedSupportTicket, setSelectedSupportTicket] = useState(null);
  const [supportReplyMessage, setSupportReplyMessage] = useState('');
  const [unreadSupportCount, setUnreadSupportCount] = useState(0);
  const [supportTabFilter, setSupportTabFilter] = useState('active'); // 'active', 'calls', 'resolved'
  // Candidate Support State
  const [candidateSupportTickets, setCandidateSupportTickets] = useState([]);
  const [isCallRequestDialogOpen, setIsCallRequestDialogOpen] = useState(false);
  const [isSupportTicketDialogOpen, setIsSupportTicketDialogOpen] = useState(false);
  const [callRequestForm, setCallRequestForm] = useState({
    name: '',
    phone: '',
    preferred_date: '',
    preferred_time: '',
    reason: ''
  });
  const [supportTicketForm, setSupportTicketForm] = useState({
    subject: '',
    message: ''
  });
  // Companies State
  const [companies, setCompanies] = useState([]);
  const [isCompanyDialogOpen, setIsCompanyDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [newCompany, setNewCompany] = useState({ name: '', description: '', website: '', logo_url: '' });
  const [uploadingCompanyLogo, setUploadingCompanyLogo] = useState(false);
  
  // All Referrals State (from Lead Specialists + Candidates)
  const [allReferrals, setAllReferrals] = useState([]);
  const [referralFormLink, setReferralFormLink] = useState('');
  const [editingReferralFormLink, setEditingReferralFormLink] = useState(false);
  
  // Banner Announcements State
  const [banners, setBanners] = useState([]);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  
  // Footer Config State
  const [footerConfig, setFooterConfig] = useState({
    company_name: 'Officium Connect',
    company_tagline: "India's trusted platform for verified careers - Remote & Office",
    contact_email: '',
    contact_phone: '',
    social_links: { linkedin: '', twitter: '', instagram: '', facebook: '' },
    company_links: [
      { label: 'Our Story', url: '/our-story', is_active: true },
      { label: 'Reviews', url: '/reviews', is_active: true },
      { label: 'Pricing', url: '/pricing', is_active: true }
    ],
    support_links: [
      { label: 'Sign In', url: '/login', is_active: true },
      { label: 'Contact Us', url: '/contact', is_active: true },
      { label: 'FAQ', url: '/faq', is_active: true }
    ],
    legal_links: [
      { label: 'Terms of Service', url: '/terms', is_active: true },
      { label: 'Privacy Policy', url: '/privacy', is_active: true },
      { label: 'Refund Policy', url: '/refund', is_active: true }
    ],
    social_instagram: '',
    social_facebook: '',
    social_linkedin: '',
    social_twitter: '',
    copyright_text: '',
    made_in_text: 'MADE WITH ❤️ IN INDIA'
  });
  const [isFooterDialogOpen, setIsFooterDialogOpen] = useState(false);
  
  // CMS Page Content State
  const [cmsPages, setCmsPages] = useState([]);
  const [isPageEditorOpen, setIsPageEditorOpen] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [pageEditorData, setPageEditorData] = useState({
    slug: '',
    title: '',
    content: '',
    is_published: true
  });
  const [savingPage, setSavingPage] = useState(false);
  
  // Application Search State
  const [applicationSearchQuery, setApplicationSearchQuery] = useState('');
  const [groupByCandidate, setGroupByCandidate] = useState(true);  // Default to grouped view
  const [expandedCandidates, setExpandedCandidates] = useState({}); // Track expanded candidate cards
  const [positionFilter, setPositionFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  
  // Success Stories State
  const [successStories, setSuccessStories] = useState([]);
  const [newStory, setNewStory] = useState({
    candidate_name: '',
    testimonial: '',
    position: '',
    category: ''
  });
  
  // Custom Forms State
  const [customForms, setCustomForms] = useState([]);
  const [formSubmissions, setFormSubmissions] = useState([]);
  const [deletedFormSubmissions, setDeletedFormSubmissions] = useState([]); // For orphaned submissions
  const [selectedFormForResponses, setSelectedFormForResponses] = useState(null);
  const [isFormResponsesDialogOpen, setIsFormResponsesDialogOpen] = useState(false);
  const [responseSearchQuery, setResponseSearchQuery] = useState('');
  const [statusReasonDialog, setStatusReasonDialog] = useState({ open: false, submissionId: null, newStatus: null });
  const [statusReason, setStatusReason] = useState('');
  const [expandedResponses, setExpandedResponses] = useState({});
  const [isFormBuilderOpen, setIsFormBuilderOpen] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [newForm, setNewForm] = useState({
    title: '',
    description: '',
    header_image_id: null,
    pipeline_name: '',
    thank_you_message: 'Thank you for your submission!',
    fields: []
  });
  const [uploadingFormHeader, setUploadingFormHeader] = useState(false);
  
  // Email Templates State
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [newTemplate, setNewTemplate] = useState({
    status_trigger: '',
    subject: '',
    body: '',
    is_active: true
  });
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [sendEmailOnStatusChange, setSendEmailOnStatusChange] = useState(true);
  const [emailLogoUrl, setEmailLogoUrl] = useState(null);
  const [uploadingEmailLogo, setUploadingEmailLogo] = useState(false);
  const [customStatusInput, setCustomStatusInput] = useState('');
  const [useCustomStatus, setUseCustomStatus] = useState(false);
  const [viewingEmailLog, setViewingEmailLog] = useState(null);
  
  // Default application statuses + custom ones from templates
  const DEFAULT_STATUSES = ['Applied', 'Called', 'Shortlisted', 'Emails Sent', 'Rejected', 'Did Not Pick Up', 'Follow-Up', 'Not Reachable'];
  
  // Statuses served by the backend (defaults + template triggers + statuses already in use)
  const [serverApplicationStatuses, setServerApplicationStatuses] = useState([]);

  // Statuses are compared case/space-insensitively - "social media" and "Social Media" are one status
  const sameStatus = (a, b) => (a || '').trim().toLowerCase() === (b || '').trim().toLowerCase();

  const allApplicationStatuses = useMemo(() => {
    const templateStatuses = emailTemplates.map(t => t.status_trigger).filter(Boolean);
    const seen = new Set();
    return [...DEFAULT_STATUSES, ...serverApplicationStatuses, ...templateStatuses]
      .map(status => (status || '').trim()).filter(Boolean)
      .filter(status => { const key = status.toLowerCase(); if (seen.has(key)) return false; seen.add(key); return true; });
  }, [emailTemplates, serverApplicationStatuses]);

  // The option label that matches an application's stored status, whatever its casing
  const statusOption = status => allApplicationStatuses.find(option => sameStatus(option, status)) || status;
  
  // Visitor Analytics State
  const [visitorAnalytics, setVisitorAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  
  // App Visits Analytics State
  const [appVisitsData, setAppVisitsData] = useState(null);
  const [appVisitsPeriod, setAppVisitsPeriod] = useState('7d');
  const [loadingAppVisits, setLoadingAppVisits] = useState(false);
  
  // Telesales CRM State
  const [telesalesLeads, setTelesalesLeads] = useState([]);
  const [telesalesAnalytics, setTelesalesAnalytics] = useState(null);
  const [telesalesMembers, setTelesalesMembers] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadStatusDialog, setLeadStatusDialog] = useState(false);
  const [leadStatusData, setLeadStatusData] = useState({ status: '', feedback: '', follow_up_date: '' });
  const [leadSearchQuery, setLeadSearchQuery] = useState('');
  const [leadFilterStatus, setLeadFilterStatus] = useState('all');
  const [loadingLeads, setLoadingLeads] = useState(false);
  
  // User Management State
  const [allUsers, setAllUsers] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userListPage, setUserListPage] = useState(1);
  useEffect(() => { setUserListPage(1); }, [userSearchQuery, allUsers.length]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  // Tab Permissions Dialog State
  const [tabPermissionsDialogOpen, setTabPermissionsDialogOpen] = useState(false);
  const [editingUserPermissions, setEditingUserPermissions] = useState(null);
  const [selectedTabPermissions, setSelectedTabPermissions] = useState([]);
  
  // Position Requests State
  const [positionRequests, setPositionRequests] = useState([]);
  const [positionRequestSearch, setPositionRequestSearch] = useState('');
  const [loadingPositionRequests, setLoadingPositionRequests] = useState(false);

  // Schedule Notification Dialog State
  const [notifyDialogOpen, setNotifyDialogOpen] = useState(false);
  const [notifyDialogData, setNotifyDialogData] = useState({
    performanceId: '',
    sessionType: '',
    candidateName: '',
    customMessage: '',
    meetingLink: ''
  });

  // Session editor state - one editor for the pencil dialogs and the global Update button
  const [sessionEditor, setSessionEditor] = useState({ open: false, performance: null, scope: { type: 'all' } });

  // Interview Questions State (Admin & CRM Only)
  const [interviewQuestions, setInterviewQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [isQuestionDialogOpen, setIsQuestionDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [addAnotherMode, setAddAnotherMode] = useState(false);  // For adding multiple questions
  const [lastAddedJobId, setLastAddedJobId] = useState('');  // Remember job role for "Add Another"
  const [questionsAddedCount, setQuestionsAddedCount] = useState(0);  // Count questions added in session
  const [topicImages, setTopicImages] = useState({});  // Store topic images by topic_key
  const [uploadingTopicImage, setUploadingTopicImage] = useState(null);  // Track which topic is uploading
  
  // Predefined question categories
  const PREDEFINED_CATEGORIES = [
    'HR Round Questions',
    'Guidelines',
    'Promotion Messages',
    'Interview/Demo Interview Questions',
    'Other'
  ];
  const [customCategories, setCustomCategories] = useState([]);  // User-created custom categories
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [categoryTopicMapping, setCategoryTopicMapping] = useState({});  // Maps category -> list of topic keys
  const [isManageTopicsDialogOpen, setIsManageTopicsDialogOpen] = useState(false);  // Dialog to manage topics for a category
  const [managingCategory, setManagingCategory] = useState(null);  // Which category we're managing topics for
  const [newQuestion, setNewQuestion] = useState({
    question: '',
    category: 'Interview/Demo Interview Questions',  // Default to most common
    difficulty: 'Medium',
    expected_answer: '',
    hints: '',
    job_id: '',
    custom_headline: '',  // For "Other" option custom topic/headline
    tags: [],
    is_active: true,
    image_url: '',
    images: [],  // Multiple images support
    question_type: 'text',
    options: ['', '', '', ''],
    correct_option: null,
    time_limit: null,
    points: 1,
    permitted_emails: [],
    is_public: false
  });
  const [permissionEmailInput, setPermissionEmailInput] = useState(''); // For adding emails to permitted list
  const [questionImagePreviews, setQuestionImagePreviews] = useState([]);  // Multiple images preview
  const [uploadingQuestionImage, setUploadingQuestionImage] = useState(false);  // Loading state for question image upload
  const [questionSearchQuery, setQuestionSearchQuery] = useState('');
  const [questionFilterCategory, setQuestionFilterCategory] = useState('all');
  const [questionFilterDifficulty, setQuestionFilterDifficulty] = useState('all');
  const [questionFilterTag, setQuestionFilterTag] = useState('all');
  const [tagInput, setTagInput] = useState('');
  const [expandedJobRoles, setExpandedJobRoles] = useState({});  // Track which job roles are expanded
  const [questionFilterJobRole, setQuestionFilterJobRole] = useState('all');  // Filter by specific job role
  const [viewingQuestion, setViewingQuestion] = useState(null);  // For full question view modal
  const [isGiveAccessDialogOpen, setIsGiveAccessDialogOpen] = useState(false);  // Give Access dialog
  const [giveAccessQuestion, setGiveAccessQuestion] = useState(null);  // Question to give access to
  const [giveAccessEmail, setGiveAccessEmail] = useState('');  // Email to give access to
  const [isGrantingAccess, setIsGrantingAccess] = useState(false);  // Loading state
  const [isRevokeAccessDialogOpen, setIsRevokeAccessDialogOpen] = useState(false);  // Revoke Access dialog
  const [revokeAccessData, setRevokeAccessData] = useState(null);  // Data for revoking access
  const [revokeAccessEmail, setRevokeAccessEmail] = useState('');  // Email to revoke access from
  const [isRevokingAccess, setIsRevokingAccess] = useState(false);  // Loading state
  
  // Lock Group State
  const [isLockGroupDialogOpen, setIsLockGroupDialogOpen] = useState(false);  // Lock/Unlock dialog
  const [lockGroupData, setLockGroupData] = useState(null);  // Data for locking/unlocking { jobKey, jobTitle, questions, mode: 'lock'|'unlock' }
  const [lockGroupEmail, setLockGroupEmail] = useState('');  // Email to lock/unlock
  const [isLockingGroup, setIsLockingGroup] = useState(false);  // Loading state
  
  // Form Lock State
  const [isFormLockDialogOpen, setIsFormLockDialogOpen] = useState(false);
  const [formLockData, setFormLockData] = useState(null);  // { forms: [], mode: 'lock'|'unlock' }
  const [formLockEmail, setFormLockEmail] = useState('');
  const [isLockingForm, setIsLockingForm] = useState(false);
  
  // Skill Assessments State
  const [skillAssessments, setSkillAssessments] = useState([]);
  const [assessmentDialogOpen, setAssessmentDialogOpen] = useState(false);
  const [newAssessment, setNewAssessment] = useState({
    skill_name: '',
    description: '',
    passing_score: 70,
    time_limit_minutes: 15,
    questions: []
  });
  const [editingAssessmentId, setEditingAssessmentId] = useState(null); // For edit mode
  const [deleteAssessmentConfirm, setDeleteAssessmentConfirm] = useState(null); // For delete confirmation
  
  // Career Paths State
  const [careerPaths, setCareerPaths] = useState([]);
  const [careerPathDialogOpen, setCareerPathDialogOpen] = useState(false);
  const [newCareerPath, setNewCareerPath] = useState({
    domain: '',
    description: '',
    steps: []
  });
  
  // Assessment Results State (Admin view)
  const [assessmentResults, setAssessmentResults] = useState([]);
  const [loadingAssessmentResults, setLoadingAssessmentResults] = useState(false);
  
  // Rejection Dialog with Cooling Period State
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [rejectingApplication, setRejectingApplication] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [coolingPeriodDays, setCoolingPeriodDays] = useState(0);
  
  // Blacklist State
  const [blacklist, setBlacklist] = useState([]);
  const [isBlacklistDialogOpen, setIsBlacklistDialogOpen] = useState(false);
  const [blacklistData, setBlacklistData] = useState({ email: '', name: '', reason: '' });
  const [loadingBlacklist, setLoadingBlacklist] = useState(false);
  
  // Cooling Period Applications State
  const [coolingPeriodApps, setCoolingPeriodApps] = useState([]);
  const [loadingCoolingPeriodApps, setLoadingCoolingPeriodApps] = useState(false);
  const [editCoolingPeriodDialog, setEditCoolingPeriodDialog] = useState({ open: false, app: null });
  const [newCoolingEndDate, setNewCoolingEndDate] = useState('');
  
  // Telephonic Screening State
  const [isTelephonicScreeningOpen, setIsTelephonicScreeningOpen] = useState(false);
  const [selectedAppForScreening, setSelectedAppForScreening] = useState(null);
  const [screeningHistoryOpen, setScreeningHistoryOpen] = useState(false);
  const [selectedAppForScreeningHistory, setSelectedAppForScreeningHistory] = useState(null);
  
  // Assessment Lock State
  const [isLockAssessmentDialogOpen, setIsLockAssessmentDialogOpen] = useState(false);
  const [lockAssessmentData, setLockAssessmentData] = useState(null); // { assessment, mode: 'lock'|'unlock' }
  const [lockAssessmentEmail, setLockAssessmentEmail] = useState('');
  
  // Job Creator State
  const [newJob, setNewJob] = useState({
    company_name: '',
    position_name: '',
    job_description: '',
    requirements: '',
    salary_range: '',
    location: '',
    education: '',
    experience: '',
    work_type: '',
    salary_currency: '₹',
    career_site_link: '',
    company_logo_url: '',
    job_image_url: ''
  });
  const [editingJob, setEditingJob] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingJobImage, setUploadingJobImage] = useState(false);
  const [jobSearchQuery, setJobSearchQuery] = useState('');
  const [jobPublishFilter, setJobPublishFilter] = useState('all'); // 'all', 'published', 'unpublished'
  
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    deadline: '',
    priority: 'Medium',
    assigned_to_email: ''
  });

  // Candidate Status state
  const [candidateStatuses, setCandidateStatuses] = useState([]);
  const [allCandidates, setAllCandidates] = useState([]);
  const [isCandidateDialogOpen, setIsCandidateDialogOpen] = useState(false);
  const [editingCandidate, setEditingCandidate] = useState(null);
  const [viewingCandidateActivity, setViewingCandidateActivity] = useState(null); // For Activity Feed
  const [sendingSignatureToCandidate, setSendingSignatureToCandidate] = useState(null); // For RabbitSign
  const [candidateSearch, setCandidateSearch] = useState('');
  const [candidateData, setCandidateData] = useState({
    company_name: '',
    candidate_name: '',
    email: '',
    phone: '',
    actual_contact_number: '',
    process: '',
    original_location: '',
    job_location: '',
    initial_payment: 'pending',
    registration_update: 'not done',
    video_received: 'not received',
    assessment_update: 'pending',
    pdf_sent: 'pending',
    demo_hr_round: 'pending',
    final_outcome: 'pending',
    failure_reason: '',
    full_payment: 'pending',
    agreement_signed: 'no'
  });

  // Candidate Performance state
  // New HR Performance Module state
  const [isNewPerformanceDialogOpen, setIsNewPerformanceDialogOpen] = useState(false);
  const [selectedCandidateForNewPerformance, setSelectedCandidateForNewPerformance] = useState(null);

  // HR Rounds Expand/Collapse State - tracks which performance cards are expanded
  const [expandedHrRounds, setExpandedHrRounds] = useState({});

  // All performances state for HR Rounds tab
  const [allPerformances, setAllPerformances] = useState([]);
  const [hrRoundSearch, setHrRoundSearch] = useState('');
  const [rescheduleRequests, setRescheduleRequests] = useState([]);
  const [sendingScheduleEmail, setSendingScheduleEmail] = useState(null);  // Track which session is sending
  const [expandedHRPerformances, setExpandedHRPerformances] = useState({});  // Track which HR performance cards are expanded

  // Toggle expand/collapse for HR performance card
  const toggleHRPerformanceExpanded = (perfId) => {
    setExpandedHRPerformances(prev => ({
      ...prev,
      [perfId]: !prev[perfId]
    }));
  };

  // Expand all HR performance cards
  const expandAllHRPerformances = () => {
    const allExpanded = {};
    allPerformances.forEach(perf => {
      allExpanded[perf.id] = true;
    });
    setExpandedHRPerformances(allExpanded);
  };

  // Collapse all HR performance cards
  const collapseAllHRPerformances = () => {
    setExpandedHRPerformances({});
  };

  const priorityColors = {
    Urgent: 'bg-red-500',
    High: 'bg-orange-500',
    Medium: 'bg-blue-500',
    Low: 'bg-gray-500',
    urgent: 'bg-red-500',
    high: 'bg-orange-500',
    medium: 'bg-blue-500',
    low: 'bg-gray-500'
  };

  const priorityBorderColors = {
    Urgent: '#ef4444',
    High: '#f97316',
    Medium: '#3b82f6',
    Low: '#6b7280',
    urgent: '#ef4444',
    high: '#f97316',
    medium: '#3b82f6',
    low: '#6b7280'
  };

  const statusColors = {
    'Pending': 'bg-yellow-500',
    'In Progress': 'bg-blue-500',
    'Completed': 'bg-green-500',
    'pending': 'bg-yellow-500',
    'in_progress': 'bg-blue-500',
    'completed': 'bg-green-500'
  };

  const statusBorderColors = {
    'Pending': '#eab308',
    'In Progress': '#3b82f6',
    'Completed': '#22c55e',
    'pending': '#eab308',
    'in_progress': '#3b82f6',
    'completed': '#22c55e'
  };

  const announcementPriorityColors = {
    Urgent: 'from-red-500 to-red-600',
    High: 'from-orange-500 to-orange-600',
    Medium: 'from-blue-500 to-indigo-600',
    Low: 'from-gray-500 to-gray-600'
  };

  const priorityOrder = { Urgent: 1, High: 2, Medium: 3, Low: 4 };
  const bootstrapped = useRef(false);
  const fetchingDashboard = useRef(false);
  const dashboardLoaded = useRef(false);
  const [sectionRevision, setSectionRevision] = useState(0);
  useEffect(() => {
    if (!currentUser) return;
    const url = new URL(window.location.href);
    if (activeTab === 'google-sheets') url.searchParams.set('tab', 'google-sheets');
    else if (url.searchParams.get('tab') === 'google-sheets') { url.searchParams.delete('tab'); url.searchParams.delete('sheet'); }
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  }, [activeTab, currentUser?.email]);
  useSectionData({ api: API, token: getAuthToken(), user: currentUser, tab: activeTab,
    searching: universalSearch.length >= 2, revision: sectionRevision, hasAccess: hasTabAccess,
    set: { announcements: setAnnouncements, candidateStatuses: setCandidateStatuses, allCandidates: setAllCandidates,
      allPerformances: setAllPerformances, jobUpdates: setJobUpdates, supportTickets: setSupportTickets, companies: setCompanies,
      allApplications: data => { setAllApplications(data); setPendingApplicationsCount(data.filter(item => item.status === 'Applied').length); },
      customForms: setCustomForms, formSubmissions: setFormSubmissions, deletedFormSubmissions: setDeletedFormSubmissions,
      allReferrals: setAllReferrals, banners: setBanners, successStories: setSuccessStories,
      emailTemplates: setEmailTemplates, emailLogs: setEmailLogs, emailConfig: data => setEmailLogoUrl(data.email_logo_url || ''),
      applicationStatuses: data => setServerApplicationStatuses(data?.statuses || []),
      referralFormLink: data => setReferralFormLink(data.link || ''), popupAnnouncements: setPopupAnnouncements,
      pendingEmployers: setPendingEmployers, cmsPages: setCmsPages } });
  useEffect(() => {
    if (['visitor-analytics', 'analytics'].includes(activeTab) && (currentUser?.is_admin || currentUser?.role === 'admin')) {
      fetchVisitorAnalytics(); fetchAppVisitsAnalytics('7d');
    }
  }, [activeTab, currentUser?.email]);

  useEffect(() => {
    if (!bootstrapped.current) { bootstrapped.current = true; fetchData(); fetchFooterConfig(); subscribeToPushNotifications(); }
    const interval = setInterval(fetchNotifications, 30000);
    
    // Subscribe to push notifications
    
    // Real-time task notification polling (every 5 seconds for immediate alerts)
    const taskNotificationInterval = setInterval(checkRecentNotifications, 15000);
    
    return () => {
      clearInterval(interval);
      clearInterval(taskNotificationInterval);
    };
  }, []);

  // Handle browser back button - prevent going to login page
  useEffect(() => {
    // Push a state to history so back button doesn't leave the app
    window.history.pushState(null, '', window.location.href);
    
    const handlePopState = (event) => {
      // When back button is pressed, push state again to stay on page
      window.history.pushState(null, '', window.location.href);
      
      // Show a toast to indicate the app is still active
      toast.info('Press back again to stay in app', { duration: 1500 });
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Subscribe to push notifications
  const subscribeToPushNotifications = async () => {
    try {
      // Check if service worker and push are supported
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        console.log('Push notifications not supported');
        return;
      }

      // Request notification permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.log('Notification permission denied');
        // Only show toast if permission was explicitly denied (not default)
        if (permission === 'denied') {
          toast.info('Enable notifications in browser settings to receive message alerts', { duration: 5000 });
        }
        return;
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;
      
      // Get VAPID public key from server
      const token = getAuthToken();
      const vapidRes = await axios.get(`${API}/push/vapid-public-key`);
      const vapidPublicKey = vapidRes.data.vapid_public_key;
      
      if (!vapidPublicKey) {
        console.log('VAPID public key not available');
        return;
      }

      // Convert VAPID key to Uint8Array
      const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
          outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
      };

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Send subscription to server
      await axios.post(`${API}/push/subscribe`, subscription.toJSON(), {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Push notification subscription successful');
      toast.success('Notifications enabled! You will receive alerts for new messages.');
    } catch (error) {
      console.error('Push notification subscription failed:', error);
      // Don't show error toast as this is expected in some browsers/environments
    }
  };

  // Handle service worker messages (for notification reply functionality)
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handleServiceWorkerMessage = async (event) => {
      const { type, senderEmail, groupId, callId, senderName, offer } = event.data || {};
      
      if (type === 'OPEN_CHAT') {
        // Switch to messages tab and open the chat
        setActiveTab('messages');
        if (groupId) {
          const group = groups.find(g => g.id === groupId);
          if (group) openGroupChat(group);
        } else if (senderEmail) {
          const user = users.find(u => u.email === senderEmail);
          if (user) {
            openChat(user);
          }
        }
      } else if (type === 'MARK_READ' && senderEmail) {
        // Mark messages as read
        try {
          const token = getAuthToken();
          await axios.post(`${API}/messages/${senderEmail}/mark-all-read`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } catch (err) {
          console.log('Mark read failed:', err);
        }
      } else if (type === 'ANSWER_CALL' || type === 'SHOW_INCOMING_CALL') {
        // Handle incoming call from push notification
        console.log('[Dashboard] Received incoming call from service worker:', { callId, senderEmail, senderName });
        setActiveTab('messages');
        // The CallContext will handle showing the call UI when it receives the incoming call signal
        // Store the call info in sessionStorage so CallContext can pick it up
        if (callId && senderEmail) {
          sessionStorage.setItem('pendingIncomingCall', JSON.stringify({
            callId,
            senderEmail,
            senderName,
            offer,
            timestamp: Date.now()
          }));
        }
      } else if (type === 'DECLINE_CALL') {
        // Handle decline from notification
        console.log('[Dashboard] Call declined from notification:', callId);
        sessionStorage.removeItem('pendingIncomingCall');
      }
    };

    navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
    };
  }, [users, groups]);

  // Update app badge when notifications change
  useEffect(() => {
    const unreadCount = notifications.filter(n => !n.read).length;
    updateAppBadge(unreadCount);
  }, [notifications]);

  // Keep refs in sync with state for stable polling
  useEffect(() => {
    selectedChatRef.current = selectedChat;
  }, [selectedChat]);
  
  useEffect(() => {
    isMessagingOpenRef.current = isMessagingOpen;
  }, [isMessagingOpen]);

  // Sync fullscreen messages state to ref
  useEffect(() => {
    isMessagesFullscreenRef.current = isMessagesFullscreen;
  }, [isMessagesFullscreen]);

  // Message polling and real-time notification (stable 3-second interval)
  useEffect(() => {
    let running = false;
    let lastPoll = 0;
    const pollMessages = async () => {
      const chatting = isMessagingOpenRef.current || isMessagesFullscreenRef.current;
      if (document.hidden || running || Date.now() - lastPoll < (chatting ? 3000 : 15000)) return;
      running = true; lastPoll = Date.now();
      try {
        const token = getAuthToken();
        if (!token) return;
        
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const res = await axios.get(`${API}/messages/unread/count`, config);
        const newUnreadCount = res.data.unread_count || 0;
        
        // Show toast notification if unread count increased and messaging panel is not open
        if (newUnreadCount > prevUnreadCountRef.current && !isMessagingOpenRef.current && !isMessagesFullscreenRef.current) {
          toast('📩 New message received!', {
            description: 'Click the message icon to view',
            action: {
              label: 'View',
              onClick: () => {
                setIsMessagingOpen(true);
                fetchConversations();
              }
            }
          });
        }
        
        // Auto-refresh current chat messages for real-time updates (dialog or fullscreen)
        const currentChat = selectedChatRef.current;
        if (currentChat && (isMessagingOpenRef.current || isMessagesFullscreenRef.current)) {
          const cacheKey = currentChat.isGroup ? `group-${currentChat.groupId}` : `dm-${currentChat.email}`;
          let msgRes;
          
          if (currentChat.isGroup) {
            msgRes = await axios.get(`${API}/groups/${currentChat.groupId}/messages`, config);
          } else if (currentChat.email) {
            msgRes = await axios.get(`${API}/messages/${currentChat.email}`, config);
          }
          
          if (msgRes?.data) {
            const newMessages = msgRes.data;
            const lastInfo = lastMessageInfoRef.current[cacheKey] || { count: 0, lastTimestamp: null };
            const newLastTimestamp = newMessages.length > 0 ? newMessages[newMessages.length - 1]?.created_at : null;
            
            // Only update if message count changed or last message timestamp is different
            if (newMessages.length !== lastInfo.count || newLastTimestamp !== lastInfo.lastTimestamp) {
              messageCacheRef.current[cacheKey] = newMessages;
              messageCacheTimestampsRef.current[cacheKey] = Date.now();
              lastMessageInfoRef.current[cacheKey] = { count: newMessages.length, lastTimestamp: newLastTimestamp };
              setChatMessages(newMessages);
            }
          }
        }
        
        prevUnreadCountRef.current = newUnreadCount;
        setUnreadMessageCount(newUnreadCount);
      } catch (error) {
        console.error('Failed to poll messages:', error);
      } finally {
        running = false;
      }
    };

    // Poll every 3 seconds for new messages (stable interval - no deps that change)
    const messageInterval = setInterval(pollMessages, 3000);
    pollMessages(); // Initial poll immediately
    
    return () => clearInterval(messageInterval);
  }, []); // Empty deps - refs handle all dynamic values

  const fetchData = async () => {
    if (fetchingDashboard.current) return;
    fetchingDashboard.current = true;
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };

      // Phase 1: Critical data - User info (must succeed for dashboard to work)
      let userRes;
      try {
        userRes = await axios.get(`${API}/auth/me`, config);
        localStorage.setItem('officium_staff', '1');  // CRM staff never see ads
      } catch (error) {
        console.error('Failed to fetch user data:', error);
        if (error.response?.status === 401 || error.response?.status === 403) {
          removeAuthToken();
          navigate('/login');
          return;
        }
        toast.error('Failed to load user data. Please refresh the page.');
        setLoading(false);
        return;
      }

      setCurrentUser(userRes.data);
      
      // Set initial active tab based on user permissions
      const userData = userRes.data;
      if (!dashboardLoaded.current && !userData.is_admin && userData.tab_permissions && userData.tab_permissions.length > 0) {
        const tabOrder = ['tasks', 'interviews', 'google-sheets', 'messages', 'support', 'analytics', 'candidates', 'hr-rounds', 'job-updates', 'all-applications', 'companies', 'officium-pages', 'grammar', 'forms', 'emails', 'all-referrals'];
        const firstAllowed = tabOrder.find(tab => userData.tab_permissions.includes(tab));
        const requested = new URLSearchParams(window.location.search).get('tab');
        if (firstAllowed && !(['interviews', 'google-sheets'].includes(requested) && hasTabAccess(userData, requested))) {
          setActiveTab(firstAllowed);
        }
      }
      
      // Redirect candidates and lead specialists to their dedicated dashboards
      if (userRes.data.role === 'candidate') {
        navigate('/candidate');
        return;
      } else if (userRes.data.role === 'lead_specialist') {
        navigate('/lead-specialist');
        return;
      }
      
      setAvailabilityStatus(userRes.data.availability_status || 'available');

      // Phase 1b: Other critical data - Load independently so one failure doesn't break all
      const [tasksRes, usersRes, notifRes, unreadRes, unreadSupportRes] = await Promise.all([
        axios.get(`${API}/tasks`, config).catch(err => { console.error('Tasks fetch failed:', err); return { data: [] }; }),
        axios.get(`${API}/users`, config).catch(err => { console.error('Users fetch failed:', err); return { data: [] }; }),
        axios.get(`${API}/notifications`, config).catch(err => { console.error('Notifications fetch failed:', err); return { data: [] }; }),
        axios.get(`${API}/messages/unread/count`, config).catch(() => ({ data: { unread_count: 0 } })),
        axios.get(`${API}/support-tickets/unread/count`, config).catch(() => ({ data: { unread_count: 0 } }))
      ]);

      setUnreadMessageCount(unreadRes.data?.unread_count || 0);
      setUnreadSupportCount(unreadSupportRes.data?.unread_count || 0);
      setUsers(usersRes.data || []);
      setNotifications(notifRes.data || []);
      
      // Enrich tasks with assignedBy user's phone number
      const usersData = usersRes.data || [];
      const tasksData = tasksRes.data || [];
      const usersByEmail = new Map(usersData.map(user => [user.email, user]));
      const enrichedTasks = tasksData.map(task => {
        const assignedByUser = usersByEmail.get(task.assigned_by);
        return {
          ...task,
          assigned_by_phone: assignedByUser?.phone || null,
          assigned_by_name: assignedByUser?.name || task.assigned_by
        };
      });
      setTasks(enrichedTasks);

      // Secondary collections are fetched only for the active section (useSectionData).
      if (dashboardLoaded.current) setSectionRevision(value => value + 1);
      dashboardLoaded.current = true;
    } catch (error) {
      console.error('Dashboard fetch error:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        removeAuthToken();
        navigate('/login');
        return;
      }
      if (error.message !== 'Network Error') {
        toast.error('Failed to load data. Please refresh the page.');
      }
    } finally {
      fetchingDashboard.current = false;
      setLoading(false);
    }
  };

  // Fetch all users when user-management tab is active
  useEffect(() => {
    if (activeTab === 'user-management' && currentUser?.is_admin) {
      fetchAllUsers();
      fetchBlacklist();
    }
  }, [activeTab, currentUser?.is_admin]);
  
  // Fetch blacklist and cooling period apps when Restrictions tab is active
  useEffect(() => {
    if (activeTab === 'blacklist-restrictions' && currentUser?.is_admin) {
      fetchBlacklist();
      fetchCoolingPeriodApps();
    }
  }, [activeTab, currentUser?.is_admin]);

  // Handle URL parameters for notification reply (open chat from notification)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get('tab');
    const openChatEmail = urlParams.get('openChat');
    const openGroupId = urlParams.get('openGroup');
    
    if (tabParam === 'messages' && (openChatEmail || openGroupId)) {
      setActiveTab('messages');
      
      // Wait for users/groups to be loaded then open chat
      const checkAndOpenChat = () => {
        if (openGroupId && groups.length > 0) {
          const group = groups.find(g => g.id === openGroupId);
          if (group) {
            openGroupChat(group);
          }
          // Clear URL params
          window.history.replaceState({}, '', window.location.pathname);
        } else if (openChatEmail && users.length > 0) {
          const user = users.find(u => u.email === openChatEmail);
          if (user) {
            openChat(user);
          }
          // Clear URL params
          window.history.replaceState({}, '', window.location.pathname);
        }
      };
      
      checkAndOpenChat();
    }
  }, [users, groups]);

  // Fetch position requests when position-requests tab is active
  useEffect(() => {
    if (activeTab === 'position-requests' && currentUser?.is_admin) {
      fetchPositionRequests();
    }
  }, [activeTab, currentUser?.is_admin]);

  // Fetch interview questions when interview-questions tab is active (Admin, CRM & Internal Users)
  useEffect(() => {
    if (activeTab === 'interview-questions' && (currentUser?.is_admin || currentUser?.role === 'crm_user' || currentUser?.role === 'user')) {
      fetchInterviewQuestions();
    }
  }, [activeTab, currentUser?.is_admin, currentUser?.role]);

  // Fetch skill assessments when skill-assessments tab is active
  useEffect(() => {
    if (activeTab === 'skill-assessments') {
      fetchSkillAssessments();
      if (currentUser?.is_admin) {
        fetchAssessmentResults();
      }
    }
  }, [activeTab]);

  // Fetch career paths when career-paths tab is active
  useEffect(() => {
    if (activeTab === 'career-paths') {
      fetchCareerPaths();
    }
  }, [activeTab]);

  // Fetch conversations and groups when messages tab is active
  useEffect(() => {
    if (activeTab === 'messages') {
      fetchConversations();
    }
  }, [activeTab]);

  // Messaging functions
  const fetchConversations = async () => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API}/messages/conversations`, config);
      setConversations(res.data);
      // Also fetch groups
      const groupsRes = await axios.get(`${API}/groups`, config);
      setGroups(groupsRes.data);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    }
  };

  // Fetch call history
  const fetchCallHistory = async () => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const [historyRes, missedRes] = await Promise.all([
        axios.get(`${API}/calls/history`, config),
        axios.get(`${API}/calls/missed-count`, config)
      ]);
      setCallHistory(historyRes.data);
      setMissedCallCount(missedRes.data.count);
    } catch (error) {
      console.error('Failed to fetch call history:', error);
    }
  };

  const fetchMessages = async (partnerEmail, isGroup = false, groupId = null, forceRefresh = false) => {
    const cacheKey = isGroup ? `group-${groupId}` : `dm-${partnerEmail}`;
    const cacheTime = messageCacheTimestampsRef.current[cacheKey];
    const isCacheFresh = cacheTime && (Date.now() - cacheTime) < 10000; // Cache valid for 10 seconds
    
    // If cache is fresh and not forcing refresh, use cached data only
    if (!forceRefresh && messageCacheRef.current[cacheKey] && isCacheFresh) {
      setChatMessages(messageCacheRef.current[cacheKey]);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'auto' }), 10);
      setMessagesLoading(false);
      return; // Don't fetch again if cache is fresh
    }
    
    // Show cached messages immediately while fetching (if available)
    if (messageCacheRef.current[cacheKey]) {
      setChatMessages(messageCacheRef.current[cacheKey]);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'auto' }), 10);
    } else {
      setChatMessages([]); // Clear old messages immediately
    }
    
    setMessagesLoading(true);
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      let res;
      if (isGroup && groupId) {
        res = await axios.get(`${API}/groups/${groupId}/messages`, config);
      } else {
        res = await axios.get(`${API}/messages/${partnerEmail}`, config);
        
        // Mark all messages from this partner as read (fire and forget)
        axios.post(`${API}/messages/${partnerEmail}/mark-all-read`, {}, config).catch(() => {});
      }
      
      // Update cache with timestamp using refs (stable for polling)
      messageCacheRef.current[cacheKey] = res.data;
      messageCacheTimestampsRef.current[cacheKey] = Date.now();
      setChatMessages(res.data);
      
      // Scroll to bottom
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
      
      // Refresh unread count and conversations in background
      axios.get(`${API}/messages/unread/count`, config)
        .then(unreadRes => setUnreadMessageCount(unreadRes.data.unread_count || 0))
        .catch(() => {});
      
      // Refresh conversations to update individual unread counts
      axios.get(`${API}/messages/conversations`, config)
        .then(convRes => setConversations(convRes.data))
        .catch(() => {});
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setMessagesLoading(false);
    }
  };

  // Typing indicator functions
  const sendTypingStatus = async (isTyping) => {
    if (!selectedChat) return;
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/messages/typing`, {
        partner_email: selectedChat.isGroup ? null : selectedChat.email,
        group_id: selectedChat.isGroup ? selectedChat.groupId : null,
        is_typing: isTyping
      }, config);
    } catch (error) {
      // Silently fail
    }
  };

  const handleTyping = () => {
    // Send typing status
    const now = Date.now();
    if (!lastTypingRef.current || now - lastTypingRef.current > 2000) {
      sendTypingStatus(true);
      lastTypingRef.current = now;
    }
    
    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Set timeout to stop typing
    typingTimeoutRef.current = setTimeout(() => {
      sendTypingStatus(false);
      lastTypingRef.current = null;
    }, 3000);
  };

  const fetchTypingStatus = async () => {
    if (!selectedChat || document.hidden) return;
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const endpoint = selectedChat.isGroup 
        ? `${API}/groups/${selectedChat.groupId}/typing`
        : `${API}/messages/typing/${selectedChat.email}`;
      const res = await axios.get(endpoint, config);
      setTypingUsers(res.data.typing_users || []);
    } catch (error) {
      setTypingUsers([]);
    }
  };

  // Poll for typing status when chat is open
  useEffect(() => {
    if (!selectedChat || !isMessagingOpen) {
      setTypingUsers([]);
      return;
    }
    
    fetchTypingStatus();
    const interval = setInterval(fetchTypingStatus, 5000); // Reduced from 2s to 5s for better performance
    return () => clearInterval(interval);
  }, [selectedChat?.email, selectedChat?.groupId, isMessagingOpen]);

  const [isSendingMessage, setIsSendingMessage] = useState(false);
  
  const handleSendMessage = async (e) => {
    e.preventDefault();
    // Strip HTML tags for empty check
    const plainTextContent = newMessage.replace(/<[^>]*>/g, '').trim();
    if (!plainTextContent && !imagePreview) return;
    if (!selectedChat) return;
    if (isSendingMessage) return; // Prevent double sending
    
    setIsSendingMessage(true);
    
    const messageContent = newMessage.trim() || (imagePreview ? '📷 Image' : '');
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Clear input IMMEDIATELY for instant feel
    const savedMessage = newMessage;
    const savedImagePreview = imagePreview;
    const savedReplyTo = replyToMessage;
    setNewMessage('');
    setImagePreview(null);
    setMessageType('text');
    setReplyToMessage(null); // Clear reply
    
    // Optimistic update - add message immediately to UI
    const optimisticMessage = {
      id: tempId,
      content: messageContent,
      message_type: savedImagePreview ? 'image' : 'text',
      image_url: savedImagePreview || null,
      sender_email: currentUser?.email,
      sender_name: currentUser?.name,
      created_at: new Date().toISOString(),
      is_sending: true,
      status: 'sending',
      reply_to: savedReplyTo ? {
        id: savedReplyTo.id,
        content: savedReplyTo.content,
        sender_name: savedReplyTo.sender_name
      } : null
    };
    
    // Add message to current messages immediately
    setChatMessages(prev => [...prev, optimisticMessage]);
    
    // Update cache immediately too using refs
    const cacheKey = selectedChat.isGroup ? `group-${selectedChat.groupId}` : `dm-${selectedChat.email}`;
    messageCacheRef.current[cacheKey] = [...(messageCacheRef.current[cacheKey] || []), optimisticMessage];
    messageCacheTimestampsRef.current[cacheKey] = Date.now();
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // Upload image first if there is one
      let imageUrl = null;
      if (savedImagePreview && savedImagePreview.startsWith('data:')) {
        try {
          // Convert base64 to blob
          const response = await fetch(savedImagePreview);
          const blob = await response.blob();
          const formData = new FormData();
          formData.append('file', blob, 'image.jpg');
          
          const uploadResponse = await axios.post(`${API}/upload`, formData, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          });
          
          imageUrl = uploadResponse.data.url || uploadResponse.data.file_url;
        } catch (uploadError) {
          console.error('Image upload failed:', uploadError);
          toast.error('Failed to upload image');
          // Continue without image or mark as failed
          setChatMessages(prev => prev.map(m => 
            m.id === tempId ? { ...m, is_sending: false, status: 'failed' } : m
          ));
          setIsSendingMessage(false);
          return;
        }
      }
      
      const messageData = {
        content: savedMessage || (savedImagePreview ? '📷 Image' : ''),
        message_type: savedImagePreview ? 'image' : 'text',
        image_url: imageUrl,
        reply_to_id: savedReplyTo?.id || null
      };
      
      if (selectedChat.isGroup) {
        messageData.group_id = selectedChat.groupId;
      } else {
        messageData.receiver_email = selectedChat.email;
      }
      
      const messageResponse = await axios.post(`${API}/messages`, messageData, config);
      const sentMessage = messageResponse.data;
      
      // Replace optimistic message with real server message
      setChatMessages(prev => prev.map(m => 
        m.id === tempId ? { ...sentMessage, is_sending: false, status: 'sent' } : m
      ));
      
      // Update cache with the real message using refs
      messageCacheRef.current[cacheKey] = (messageCacheRef.current[cacheKey] || []).map(m => 
        m.id === tempId ? { ...sentMessage, is_sending: false, status: 'sent' } : m
      );
      
      // Refresh conversations list (for last message preview)
      fetchConversations();
    } catch (error) {
      console.error('Message send error:', error);
      // Mark as failed and keep in UI so user can see it
      setChatMessages(prev => prev.map(m => 
        m.id === tempId ? { ...m, is_sending: false, status: 'failed' } : m
      ));
      // Also update cache using refs
      messageCacheRef.current[cacheKey] = (messageCacheRef.current[cacheKey] || []).map(m => 
        m.id === tempId ? { ...m, is_sending: false, status: 'failed' } : m
      );
      // Restore input for retry
      setNewMessage(savedMessage);
      setImagePreview(savedImagePreview);
      toast.error('Failed to send message. Tap to retry.');
    } finally {
      setIsSendingMessage(false);
    }
  };

  // Retry failed message
  const retryFailedMessage = async (failedMsg) => {
    // Remove the failed message
    setChatMessages(prev => prev.filter(m => m.id !== failedMsg.id));
    
    // Set the message content back and trigger send
    setNewMessage(failedMsg.content || '');
    if (failedMsg.image_url) {
      setImagePreview(failedMsg.image_url);
    }
    if (failedMsg.reply_to) {
      setReplyToMessage(failedMsg.reply_to);
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim() || selectedGroupMembers.length === 0) {
      toast.error('Please enter group name and select members');
      return;
    }
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.post(`${API}/groups`, {
        name: newGroupName,
        member_emails: selectedGroupMembers
      }, config);
      
      toast.success('Group created successfully!');
      setIsCreateGroupOpen(false);
      setNewGroupName('');
      setSelectedGroupMembers([]);
      fetchConversations();
    } catch (error) {
      toast.error('Failed to create group');
    }
  };

  // Update group name and/or image
  const handleUpdateGroup = async (newImage = null) => {
    if (!selectedChat?.groupId) return;
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const updateData = {};
      if (editGroupName.trim() && editGroupName !== selectedChat.name) {
        updateData.name = editGroupName.trim();
      }
      if (newImage) {
        updateData.group_image = newImage;
      }
      
      if (Object.keys(updateData).length === 0) {
        toast.info('No changes to save');
        return;
      }
      
      await axios.put(`${API}/groups/${selectedChat.groupId}`, updateData, config);
      
      toast.success('Group updated successfully!');
      setIsEditGroupOpen(false);
      
      // Update local state
      setSelectedChat(prev => ({
        ...prev,
        name: updateData.name || prev.name,
        groupImage: updateData.group_image || prev.groupImage
      }));
      
      fetchConversations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update group');
    }
  };

  // Handle group image upload for editing
  const handleEditGroupImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setIsGroupImageUploading(true);
    try {
      const token = getAuthToken();
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/upload`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.url) {
        await handleUpdateGroup(response.data.url);
      }
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setIsGroupImageUploading(false);
    }
  };

  const handleCreateMeeting = async () => {
    try {
      // Generate a unique meeting ID using Jitsi Meet (free, no login required)
      const meetingId = `officium-${Date.now()}-${Math.random().toString(36).substring(7)}`;
      const meetLink = `https://meet.jit.si/${meetingId}`;
      
      // Send the meeting link as a message
      if (selectedChat) {
        const token = getAuthToken();
        const config = { headers: { Authorization: `Bearer ${token}` } };
        
        const messageData = {
          content: `📹 Join Video Meeting: ${meetLink}`,
          message_type: 'meeting',
          meeting_link: meetLink
        };
        
        if (selectedChat.isGroup) {
          messageData.group_id = selectedChat.groupId;
        } else {
          messageData.receiver_email = selectedChat.email;
        }
        
        await axios.post(`${API}/messages`, messageData, config);
        
        if (selectedChat.isGroup) {
          fetchMessages(null, true, selectedChat.groupId);
        } else {
          fetchMessages(selectedChat.email);
        }
        fetchConversations();
        
        toast.success('Meeting created! Opening in new tab...');
        
        // Open the meeting link in a new tab
        window.open(meetLink, '_blank');
      } else {
        toast.error('Please select a chat first');
      }
    } catch (error) {
      toast.error('Failed to create meeting');
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setMessageType('image');
      };
      reader.readAsDataURL(file);
    }
  };

  const openChat = (user) => {
    // Clear previous state immediately
    setSelectedMessageId(null);
    setEditingMessage(null);
    setReplyToMessage(null);
    
    // Track previous tab for mobile back navigation
    setPreviousTabBeforeChat(activeTab);
    
    // Set selected chat immediately for instant UI response
    setSelectedChat({ ...user, isGroup: false, profile_picture: user.profile_picture });
    
    // Enable mobile fullscreen chat
    if (window.innerWidth < 768) {
      setIsMobileFullscreenChat(true);
    }
    
    const cacheKey = `dm-${user.email}`;
    const cacheTime = messageCacheTimestampsRef.current[cacheKey];
    const isCacheFresh = cacheTime && (Date.now() - cacheTime) < 5000; // 5 seconds cache
    
    // Show cached messages immediately (instant load)
    if (messageCacheRef.current[cacheKey]) {
      setChatMessages(messageCacheRef.current[cacheKey]);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'auto' }), 10);
      
      // Fetch in background if cache is stale
      if (!isCacheFresh) {
        fetchMessages(user.email);
      }
    } else {
      setChatMessages([]);
      fetchMessages(user.email);
    }
  };

  const openGroupChat = (group) => {
    // Clear previous state immediately
    setSelectedMessageId(null);
    setEditingMessage(null);
    setReplyToMessage(null);
    
    // Track previous tab for mobile back navigation
    setPreviousTabBeforeChat(activeTab);
    
    // Set selected chat immediately for instant UI response
    setSelectedChat({ 
      name: group.name, 
      email: null, 
      isGroup: true, 
      groupId: group.id,
      memberCount: group.member_emails?.length || 0,
      members: group.member_emails || [],
      createdBy: group.created_by,
      group_image: group.group_image
    });
    
    // Enable mobile fullscreen chat
    if (window.innerWidth < 768) {
      setIsMobileFullscreenChat(true);
    }
    
    const cacheKey = `group-${group.id}`;
    const cacheTime = messageCacheTimestampsRef.current[cacheKey];
    const isCacheFresh = cacheTime && (Date.now() - cacheTime) < 5000; // 5 seconds cache
    
    // Show cached messages immediately (instant load)
    if (messageCacheRef.current[cacheKey]) {
      setChatMessages(messageCacheRef.current[cacheKey]);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'auto' }), 10);
      
      // Fetch in background if cache is stale
      if (!isCacheFresh) {
        fetchMessages(null, true, group.id);
      }
    } else {
      setChatMessages([]);
      fetchMessages(null, true, group.id);
    }
    
    // Mark group messages as read
    const token = getAuthToken();
    if (token) {
      axios.post(`${API}/groups/${group.id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(() => {
        // Update local conversations state to reset unread count
        setConversations(prev => prev.map(c => 
          c.group_id === group.id ? { ...c, unread_count: 0 } : c
        ));
        // Refresh unread count
        axios.get(`${API}/messages/unread/count`, { headers: { Authorization: `Bearer ${token}` } })
          .then(res => setUnreadMessageCount(res.data.unread_count || 0))
          .catch(() => {});
      }).catch(() => {});
    }
  };

  const handleAddMemberToGroup = async () => {
    // Support both single member and multiple members
    const membersToAdd = selectedMembersToAdd.length > 0 ? selectedMembersToAdd : (memberToAdd ? [memberToAdd] : []);
    
    if (membersToAdd.length === 0 || !selectedChat?.groupId) {
      toast.error('Please select member(s) to add');
      return;
    }
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // Add each member
      for (const email of membersToAdd) {
        await axios.post(`${API}/groups/${selectedChat.groupId}/members?email=${email}`, {}, config);
      }
      
      toast.success(`${membersToAdd.length} member(s) added successfully!`);
      setIsAddMemberOpen(false);
      setMemberToAdd('');
      setSelectedMembersToAdd([]);
      fetchConversations();
      // Refresh group info
      const groupsRes = await axios.get(`${API}/groups`, config);
      setGroups(groupsRes.data);
      const updatedGroup = groupsRes.data.find(g => g.id === selectedChat.groupId);
      if (updatedGroup) {
        openGroupChat(updatedGroup);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add member(s)');
    }
  };

  // Close mobile fullscreen chat and return to previous tab
  const closeMobileChat = () => {
    setSelectedChat(null);
    setIsMobileFullscreenChat(false);
    setIsMessagingOpen(false);
    // Return to previous tab if set
    if (previousTabBeforeChat) {
      setActiveTab(previousTabBeforeChat);
      setPreviousTabBeforeChat(null);
    }
  };

  const handleDeleteGroup = async () => {
    if (!selectedChat?.groupId) return;
    
    if (!confirm(`Are you sure you want to delete the group "${selectedChat.name}"? This will delete all messages in this group.`)) {
      return;
    }
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.delete(`${API}/groups/${selectedChat.groupId}`, config);
      
      toast.success('Group deleted successfully!');
      setSelectedChat(null);
      fetchConversations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete group');
    }
  };

  const handleRemoveMemberFromGroup = async (email) => {
    if (!selectedChat?.groupId) return;
    
    if (!confirm(`Remove ${email} from this group?`)) return;
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.delete(`${API}/groups/${selectedChat.groupId}/members/${email}`, config);
      
      toast.success('Member removed!');
      fetchConversations();
      // Refresh group info
      const groupsRes = await axios.get(`${API}/groups`, config);
      setGroups(groupsRes.data);
      const updatedGroup = groupsRes.data.find(g => g.id === selectedChat.groupId);
      if (updatedGroup) {
        openGroupChat(updatedGroup);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  // Upload group profile image
  const handleGroupImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedChat?.groupId) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    setIsGroupImageUploading(true);
    try {
      const token = getAuthToken();
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(
        `${API}/groups/${selectedChat.groupId}/upload-image`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      
      toast.success('Group image updated!');
      
      // Update selectedChat with new image
      setSelectedChat(prev => ({ ...prev, group_image: res.data.group_image }));
      
      // Refresh groups list
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const groupsRes = await axios.get(`${API}/groups`, config);
      setGroups(groupsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setIsGroupImageUploading(false);
      if (groupImageInputRef.current) {
        groupImageInputRef.current.value = '';
      }
    }
  };

  // Admin: View all messages
  const fetchAdminConversations = async () => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API}/admin/messages/conversations`, config);
      setAdminConversations(res.data);
    } catch (error) {
      toast.error('Failed to load conversations');
    }
  };

  const fetchAdminMessages = async (user1, user2) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API}/admin/messages/between/${user1}/${user2}`, config);
      setAdminChatMessages(res.data);
    } catch (error) {
      toast.error('Failed to load messages');
    }
  };

  const handleOpenAdminMessages = () => {
    setIsAdminViewOpen(true);
    fetchAdminConversations();
  };

  const handleOpenMessaging = () => {
    setIsMessagingOpen(true);
    fetchConversations();
  };

  // Delete message function
  const handleDeleteMessage = async (messageId, deleteType) => {
    // Optimistic UI update - remove/mark immediately for instant feel
    const previousMessages = [...chatMessages];
    
    if (deleteType === 'for_everyone') {
      // Mark as deleted in UI immediately
      setChatMessages(prev => prev.map(msg => 
        msg.id === messageId 
          ? { ...msg, deleted_for_all: true, content: 'This message was deleted', image_url: null }
          : msg
      ));
    } else {
      // Remove from UI immediately for "delete for me"
      setChatMessages(prev => prev.filter(msg => msg.id !== messageId));
    }
    
    // Close delete menu immediately
    setMessageToDelete(null);
    
    try {
      const token = getAuthToken();
      const config = { 
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        data: { delete_type: deleteType }
      };
      
      await axios.delete(`${API}/messages/${messageId}`, config);
      toast.success(deleteType === 'for_everyone' ? 'Message deleted for everyone' : 'Message deleted');
    } catch (error) {
      // Revert on failure
      setChatMessages(previousMessages);
      toast.error(error.response?.data?.detail || 'Failed to delete message');
    }
  };

  // Copy message to clipboard
  const handleCopyMessage = (content) => {
    navigator.clipboard.writeText(content).then(() => {
      toast.success('Message copied!');
    }).catch(() => {
      toast.error('Failed to copy');
    });
  };

  // Start editing a message
  const handleEditMessage = (msg) => {
    setEditingMessage(msg);
    setNewMessage(msg.content);
  };

  // Save edited message
  const handleSaveEdit = async () => {
    const plainTextContent = newMessage.replace(/<[^>]*>/g, '').trim();
    if (!editingMessage || !plainTextContent) return;
    
    const previousMessages = [...chatMessages];
    const editedContent = newMessage.trim();
    
    // Optimistic update
    setChatMessages(prev => prev.map(m => 
      m.id === editingMessage.id ? { ...m, content: editedContent, edited: true } : m
    ));
    
    setEditingMessage(null);
    setNewMessage('');
    
    try {
      const token = getAuthToken();
      await axios.put(`${API}/messages/${editingMessage.id}`, 
        { content: editedContent },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Message updated');
    } catch (error) {
      setChatMessages(previousMessages);
      toast.error('Failed to edit message');
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingMessage(null);
    setNewMessage('');
  };

  // PWA Install prompt
  useEffect(() => {
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstall(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    if (window.matchMedia('(display-mode: standalone)').matches) {
      setShowInstall(false);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      toast.info('App can be installed from browser menu');
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      toast.success('App installed successfully!');
      setShowInstall(false);
    }
    setDeferredPrompt(null);
  };

  const fetchNotifications = async () => {
    if (document.hidden) return;
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API}/notifications`, config);
      setNotifications(res.data);
    } catch (error) {
      console.error('Failed to fetch notifications');
    }
  };

  // Track shown notification IDs to avoid duplicate toasts
  const shownNotificationIds = React.useRef(new Set());

  // Check for recent notifications (real-time polling for task alerts)
  const checkRecentNotifications = async () => {
    if (document.hidden) return;
    try {
      const token = getAuthToken();
      if (!token) return;
      
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API}/notifications/recent?seconds=60`, config);
      
      if (res.data.notifications && res.data.notifications.length > 0) {
        res.data.notifications.forEach(notif => {
          // Only show toast if we haven't shown it before
          if (!shownNotificationIds.current.has(notif.id)) {
            shownNotificationIds.current.add(notif.id);
            
            // Show toast based on notification type
            if (notif.type === 'task' || notif.message?.includes('task')) {
              toast.info(notif.message, {
                icon: '📋',
                duration: 6000,
                style: {
                  background: '#EEF2FF',
                  color: '#4338CA',
                  border: '1px solid #C7D2FE'
                }
              });
            } else if (notif.type === 'admin_broadcast' || notif.type === 'admin_direct') {
              toast(notif.message, {
                icon: '📢',
                duration: 8000,
                style: {
                  background: '#FEF3C7',
                  color: '#92400E',
                  border: '1px solid #FCD34D'
                }
              });
            } else {
              toast(notif.message, {
                icon: '🔔',
                duration: 5000
              });
            }
          }
        });
        
        // Refresh full notification list
        fetchNotifications();
      }
    } catch (error) {
      // Silently fail - this is background polling
      console.debug('Recent notifications check failed:', error);
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };

      // Check if assigning to all users
      if (newTask.assigned_to_email === '__ALL_USERS__') {
        // Create task for each approved user
        const approvedUsers = users.filter(u => u.approval_status === 'approved');
        let successCount = 0;
        
        for (const user of approvedUsers) {
          try {
            await axios.post(`${API}/tasks`, {
              ...newTask,
              assigned_to_email: user.email
            }, config);
            successCount++;
          } catch (err) {
            console.error(`Failed to create task for ${user.email}:`, err);
          }
        }
        
        toast.success(`Task created for ${successCount} users!`);
      } else {
        // Create task for single user
        await axios.post(`${API}/tasks`, newTask, config);
        toast.success('Task created successfully!');
      }
      
      setIsCreateDialogOpen(false);
      setNewTask({
        title: '',
        description: '',
        deadline: '',
        priority: 'Medium',
        assigned_to_email: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create task');
    }
  };

  const handleUpdateStatus = async (taskId, newStatus) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.patch(`${API}/tasks/${taskId}`, { status: newStatus }, config);
      toast.success('Task status updated!');
      fetchData();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  // Delete task handler
  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task? This action cannot be undone.')) {
      return;
    }
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // Try admin endpoint first, then user endpoint
      try {
        await axios.delete(`${API}/admin/tasks/${taskId}`, config);
      } catch (adminError) {
        // If admin endpoint fails, try user endpoint
        await axios.delete(`${API}/tasks/${taskId}`, config);
      }
      
      toast.success('Task deleted successfully!');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  // Task Reply handlers
  const openTaskReplyDialog = async (task) => {
    setSelectedTaskForReply(task);
    setIsReplyDialogOpen(true);
    setNewReply('');
    setReplyImageFile(null);
    setReplyImagePreview(null);
    await fetchTaskReplies(task.id);
  };

  const fetchTaskReplies = async (taskId) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/tasks/${taskId}/replies`, config);
      setTaskReplies(response.data);
    } catch (error) {
      console.error('Failed to fetch replies:', error);
      setTaskReplies([]);
    }
  };

  // Fetch job applications when a job is selected
  const fetchJobApplications = async (jobId) => {
    setLoadingApplications(true);
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/job-applications/by-job/${jobId}`, config);
      setJobApplications(response.data);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
      setJobApplications([]);
    } finally {
      setLoadingApplications(false);
    }
  };

  // Update application status
  const updateApplicationStatus = async (applicationId, newStatus) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.patch(`${API}/job-applications/${applicationId}?status=${encodeURIComponent(newStatus.trim())}`, {}, config);
      toast.success(`Application status updated to ${newStatus}`);
      // Refresh applications
      if (selectedJob) {
        fetchJobApplications(selectedJob.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  // Handle application status change from CRM Applications tab
  const handleApplicationStatusChange = async (applicationId, newStatus) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.patch(
        `${API}/job-applications/${applicationId}?status=${encodeURIComponent(newStatus.trim())}&send_email=${sendEmailOnStatusChange}`, 
        {}, 
        config
      );
      
      if (response.data.email_sent) {
        toast.success(`Status updated to ${newStatus}. Email sent to candidate!`);
      } else {
        toast.success(`Status updated to ${newStatus}`);
        if (sendEmailOnStatusChange && response.data.email_details?.reason) {
          toast.info(`Email not sent: ${response.data.email_details.reason}`);
        }
      }
      
      // Update local state
      setAllApplications(prev => {
        const updated = prev.map(app => 
          app.id === applicationId ? { ...app, status: newStatus } : app
        );
        // Update pending count
        const newPendingCount = updated.filter(app => app.status === 'Applied').length;
        setPendingApplicationsCount(newPendingCount);
        return updated;
      });
      
      // Refresh notifications
      fetchNotifications();
      
      // Refresh email logs if emails tab is active
      if (activeTab === 'emails') {
        const logsRes = await axios.get(`${API}/email-logs?limit=500`, config);
        setEmailLogs(logsRes.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  // Quick Action Handlers for Applications
  const handleQuickCall = (app) => {
    if (app.phone) {
      window.open(`tel:${app.phone}`, '_self');
      // Auto-update status to Called
      handleApplicationStatusChange(app.id, 'Called');
    } else {
      toast.error('No phone number available');
    }
  };

  const handleQuickEmail = (app) => {
    setManualEmailData({
      to: app.email,
      subject: `Regarding your application for ${app.job_title}`,
      body: `Dear ${app.full_name},\n\nThank you for your application for the ${app.job_title} position at ${app.company_name}.\n\n`,
      applicantName: app.full_name,
      isHtml: false
    });
    setIsManualEmailDialogOpen(true);
  };

  const handleQuickShortlist = async (app) => {
    await handleApplicationStatusChange(app.id, 'Shortlisted');
  };

  const handleQuickReject = async (app) => {
    // Open rejection dialog instead of quick reject
    setRejectingApplication(app);
    setRejectionReason('');
    setCoolingPeriodDays(0);
    setIsRejectDialogOpen(true);
  };

  // Handle rejection with cooling period
  const handleRejectWithCoolingPeriod = async () => {
    if (!rejectingApplication) return;
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      let url = `${API}/job-applications/${rejectingApplication.id}?status=Rejected&send_email=${sendEmailOnStatusChange}`;
      if (rejectionReason) {
        url += `&rejection_reason=${encodeURIComponent(rejectionReason)}`;
      }
      if (coolingPeriodDays > 0) {
        url += `&cooling_period_days=${coolingPeriodDays}`;
      }
      
      const response = await axios.patch(url, {}, config);
      
      if (coolingPeriodDays > 0) {
        toast.success(`Application rejected with ${coolingPeriodDays}-day cooling period`);
      } else {
        toast.success('Application rejected');
      }
      
      // Update local state
      setAllApplications(prev => prev.map(app => 
        app.id === rejectingApplication.id ? { ...app, status: 'Rejected' } : app
      ));
      
      setIsRejectDialogOpen(false);
      setRejectingApplication(null);
      fetchNotifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject application');
    }
  };

  // Blacklist Functions
  const fetchBlacklist = async () => {
    setLoadingBlacklist(true);
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API}/admin/blacklist`, config);
      setBlacklist(res.data || []);
    } catch (e) {
      console.error('Error fetching blacklist:', e);
    } finally {
      setLoadingBlacklist(false);
    }
  };
  
  // Cooling Period Applications Functions
  const fetchCoolingPeriodApps = async () => {
    setLoadingCoolingPeriodApps(true);
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API}/admin/cooling-period-applications`, config);
      setCoolingPeriodApps(res.data || []);
    } catch (e) {
      console.error('Error fetching cooling period applications:', e);
    } finally {
      setLoadingCoolingPeriodApps(false);
    }
  };

  const handleBlacklistCandidate = async () => {
    if (!blacklistData.email || !blacklistData.reason) {
      toast.error('Please provide email and reason');
      return;
    }
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(
        `${API}/admin/blacklist?candidate_email=${encodeURIComponent(blacklistData.email)}&reason=${encodeURIComponent(blacklistData.reason)}${blacklistData.name ? `&candidate_name=${encodeURIComponent(blacklistData.name)}` : ''}`,
        {},
        config
      );
      toast.success(`${blacklistData.email} has been blacklisted`);
      setIsBlacklistDialogOpen(false);
      setBlacklistData({ email: '', name: '', reason: '' });
      fetchBlacklist();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to blacklist candidate');
    }
  };

  const handleRemoveFromBlacklist = async (entryId, email) => {
    if (!window.confirm(`Are you sure you want to remove ${email} from the blacklist?`)) return;
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API}/admin/blacklist/${entryId}`, config);
      toast.success('Removed from blacklist');
      fetchBlacklist();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove from blacklist');
    }
  };

  // Cooling Period Edit/Remove Functions
  const handleEditCoolingPeriod = async () => {
    if (!editCoolingPeriodDialog.app) return;
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.patch(
        `${API}/admin/cooling-period/${editCoolingPeriodDialog.app.id}?new_end_date=${encodeURIComponent(newCoolingEndDate)}`,
        {},
        config
      );
      toast.success('Cooling period updated successfully');
      setEditCoolingPeriodDialog({ open: false, app: null });
      setNewCoolingEndDate('');
      fetchCoolingPeriodApps();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update cooling period');
    }
  };

  const handleRemoveCoolingPeriod = async (appId, candidateName) => {
    if (!window.confirm(`Are you sure you want to remove the cooling period for ${candidateName}? They will be able to apply immediately.`)) return;
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.patch(`${API}/admin/cooling-period/${appId}?remove=true`, {}, config);
      toast.success('Cooling period removed successfully');
      fetchCoolingPeriodApps();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove cooling period');
    }
  };

  // Assessment Global Lock Functions
  const handleLockAssessmentForAll = async (assessmentId, assessmentName) => {
    if (!window.confirm(`Are you sure you want to lock "${assessmentName}" for ALL candidates?`)) return;
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/admin/skill-assessments/${assessmentId}/lock-all`, {}, config);
      toast.success(`"${assessmentName}" locked for all candidates`);
      fetchSkillAssessments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to lock assessment');
    }
  };

  const handleUnlockAssessmentForAll = async (assessmentId, assessmentName) => {
    if (!window.confirm(`Are you sure you want to unlock "${assessmentName}" for all candidates?`)) return;
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/admin/skill-assessments/${assessmentId}/unlock-all`, {}, config);
      toast.success(`"${assessmentName}" unlocked for all candidates`);
      fetchSkillAssessments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlock assessment');
    }
  };

  // Quick blacklist from application
  const handleQuickBlacklist = (app) => {
    setBlacklistData({
      email: app.email || app.applicant_email,
      name: app.full_name || app.referred_name || '',
      reason: ''
    });
    setIsBlacklistDialogOpen(true);
  };

  // New Follow-up Action Handlers
  const handleFollowUp = async (app) => {
    await handleApplicationStatusChange(app.id, 'Follow-Up');
    toast.success(`Follow-up scheduled for ${app.full_name}`);
  };

  const handleDidNotPickUp = async (app) => {
    await handleApplicationStatusChange(app.id, 'Did Not Pick Up');
  };

  const handleNotReachable = async (app) => {
    await handleApplicationStatusChange(app.id, 'Not Reachable');
  };

  // Update ACN (Actual Contact Number) for application
  const handleUpdateACN = async (applicationId, acn) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.put(`${API}/job-applications/${applicationId}`, {
        actual_contact_number: acn
      }, config);
      
      // Update local state
      setAllApplications(prev => 
        prev.map(app => 
          app.id === applicationId ? { ...app, actual_contact_number: acn } : app
        )
      );
      
      toast.success('ACN updated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update ACN');
    }
  };

  // Click to Call - opens phone dialer
  const handleClickToCall = (phone) => {
    if (phone) {
      window.location.href = `tel:${phone.replace(/\s+/g, '')}`;
    } else {
      toast.error('No phone number available');
    }
  };

  // Feedback Handler
  const handleOpenFeedback = (app) => {
    setFeedbackData({
      applicationId: app.id,
      applicantName: app.full_name,
      feedback: app.feedback || '',
      rating: app.rating || 0,
      feedback_by: app.feedback_by || '',
      feedback_at: app.feedback_at || ''
    });
    setIsFeedbackDialogOpen(true);
  };

  const handleSaveFeedback = async () => {
    setSavingFeedback(true);
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.put(`${API}/applications/${feedbackData.applicationId}/feedback`, {
        feedback: feedbackData.feedback,
        rating: feedbackData.rating
      }, config);
      
      // Optimistic state update - immediately update local state with new feedback/rating and history
      const now = new Date().toISOString();
      setAllApplications(prev => prev.map(app => 
        app.id === feedbackData.applicationId 
          ? { ...app, feedback: feedbackData.feedback, rating: feedbackData.rating, feedback_by: currentUser?.email, feedback_at: now }
          : app
      ));
      
      toast.success('Feedback saved successfully!');
      setIsFeedbackDialogOpen(false);
      fetchData(); // Still fetch to sync any other changes
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save feedback');
    } finally {
      setSavingFeedback(false);
    }
  };

  // Form Submission Status Update with Reason
  const handleSubmissionStatusUpdate = async (submissionId, newStatus, reason) => {
    try {
      const token = getAuthToken();
      await axios.patch(`${API}/form-submissions/${submissionId}/status`,
        { status: newStatus, status_reason: reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFormSubmissions(prev => prev.map(s =>
        s.id === submissionId ? { ...s, status: newStatus, status_reason: reason } : s
      ));
      toast.success('Status updated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Follow-up Email Handler
  const handleFollowUpEmail = (app) => {
    setManualEmailData({
      to: app.email,
      subject: `Follow-up: Your application for ${app.job_title}`,
      body: `Dear ${app.full_name},\n\nThis is a follow-up regarding your application for the ${app.job_title} position at ${app.company_name}.\n\nWe tried to reach you earlier but were unable to connect. Please let us know a suitable time for us to call you.\n\nBest regards,\nOfficium Connect Team`,
      applicantName: app.full_name,
      isHtml: false
    });
    setIsManualEmailDialogOpen(true);
  };

  // Insert Link Helper
  const insertHyperlink = () => {
    const url = prompt('Enter the URL:');
    const text = prompt('Enter the link text:');
    if (url && text) {
      const link = `<a href="${url}" style="color: #6366f1; text-decoration: underline;">${text}</a>`;
      setManualEmailData({
        ...manualEmailData,
        body: manualEmailData.body + link,
        isHtml: true
      });
    }
  };

  const handleSendManualEmail = async () => {
    setSendingManualEmail(true);
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      if (manualEmailData.emailType === 'bulk') {
        // Bulk email mode
        const recipients = manualEmailData.bulkRecipients
          .split('\n')
          .map(email => email.trim())
          .filter(email => email && email.includes('@'))
          .map(email => ({ email, name: email.split('@')[0] }));
        
        if (recipients.length === 0) {
          toast.error('Please enter valid email addresses');
          setSendingManualEmail(false);
          return;
        }
        
        const response = await axios.post(`${API}/email/send-bulk`, {
          recipients,
          subject: manualEmailData.subject,
          message: manualEmailData.body
        }, config);
        
        toast.success(`Email sent to ${response.data.sent_count} recipients!`);
      } else {
        // Single email mode - default sender keeps the existing no-reply flow untouched
        if (selectedEmailSender && selectedEmailSender !== 'default') {
          await axios.post(`${API}/mailboxes/${selectedEmailSender}/send`, {
            to: manualEmailData.to.split(',').map(a => a.trim()).filter(Boolean),
            subject: manualEmailData.subject,
            body: manualEmailData.body,
            is_html: !!manualEmailData.isHtml
          }, config);
          const sender = emailSenders.find(s => s.id === selectedEmailSender);
          toast.success(`Email sent from ${sender?.email_address || 'mailbox'}`);
        } else {
          await axios.post(`${API}/send-manual-email`, {
            to: manualEmailData.to,
            subject: manualEmailData.subject,
            body: manualEmailData.body,
            is_html: manualEmailData.isHtml
          }, config);
          toast.success('Email sent successfully!');
        }
      }
      
      setIsManualEmailDialogOpen(false);
      setManualEmailData({ to: '', subject: '', body: '', applicantName: '', isHtml: false, emailType: 'single', bulkRecipients: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingManualEmail(false);
    }
  };

  // Delete Application Handler
  const handleDeleteApplication = async (app) => {
    if (!window.confirm(`Are you sure you want to permanently delete ${app.full_name}'s application? This cannot be undone.`)) {
      return;
    }
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // Remove from local state immediately for better UX
      setAllApplications(prev => prev.filter(a => a.id !== app.id));
      
      await axios.delete(`${API}/job-applications/${app.id}`, config);
      toast.success('Application deleted successfully');
    } catch (error) {
      // Revert by adding the app back on error
      setAllApplications(prev => [...prev, app]);
      toast.error(error.response?.data?.detail || 'Failed to delete application');
    }
  };

  // Archive Application Handler
  const handleArchiveApplication = async (applicationId) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // Make API call first to ensure it succeeds
      const response = await axios.put(`${API}/job-applications/${applicationId}`, { is_archived: true }, config);
      
      if (response.status === 200) {
        // Only update local state after successful API call
        setAllApplications(prev => prev.map(app => 
          app.id === applicationId ? { ...app, is_archived: true } : app
        ));
        toast.success('Application archived successfully');
      }
    } catch (error) {
      console.error('Archive error:', error);
      toast.error(error.response?.data?.detail || 'Failed to archive application');
    }
  };

  // Restore Application from Archive
  const handleRestoreApplication = async (applicationId) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // Make API call first to ensure it succeeds
      const response = await axios.put(`${API}/job-applications/${applicationId}`, { is_archived: false }, config);
      
      if (response.status === 200) {
        // Only update local state after successful API call
        setAllApplications(prev => prev.map(app => 
          app.id === applicationId ? { ...app, is_archived: false } : app
        ));
        toast.success('Application restored successfully');
      }
    } catch (error) {
      console.error('Restore error:', error);
      toast.error(error.response?.data?.detail || 'Failed to restore application');
    }
  };

  // Open Edit Application Dialog (Admin Only)
  const handleOpenEditApplication = (app) => {
    setEditingApplication(app);
    setEditApplicationForm({
      full_name: app.full_name || '',
      email: app.email || '',
      phone: app.phone || '',
      actual_contact_number: app.actual_contact_number || '',
      current_city_state: app.current_city_state || '',
      aadhar_address: app.aadhar_address || '',
      education: app.education || '',
      education_other: app.education_other || '',
      university_name: app.university_name || '',
      college_name: app.college_name || '',
      total_experience: app.total_experience || '',
      previous_company_name: app.previous_company_name || '',
      previous_lpa: app.previous_lpa || '',
      expected_salary: app.expected_salary || '',
      hear_about_us: app.hear_about_us || '',
      skills: app.skills || '',
      current_company: app.current_company || '',
      current_position: app.current_position || '',
      notes: app.notes || ''
    });
    setIsEditApplicationDialogOpen(true);
  };

  // Save Edited Application (Admin Only)
  const handleSaveEditedApplication = async () => {
    if (!editingApplication) return;
    
    setSavingApplicationEdit(true);
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } };
      
      await axios.put(`${API}/job-applications/${editingApplication.id}`, editApplicationForm, config);
      
      // Update local state
      setAllApplications(prev => prev.map(app => 
        app.id === editingApplication.id ? { ...app, ...editApplicationForm } : app
      ));
      
      toast.success('Application details updated successfully');
      setIsEditApplicationDialogOpen(false);
      setEditingApplication(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update application');
    } finally {
      setSavingApplicationEdit(false);
    }
  };

  // Bulk Email Functions
  const toggleSelectForBulkEmail = (application) => {
    setSelectedForBulkEmail(prev => {
      const isSelected = prev.some(app => app.id === application.id);
      if (isSelected) {
        return prev.filter(app => app.id !== application.id);
      } else {
        return [...prev, application];
      }
    });
  };

  const selectAllForBulkEmail = () => {
    const visibleApps = filteredApplications.filter(app => !app.is_archived);
    if (selectedForBulkEmail.length === visibleApps.length) {
      setSelectedForBulkEmail([]);
    } else {
      setSelectedForBulkEmail(visibleApps);
    }
  };

  const handleSendBulkEmail = async () => {
    if (selectedForBulkEmail.length === 0) {
      toast.error('Please select at least one candidate');
      return;
    }
    if (!bulkEmailData.subject.trim() || !bulkEmailData.message.trim()) {
      toast.error('Please fill in subject and message');
      return;
    }

    setSendingBulkEmail(true);
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const recipients = selectedForBulkEmail.map(app => ({
        email: app.email,
        name: app.full_name,
        application_id: app.id
      }));

      const response = await axios.post(`${API}/email/send-bulk`, {
        recipients,
        subject: bulkEmailData.subject,
        message: bulkEmailData.message,
        template_id: bulkEmailData.useTemplate ? bulkEmailData.templateId : null
      }, config);

      toast.success(`Bulk email sent to ${response.data.sent_count} recipients!`);
      setIsBulkEmailDialogOpen(false);
      setBulkEmailData({ subject: '', message: '', useTemplate: false, templateId: '' });
      setSelectedForBulkEmail([]);
      setIsBulkEmailMode(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send bulk email');
    } finally {
      setSendingBulkEmail(false);
    }
  };

  // Popup Announcements Functions
  const fetchPopupAnnouncements = async () => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/popup-announcements`, config);
      setPopupAnnouncements(response.data);
    } catch (error) {
      console.error('Failed to fetch popup announcements:', error);
    }
  };

  // Footer Config Functions
  const fetchFooterConfig = async () => {
    try {
      const response = await axios.get(`${API}/footer-config`);
      if (response.data) {
        setFooterConfig(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Failed to fetch footer config:', error);
    }
  };

  // Fetch Visitor Analytics
  const fetchVisitorAnalytics = async () => {
    setLoadingAnalytics(true);
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/admin/visitor-analytics`, config);
      setVisitorAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch visitor analytics:', error);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  // Fetch App Visits Analytics
  const fetchAppVisitsAnalytics = async (period = '7d') => {
    setLoadingAppVisits(true);
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/admin/app-visits-analytics?period=${period}`, config);
      setAppVisitsData(response.data);
    } catch (error) {
      console.error('Failed to fetch app visits analytics:', error);
    } finally {
      setLoadingAppVisits(false);
    }
  };

  // Telesales CRM Functions
  const fetchTelesalesLeads = async () => {
    setLoadingLeads(true);
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/leads`, config);
      setTelesalesLeads(response.data || []);
    } catch (error) {
      console.error('Failed to fetch leads:', error);
    } finally {
      setLoadingLeads(false);
    }
  };

  const fetchTelesalesAnalytics = async () => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/telesales/analytics`, config);
      setTelesalesAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch telesales analytics:', error);
    }
  };

  const fetchTelesalesMembers = async () => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/telesales/members`, config);
      setTelesalesMembers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch telesales members:', error);
    }
  };

  const handleAssignLead = async (leadId, assignedTo) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.patch(`${API}/leads/${leadId}/assign?assigned_to=${encodeURIComponent(assignedTo)}`, {}, config);
      toast.success('Lead assigned successfully');
      fetchTelesalesLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign lead');
    }
  };

  const handleUpdateLeadStatus = async () => {
    if (!selectedLead || !leadStatusData.status) return;
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.patch(`${API}/leads/${selectedLead.id}/status`, leadStatusData, config);
      toast.success(`Lead marked as ${leadStatusData.status}`);
      setLeadStatusDialog(false);
      setSelectedLead(null);
      setLeadStatusData({ status: '', feedback: '', follow_up_date: '' });
      fetchTelesalesLeads();
      fetchTelesalesAnalytics();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update lead status');
    }
  };

  const openLeadStatusDialog = (lead, status) => {
    setSelectedLead(lead);
    setLeadStatusData({ status, feedback: '', follow_up_date: '' });
    setLeadStatusDialog(true);
  };

  const filteredLeads = telesalesLeads.filter(lead => {
    const matchesSearch = leadSearchQuery === '' || 
      lead.name?.toLowerCase().includes(leadSearchQuery.toLowerCase()) ||
      lead.email?.toLowerCase().includes(leadSearchQuery.toLowerCase()) ||
      lead.phone?.includes(leadSearchQuery);
    const matchesStatus = leadFilterStatus === 'all' || lead.status === leadFilterStatus;
    return matchesSearch && matchesStatus;
  });

  // Handle user role change (Admin only)
  const handleChangeUserRole = async (userId, newRole) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.patch(`${API}/admin/users/${userId}/role?role=${encodeURIComponent(newRole)}`, {}, config);
      toast.success(`User role changed to ${newRole.replace('_', ' ')}`);
      // Refresh both users lists
      const usersRes = await axios.get(`${API}/admin/users`, config);
      setUsers(usersRes.data);
      fetchAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change user role');
    }
  };

  // Fetch all users including candidates for User Management tab
  const fetchAllUsers = async () => {
    setLoadingUsers(true);
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/admin/all-users`, config);
      setAllUsers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch all users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Toggle admin status
  const handleToggleAdmin = async (userId, currentIsAdmin) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const newRole = currentIsAdmin ? 'user' : 'admin';
      await axios.patch(`${API}/admin/users/${userId}/role?role=${newRole}`, {}, config);
      toast.success(currentIsAdmin ? 'Admin privileges removed' : 'Admin privileges granted');
      fetchAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle admin status');
    }
  };

  // Toggle Questions Tab Access
  const handleToggleQuestionsAccess = async (userId, currentAccess) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const grant = !currentAccess;
      await axios.patch(`${API}/admin/users/${userId}/questions-access?grant=${grant}`, {}, config);
      toast.success(grant ? 'Questions tab access granted' : 'Questions tab access revoked');
      fetchAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle questions access');
    }
  };

  // Delete user
  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete user "${userName}"? This action cannot be undone.`)) {
      return;
    }
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API}/admin/users/${userId}`, config);
      toast.success('User deleted successfully');
      fetchAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  // Revoke user access (set to pending)
  const handleRevokeAccess = async (userId) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.patch(`${API}/admin/users/${userId}/revoke`, {}, config);
      toast.success('User access revoked');
      fetchAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke access');
    }
  };

  // Open tab permissions dialog
  const handleOpenTabPermissions = (user) => {
    setEditingUserPermissions(user);
    // Initialize with user's current tab permissions or empty array
    setSelectedTabPermissions(user.tab_permissions || []);
    setTabPermissionsDialogOpen(true);
  };

  // Save tab permissions
  const handleSaveTabPermissions = async () => {
    if (!editingUserPermissions) return;
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.patch(`${API}/admin/users/${editingUserPermissions.id}/tab-permissions`, {
        tab_permissions: selectedTabPermissions
      }, config);
      toast.success('Tab permissions updated successfully');
      setTabPermissionsDialogOpen(false);
      setEditingUserPermissions(null);
      fetchAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update tab permissions');
    }
  };

  // Toggle single tab permission
  const handleToggleTabPermission = (tabId) => {
    setSelectedTabPermissions(prev => 
      prev.includes(tabId) 
        ? prev.filter(id => id !== tabId)
        : [...prev, tabId]
    );
  };

  // Select/Deselect all tabs
  const handleSelectAllTabs = (selectAll) => {
    if (selectAll) {
      setSelectedTabPermissions(ALL_TABS.map(tab => tab.id));
    } else {
      setSelectedTabPermissions([]);
    }
  };

  // Grammar Check Handler
  const handleGrammarCheck = async () => {
    if (!grammarText.trim()) {
      toast.error('Please enter some text to check');
      return;
    }
    
    setGrammarLoading(true);
    setGrammarResult(null);
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.post(`${API}/grammar/check`, {
        text: grammarText,
        mode: grammarMode
      }, config);
      
      setGrammarResult(response.data);
      toast.success('Grammar check complete!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Grammar check failed');
    } finally {
      setGrammarLoading(false);
    }
  };

  // Copy corrected text to clipboard
  const handleCopyGrammarResult = () => {
    if (grammarResult?.corrected) {
      navigator.clipboard.writeText(grammarResult.corrected);
      toast.success('Copied to clipboard!');
    }
  };

  // Apply corrected text back to input
  const handleApplyCorrection = () => {
    if (grammarResult?.corrected) {
      setGrammarText(grammarResult.corrected);
      setGrammarResult(null);
      toast.success('Correction applied!');
    }
  };

  // Filter users by search
  const filteredAllUsers = allUsers.filter(user => {
    if (!userSearchQuery) return true;
    const query = userSearchQuery.trim().toLowerCase();
    return [user.id, user.name, user.email, user.phone, user.role]
      .some(value => String(value ?? '').toLowerCase().includes(query));
  });

  // Position Request Functions
  const fetchPositionRequests = async () => {
    setLoadingPositionRequests(true);
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/position-requests`, config);
      setPositionRequests(response.data || []);
    } catch (error) {
      console.error('Failed to fetch position requests:', error);
    } finally {
      setLoadingPositionRequests(false);
    }
  };

  const handleUpdatePositionRequestStatus = async (requestId, status, adminNotes = '') => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.patch(`${API}/position-requests/${requestId}/status?status=${encodeURIComponent(status)}${adminNotes ? `&admin_notes=${encodeURIComponent(adminNotes)}` : ''}`, {}, config);
      toast.success(`Request marked as ${status}`);
      fetchPositionRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update request');
    }
  };

  const handleDeletePositionRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this request?')) return;
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API}/position-requests/${requestId}`, config);
      toast.success('Request deleted');
      fetchPositionRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete request');
    }
  };

  const filteredPositionRequests = positionRequests.filter(req => {
    if (!positionRequestSearch) return true;
    const query = positionRequestSearch.toLowerCase();
    return (
      req.candidate_name?.toLowerCase().includes(query) ||
      req.company_name?.toLowerCase().includes(query) ||
      req.position_name?.toLowerCase().includes(query) ||
      req.requester_email?.toLowerCase().includes(query)
    );
  });

  // Interview Questions Functions (Admin & CRM Only)
  const fetchInterviewQuestions = async () => {
    setLoadingQuestions(true);
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // Use endpoint with lock status for non-admin users, regular endpoint for admins
      const endpoint = currentUser?.is_admin 
        ? `${API}/interview-questions` 
        : `${API}/interview-questions/candidate/with-lock-status`;
      
      const response = await axios.get(endpoint, config);
      setInterviewQuestions(response.data || []);
      
      // Extract custom categories from existing questions (not in predefined list)
      const existingCategories = new Set();
      (response.data || []).forEach(q => {
        if (q.category && !PREDEFINED_CATEGORIES.includes(q.category)) {
          existingCategories.add(q.category);
        }
      });
      setCustomCategories(Array.from(existingCategories).sort());
      
      // Also fetch topic images
      try {
        const imagesResponse = await axios.get(`${API}/topic-images`, config);
        const imagesMap = {};
        (imagesResponse.data || []).forEach(img => {
          imagesMap[img.topic_key] = img.image;
        });
        setTopicImages(imagesMap);
      } catch (imgError) {
        console.error('Failed to fetch topic images:', imgError);
      }
      
      // Fetch category-topic mappings
      try {
        const mappingsResponse = await axios.get(`${API}/category-topic-mappings`, config);
        if (mappingsResponse.data?.mappings) {
          setCategoryTopicMapping(mappingsResponse.data.mappings);
        }
      } catch (mappingError) {
        console.error('Failed to fetch category-topic mappings:', mappingError);
      }
    } catch (error) {
      console.error('Failed to fetch interview questions:', error);
    } finally {
      setLoadingQuestions(false);
    }
  };

  // Save category-topic mappings to backend
  const saveCategoryTopicMapping = async (newMapping) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.put(`${API}/category-topic-mappings`, { mappings: newMapping }, config);
    } catch (error) {
      console.error('Failed to save category-topic mappings:', error);
      toast.error('Failed to save topic assignments');
    }
  };

  // Handle topic image upload
  const handleTopicImageUpload = async (topicKey, file) => {
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB');
      return;
    }
    
    setUploadingTopicImage(topicKey);
    
    // Create a timeout Promise
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Upload timed out after 30 seconds')), 30000);
    });
    
    try {
      // Convert to base64 and resize with timeout
      const imageProcessPromise = new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const img = new Image();
          img.onload = () => {
            try {
              // Resize to 100x100 max for icons
              const canvas = document.createElement('canvas');
              const maxSize = 100;
              let width = img.width;
              let height = img.height;
              
              if (width > height) {
                if (width > maxSize) {
                  height *= maxSize / width;
                  width = maxSize;
                }
              } else {
                if (height > maxSize) {
                  width *= maxSize / height;
                  height = maxSize;
                }
              }
              
              canvas.width = width;
              canvas.height = height;
              const ctx = canvas.getContext('2d');
              ctx.drawImage(img, 0, 0, width, height);
              
              const result = canvas.toDataURL('image/jpeg', 0.8);
              resolve(result);
            } catch (err) {
              reject(err);
            }
          };
          img.onerror = () => reject(new Error('Failed to load image'));
          img.src = reader.result;
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
      });
      
      const compressedBase64 = await Promise.race([imageProcessPromise, timeoutPromise]);
      
      // Save to backend
      const token = getAuthToken();
      const config = { 
        headers: { Authorization: `Bearer ${token}` },
        timeout: 25000
      };
      
      await axios.put(`${API}/topic-images/${encodeURIComponent(topicKey)}`, {
        topic_key: topicKey,
        image: compressedBase64
      }, config);
      
      // Update local state
      setTopicImages(prev => ({
        ...prev,
        [topicKey]: compressedBase64
      }));
      
      toast.success('Topic image updated!');
    } catch (error) {
      console.error('Topic image upload error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to save topic image';
      toast.error(errorMessage);
    } finally {
      // Always clear the loading state
      setUploadingTopicImage(null);
    }
  };

  // Handle topic image removal
  const handleTopicImageRemove = async (topicKey) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API}/topic-images/${encodeURIComponent(topicKey)}`, config);
      
      // Update local state
      setTopicImages(prev => {
        const newImages = { ...prev };
        delete newImages[topicKey];
        return newImages;
      });
      
      toast.success('Topic image removed!');
    } catch (error) {
      toast.error('Failed to remove topic image');
    }
  };

  // Fetch Skill Assessments
  const fetchSkillAssessments = async () => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API}/admin/skill-assessments`, config);
      setSkillAssessments(res.data || []);
    } catch (e) {
      console.error('Error fetching assessments:', e);
    }
  };

  // Create or Update assessment
  const handleCreateAssessment = async () => {
    if (!newAssessment.skill_name || newAssessment.questions.length < 3) {
      toast.error('Please provide skill name and at least 3 questions');
      return;
    }
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      if (editingAssessmentId) {
        // Update existing assessment
        await axios.put(`${API}/admin/skill-assessments/${editingAssessmentId}`, newAssessment, config);
        toast.success('Assessment updated successfully!');
      } else {
        // Create new assessment
        await axios.post(`${API}/admin/skill-assessments`, newAssessment, config);
        toast.success('Assessment created successfully!');
      }
      
      setAssessmentDialogOpen(false);
      setEditingAssessmentId(null);
      setNewAssessment({ skill_name: '', description: '', passing_score: 70, time_limit_minutes: 15, questions: [] });
      fetchSkillAssessments();
    } catch (error) {
      // Handle validation errors properly
      const errorDetail = error.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        const messages = errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        toast.error(messages || 'Validation error');
      } else if (typeof errorDetail === 'object') {
        toast.error(errorDetail.msg || errorDetail.message || 'Failed to save assessment');
      } else {
        toast.error(errorDetail || 'Failed to save assessment');
      }
    }
  };

  // Delete assessment
  const handleDeleteAssessment = async (assessmentId) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API}/admin/skill-assessments/${assessmentId}`, config);
      toast.success('Assessment deleted successfully!');
      setDeleteAssessmentConfirm(null);
      fetchSkillAssessments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete assessment');
    }
  };

  // Edit assessment - open dialog with pre-filled data
  const handleEditAssessment = (assessment) => {
    setNewAssessment({
      skill_name: assessment.skill_name || '',
      description: assessment.description || '',
      passing_score: assessment.passing_score || 70,
      time_limit_minutes: assessment.time_limit_minutes || 15,
      questions: assessment.questions || []
    });
    setEditingAssessmentId(assessment.id);
    setAssessmentDialogOpen(true);
  };

  // Add question to current assessment
  const addQuestionToAssessment = (type = 'mcq') => {
    const newQ = {
      question: '',
      question_type: type, // 'mcq' or 'theoretical'
      options: type === 'mcq' ? ['', '', '', ''] : [],
      correct_answer: type === 'mcq' ? 0 : null,
      model_answer: type === 'theoretical' ? '' : null,
      max_marks: type === 'theoretical' ? 5 : 1
    };
    setNewAssessment(prev => ({
      ...prev,
      questions: [...prev.questions, newQ]
    }));
  };

  // Remove question from assessment
  const removeQuestionFromAssessment = (index) => {
    setNewAssessment(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index)
    }));
  };

  // Lock/Unlock Assessment for User
  const handleLockAssessment = async (assessmentId, email, skillName) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/admin/skill-assessments/${assessmentId}/lock?email=${encodeURIComponent(email)}`, {}, config);
      toast.success(`Assessment "${skillName}" locked for ${email}`);
      fetchSkillAssessments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to lock assessment');
    }
  };

  const handleUnlockAssessment = async (assessmentId, email, skillName) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/admin/skill-assessments/${assessmentId}/unlock?email=${encodeURIComponent(email)}`, {}, config);
      toast.success(`Assessment "${skillName}" unlocked for ${email}`);
      fetchSkillAssessments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlock assessment');
    }
  };

  // Fetch Career Paths
  const fetchCareerPaths = async () => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API}/admin/career-paths`, config);
      setCareerPaths(res.data || []);
    } catch (e) {
      console.error('Error fetching career paths:', e);
    }
  };

  // Create new career path
  const handleCreateCareerPath = async () => {
    if (!newCareerPath.domain || newCareerPath.steps.length < 2) {
      toast.error('Please provide domain name and at least 2 career stages');
      return;
    }
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/admin/career-paths`, newCareerPath, config);
      toast.success('Career path created successfully!');
      setCareerPathDialogOpen(false);
      setNewCareerPath({ domain: '', description: '', steps: [] });
      fetchCareerPaths();
    } catch (error) {
      // Handle validation errors properly
      const errorDetail = error.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        // Pydantic validation error - extract message
        const messages = errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        toast.error(messages || 'Validation error');
      } else if (typeof errorDetail === 'object') {
        toast.error(errorDetail.msg || errorDetail.message || 'Failed to create career path');
      } else {
        toast.error(errorDetail || 'Failed to create career path');
      }
    }
  };

  // Add step to career path
  const addStepToCareerPath = () => {
    const newStep = {
      title: '',
      level: String(newCareerPath.steps.length + 1),
      skills: [],
      salary_range: '',
      years_experience: ''
    };
    setNewCareerPath(prev => ({
      ...prev,
      steps: [...prev.steps, newStep]
    }));
  };

  // Remove step from career path
  const removeStepFromCareerPath = (index) => {
    setNewCareerPath(prev => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== index)
    }));
  };

  // Fetch Assessment Results (Admin)
  const fetchAssessmentResults = async () => {
    setLoadingAssessmentResults(true);
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API}/admin/assessment-results`, config);
      setAssessmentResults(res.data || []);
    } catch (e) {
      console.error('Error fetching assessment results:', e);
    } finally {
      setLoadingAssessmentResults(false);
    }
  };

  // Reassign Assessment (Allow Retake)
  const handleReassignAssessment = async (candidateEmail, assessmentId, skillName) => {
    if (!window.confirm(`Are you sure you want to allow ${candidateEmail} to retake the "${skillName}" assessment? Their previous score will be deleted.`)) {
      return;
    }
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/admin/reassign-assessment?candidate_email=${encodeURIComponent(candidateEmail)}&assessment_id=${assessmentId}`, {}, config);
      toast.success(`Assessment reassigned! ${candidateEmail} can now retake the test.`);
      fetchAssessmentResults();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reassign assessment');
    }
  };

  // Handle adding a new custom category
  const handleAddCustomCategory = () => {
    const trimmedName = newCategoryName.trim();
    if (!trimmedName) {
      toast.error('Please enter a category name');
      return;
    }
    
    // Check if category already exists
    const allCategories = [...PREDEFINED_CATEGORIES, ...customCategories];
    if (allCategories.some(c => c.toLowerCase() === trimmedName.toLowerCase())) {
      toast.error('This category already exists');
      return;
    }
    
    // Add to custom categories
    setCustomCategories(prev => [...prev, trimmedName].sort());
    // Set as selected category
    setNewQuestion(prev => ({ ...prev, category: trimmedName }));
    // Reset input
    setNewCategoryName('');
    setShowNewCategoryInput(false);
    toast.success(`Category "${trimmedName}" created!`);
  };

  const handleCreateQuestion = async (e, addAnother = false) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!newQuestion.question.trim()) {
      toast.error('Question text is required');
      return;
    }
    
    // Check if using custom topic (either 'other' or 'custom_topic:*')
    const isCustomTopic = newQuestion.job_id === 'other' || newQuestion.job_id.startsWith('custom_topic:');
    
    // Extract custom topic name - memoize to avoid recalculation
    const customTopicName = isCustomTopic 
      ? (newQuestion.job_id.startsWith('custom_topic:') 
          ? newQuestion.job_id.replace('custom_topic:', '')
          : newQuestion.custom_headline?.trim())
      : null;
    
    // Validate custom headline if using custom topic
    if (isCustomTopic && !customTopicName) {
      toast.error('Please enter a custom topic/headline');
      return;
    }
    
    // Determine job_title for the request
    // For custom topics, use the custom topic name
    // For regular jobs, get the job title from jobUpdates
    let jobTitle = undefined;
    let actualJobId = newQuestion.job_id;
    
    if (isCustomTopic) {
      jobTitle = customTopicName;
      actualJobId = '';  // Clear job_id for custom topics
    } else if (newQuestion.job_id && newQuestion.job_id !== '') {
      // Find the job in jobUpdates and get its title
      const selectedJob = jobUpdates.find(j => j.id === newQuestion.job_id);
      if (selectedJob) {
        jobTitle = selectedJob.position_name;
      }
    }
    
    // Final validation: ensure job_title is not empty for custom topics
    if (isCustomTopic && (!jobTitle || jobTitle.trim() === '')) {
      toast.error('Custom topic name cannot be empty');
      return;
    }
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // Prepare question data - use computed job_title and job_id
      const questionData = {
        ...newQuestion,
        job_title: jobTitle,
        job_id: actualJobId  // Use actual job_id (empty string for custom topics)
      };
      
      // Remove custom_headline before sending to backend
      delete questionData.custom_headline;
      
      await axios.post(`${API}/interview-questions`, questionData, config);
      
      // Save states for "Add Another" - preserve custom topic format using already computed values
      const savedJobId = isCustomTopic ? `custom_topic:${customTopicName}` : newQuestion.job_id;
      
      if (addAnother) {
        // Keep the dialog open, reset form but keep job_id/custom topic
        const newCount = questionsAddedCount + 1;
        setQuestionsAddedCount(newCount);
        setAddAnotherMode(true);
        setLastAddedJobId(savedJobId);
        toast.success(`Question ${newCount} added! Add another question.`);
        
        // Switch filter to show the newly added question's category
        if (newQuestion.category && newQuestion.category !== questionFilterCategory) {
          setQuestionFilterCategory(newQuestion.category);
        }
        
        setNewQuestion({
          question: '',
          category: newQuestion.category, // Keep the same category for convenience
          difficulty: 'Medium',
          expected_answer: '',
          hints: '',
          job_id: savedJobId,  // Keep the same job role or custom topic
          custom_headline: customTopicName || '',  // Keep custom headline for display
          tags: [],
          is_active: true,
          image_url: '',
          question_type: 'text',
          options: ['', '', '', ''],
          correct_option: null,
          time_limit: null,
          points: 1,
          permitted_emails: [],
          is_public: false
        });
        setPermissionEmailInput('');
        setQuestionImagePreviews([]);
        fetchInterviewQuestions();
      } else {
        // Close dialog completely
        const totalAdded = questionsAddedCount + 1;
        toast.success(totalAdded > 1 ? `${totalAdded} questions created successfully!` : 'Question created successfully!');
        
        // Switch filter to show the newly added question's category
        if (newQuestion.category) {
          setQuestionFilterCategory(newQuestion.category);
          toast.info(`Showing "${newQuestion.category}" category`);
        }
        
        setIsQuestionDialogOpen(false);
        setAddAnotherMode(false);
        setLastAddedJobId('');
        setQuestionsAddedCount(0);
        setNewQuestion({
          question: '',
          category: 'General',
          difficulty: 'Medium',
          expected_answer: '',
          hints: '',
          job_id: '',
          custom_headline: '',
          tags: [],
          is_active: true,
          image_url: '',
          images: [],
          question_type: 'text',
          options: ['', '', '', ''],
          correct_option: null,
          time_limit: null,
          points: 1,
          permitted_emails: [],
          is_public: false
        });
        setPermissionEmailInput('');
        setQuestionImagePreviews([]);
        fetchInterviewQuestions();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create question');
    }
  };

  const handleUpdateQuestion = async (e) => {
    e.preventDefault();
    if (!editingQuestion) return;
    
    // Check if using custom topic (either 'other' or 'custom_topic:*')
    const isCustomTopic = newQuestion.job_id === 'other' || newQuestion.job_id.startsWith('custom_topic:');
    const customTopicName = newQuestion.job_id.startsWith('custom_topic:') 
      ? newQuestion.job_id.replace('custom_topic:', '')
      : newQuestion.custom_headline?.trim();
    
    // Validate custom headline if using custom topic
    if (isCustomTopic && !customTopicName) {
      toast.error('Please enter a custom topic/headline');
      return;
    }
    
    // Determine job_title for the update
    // For custom topics, use the custom topic name
    // For regular jobs, get the job title from jobUpdates
    let jobTitle = undefined;
    let actualJobId = newQuestion.job_id;
    
    if (isCustomTopic) {
      jobTitle = customTopicName;
      actualJobId = '';  // Clear job_id for custom topics
    } else if (newQuestion.job_id && newQuestion.job_id !== '') {
      // Find the job in jobUpdates and get its title
      const selectedJob = jobUpdates.find(j => j.id === newQuestion.job_id);
      if (selectedJob) {
        jobTitle = selectedJob.position_name;
      }
    }
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // Prepare update data - use computed job_title and job_id
      const updateData = {
        ...newQuestion,
        job_title: jobTitle,
        job_id: actualJobId
      };
      
      // Remove the custom_headline before sending
      delete updateData.custom_headline;
      
      await axios.put(`${API}/interview-questions/${editingQuestion.id}`, updateData, config);
      toast.success('Question updated');
      
      // If category was changed, update the filter to show the question in its new location
      if (updateData.category && updateData.category !== editingQuestion.category) {
        setQuestionFilterCategory(updateData.category);
        toast.info(`Switched to "${updateData.category}" category`);
      }
      
      setIsQuestionDialogOpen(false);
      setEditingQuestion(null);
      setNewQuestion({
        question: '',
        category: 'General',
        difficulty: 'Medium',
        expected_answer: '',
        hints: '',
        job_id: '',
        custom_headline: '',
        tags: [],
        is_active: true,
        image_url: '',
        images: [],
        question_type: 'text',
        options: ['', '', '', ''],
        correct_option: null,
        time_limit: null,
        points: 1,
        permitted_emails: [],
        is_public: false
      });
      setPermissionEmailInput('');
      setQuestionImagePreviews([]);
      fetchInterviewQuestions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update question');
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question? This will also delete all related evaluations.')) return;
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API}/interview-questions/${questionId}`, config);
      toast.success('Question deleted');
      fetchInterviewQuestions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete question');
    }
  };

  // Delete entire group of questions
  const handleDeleteQuestionGroup = async (questionIds) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      let deleted = 0;
      for (const questionId of questionIds) {
        try {
          await axios.delete(`${API}/interview-questions/${questionId}`, config);
          deleted++;
        } catch (error) {
          console.error(`Failed to delete question ${questionId}:`, error);
        }
      }
      
      toast.success(`Deleted ${deleted} question${deleted !== 1 ? 's' : ''} successfully`);
      fetchInterviewQuestions();
    } catch (error) {
      toast.error('Failed to delete questions');
    }
  };

  // Grant access to a question and send email
  const handleGrantAccess = async () => {
    if (!giveAccessQuestion || !giveAccessEmail.trim()) return;
    
    const email = giveAccessEmail.trim().toLowerCase();
    
    setIsGrantingAccess(true);
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // Check if granting access to entire role
      if (giveAccessQuestion.isRoleAccess && giveAccessQuestion.questions) {
        // Use the new bulk endpoint - sends ONE email with all questions
        const questionIds = giveAccessQuestion.questions
          .filter(q => !q.permitted_emails?.includes(email))
          .map(q => q.id);
        
        if (questionIds.length === 0) {
          toast.info('All questions already shared with this email');
          setIsGrantingAccess(false);
          return;
        }
        
        const response = await axios.post(`${API}/interview-questions/grant-role-access`, {
          email: email,
          job_title: giveAccessQuestion.jobTitle,
          question_ids: questionIds
        }, config);
        
        toast.success(`Access granted to ${response.data.questions_count} questions! One email sent to ${email}`);
      } else {
        // Single question access
        if (giveAccessQuestion.permitted_emails?.includes(email)) {
          toast.error('This question is already shared with this email');
          setIsGrantingAccess(false);
          return;
        }
        
        await axios.post(`${API}/interview-questions/${giveAccessQuestion.id}/grant-access`, { email }, config);
        toast.success(`Access granted! Email sent to ${email}`);
      }
      
      setIsGiveAccessDialogOpen(false);
      setGiveAccessEmail('');
      setGiveAccessQuestion(null);
      fetchInterviewQuestions(); // Refresh to show updated shared count
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant access');
    } finally {
      setIsGrantingAccess(false);
    }
  };

  // Handle revoking access to questions (different from user access revocation)
  const handleRevokeQuestionAccess = async () => {
    if (!revokeAccessData || !revokeAccessEmail.trim()) return;
    
    const email = revokeAccessEmail.trim().toLowerCase();
    
    setIsRevokingAccess(true);
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // Revoke access to all questions in the role
      const questionIds = revokeAccessData.questions
        .filter(q => q.permitted_emails?.some(e => e.toLowerCase() === email))
        .map(q => q.id);
      
      if (questionIds.length === 0) {
        toast.info('This email does not have access to any questions in this role');
        setIsRevokingAccess(false);
        return;
      }
      
      const response = await axios.post(`${API}/interview-questions/revoke-role-access`, {
        email: email,
        job_title: revokeAccessData.jobTitle,
        question_ids: questionIds
      }, config);
      
      toast.success(`Access revoked for ${response.data.revoked_count} questions`);
      
      setIsRevokeAccessDialogOpen(false);
      setRevokeAccessEmail('');
      setRevokeAccessData(null);
      fetchInterviewQuestions(); // Refresh to show updated shared count
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke access');
    } finally {
      setIsRevokingAccess(false);
    }
  };

  // Lock/Unlock Question Group for a user
  const handleLockUnlockGroup = async () => {
    if (!lockGroupData || !lockGroupEmail.trim()) return;
    
    const email = lockGroupEmail.trim().toLowerCase();
    const isLocking = lockGroupData.mode === 'lock';
    
    setIsLockingGroup(true);
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const questionIds = lockGroupData.questions.map(q => q.id);
      const endpoint = isLocking ? 'lock' : 'unlock';
      
      const response = await axios.post(`${API}/interview-questions/group/${endpoint}`, {
        question_ids: questionIds,
        email: email
      }, config);
      
      toast.success(isLocking 
        ? `Question group locked for ${email}` 
        : `Question group unlocked for ${email}`
      );
      
      setIsLockGroupDialogOpen(false);
      setLockGroupEmail('');
      setLockGroupData(null);
      fetchInterviewQuestions(); // Refresh to show updated lock status
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${isLocking ? 'lock' : 'unlock'} group`);
    } finally {
      setIsLockingGroup(false);
    }
  };


  // Lock/Unlock Forms for a user
  const handleLockUnlockForms = async () => {
    if (!formLockData || !formLockEmail.trim()) return;
    
    const email = formLockEmail.trim().toLowerCase();
    const isLocking = formLockData.mode === 'lock';
    
    setIsLockingForm(true);
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const formIds = formLockData.forms.map(f => f.id);
      const endpoint = isLocking ? 'lock' : 'unlock';
      
      await axios.post(`${API}/forms/${endpoint}`, {
        form_ids: formIds,
        email: email
      }, config);
      
      toast.success(isLocking 
        ? `Form(s) locked for ${email}` 
        : `Form(s) unlocked for ${email}`
      );
      
      setIsFormLockDialogOpen(false);
      setFormLockEmail('');
      setFormLockData(null);
      
      // Refresh forms
      const formsRes = await axios.get(`${API}/forms/with-lock-status`, config);
      setCustomForms(formsRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${isLocking ? 'lock' : 'unlock'} form(s)`);
    } finally {
      setIsLockingForm(false);
    }
  };




  const openEditQuestion = (question) => {
    setEditingQuestion(question);
    // Get images array - support both legacy single image and new multiple images
    const existingImages = question.images && question.images.length > 0 
      ? question.images 
      : (question.image_url ? [question.image_url] : []);
    
    // Determine if this was a custom headline (job_title exists but job_id is empty/not matching a job)
    const hasCustomHeadline = question.job_title && !question.job_id;
    
    // For custom topics, set job_id to 'custom_topic:TopicName' so dropdown shows the correct selection
    let effectiveJobId = question.job_id || '';
    if (hasCustomHeadline) {
      effectiveJobId = `custom_topic:${question.job_title}`;
    }
    
    setNewQuestion({
      question: question.question,
      category: question.category || 'General',
      difficulty: question.difficulty || 'Medium',
      expected_answer: question.expected_answer || '',
      hints: question.hints || '',
      job_id: effectiveJobId,
      custom_headline: hasCustomHeadline ? question.job_title : '',
      tags: question.tags || [],
      is_active: question.is_active !== false,
      image_url: question.image_url || '',
      images: existingImages,
      question_type: question.question_type || 'text',
      options: question.options || ['', '', '', ''],
      correct_option: question.correct_option ?? null,
      time_limit: question.time_limit ?? null,
      points: question.points || 1,
      permitted_emails: question.permitted_emails || []
    });
    setQuestionImagePreviews(existingImages.map(url => getImageUrl(url, API)));
    setIsQuestionDialogOpen(true);
  };

  // Handle question image upload - supports multiple images
  // Compress image if it exceeds size limit
  const compressImage = async (file, maxSizeMB = 10) => {
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    
    // If file is under limit, return as-is
    if (file.size <= maxSizeBytes) {
      return file;
    }
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let { width, height } = img;
          
          // Calculate compression ratio based on file size
          const ratio = Math.sqrt(maxSizeBytes / file.size);
          width = Math.floor(width * ratio);
          height = Math.floor(height * ratio);
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to blob with quality adjustment
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now()
                });
                resolve(compressedFile);
              } else {
                reject(new Error('Compression failed'));
              }
            },
            'image/jpeg',
            0.8 // Quality 80%
          );
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  const handleQuestionImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    setUploadingQuestionImage(true);
    
    try {
      const token = getAuthToken();
      const uploadedUrls = [];
      
      for (const file of files) {
        // Compress image if over 10MB
        let fileToUpload = file;
        if (file.size > 10 * 1024 * 1024) {
          toast.info(`Compressing ${file.name}...`);
          try {
            fileToUpload = await compressImage(file, 10);
          } catch (compressError) {
            toast.error(`Failed to compress ${file.name}. Try a smaller image.`);
            continue;
          }
        }
        
        const formData = new FormData();
        formData.append('file', fileToUpload);
        
        const res = await axios.post(`${API}/upload`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        uploadedUrls.push(res.data.url);
      }
      
      if (uploadedUrls.length > 0) {
        // Add new images to existing ones
        setNewQuestion(prev => ({ 
          ...prev, 
          images: [...prev.images, ...uploadedUrls],
          image_url: prev.image_url || uploadedUrls[0] // Keep first image as legacy image_url
        }));
        setQuestionImagePreviews(prev => [...prev, ...uploadedUrls.map(url => getImageUrl(url, API))]);
        toast.success(`${uploadedUrls.length} image(s) uploaded`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to upload image(s)';
      toast.error(errorMessage);
    } finally {
      setUploadingQuestionImage(false);
    }
    
    // Reset file input
    e.target.value = '';
  };

  // Remove a specific image from the question
  const removeQuestionImage = (indexToRemove) => {
    setNewQuestion(prev => ({
      ...prev,
      images: prev.images.filter((_, idx) => idx !== indexToRemove),
      image_url: indexToRemove === 0 ? (prev.images[1] || '') : prev.image_url
    }));
    setQuestionImagePreviews(prev => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const addTag = () => {
    if (tagInput.trim() && !newQuestion.tags.includes(tagInput.trim())) {
      setNewQuestion(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setNewQuestion(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  // Get all unique tags from questions
  const allQuestionTags = useMemo(() => {
    const tags = new Set();
    interviewQuestions.forEach(q => {
      q.tags?.forEach(tag => tags.add(tag));
    });
    return Array.from(tags).sort();
  }, [interviewQuestions]);

  const filteredInterviewQuestions = useMemo(() => {
    return interviewQuestions.filter(q => {
      // Search filter
      if (questionSearchQuery) {
        const query = questionSearchQuery.toLowerCase();
        const matchesSearch = 
          q.question?.toLowerCase().includes(query) ||
          q.expected_answer?.toLowerCase().includes(query) ||
          q.tags?.some(tag => tag.toLowerCase().includes(query)) ||
          q.job_title?.toLowerCase().includes(query) ||
          q.company_name?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      
      // Category filter - enhanced to include questions from mapped topics
      if (questionFilterCategory !== 'all') {
        // Get the topic key for this question
        const questionTopicKey = q.job_title 
          ? `title_${q.job_title}` 
          : (q.job_id && q.job_id !== 'other' ? q.job_id : 'general');
        
        // Check if this question's category matches OR if its topic is mapped to this category
        const topicsForCategory = categoryTopicMapping[questionFilterCategory] || [];
        const matchesCategory = q.category === questionFilterCategory;
        const matchesMappedTopic = topicsForCategory.includes(questionTopicKey);
        
        if (!matchesCategory && !matchesMappedTopic) {
          return false;
        }
      }
      
      // Difficulty filter
      if (questionFilterDifficulty !== 'all' && q.difficulty !== questionFilterDifficulty) {
        return false;
      }
      // Job role filter - use same logic as grouping
      if (questionFilterJobRole !== 'all') {
        const questionJobKey = q.job_title 
          ? `title_${q.job_title}` 
          : (q.job_id && q.job_id !== 'other' ? q.job_id : 'general');
        if (questionJobKey !== questionFilterJobRole) {
          return false;
        }
      }
      // Tag filter
      if (questionFilterTag !== 'all' && !q.tags?.includes(questionFilterTag)) {
        return false;
      }
      return true;
    });
  }, [interviewQuestions, questionSearchQuery, questionFilterCategory, questionFilterDifficulty, questionFilterJobRole, questionFilterTag, categoryTopicMapping]);

  // Group questions by job role for accordion display
  const groupedQuestionsByJobRole = useMemo(() => {
    const groups = {};
    
    filteredInterviewQuestions.forEach(q => {
      // Use job_title as key for custom "other" topics, job_id for regular jobs, or 'general' for no job
      // This ensures different custom headlines create separate groups
      const jobKey = q.job_title 
        ? `title_${q.job_title}` 
        : (q.job_id && q.job_id !== 'other' ? q.job_id : 'general');
      const jobLabel = q.job_title 
        ? `${q.job_title}${q.company_name ? ` @ ${q.company_name}` : ''}`
        : 'General Questions';
      
      if (!groups[jobKey]) {
        groups[jobKey] = {
          jobKey,
          jobLabel,
          jobTitle: q.job_title || 'General Questions',
          companyName: q.company_name || '',
          questions: []
        };
      }
      groups[jobKey].questions.push(q);
    });
    
    // Sort by job label, but put "General Questions" at the end
    return Object.values(groups).sort((a, b) => {
      if (a.jobKey === 'general') return 1;
      if (b.jobKey === 'general') return -1;
      return a.jobLabel.localeCompare(b.jobLabel);
    });
  }, [filteredInterviewQuestions]);

  // Get unique job roles for filter dropdown
  const uniqueJobRolesForFilter = useMemo(() => {
    const roles = new Map();
    interviewQuestions.forEach(q => {
      // Use same logic as grouping - job_title for custom topics, job_id for regular jobs
      const jobKey = q.job_title 
        ? `title_${q.job_title}` 
        : (q.job_id && q.job_id !== 'other' ? q.job_id : 'general');
      const jobLabel = q.job_title 
        ? `${q.job_title}${q.company_name ? ` @ ${q.company_name}` : ''}`
        : 'General Questions';
      if (!roles.has(jobKey)) {
        roles.set(jobKey, jobLabel);
      }
    });
    return Array.from(roles.entries());
  }, [interviewQuestions]);

  // Get unique existing custom topics (job_titles that don't match any jobUpdates)
  const existingCustomTopics = useMemo(() => {
    const jobUpdateIds = new Set(jobUpdates.map(j => j.id));
    const jobUpdateTitles = new Set(jobUpdates.map(j => j.position_name?.toLowerCase()));
    
    // Get all unique job_titles from questions that are custom topics
    const customTopics = new Set();
    interviewQuestions.forEach(q => {
      if (q.job_title) {
        // It's a custom topic if:
        // 1. job_id is empty/null OR
        // 2. job_id doesn't match any existing job update
        const isCustom = !q.job_id || !jobUpdateIds.has(q.job_id);
        if (isCustom) {
          customTopics.add(q.job_title);
        }
      }
    });
    
    return Array.from(customTopics).sort();
  }, [interviewQuestions, jobUpdates]);

  // Toggle expand/collapse for a job role
  const toggleJobRoleExpanded = (jobKey) => {
    setExpandedJobRoles(prev => ({
      ...prev,
      [jobKey]: !prev[jobKey]
    }));
  };

  // Expand all job roles
  const expandAllJobRoles = () => {
    const allExpanded = {};
    groupedQuestionsByJobRole.forEach(group => {
      allExpanded[group.jobKey] = true;
    });
    setExpandedJobRoles(allExpanded);
  };

  // Collapse all job roles
  const collapseAllJobRoles = () => {
    setExpandedJobRoles({});
  };

  const questionCategories = ['General', 'Technical', 'Behavioral', 'HR', 'Situational', 'Problem Solving'];
  const questionDifficulties = ['Easy', 'Medium', 'Hard'];

  const handleSaveFooterConfig = async () => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.put(`${API}/footer-config`, footerConfig, config);
      toast.success('Footer configuration saved!');
      setIsFooterDialogOpen(false);
    } catch (error) {
      toast.error('Failed to save footer configuration');
    }
  };

  // Database Backup Functions
  const handleExportData = async () => {
    setIsExporting(true);
    try {
      const { data } = await archiveApi.post('/snapshots');
      toast.info('Preparing a verified complete backup…');
      for (let attempt = 0; attempt < 180; attempt++) {
        const { data: snapshot } = await archiveApi.get(`/snapshots/${data.id}`);
        if (snapshot.status === 'ready') {
          await downloadArchive(data.id);
          toast.success('Verified backup download started. Save the file securely.');
          return;
        }
        if (snapshot.status === 'failed') throw new Error(snapshot.error || 'Backup failed');
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      toast.info('Backup is still building. You can download it from Backup & data safety when ready.');
    } catch (error) {
      toast.error(archiveError(error, error.message || 'Could not prepare backup'));
    } finally {
      setIsExporting(false);
    }
  };

  const handleImportData = async () => {
    if (!importFile) {
      toast.error('Please select a backup file first');
      return;
    }
    
    // Validate file type
    if (!/\.jsonl?$/i.test(importFile.name)) {
      toast.error('Please select a valid JSON backup file');
      return;
    }
    
    setIsImporting(true);
    try {
      const { data } = await archiveApi.post('/uploads');
      await axios.put(`${ARCHIVE_API}/uploads/${data.id}`, importFile, {
        ...archiveConfig(), headers: { ...archiveConfig().headers, 'Content-Type': 'application/octet-stream',
          'X-Backup-Format': importFile.name.toLowerCase().endsWith('.jsonl') ? 'jsonl' : 'json' }
      });
      toast.success('Backup verified. Opening an isolated CRM preview; live data is unchanged.');
      setImportFile(null);
      if (importFileInputRef.current) {
        importFileInputRef.current.value = '';
      }
      
      navigate(`/backup-preview/${data.id}`);
    } catch (error) {
      console.error('Import failed:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to import data. Make sure the file is a valid backup.';
      toast.error(errorMsg, { duration: 5000 });
    } finally {
      setIsImporting(false);
    }
  };

  // CMS Page Functions
  const fetchCmsPages = async () => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/page-content`, config);
      setCmsPages(response.data || []);
    } catch (error) {
      console.error('Failed to fetch CMS pages:', error);
    }
  };

  const openPageEditor = async (slug) => {
    try {
      const response = await axios.get(`${API}/page-content/${slug}`);
      const pageData = response.data;
      setPageEditorData({
        slug: slug,
        title: pageData.title || slug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        content: pageData.content || '',
        is_published: pageData.is_published !== false
      });
      setEditingPage(slug);
      setIsPageEditorOpen(true);
    } catch (error) {
      // Page doesn't exist yet, create empty
      setPageEditorData({
        slug: slug,
        title: slug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        content: '',
        is_published: true
      });
      setEditingPage(slug);
      setIsPageEditorOpen(true);
    }
  };

  const handleSavePage = async () => {
    setSavingPage(true);
    try {
      const token = getAuthToken();
      await axios.put(
        `${API}/page-content/${pageEditorData.slug}?title=${encodeURIComponent(pageEditorData.title)}&is_published=${pageEditorData.is_published}`,
        pageEditorData.content,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'text/plain'
          }
        }
      );
      toast.success('Page saved successfully!');
      setIsPageEditorOpen(false);
      setEditingPage(null);
      fetchCmsPages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save page');
    } finally {
      setSavingPage(false);
    }
  };

  const handleCreatePopupAnnouncement = async () => {
    if (!newPopupAnnouncement.title || !newPopupAnnouncement.content) {
      toast.error('Title and content are required');
      return;
    }
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.post(`${API}/popup-announcements`, newPopupAnnouncement, config);
      
      // If there's an image to upload
      if (popupImagePreview && !newPopupAnnouncement.image_url) {
        const formData = new FormData();
        const input = document.querySelector('[data-testid="popup-image-input"]');
        if (input?.files?.[0]) {
          formData.append('file', input.files[0]);
          await axios.post(`${API}/popup-announcements/${response.data.id}/image`, formData, {
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
          });
        }
      }
      
      toast.success('Popup announcement created!');
      setIsPopupAnnouncementDialogOpen(false);
      setNewPopupAnnouncement({
        title: '',
        content: '',
        image_url: '',
        button_text: '',
        button_link: '',
        priority: 'normal',
        is_active: true,
        start_date: '',
        end_date: '',
        mascot_type: 'robot'
      });
      setPopupImagePreview(null);
      fetchPopupAnnouncements();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create popup');
    }
  };

  const handlePopupImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => setPopupImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleToggleAnnouncementStatus = async (announcementId, currentStatus, announcement) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.put(`${API}/popup-announcements/${announcementId}`, {
        ...announcement,
        is_active: !currentStatus
      }, config);
      toast.success(`Popup ${!currentStatus ? 'activated' : 'deactivated'}!`);
      fetchPopupAnnouncements();
    } catch (error) {
      toast.error('Failed to update popup');
    }
  };

  const handleDeleteAnnouncement = async (announcementId) => {
    if (!window.confirm('Delete this popup announcement?')) return;
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API}/popup-announcements/${announcementId}`, config);
      toast.success('Popup deleted!');
      fetchPopupAnnouncements();
    } catch (error) {
      toast.error('Failed to delete popup');
    }
  };

  // Pending Employers Functions
  const fetchPendingEmployers = async () => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/admin/pending-employers`, config);
      setPendingEmployers(response.data);
    } catch (error) {
      console.error('Failed to fetch pending employers:', error);
    }
  };

  const handleApproveEmployer = async (userId) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/admin/approve-employer/${userId}`, {}, config);
      toast.success('Employer approved successfully!');
      fetchPendingEmployers();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve employer');
    }
  };

  const handleRejectEmployer = async (userId) => {
    if (!window.confirm('Are you sure you want to reject this employer?')) return;
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/admin/reject-employer/${userId}`, {}, config);
      toast.success('Employer rejected');
      fetchPendingEmployers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject employer');
    }
  };

  // Filter applications based on selected filter and search query
  const filteredApplications = useMemo(() => {
    // First exclude archived applications
    let filtered = allApplications.filter(app => !app.is_archived);
    
    // Apply status filter
    if (applicationFilter !== 'all') {
      filtered = filtered.filter(app => sameStatus(app.status, applicationFilter));
    }
    
    // Apply position filter
    if (positionFilter) {
      filtered = filtered.filter(app => app.job_title === positionFilter);
    }
    
    // Apply company filter
    if (companyFilter) {
      filtered = filtered.filter(app => app.company_name === companyFilter);
    }
    
    // Apply search filter
    if (applicationSearchQuery.trim()) {
      const query = applicationSearchQuery.toLowerCase().trim();
      filtered = filtered.filter(app => 
        app.full_name?.toLowerCase().includes(query) ||
        app.email?.toLowerCase().includes(query) ||
        app.phone?.toLowerCase().includes(query) ||
        app.job_title?.toLowerCase().includes(query) ||
        app.company_name?.toLowerCase().includes(query) ||
        app.experience_years?.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  }, [allApplications, applicationFilter, applicationSearchQuery, positionFilter, companyFilter]);
  
  // Archived applications
  const archivedApplications = useMemo(() => {
    return allApplications.filter(app => app.is_archived);
  }, [allApplications]);
  
  // Grouped applications by candidate email
  const groupedApplications = useMemo(() => {
    if (!groupByCandidate) return null;
    
    const groups = {};
    filteredApplications.forEach(app => {
      const email = app.email?.toLowerCase() || 'unknown';
      if (!groups[email]) {
        groups[email] = {
          email: app.email,
          full_name: app.full_name,
          phone: app.phone,
          applications: []
        };
      }
      groups[email].applications.push(app);
    });
    
    return Object.values(groups).sort((a, b) => 
      b.applications.length - a.applications.length
    );
  }, [filteredApplications, groupByCandidate]);
  
  // Toggle candidate expansion
  const toggleCandidateExpansion = (email) => {
    setExpandedCandidates(prev => ({
      ...prev,
      [email]: !prev[email]
    }));
  };
  
  // Get unique positions and companies for filter dropdowns
  const uniquePositions = useMemo(() => {
    return [...new Set(allApplications.map(a => a.job_title).filter(Boolean))].sort();
  }, [allApplications]);
  
  const uniqueCompanies = useMemo(() => {
    return [...new Set(allApplications.map(a => a.company_name).filter(Boolean))].sort();
  }, [allApplications]);

  // Email Template Functions
  const handleCreateTemplate = async () => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/email-templates`, newTemplate, config);
      toast.success('Template created!');
      setIsTemplateDialogOpen(false);
      setNewTemplate({ status_trigger: '', subject: '', body: '', is_active: true });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create template');
    }
  };

  const handleUpdateTemplate = async () => {
    if (!editingTemplate) return;
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.patch(`${API}/email-templates/${editingTemplate.id}`, newTemplate, config);
      toast.success('Template updated!');
      setIsTemplateDialogOpen(false);
      setEditingTemplate(null);
      setNewTemplate({ status_trigger: '', subject: '', body: '', is_active: true });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API}/email-templates/${templateId}`, config);
      toast.success('Template deleted!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete template');
    }
  };

  const handleSeedDefaultTemplates = async () => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.post(`${API}/email-templates/seed-defaults`, {}, config);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to seed templates');
    }
  };

  const handlePreviewTemplate = async (templateId) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.post(`${API}/email-templates/preview?template_id=${templateId}`, {}, config);
      setPreviewTemplate(response.data);
    } catch (error) {
      toast.error('Failed to preview template');
    }
  };

  const openEditTemplate = (template) => {
    setEditingTemplate(template);
    setNewTemplate({
      status_trigger: template.status_trigger,
      subject: template.subject,
      body: template.body,
      is_active: template.is_active
    });
    setIsTemplateDialogOpen(true);
  };

  // Support ticket functions
  const handleSelectSupportTicket = async (ticket) => {
    setSelectedSupportTicket(ticket);
    // Mark as read if it has new reply
    if (ticket.has_new_reply) {
      try {
        const token = getAuthToken();
        const config = { headers: { Authorization: `Bearer ${token}` } };
        await axios.post(`${API}/support-tickets/${ticket.id}/mark-read`, {}, config);
        // Update unread count
        const unreadRes = await axios.get(`${API}/support-tickets/unread/count`, config);
        setUnreadSupportCount(unreadRes.data.unread_count || 0);
        // Update ticket in list
        setSupportTickets(prev => prev.map(t => t.id === ticket.id ? {...t, has_new_reply: false} : t));
      } catch (error) {
        console.error('Failed to mark ticket as read');
      }
    }
  };

  const [isSendingSupportReply, setIsSendingSupportReply] = useState(false);
  
  const handleSupportReply = async () => {
    if (!supportReplyMessage.trim() || !selectedSupportTicket) return;
    if (isSendingSupportReply) return; // Prevent double sending
    
    setIsSendingSupportReply(true);
    const messageToSend = supportReplyMessage.trim();
    const tempId = `temp-${Date.now()}-${Math.random()}`;
    
    // Clear input immediately
    setSupportReplyMessage('');
    
    // Optimistic update
    const optimisticReply = {
      id: tempId,
      message: messageToSend,
      sender_email: currentUser?.email,
      sender_name: currentUser?.name || 'Staff',
      sender_role: currentUser?.role || 'admin',
      created_at: new Date().toISOString(),
      is_sending: true,
      status: 'sending'
    };
    
    setSelectedSupportTicket(prev => ({
      ...prev,
      replies: [...(prev.replies || []), optimisticReply]
    }));
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/support-tickets/${selectedSupportTicket.id}/reply`, { message: messageToSend }, config);
      
      // Mark as sent
      setSelectedSupportTicket(prev => ({
        ...prev,
        replies: (prev.replies || []).map(r => 
          r.id === tempId ? { ...r, is_sending: false, status: 'sent' } : r
        )
      }));
      
      // Refresh tickets list silently
      const ticketsRes = await axios.get(`${API}/support-tickets`, config);
      setSupportTickets(ticketsRes.data);
      
      const updatedTicket = ticketsRes.data.find(t => t.id === selectedSupportTicket.id);
      if (updatedTicket) {
        setSelectedSupportTicket(prev => {
          const serverReplies = updatedTicket.replies || [];
          const pendingOptimistic = (prev.replies || []).filter(r => 
            r.id.startsWith('temp-') && !serverReplies.some(sr => sr.message === r.message)
          );
          return { ...updatedTicket, replies: [...serverReplies, ...pendingOptimistic] };
        });
      }
    } catch (error) {
      setSelectedSupportTicket(prev => ({
        ...prev,
        replies: (prev.replies || []).map(r => 
          r.id === tempId ? { ...r, is_sending: false, status: 'failed' } : r
        )
      }));
      toast.error('Failed to send reply');
    } finally {
      setIsSendingSupportReply(false);
    }
  };

  const handleUpdateTicketStatus = async (ticketId, status) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.patch(`${API}/support-tickets/${ticketId}/status?status=${status}`, {}, config);
      toast.success(`Ticket ${status.toLowerCase()}`);
      const ticketsRes = await axios.get(`${API}/support-tickets`, config);
      setSupportTickets(ticketsRes.data);
      
      // Refresh unread count after resolving/closing
      if (status === 'Resolved' || status === 'Closed') {
        const unreadRes = await axios.get(`${API}/support-tickets/unread/count`, config);
        setUnreadSupportCount(unreadRes.data.unread_count || 0);
      }
      
      if (selectedSupportTicket?.id === ticketId) {
        const updatedTicket = ticketsRes.data.find(t => t.id === ticketId);
        if (updatedTicket) setSelectedSupportTicket(updatedTicket);
      }
    } catch (error) {
      toast.error('Failed to update ticket status');
    }
  };

  const getTicketStatusColor = (status) => {
    switch (status) {
      case 'Open': return 'bg-blue-100 text-blue-700';
      case 'In Progress': return 'bg-yellow-100 text-yellow-700';
      case 'Resolved': return 'bg-green-100 text-green-700';
      case 'Closed': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getCallStatusColor = (status) => {
    switch (status) {
      case 'Pending': return 'bg-orange-100 text-orange-700';
      case 'Called': return 'bg-blue-100 text-blue-700';
      case 'Completed': return 'bg-green-100 text-green-700';
      case 'Cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  // Candidate Support Functions
  const fetchCandidateSupportTickets = async () => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API}/support-tickets/my`, config);
      setCandidateSupportTickets(res.data || []);
    } catch (error) {
      console.error('Failed to fetch support tickets');
    }
  };

  const handleSubmitCallRequest = async () => {
    if (!callRequestForm.phone || !callRequestForm.preferred_date || !callRequestForm.preferred_time || !callRequestForm.reason) {
      toast.error('Please fill all required fields');
      return;
    }
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/support-tickets`, {
        subject: 'Call Request',
        message: callRequestForm.reason,
        ticket_type: 'call_request',
        phone: callRequestForm.phone,
        preferred_date: callRequestForm.preferred_date,
        preferred_time: callRequestForm.preferred_time
      }, config);
      toast.success('Call request submitted successfully! Our team will contact you.');
      setIsCallRequestDialogOpen(false);
      setCallRequestForm({ name: '', phone: '', preferred_date: '', preferred_time: '', reason: '' });
      fetchCandidateSupportTickets();
    } catch (error) {
      toast.error('Failed to submit call request');
    }
  };

  const handleSubmitSupportTicket = async () => {
    if (!supportTicketForm.subject || !supportTicketForm.message) {
      toast.error('Please fill all required fields');
      return;
    }
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/support-tickets`, {
        subject: supportTicketForm.subject,
        message: supportTicketForm.message,
        ticket_type: 'general'
      }, config);
      toast.success('Support ticket submitted successfully!');
      setIsSupportTicketDialogOpen(false);
      setSupportTicketForm({ subject: '', message: '' });
      fetchCandidateSupportTickets();
    } catch (error) {
      toast.error('Failed to submit support ticket');
    }
  };

  const handleUpdateCallStatus = async (ticketId, callStatus) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.patch(`${API}/support-tickets/${ticketId}/call-status?call_status=${callStatus}`, {}, config);
      toast.success(`Call status updated to ${callStatus}`);
      const ticketsRes = await axios.get(`${API}/support-tickets`, config);
      setSupportTickets(ticketsRes.data);
      
      // Refresh unread count after completing/cancelling call
      if (callStatus === 'Completed' || callStatus === 'Cancelled') {
        const unreadRes = await axios.get(`${API}/support-tickets/unread/count`, config);
        setUnreadSupportCount(unreadRes.data.unread_count || 0);
      }
      
      if (selectedSupportTicket?.id === ticketId) {
        const updatedTicket = ticketsRes.data.find(t => t.id === ticketId);
        if (updatedTicket) setSelectedSupportTicket(updatedTicket);
      }
    } catch (error) {
      toast.error('Failed to update call status');
    }
  };

  const handleUpdateAdminNotes = async (ticketId, notes) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.patch(`${API}/support-tickets/${ticketId}/notes`, { admin_notes: notes }, config);
      toast.success('Notes saved');
      const ticketsRes = await axios.get(`${API}/support-tickets`, config);
      setSupportTickets(ticketsRes.data);
      if (selectedSupportTicket?.id === ticketId) {
        const updatedTicket = ticketsRes.data.find(t => t.id === ticketId);
        if (updatedTicket) setSelectedSupportTicket(updatedTicket);
      }
    } catch (error) {
      toast.error('Failed to save notes');
    }
  };

  // Available time slots for call requests (10AM - 10PM, Mon-Sat; 10AM - 4PM on Sunday)
  const callTimeSlots = [
    '10:00 AM - 11:00 AM',
    '11:00 AM - 12:00 PM',
    '12:00 PM - 1:00 PM',
    '1:00 PM - 2:00 PM',
    '2:00 PM - 3:00 PM',
    '3:00 PM - 4:00 PM',
    '4:00 PM - 5:00 PM',
    '5:00 PM - 6:00 PM',
    '6:00 PM - 7:00 PM',
    '7:00 PM - 8:00 PM',
    '8:00 PM - 9:00 PM',
    '9:00 PM - 10:00 PM'
  ];

  // Check if a date is valid (all days available - Mon-Sat full, Sunday half day)
  const isValidDay = (dateStr) => {
    return true; // All days available
  };

  const handleReplyImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }
      setReplyImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setReplyImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadReplyImage = async () => {
    if (!replyImageFile) return null;
    
    setUploadingReplyImage(true);
    try {
      const token = getAuthToken();
      const formData = new FormData();
      formData.append('file', replyImageFile);
      
      const response = await axios.post(`${API}/tasks/reply-image/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data.image_url;
    } catch (error) {
      toast.error('Failed to upload image');
      return null;
    } finally {
      setUploadingReplyImage(false);
    }
  };

  // Email Logo Upload Handler
  const handleEmailLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB');
      return;
    }
    
    setUploadingEmailLogo(true);
    try {
      const token = getAuthToken();
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/email-config/logo`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setEmailLogoUrl(response.data.logo_url);
      toast.success('Email logo uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setUploadingEmailLogo(false);
    }
  };

  const handleDeleteEmailLogo = async () => {
    try {
      const token = getAuthToken();
      await axios.delete(`${API}/email-config/logo`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailLogoUrl(null);
      toast.success('Email logo deleted');
    } catch (error) {
      toast.error('Failed to delete logo');
    }
  };

  const handleSubmitReply = async () => {
    if (!newReply.trim() && !replyImageFile) {
      toast.error('Please add a message or image');
      return;
    }

    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      let imageUrl = null;
      if (replyImageFile) {
        imageUrl = await uploadReplyImage();
      }
      
      await axios.post(`${API}/tasks/${selectedTaskForReply.id}/replies`, {
        comment: newReply.trim() || 'Attached image',
        image_url: imageUrl
      }, config);
      
      toast.success('Reply added!');
      setNewReply('');
      setReplyImageFile(null);
      setReplyImagePreview(null);
      await fetchTaskReplies(selectedTaskForReply.id);
    } catch (error) {
      toast.error('Failed to add reply');
    }
  };

  // Job Creator Functions
  const handleUploadJobImage = async (file, type) => {
    if (type === 'logo') setUploadingLogo(true);
    else setUploadingJobImage(true);
    
    try {
      const token = getAuthToken();
      const formData = new FormData();
      formData.append('file', file);
      formData.append('image_type', type);
      
      const response = await axios.post(`${API}/job-updates/upload-image`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (type === 'logo') {
        setNewJob(prev => ({ ...prev, company_logo_url: response.data.image_url }));
      } else {
        setNewJob(prev => ({ ...prev, job_image_url: response.data.image_url }));
      }
      toast.success('Image uploaded!');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      if (type === 'logo') setUploadingLogo(false);
      else setUploadingJobImage(false);
    }
  };

  const handleCreateJob = async (e) => {
    e.preventDefault();
    if (!newJob.company_name || !newJob.job_description) {
      toast.error('Please fill required fields');
      return;
    }
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      if (editingJob) {
        await axios.patch(`${API}/job-updates/${editingJob.id}`, newJob, config);
        toast.success('Job updated!');
      } else {
        await axios.post(`${API}/job-updates`, newJob, config);
        toast.success('Job created!');
      }
      
      setEditingJob(null);
      setIsJobFormOpen(false);
      setNewJob({
        company_name: '',
        position_name: '',
        job_description: '',
        requirements: '',
        salary_range: '',
        salary_currency: '₹',
        location: '',
        education: '',
        experience: '',
        work_type: '',
        career_site_link: '',
        company_logo_url: '',
        job_image_url: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save job');
    }
  };

  const handleEditJob = (job) => {
    setEditingJob(job);
    setNewJob({
      company_name: job.company_name,
      position_name: job.position_name || '',
      job_description: job.job_description,
      requirements: job.requirements || '',
      salary_range: job.salary_range || '',
      salary_currency: job.salary_currency || '₹',
      location: job.location || '',
      education: job.education || '',
      experience: job.experience || '',
      work_type: job.work_type || '',
      career_site_link: job.career_site_link || '',
      company_logo_url: job.company_logo_url || '',
      job_image_url: job.job_image_url || ''
    });
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job posting?')) return;
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API}/job-updates/${jobId}`, config);
      toast.success('Job deleted!');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete job');
    }
  };

  const handlePublishJob = async (jobId, shouldPublish) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.patch(`${API}/job-updates/${jobId}/publish?publish=${shouldPublish}`, {}, config);
      toast.success(shouldPublish ? 'Job published! Now visible to Lead Specialists and Candidates.' : 'Job unpublished.');
      fetchData();
    } catch (error) {
      toast.error('Failed to update job status');
    }
  };

  const cancelEditJob = () => {
    setEditingJob(null);
    setNewJob({
      company_name: '',
      position_name: '',
      job_description: '',
      requirements: '',
      salary_range: '',
      salary_currency: '₹',
      location: '',
      education: '',
      experience: '',
      work_type: '',
      career_site_link: '',
      company_logo_url: '',
      job_image_url: ''
    });
  };

  // Company Management Handlers
  const handleCompanyLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingCompanyLogo(true);
    try {
      const token = getAuthToken();
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/companies/upload-logo`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      
      setNewCompany(prev => ({ ...prev, logo_url: response.data.logo_url }));
      toast.success('Logo uploaded!');
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setUploadingCompanyLogo(false);
    }
  };

  const handleSaveCompany = async () => {
    if (!newCompany.name.trim()) {
      toast.error('Company name is required');
      return;
    }
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      if (editingCompany) {
        await axios.patch(`${API}/companies/${editingCompany.id}`, newCompany, config);
        toast.success('Company updated!');
      } else {
        await axios.post(`${API}/companies`, newCompany, config);
        toast.success('Company added!');
      }
      
      setIsCompanyDialogOpen(false);
      setNewCompany({ name: '', description: '', website: '', logo_url: '' });
      setEditingCompany(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save company');
    }
  };

  const handleDeleteCompany = async (companyId) => {
    if (!window.confirm('Are you sure you want to delete this company?')) return;
    
    try {
      const token = getAuthToken();
      await axios.delete(`${API}/companies/${companyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Company deleted!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete company');
    }
  };

  const handleUpdateReferralStatus = async (referralId, newStatus) => {
    try {
      const token = getAuthToken();
      await axios.patch(`${API}/lead-specialist/job-referrals/${referralId}/status?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Status updated to ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleLogout = async () => {
    // End activity session before logout
    await endSession();
    removeAuthToken();
    navigate('/login');
  };

  // Candidate Status handlers
  const resetCandidateForm = () => {
    setCandidateData({
      company_name: '',
      candidate_name: '',
      email: '',
      phone: '',
      actual_contact_number: '',
      process: '',
      original_location: '',
      job_location: '',
      initial_payment: 'pending',
      registration_update: 'not done',
      video_received: 'not received',
      assessment_update: 'pending',
      pdf_sent: 'pending',
      demo_hr_round: 'pending',
      final_outcome: 'pending',
      failure_reason: '',
      full_payment: 'pending'
    });
    setEditingCandidate(null);
  };

  const handleOpenCandidateDialog = (candidate = null) => {
    if (candidate) {
      setEditingCandidate(candidate);
      setCandidateData({
        company_name: candidate.company_name || '',
        candidate_name: candidate.candidate_name || '',
        email: candidate.email || '',
        phone: candidate.phone || '',
        actual_contact_number: candidate.actual_contact_number || '',
        process: candidate.process || '',
        original_location: candidate.original_location || '',
        job_location: candidate.job_location || '',
        initial_payment: candidate.initial_payment || 'pending',
        registration_update: candidate.registration_update || 'not done',
        video_received: candidate.video_received || 'not received',
        assessment_update: candidate.assessment_update || 'pending',
        pdf_sent: candidate.pdf_sent || 'pending',
        demo_hr_round: candidate.demo_hr_round || 'pending',
        final_outcome: candidate.final_outcome || 'pending',
        failure_reason: candidate.failure_reason || '',
        full_payment: candidate.full_payment || 'pending'
      });
    } else {
      resetCandidateForm();
    }
    setIsCandidateDialogOpen(true);
  };

  const handleSaveCandidate = async () => {
    if (!candidateData.company_name || !candidateData.candidate_name) {
      toast.error('Company name and Candidate name are required');
      return;
    }

    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };

      if (editingCandidate) {
        await axios.patch(`${API}/candidate-status/${editingCandidate.id}`, candidateData, config);
        toast.success('Candidate status updated!');
      } else {
        await axios.post(`${API}/candidate-status`, candidateData, config);
        toast.success('Candidate status created!');
      }

      setIsCandidateDialogOpen(false);
      resetCandidateForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save candidate status');
    }
  };

  // Filter candidates based on search - Admin sees ALL candidates, regular users see their own
  const candidatesToShow = currentUser?.is_admin ? allCandidates : candidateStatuses;
  const filteredCandidates = candidatesToShow.filter(candidate => {
    const searchLower = candidateSearch.toLowerCase();
    return (
      candidate.candidate_name?.toLowerCase().includes(searchLower) ||
      candidate.company_name?.toLowerCase().includes(searchLower) ||
      candidate.email?.toLowerCase().includes(searchLower) ||
      candidate.phone?.includes(searchLower) ||
      candidate.process?.toLowerCase().includes(searchLower)
    );
  });

  const handleDeleteCandidate = async (id) => {
    if (!window.confirm('Are you sure you want to delete this candidate status?')) return;
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API}/candidate-status/${id}`, config);
      toast.success('Candidate status deleted!');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete candidate status');
    }
  };

  const handleArchiveCandidate = async (id) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.put(`${API}/candidate-status/${id}`, { is_archived: true }, config);
      toast.success('Candidate archived!');
      fetchData();
    } catch (error) {
      toast.error('Failed to archive candidate');
    }
  };

  const getStatusBadgeColor = (status) => {
    switch(status) {
      case 'done':
      case 'pass':
      case 'received':
      case 'sent':
        return 'bg-green-500';
      case 'pending':
      case 'not done':
      case 'not received':
        return 'bg-yellow-500';
      case 'failed':
        return 'bg-red-500';
      case 'not-need':
      case 'not needed':
        return 'bg-gray-400';
      default:
        return 'bg-gray-500';
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, config);
      
      toast.success('Password changed successfully!');
      setIsPasswordDialogOpen(false);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  // Profile update function
  const handleUpdateProfile = async () => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.put(`${API}/users/me/profile`, {
        name: profileData.name,
        profile_picture: profileData.profile_picture
      }, config);
      
      // Update current user state
      setCurrentUser(prev => ({
        ...prev,
        name: profileData.name,
        profile_picture: profileData.profile_picture
      }));
      
      toast.success('Profile updated successfully!');
      setIsProfileDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    }
  };

  // Open profile dialog with current data
  const openProfileDialog = () => {
    setProfileData({
      name: currentUser?.name || '',
      profile_picture: currentUser?.profile_picture || ''
    });
    setIsProfileDialogOpen(true);
  };

  // Candidate Performance functions
  const refreshPerformances = async () => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const perfRes = await axios.get(`${API}/candidate-performance`, config);
      const enrichedPerf = perfRes.data.map(perf => {
        const candidate = allCandidates.find(c => c.id === perf.candidate_status_id);
        return {
          ...perf,
          candidate_name: candidate?.candidate_name || perf.candidate_name || 'Unknown',
          company_name: candidate?.company_name || perf.company_name || '',
          email: candidate?.email || '',
          phone: candidate?.phone || '',
          process: candidate?.process || perf.process || '',
          original_location: candidate?.original_location || '',
          job_location: candidate?.job_location || '',
          initial_payment: candidate?.initial_payment || '',
          demo_hr_round: candidate?.demo_hr_round || '',
          final_outcome: candidate?.final_outcome || ''
        };
      });
      setAllPerformances(enrichedPerf);
    } catch (error) {
      console.error('Failed to refresh performances', error);
    }
  };

  const openSessionEditor = (performance, scope = { type: 'all' }) => {
    setSessionEditor({ open: true, performance, scope });
  };

  const handleDeletePerformance = async (performanceId, candidateName) => {
    if (!confirm(`Are you sure you want to delete performance record for "${candidateName}"? This action cannot be undone.`)) return;
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API}/candidate-performance/${performanceId}`, config);
      toast.success('Performance record deleted');
      // Refresh performances
      const perfRes = await axios.get(`${API}/candidate-performance`, config);
      const enrichedPerf = perfRes.data.map(perf => {
        const candidate = allCandidates.find(c => c.id === perf.candidate_status_id);
        return { 
          ...perf, 
          candidate_name: candidate?.candidate_name || 'Unknown', 
          company_name: candidate?.company_name || '',
          email: candidate?.email || '',
          phone: candidate?.phone || '',
          process: candidate?.process || '',
          original_location: candidate?.original_location || '',
          job_location: candidate?.job_location || '',
          initial_payment: candidate?.initial_payment || '',
          demo_hr_round: candidate?.demo_hr_round || '',
          final_outcome: candidate?.final_outcome || ''
        };
      });
      setAllPerformances(enrichedPerf);
    } catch (error) {
      toast.error('Failed to delete performance record');
    }
  };

  // Universal Search Function with debouncing
  const searchTimeoutRef = useRef(null);
  
  const handleUniversalSearch = useCallback((query) => {
    if (!query || query.length < 2) {
      setSearchResults({ candidates: [], jobs: [], applications: [], companies: [], users: [], tasks: [] });
      return;
    }
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Debounce search by 300ms
    searchTimeoutRef.current = setTimeout(() => {
      setSearchLoading(true);
      try {
        const searchLower = query.toLowerCase();
        
        // Only search in tabs the user has access to
        // Search in candidates (use allCandidates) - only if user has access
        const filteredCandidates = hasTabAccess(currentUser, 'candidates') 
          ? (allCandidates || []).filter(c => 
              c.name?.toLowerCase().includes(searchLower) ||
              c.email?.toLowerCase().includes(searchLower) ||
              c.phone?.includes(searchLower) ||
              c.company?.toLowerCase().includes(searchLower)
            ).slice(0, 5)
          : [];
        
        // Search in jobs - only if user has access
        const filteredJobs = hasTabAccess(currentUser, 'job-updates')
          ? (jobUpdates || []).filter(j =>
              j.title?.toLowerCase().includes(searchLower) ||
              j.company?.toLowerCase().includes(searchLower) ||
              j.location?.toLowerCase().includes(searchLower) ||
              j.position_name?.toLowerCase().includes(searchLower) ||
              j.work_type?.toLowerCase().includes(searchLower)
            ).slice(0, 5)
          : [];
        
        // Search in applications - only if user has access
        const filteredApps = hasTabAccess(currentUser, 'all-applications')
          ? (allApplications || []).filter(a =>
              a.candidate_name?.toLowerCase().includes(searchLower) ||
              a.job_title?.toLowerCase().includes(searchLower) ||
              a.company_name?.toLowerCase().includes(searchLower) ||
              a.status?.toLowerCase().includes(searchLower) ||
              a.applicant_email?.toLowerCase().includes(searchLower)
            ).slice(0, 5)
          : [];
        
        // Search in companies - only if user has access
        const filteredCompanies = hasTabAccess(currentUser, 'companies')
          ? (companies || []).filter(c =>
              c.name?.toLowerCase().includes(searchLower) ||
              c.industry?.toLowerCase().includes(searchLower) ||
              c.description?.toLowerCase().includes(searchLower)
            ).slice(0, 5)
          : [];
        
        // Search in users - only if user has access (typically admin only)
        const filteredUsers = hasTabAccess(currentUser, 'users')
          ? (users || []).filter(u =>
              u.name?.toLowerCase().includes(searchLower) ||
              u.email?.toLowerCase().includes(searchLower) ||
              u.role?.toLowerCase().includes(searchLower) ||
              u.phone?.includes(searchLower)
            ).slice(0, 5)
          : [];
        
        // Search in tasks - only if user has access
        const filteredTasks = hasTabAccess(currentUser, 'tasks')
          ? (tasks || []).filter(t =>
              t.title?.toLowerCase().includes(searchLower) ||
              t.description?.toLowerCase().includes(searchLower) ||
              t.assigned_to?.toLowerCase().includes(searchLower)
            ).slice(0, 3)
          : [];
        
        setSearchResults({
          candidates: filteredCandidates,
          jobs: filteredJobs,
          applications: filteredApps,
          companies: filteredCompanies,
          users: filteredUsers,
          tasks: filteredTasks
        });
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
  }, [allCandidates, jobUpdates, allApplications, companies, users, tasks, currentUser]);
  useEffect(() => { if (universalSearch.length >= 2) handleUniversalSearch(universalSearch); }, [handleUniversalSearch, universalSearch]);

  // Open notify dialog for schedule email
  const openNotifyDialog = (performanceId, sessionType, candidateName) => {
    setNotifyDialogData({
      performanceId,
      sessionType,
      candidateName,
      customMessage: '',
      meetingLink: ''
    });
    setNotifyDialogOpen(true);
  };

  // Send schedule notification email to candidate
  const handleSendScheduleEmail = async () => {
    const { performanceId, sessionType, candidateName, customMessage, meetingLink } = notifyDialogData;
    
    const sessionLabels = {
      'briefing_1': 'Briefing 1',
      'briefing_2': 'Briefing 2',
      'demo_hr_round_1': 'Demo HR Round 1',
      'demo_hr_round_2': 'Demo HR Round 2'
    };
    
    // Build custom message with link if provided
    let fullMessage = customMessage || '';
    if (meetingLink) {
      const linkHtml = `<br/><br/><strong>Meeting Link:</strong> <a href="${meetingLink}" style="color: #3b82f6; text-decoration: underline;">${meetingLink}</a>`;
      fullMessage = fullMessage ? fullMessage + linkHtml : `Please join the session using this link: ${linkHtml}`;
    }
    
    setSendingScheduleEmail(`${performanceId}_${sessionType}`);
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/schedule/send-email`, {
        performance_id: performanceId,
        session_type: sessionType,
        custom_message: fullMessage
      }, config);
      toast.success(`Schedule email sent to ${candidateName}!`);
      setNotifyDialogOpen(false);
      setNotifyDialogData({ performanceId: '', sessionType: '', candidateName: '', customMessage: '', meetingLink: '' });
    } catch (error) {
      console.error('Failed to send schedule email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send schedule email');
    } finally {
      setSendingScheduleEmail(null);
    }
  };

  // Fetch reschedule requests
  const fetchRescheduleRequests = async () => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API}/reschedule/requests`, config);
      setRescheduleRequests(res.data);
    } catch (error) {
      console.error('Failed to fetch reschedule requests:', error);
    }
  };

  // Approve reschedule request
  const handleApproveReschedule = async (requestId) => {
    if (!confirm('Approve this reschedule request? The session time will be updated automatically.')) return;
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/reschedule/approve/${requestId}`, {}, config);
      toast.success('Reschedule approved! Session time updated.');
      fetchRescheduleRequests();
      // Refresh performances to get updated schedules
      const perfRes = await axios.get(`${API}/candidate-performance`, config);
      const enrichedPerf = perfRes.data.map(perf => {
        const candidate = allCandidates.find(c => c.id === perf.candidate_status_id);
        return { 
          ...perf, 
          candidate_name: candidate?.candidate_name || 'Unknown', 
          company_name: candidate?.company_name || '',
          email: candidate?.email || '',
          phone: candidate?.phone || '',
          process: candidate?.process || '',
          original_location: candidate?.original_location || '',
          job_location: candidate?.job_location || '',
          initial_payment: candidate?.initial_payment || '',
          demo_hr_round: candidate?.demo_hr_round || '',
          final_outcome: candidate?.final_outcome || ''
        };
      });
      setAllPerformances(enrichedPerf);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve reschedule');
    }
  };

  // Reject reschedule request
  const handleRejectReschedule = async (requestId) => {
    if (!confirm('Reject this reschedule request?')) return;
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/reschedule/reject/${requestId}`, {}, config);
      toast.success('Reschedule request rejected');
      fetchRescheduleRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject reschedule');
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.patch(`${API}/notifications/${notificationId}/read`, {}, config);
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark notification as read');
    }
  };

  const updateAvailabilityStatus = async (status) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.patch(`${API}/users/availability?status=${status}`, {}, config);
      setAvailabilityStatus(status);
      toast.success(`Status updated to ${status}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const availabilityOptions = [
    { value: 'available', label: 'Available', color: 'from-emerald-500 to-green-500', icon: '🟢' },
    { value: 'offline', label: 'Offline', color: 'from-gray-500 to-slate-600', icon: '⚫' },
    { value: 'meeting', label: 'Meeting', color: 'from-blue-500 to-indigo-600', icon: '🔵' },
    { value: 'training', label: 'Training', color: 'from-amber-500 to-orange-500', icon: '🟡' },
    { value: 'system', label: 'System', color: 'from-red-500 to-rose-600', icon: '🔴' }
  ];

  const currentAvailability = availabilityOptions.find(a => a.value === availabilityStatus) || availabilityOptions[0];

  const sortedTasks = [...tasks].sort((a, b) => {
    if (a.status === 'completed' && b.status !== 'completed') return 1;
    if (a.status !== 'completed' && b.status === 'completed') return -1;
    return priorityOrder[a.priority] - priorityOrder[b.priority];
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="h-4 w-4" />;
      case 'in_progress':
        return <Clock className="h-4 w-4" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-gradient-to-r from-[#E8EDF4] to-[#F0F4F8] sticky top-0 z-[80] shadow-sm border-b border-slate-200/50">
        <div className="max-w-full mx-auto px-2 md:px-6 py-1 flex items-center justify-between">
          {/* Hamburger Menu + Logo */}
          <div className="flex items-center gap-3 md:gap-4">
            {/* Hamburger Menu Button - Toggle Sidebar - Larger touch target */}
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-2.5 rounded-lg transition-colors text-[#1e3a5f] hover:bg-slate-200/50 active:bg-slate-300/50 touch-manipulation"
              data-testid="sidebar-toggle-btn"
              aria-label="Toggle menu"
              aria-expanded={!sidebarCollapsed}
              aria-controls="dashboard-sidebar"
            >
              <Menu className="h-6 w-6" />
            </button>
            {/* Logo with text */}
            <div className="flex items-center gap-3">
              <img 
                src="/logo.png?v=41" 
                alt="Officium Connect" 
                className="h-14 md:h-16 w-auto"
                data-testid="dashboard-logo"
              />
              <span className="font-heading font-bold text-base md:text-xl tracking-tight whitespace-nowrap text-[#1e3a5f] hidden sm:block">
                Officium Connect
              </span>
            </div>
          </div>

          {/* Universal Search Bar - Center - Show on all screens */}
          <div className="flex-1 max-w-xl mx-2 md:mx-4 relative">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Search..."
                value={universalSearch}
                onChange={(e) => {
                  setUniversalSearch(e.target.value);
                  handleUniversalSearch(e.target.value);
                }}
                onFocus={() => setIsSearchOpen(true)}
                onBlur={(e) => {
                  // Delay closing to allow click on results
                  setTimeout(() => setIsSearchOpen(false), 200);
                }}
                className="w-full pl-9 pr-8 py-1.5 md:py-2 text-sm bg-white border border-slate-200 text-slate-800 placeholder:text-slate-400 rounded-full shadow-sm focus:ring-2 focus:ring-blue-200/50 focus:border-blue-300"
                data-testid="universal-search"
              />
              {universalSearch && (
                <button 
                  onClick={() => { setUniversalSearch(''); setSearchResults({ candidates: [], jobs: [], applications: [], companies: [], users: [], tasks: [] }); setIsSearchOpen(false); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            
            {/* Search Results Dropdown */}
            {isSearchOpen && universalSearch.length >= 2 && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 max-h-96 overflow-y-auto z-50">
                {searchLoading ? (
                  <div className="p-4 text-center text-gray-500">Searching...</div>
                ) : (
                  <>
                    {searchResults.candidates.length > 0 && hasTabAccess(currentUser, 'candidates') && (
                      <div className="p-2 border-b">
                        <p className="text-xs font-semibold text-gray-400 px-2 mb-1">CANDIDATES</p>
                        {searchResults.candidates.map(c => (
                          <button
                            key={c.id}
                            onClick={() => { setActiveTab('candidates'); setUniversalSearch(''); setIsSearchOpen(false); }}
                            className="w-full flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg text-left"
                          >
                            <div className="h-8 w-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 font-semibold text-sm">
                              {c.name?.charAt(0)}
                            </div>
                            <div>
                              <p className="font-medium text-sm text-gray-800">{c.name}</p>
                              <p className="text-xs text-gray-500">{c.email}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {searchResults.jobs.length > 0 && hasTabAccess(currentUser, 'job-updates') && (
                      <div className="p-2 border-b">
                        <p className="text-xs font-semibold text-gray-400 px-2 mb-1">JOBS</p>
                        {searchResults.jobs.map(j => (
                          <button
                            key={j.id}
                            onClick={() => { setActiveTab('job-updates'); setUniversalSearch(''); setIsSearchOpen(false); }}
                            className="w-full flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg text-left"
                          >
                            <div className="h-8 w-8 rounded-full bg-amber-100 flex items-center justify-center">
                              <Briefcase className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-amber-600" />
                            </div>
                            <div>
                              <p className="font-medium text-sm text-gray-800">{j.title}</p>
                              <p className="text-xs text-gray-500">{j.company}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {searchResults.companies.length > 0 && hasTabAccess(currentUser, 'companies') && (
                      <div className="p-2 border-b">
                        <p className="text-xs font-semibold text-gray-400 px-2 mb-1">COMPANIES</p>
                        {searchResults.companies.map(c => (
                          <button
                            key={c.id}
                            onClick={() => { setActiveTab('companies'); setUniversalSearch(''); setIsSearchOpen(false); }}
                            className="w-full flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg text-left"
                          >
                            <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center">
                              <Building2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-indigo-600" />
                            </div>
                            <div>
                              <p className="font-medium text-sm text-gray-800">{c.name}</p>
                              <p className="text-xs text-gray-500">{c.industry}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {searchResults.applications.length > 0 && hasTabAccess(currentUser, 'all-applications') && (
                      <div className="p-2">
                        <p className="text-xs font-semibold text-gray-400 px-2 mb-1">APPLICATIONS</p>
                        {searchResults.applications.map(a => (
                          <button
                            key={a.id}
                            onClick={() => { setActiveTab('all-applications'); setUniversalSearch(''); setIsSearchOpen(false); }}
                            className="w-full flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg text-left"
                          >
                            <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                              <FileText className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-medium text-sm text-gray-800">{a.candidate_name}</p>
                              <p className="text-xs text-gray-500">{a.job_title} at {a.company_name}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {searchResults.users?.length > 0 && hasTabAccess(currentUser, 'users') && (
                      <div className="p-2 border-b">
                        <p className="text-xs font-semibold text-gray-400 px-2 mb-1">USERS</p>
                        {searchResults.users.map(u => (
                          <button
                            key={u.id}
                            onClick={() => { setActiveTab('users'); setUniversalSearch(''); setIsSearchOpen(false); }}
                            className="w-full flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg text-left"
                          >
                            <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 font-semibold text-sm">
                              {u.name?.charAt(0)}
                            </div>
                            <div>
                              <p className="font-medium text-sm text-gray-800">{u.name}</p>
                              <p className="text-xs text-gray-500">{u.role} • {u.email}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {searchResults.tasks?.length > 0 && hasTabAccess(currentUser, 'tasks') && (
                      <div className="p-2 border-b">
                        <p className="text-xs font-semibold text-gray-400 px-2 mb-1">TASKS</p>
                        {searchResults.tasks.map(t => (
                          <button
                            key={t.id}
                            onClick={() => { setActiveTab('tasks'); setUniversalSearch(''); setIsSearchOpen(false); }}
                            className="w-full flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg text-left"
                          >
                            <div className="h-8 w-8 rounded-full bg-orange-100 flex items-center justify-center">
                              <ClipboardList className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-orange-600" />
                            </div>
                            <div>
                              <p className="font-medium text-sm text-gray-800">{t.title}</p>
                              <p className="text-xs text-gray-500">{t.status} • {t.assigned_to}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {/* Check if no accessible results */}
                    {(
                      (!searchResults.candidates.length || !hasTabAccess(currentUser, 'candidates')) && 
                      (!searchResults.jobs.length || !hasTabAccess(currentUser, 'job-updates')) && 
                      (!searchResults.companies.length || !hasTabAccess(currentUser, 'companies')) && 
                      (!searchResults.applications.length || !hasTabAccess(currentUser, 'all-applications')) &&
                      (!searchResults.users?.length || !hasTabAccess(currentUser, 'users')) && 
                      (!searchResults.tasks?.length || !hasTabAccess(currentUser, 'tasks'))
                    ) && (
                      <div className="p-4 text-center text-gray-500">No results found</div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Clean Header Actions: Team, Messages, Notifications */}
          <div className="flex items-center gap-0.5 md:gap-2">
            {/* Team Directory - Hide on small mobile */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-10 w-10 text-[#1e3a5f] hover:bg-slate-200/50 hidden sm:flex" data-testid="team-directory-btn" title="Team Directory">
                  <UsersIcon className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto">
                <div className="p-3">
                  <p className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <UsersIcon className="h-4 w-4" />
                    Team Directory
                  </p>
                  {users.map((user) => {
                    const userStatus = availabilityOptions.find(a => a.value === user.availability_status) || availabilityOptions[0];
                    return (
                      <div key={user.id} className="p-2 rounded-lg hover:bg-slate-50 border-b border-gray-100 last:border-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium truncate">{user.name}</p>
                            <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                          </div>
                          <span className={`px-2 py-0.5 rounded-full text-xs bg-gradient-to-r ${userStatus.color} text-white`}>
                            {userStatus.label}
                          </span>
                        </div>
                        {user.phone && (
                          <a 
                            href={`tel:${user.phone}`}
                            className="mt-2 flex items-center gap-2 text-xs text-blue-600 bg-blue-50 px-2 py-1.5 rounded-lg"
                            onClick={() => toast.info(`Calling ${user.name}...`)}
                          >
                            <Phone className="h-3 w-3" />
                            <span className="font-mono">{user.phone}</span>
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Universal Notification Center - Slack-style */}
              <NotificationBell />
              {currentUser?.is_admin && <Button variant="ghost" size="sm" onClick={() => window.open('/data-backup', '_blank', 'noopener,noreferrer')} title="Backup & data safety" aria-label="Backup & data safety" data-testid="open-data-backup"><Archive className="h-5 w-5 text-slate-600" /></Button>}
          </div>
        </div>
      </header>

      {/* Sidebar + Main Content Layout */}
      <div className="flex min-h-[calc(100vh-80px)]">
        {/* Mobile Sidebar Overlay - Shows on all screens when sidebar is open */}
        {!sidebarCollapsed && (
          <div 
            className="fixed inset-0 bg-black/50 z-[60]"
            data-testid="sidebar-backdrop"
            onClick={() => setSidebarCollapsed(true)}
          />
        )}
        
        {/* Left Sidebar - Toggleable on all screens */}
        <aside 
          id="dashboard-sidebar"
          aria-hidden={sidebarCollapsed}
          inert={sidebarCollapsed ? true : undefined}
          className={`fixed left-0 top-14 bottom-0 w-64 max-w-[85vw] z-[70] flex flex-col bg-gradient-to-b from-slate-900 to-slate-800 text-white transition-transform duration-200 ${
            sidebarCollapsed ? '-translate-x-full invisible pointer-events-none' : 'translate-x-0 visible'
          }`}
          data-testid="admin-sidebar"
        >
          {/* Sidebar Header */}
          <div className="p-3 border-b border-slate-700 flex items-center justify-between">
            <span className="font-semibold text-sm text-slate-300">
              {currentUser?.is_admin ? 'Admin Panel' : currentUser?.role === 'crm_user' ? 'CRM Panel' : 'Menu'}
            </span>
            <button
              onClick={() => setSidebarCollapsed(true)}
              data-testid="sidebar-close"
              className="p-1.5 hover:bg-slate-700 rounded-lg transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Main Navigation */}
          <nav className="flex-1 min-h-0 p-2 space-y-1 overflow-y-auto">
            {/* Admin/CRM Navigation Items */}
            {(currentUser?.is_admin || currentUser?.role === 'crm_user') && (
              <>
                {[
                  { id: 'tasks', icon: ClipboardList, label: 'Tasks', color: 'from-violet-500 to-purple-600' },
                  { id: 'candidates', icon: UsersIcon, label: 'Candidates', color: 'from-emerald-500 to-teal-600' },
                  { id: 'google-sheets', icon: FileSpreadsheet, label: 'Sheets', color: 'from-emerald-500 to-teal-600' },
                  { id: 'hr-rounds', icon: Calendar, label: 'HR', color: 'from-blue-500 to-indigo-600' },
                  { id: 'interviews', icon: Calendar, label: 'Interviews', color: 'from-purple-500 to-violet-600' },
                  { id: 'job-updates', icon: Briefcase, label: 'Jobs', color: 'from-amber-500 to-orange-600' },
                  { id: 'all-applications', icon: FileText, label: 'Applications', color: 'from-amber-500 to-orange-600' },
                  { id: 'blacklist-restrictions', icon: Ban, label: 'Restrictions', color: 'from-red-500 to-rose-600', adminOnly: true },
                  { id: 'companies', icon: Building2, label: 'Companies', color: 'from-indigo-500 to-blue-600' },
                  { id: 'skill-assessments', icon: Target, label: 'Assessments', color: 'from-purple-500 to-indigo-600' },
                  { id: 'career-paths', icon: Route, label: 'Careers', color: 'from-amber-500 to-orange-600' },
                  { id: 'team-reports', icon: ClipboardCheck, label: 'Reports', color: 'from-teal-500 to-cyan-600' },
                  { id: 'user-management', icon: UserCog, label: 'Users', color: 'from-indigo-500 to-blue-600', adminOnly: true },
                  { id: 'officium-pages', icon: BookOpen, label: 'Officium Pages', color: 'from-indigo-500 to-blue-600', requiresQuestionsAccess: true },
                  { id: 'analytics', icon: BarChart3, label: 'Analytics', color: 'from-emerald-500 to-green-600' },
                ].filter(item => {
                  if (item.adminOnly && !currentUser?.is_admin) return false;
                  if (item.requiresQuestionsAccess && !currentUser?.is_admin && !currentUser?.questions_tab_access) return false;
                  return true;
                }).map((item) => (
                  <button
                    key={item.id}
                    onClick={() => { setActiveTab(item.id); setSidebarCollapsed(true); }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                      activeTab === item.id
                        ? `bg-gradient-to-r ${item.color} shadow-lg`
                        : 'hover:bg-slate-700/50 text-slate-300 hover:text-white'
                    }`}
                    data-testid={`sidebar-${item.id}`}
                  >
                    <item.icon className="h-5 w-5 flex-shrink-0" />
                    <span className="text-sm font-medium truncate">{item.label}</span>
                  </button>
                ))}
              </>
            )}

            {/* Regular User Navigation Items */}
            {!currentUser?.is_admin && currentUser?.role !== 'crm_user' && (
              <>
                {[
                  { id: 'tasks', icon: ClipboardList, label: 'My Tasks', color: 'from-violet-500 to-purple-600' },
                  { id: 'interviews', icon: Calendar, label: 'Interviews', color: 'from-purple-500 to-violet-600', requiresTabAccess: true },
                  { id: 'job-updates', icon: Briefcase, label: 'Jobs', color: 'from-amber-500 to-orange-600' },
                  { id: 'all-applications', icon: FileText, label: 'Applications', color: 'from-blue-500 to-indigo-600' },
                  { id: 'companies', icon: Building2, label: 'Companies', color: 'from-indigo-500 to-blue-600' },
                  { id: 'team-reports', icon: ClipboardCheck, label: 'Reports', color: 'from-teal-500 to-cyan-600' },
                  { id: 'officium-pages', icon: BookOpen, label: 'Officium Pages', color: 'from-indigo-500 to-blue-600', requiresQuestionsAccess: true },
                  { id: 'forms', icon: ClipboardList, label: 'Forms', color: 'from-indigo-500 to-blue-600' },
                  { id: 'support', icon: MessageCircle, label: 'Support Centre', color: 'from-cyan-500 to-teal-600' },
                ].filter(item => {
                  if (item.requiresQuestionsAccess && !currentUser?.questions_tab_access) return false;
                  if (item.requiresTabAccess && !hasTabAccess(currentUser, item.id)) return false;
                  return true;
                }).map((item) => (
                  <button
                    key={item.id}
                    onClick={() => { setActiveTab(item.id); setSidebarCollapsed(true); }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                      activeTab === item.id
                        ? `bg-gradient-to-r ${item.color} shadow-lg`
                        : 'hover:bg-slate-700/50 text-slate-300 hover:text-white'
                    }`}
                    data-testid={`sidebar-${item.id}`}
                  >
                    <item.icon className="h-5 w-5 flex-shrink-0" />
                    <span className="text-sm font-medium truncate">{item.label}</span>
                  </button>
                ))}
              </>
            )}
              
              {/* Referral Management Section */}
              {currentUser?.is_admin && (
                <div className="pt-2 mt-2 border-t border-slate-700/50">
                  <p className="px-3 py-1 text-xs font-semibold text-slate-500 uppercase tracking-wider">Referrals</p>
                  {[
                    { id: 'referral-management', icon: Gift, label: 'Manage Referrals', color: 'from-rose-500 to-pink-600' },
                    { id: 'all-referrals', icon: UsersIcon, label: 'All Referrals', color: 'from-rose-500 to-pink-600' },
                    { id: 'popup-announcements', icon: Bell, label: 'Announcements', color: 'from-orange-500 to-red-600' },
                  ].map((item) => (
                    <button
                      key={item.id}
                      onClick={() => { setActiveTab(item.id); setSidebarCollapsed(true); }}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all ${
                        activeTab === item.id
                          ? `bg-gradient-to-r ${item.color} shadow-lg`
                          : 'hover:bg-slate-700/50 text-slate-300 hover:text-white'
                      }`}
                      data-testid={`sidebar-${item.id}`}
                    >
                      <item.icon className="h-4 w-4 flex-shrink-0" />
                      <span className="text-xs font-medium truncate">{item.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </nav>

            {/* Bottom Section - Settings, Profile, Logout */}
            <div className="p-2 border-t border-slate-700 space-y-1">
              <button data-testid="sidebar-my-schedule" onClick={() => navigate('/team-management')} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-700/50 text-slate-300 hover:text-white"><Calendar className="h-5 w-5 shrink-0" /><span className="text-sm font-medium">My schedule & leave</span></button>
              {/* Workforce Analytics - Admin Only */}
              {currentUser?.is_admin && (
                <button
                  onClick={() => navigate('/workforce-analytics')}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-700/50 text-slate-300 hover:text-white transition-all"
                  data-testid="sidebar-workforce"
                >
                  <Activity className="h-5 w-5 flex-shrink-0" />
                  <span className="text-sm font-medium">Workforce Analytics</span>
                </button>
              )}
              {/* Admin Panel - Admin Only */}
              {currentUser?.is_admin && (
                <button
                  onClick={() => navigate('/admin')}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gradient-to-r hover:from-purple-600 hover:to-indigo-600 text-slate-300 hover:text-white transition-all"
                  data-testid="sidebar-admin-panel"
                >
                  <Shield className="h-5 w-5 flex-shrink-0" />
                  <span className="text-sm font-medium">Admin Panel</span>
                </button>
              )}
              {/* Settings - Admin Only */}
              {currentUser?.is_admin && (
                <button
                  onClick={() => { setActiveTab('site-settings'); setSidebarCollapsed(true); }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                    activeTab === 'site-settings'
                      ? 'bg-gradient-to-r from-slate-600 to-gray-700 shadow-lg'
                      : 'hover:bg-slate-700/50 text-slate-300 hover:text-white'
                  }`}
                  data-testid="sidebar-settings"
                >
                  <Settings className="h-5 w-5 flex-shrink-0" />
                  <span className="text-sm font-medium">Settings</span>
                </button>
              )}
              <button
                onClick={() => { openProfileDialog(); setSidebarCollapsed(true); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-700/50 text-slate-300 hover:text-white transition-all"
                data-testid="sidebar-profile"
              >
                <User className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm font-medium">Profile</span>
              </button>
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-red-600/80 text-slate-300 hover:text-white transition-all"
                data-testid="sidebar-logout"
              >
                <LogOut className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm font-medium">Logout</span>
              </button>
            </div>
          </aside>

        {/* Main Content Area - Full Width */}
        <main className={`flex-1 ${(currentUser?.is_admin || currentUser?.role === 'crm_user') ? 'lg:ml-0' : ''} w-full px-4 md:px-6 lg:px-8 py-4 md:py-8 scroll-mt-16`}>
        {/* Announcements Banner */}
        {announcements.filter(a => !dismissedAnnouncements.includes(a.id)).length > 0 && (
          <div className="mb-6 space-y-3">
            {announcements.filter(a => !dismissedAnnouncements.includes(a.id)).slice(0, 2).map((ann) => (
              <div 
                key={ann.id}
                className={`bg-gradient-to-r ${announcementPriorityColors[ann.priority] || announcementPriorityColors.Medium} rounded-sm p-4 text-white relative`}
                data-testid={`announcement-${ann.id}`}
              >
                <button
                  onClick={() => setDismissedAnnouncements([...dismissedAnnouncements, ann.id])}
                  className="absolute top-2 right-2 text-white/80 hover:text-white"
                >
                  <X className="h-4 w-4" />
                </button>
                <div className="flex items-center gap-2 mb-1">
                  <Gift className="h-5 w-5" />
                  <h3 className="font-heading font-semibold">{ann.title}</h3>
                  <Badge className="bg-white/20 text-white text-xs">{ann.priority || 'Medium'}</Badge>
                  {ann.bonus_amount && (
                    <Badge className="bg-green-400 text-green-900">{ann.bonus_amount}</Badge>
                  )}
                </div>
                <p className="text-sm text-white/90">{ann.description}</p>
                {ann.valid_until && (
                  <p className="text-xs text-white/70 mt-1">
                    Valid until: {formatISTDate(ann.valid_until)}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Lead Specialist - redirect to My Candidates */}
        {currentUser?.role === 'lead_specialist' && (
          <div className="text-center py-16">
            <div className="max-w-md mx-auto">
              <div className="h-20 w-20 rounded-full bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center mx-auto mb-6">
                <Briefcase className="h-10 w-10 text-emerald-600" />
              </div>
              <h2 className="font-heading text-2xl font-bold mb-3 bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                Welcome, {currentUser?.name}!
              </h2>
              <p className="text-muted-foreground mb-6">
                Access your assigned candidates and manage referrals from the My Candidates page.
              </p>
              <Button 
                onClick={() => navigate('/lead-specialist')}
                className="rounded-lg bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 shadow-lg shadow-emerald-200 transition-all"
              >
                <Briefcase className="h-4 w-4 mr-2" />
                Go to My Candidates
              </Button>
            </div>
          </div>
        )}

        {/* Regular users and admins - show full dashboard */}
        {currentUser && currentUser?.role !== 'lead_specialist' && currentUser?.user_type !== 'lead_specialist' && (
        <Tabs value={activeTab} onValueChange={(value) => {
          if (value === 'messages') {
            // Open fullscreen messages view instead of tab content
            setPreviousTabBeforeChat(activeTab);
            setIsMessagesFullscreen(true);
            fetchConversations();
          } else {
            setActiveTab(value);
          }
        }} className="space-y-6">
          
          {/* Modern Dashboard Navigation */}
          <div className="space-y-6">
            
            {/* Quick Actions Section - Featured Cards */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="h-1 w-1 rounded-full bg-violet-500"></div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400">Quick Actions</h3>
              </div>
              <TabsList className="grid grid-cols-2 md:grid-cols-4 gap-3 bg-transparent p-0 h-auto">
                {/* Tasks - Primary Featured Card */}
                {hasTabAccess(currentUser, 'tasks') && (
                <TabsTrigger 
                  value="tasks" 
                  data-testid="tasks-tab" 
                  className="group relative flex flex-col items-start p-4 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md hover:border-violet-200 hover:-translate-y-0.5 transition-all duration-200 data-[state=active]:border-violet-400 data-[state=active]:shadow-md data-[state=active]:bg-violet-50/50 text-left h-auto"
                >
                  <div className="flex items-center justify-between w-full mb-3">
                    <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center group-hover:bg-violet-200 group-data-[state=active]:bg-violet-500 transition-colors">
                      <ClipboardList className="h-5 w-5 text-violet-600 group-data-[state=active]:text-white" />
                    </div>
                    {sortedTasks.length > 0 && (
                      <span className="text-xs font-medium text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">{sortedTasks.length}</span>
                    )}
                  </div>
                  <span className="text-sm font-semibold text-slate-700 group-data-[state=active]:text-violet-700">Tasks</span>
                  <span className="text-xs text-slate-400 mt-0.5">Manage assignments</span>
                </TabsTrigger>
                )}

                {/* Messages */}
                {hasTabAccess(currentUser, 'messages') && (
                <TabsTrigger 
                  value="messages" 
                  data-testid="messages-tab" 
                  className="group relative flex flex-col items-start p-4 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md hover:border-violet-200 hover:-translate-y-0.5 transition-all duration-200 data-[state=active]:border-violet-400 data-[state=active]:shadow-md data-[state=active]:bg-violet-50/50 text-left h-auto"
                >
                  <div className="flex items-center justify-between w-full mb-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center group-hover:bg-emerald-200 group-data-[state=active]:bg-violet-500 transition-colors">
                      <MessageCircle className="h-5 w-5 text-emerald-600 group-data-[state=active]:text-white" />
                    </div>
                    {unreadMessageCount > 0 && (
                      <span className="text-xs font-bold text-white bg-emerald-500 px-2 py-0.5 rounded-full animate-pulse">{unreadMessageCount}</span>
                    )}
                  </div>
                  <span className="text-sm font-semibold text-slate-700 group-data-[state=active]:text-violet-700">Messages</span>
                  <span className="text-xs text-slate-400 mt-0.5">Team chat</span>
                </TabsTrigger>
                )}

                {/* Support */}
                {hasTabAccess(currentUser, 'support') && (
                <TabsTrigger 
                  value="support" 
                  data-testid="support-tab" 
                  className="group relative flex flex-col items-start p-4 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md hover:border-violet-200 hover:-translate-y-0.5 transition-all duration-200 data-[state=active]:border-violet-400 data-[state=active]:shadow-md data-[state=active]:bg-violet-50/50 text-left h-auto"
                >
                  <div className="flex items-center justify-between w-full mb-3">
                    <div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center group-hover:bg-cyan-200 group-data-[state=active]:bg-violet-500 transition-colors">
                      <Headphones className="h-5 w-5 text-cyan-600 group-data-[state=active]:text-white" />
                    </div>
                    {(() => {
                      const pendingChatCount = supportTickets?.filter(t => t.ticket_type !== 'call_request' && t.status !== 'Resolved' && t.status !== 'Closed').length || 0;
                      const pendingCallCount = supportTickets?.filter(t => t.ticket_type === 'call_request' && t.call_status !== 'Completed' && t.call_status !== 'Cancelled').length || 0;
                      const displayCount = unreadSupportCount > 0 ? unreadSupportCount : (pendingChatCount + pendingCallCount);
                      return displayCount > 0 ? (
                        <span className="text-xs font-bold text-white bg-red-500 px-2 py-0.5 rounded-full animate-pulse">{displayCount}</span>
                      ) : null;
                    })()}
                  </div>
                  <span className="text-sm font-semibold text-slate-700 group-data-[state=active]:text-violet-700">Support</span>
                  <span className="text-xs text-slate-400 mt-0.5">Help tickets</span>
                </TabsTrigger>
                )}

                {/* Analytics */}
                {hasTabAccess(currentUser, 'analytics') && (
                <TabsTrigger 
                  value="analytics" 
                  data-testid="analytics-tab" 
                  className="group relative flex flex-col items-start p-4 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md hover:border-violet-200 hover:-translate-y-0.5 transition-all duration-200 data-[state=active]:border-violet-400 data-[state=active]:shadow-md data-[state=active]:bg-violet-50/50 text-left h-auto"
                >
                  <div className="flex items-center justify-between w-full mb-3">
                    <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center group-hover:bg-indigo-200 group-data-[state=active]:bg-violet-500 transition-colors">
                      <BarChart3 className="h-5 w-5 text-indigo-600 group-data-[state=active]:text-white" />
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-slate-700 group-data-[state=active]:text-violet-700">Analytics</span>
                  <span className="text-xs text-slate-400 mt-0.5">View insights</span>
                </TabsTrigger>
                )}
              </TabsList>
            </div>

            {/* HR & Recruitment Section */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="h-1 w-1 rounded-full bg-emerald-500"></div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400">HR & Recruitment</h3>
              </div>
              <TabsList className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2 sm:gap-3 bg-transparent p-0 h-auto">
                {/* Candidates */}
                {hasTabAccess(currentUser, 'candidates') && (
                <TabsTrigger 
                  value="candidates" 
                  data-testid="candidates-tab" 
                  className="group flex flex-col items-center p-2 sm:p-3 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 hover:-translate-y-0.5 transition-all duration-200 data-[state=active]:border-violet-400 data-[state=active]:shadow-md data-[state=active]:bg-violet-50/50 h-auto"
                >
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-emerald-100 flex items-center justify-center mb-1 sm:mb-1 sm:mb-2 group-hover:bg-emerald-200 group-data-[state=active]:bg-violet-500 transition-colors">
                    <UsersIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-emerald-600 group-data-[state=active]:text-white" />
                  </div>
                  <span className="text-[10px] sm:text-[10px] sm:text-xs font-medium text-slate-600 group-data-[state=active]:text-violet-700">Candidates</span>
                </TabsTrigger>
                )}

                {/* HR Rounds */}
                {hasTabAccess(currentUser, 'hr-rounds') && (
                <TabsTrigger 
                  value="hr-rounds" 
                  data-testid="hr-rounds-tab" 
                  className="group flex flex-col items-center p-2 sm:p-3 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 hover:-translate-y-0.5 transition-all duration-200 data-[state=active]:border-violet-400 data-[state=active]:shadow-md data-[state=active]:bg-violet-50/50 h-auto"
                >
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-blue-100 flex items-center justify-center mb-1 sm:mb-2 group-hover:bg-blue-200 group-data-[state=active]:bg-violet-500 transition-colors">
                    <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-blue-600 group-data-[state=active]:text-white" />
                  </div>
                  <span className="text-[10px] sm:text-xs font-medium text-slate-600 group-data-[state=active]:text-violet-700">HR</span>
                </TabsTrigger>
                )}
                
                {/* HR Config - Admin Only */}
                {currentUser?.is_admin && (
                <TabsTrigger 
                  value="hr-config" 
                  data-testid="hr-config-tab" 
                  className="group flex flex-col items-center p-2 sm:p-3 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 hover:-translate-y-0.5 transition-all duration-200 data-[state=active]:border-violet-400 data-[state=active]:shadow-md data-[state=active]:bg-violet-50/50 h-auto"
                >
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-cyan-100 flex items-center justify-center mb-1 sm:mb-2 group-hover:bg-cyan-200 group-data-[state=active]:bg-violet-500 transition-colors">
                    <Settings className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-cyan-600 group-data-[state=active]:text-white" />
                  </div>
                  <span className="text-[10px] sm:text-xs font-medium text-slate-600 group-data-[state=active]:text-violet-700">HR Config</span>
                </TabsTrigger>
                )}

                {/* Interviews - Schedule & Manage */}
                {(hasTabAccess(currentUser, 'interviews') || hasTabAccess(currentUser, 'team-management')) && (
                <TabsTrigger 
                  value="interviews" 
                  data-testid="interviews-tab" 
                  className="group flex flex-col items-center p-2 sm:p-3 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 hover:-translate-y-0.5 transition-all duration-200 data-[state=active]:border-violet-400 data-[state=active]:shadow-md data-[state=active]:bg-violet-50/50 h-auto"
                >
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-purple-100 flex items-center justify-center mb-1 sm:mb-2 group-hover:bg-purple-200 group-data-[state=active]:bg-violet-500 transition-colors">
                    <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-purple-600 group-data-[state=active]:text-white" />
                  </div>
                  <span className="text-[10px] sm:text-xs font-medium text-slate-600 group-data-[state=active]:text-violet-700">Interviews</span>
                </TabsTrigger>
                )}

                {/* Jobs */}
                {hasTabAccess(currentUser, 'job-updates') && (
                <TabsTrigger 
                  value="job-updates" 
                  data-testid="jobs-tab" 
                  className="group flex flex-col items-center p-2 sm:p-3 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 hover:-translate-y-0.5 transition-all duration-200 data-[state=active]:border-violet-400 data-[state=active]:shadow-md data-[state=active]:bg-violet-50/50 h-auto"
                >
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-amber-100 flex items-center justify-center mb-1 sm:mb-2 group-hover:bg-amber-200 group-data-[state=active]:bg-violet-500 transition-colors">
                    <Briefcase className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-amber-600 group-data-[state=active]:text-white" />
                  </div>
                  <span className="text-[10px] sm:text-xs font-medium text-slate-600 group-data-[state=active]:text-violet-700">Jobs</span>
                </TabsTrigger>
                )}

                {/* Applications */}
                {hasTabAccess(currentUser, 'all-applications') && (
                <TabsTrigger 
                  value="all-applications" 
                  data-testid="applications-tab" 
                  className="group flex flex-col items-center p-2 sm:p-3 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 hover:-translate-y-0.5 transition-all duration-200 data-[state=active]:border-violet-400 data-[state=active]:shadow-md data-[state=active]:bg-violet-50/50 h-auto"
                >
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-indigo-100 flex items-center justify-center mb-1 sm:mb-2 group-hover:bg-indigo-200 group-data-[state=active]:bg-violet-500 transition-colors">
                    <FileText className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-indigo-600 group-data-[state=active]:text-white" />
                  </div>
                  <span className="text-[10px] sm:text-xs font-medium text-slate-600 group-data-[state=active]:text-violet-700">Applications</span>
                </TabsTrigger>
                )}

                {/* Blacklist & Restrictions */}
                {hasTabAccess(currentUser, 'blacklist-restrictions') && currentUser?.is_admin && (
                <TabsTrigger 
                  value="blacklist-restrictions" 
                  data-testid="blacklist-restrictions-tab" 
                  className="group flex flex-col items-center p-3 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 hover:-translate-y-0.5 transition-all duration-200 data-[state=active]:border-red-400 data-[state=active]:shadow-md data-[state=active]:bg-red-50/50 h-auto"
                >
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-red-100 flex items-center justify-center mb-1 sm:mb-2 group-hover:bg-red-200 group-data-[state=active]:bg-red-500 transition-colors">
                    <Ban className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-red-600 group-data-[state=active]:text-white" />
                  </div>
                  <span className="text-[10px] sm:text-xs font-medium text-slate-600 group-data-[state=active]:text-red-700">Restrictions</span>
                </TabsTrigger>
                )}

                {/* Companies */}
                {hasTabAccess(currentUser, 'companies') && (
                <TabsTrigger 
                  value="companies" 
                  data-testid="companies-tab" 
                  className="group flex flex-col items-center p-2 sm:p-3 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 hover:-translate-y-0.5 transition-all duration-200 data-[state=active]:border-violet-400 data-[state=active]:shadow-md data-[state=active]:bg-violet-50/50 h-auto"
                >
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-sky-100 flex items-center justify-center mb-1 sm:mb-2 group-hover:bg-sky-200 group-data-[state=active]:bg-violet-500 transition-colors">
                    <Building2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-sky-600 group-data-[state=active]:text-white" />
                  </div>
                  <span className="text-[10px] sm:text-xs font-medium text-slate-600 group-data-[state=active]:text-violet-700">Companies</span>
                </TabsTrigger>
                )}

                {/* Officium Pages */}
                {hasTabAccess(currentUser, 'officium-pages') && (
                <TabsTrigger 
                  value="officium-pages" 
                  data-testid="officium-pages-tab" 
                  className="group flex flex-col items-center p-2 sm:p-3 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 hover:-translate-y-0.5 transition-all duration-200 data-[state=active]:border-violet-400 data-[state=active]:shadow-md data-[state=active]:bg-violet-50/50 h-auto"
                >
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-purple-100 flex items-center justify-center mb-1 sm:mb-2 group-hover:bg-purple-200 group-data-[state=active]:bg-violet-500 transition-colors">
                    <BookOpen className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-indigo-600 group-data-[state=active]:text-white" />
                  </div>
                  <span className="text-[10px] sm:text-xs font-medium text-slate-600 group-data-[state=active]:text-violet-700">Officium Pages</span>
                </TabsTrigger>
                )}
                
                {/* Question Banks - Telephonic Screening */}
                {(currentUser?.is_admin || currentUser?.role === 'crm_user') && (
                <TabsTrigger 
                  value="question-banks" 
                  data-testid="question-banks-tab" 
                  className="group flex flex-col items-center p-2 sm:p-3 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 hover:-translate-y-0.5 transition-all duration-200 data-[state=active]:border-violet-400 data-[state=active]:shadow-md data-[state=active]:bg-violet-50/50 h-auto"
                >
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-emerald-100 flex items-center justify-center mb-1 sm:mb-2 group-hover:bg-emerald-200 group-data-[state=active]:bg-violet-500 transition-colors">
                    <Phone className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-emerald-600 group-data-[state=active]:text-white" />
                  </div>
                  <span className="text-[10px] sm:text-xs font-medium text-slate-600 group-data-[state=active]:text-violet-700">Screening</span>
                </TabsTrigger>
                )}
              </TabsList>
            </div>

            {/* Tools & Content Section */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="h-1 w-1 rounded-full bg-blue-500"></div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400">Tools & Content</h3>
              </div>
              <TabsList className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2 sm:gap-3 bg-transparent p-0 h-auto">
                {/* Grammar */}
                {hasTabAccess(currentUser, 'grammar') && (
                <TabsTrigger 
                  value="grammar" 
                  data-testid="grammar-tab" 
                  className="group flex flex-col items-center p-2 sm:p-3 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 hover:-translate-y-0.5 transition-all duration-200 data-[state=active]:border-violet-400 data-[state=active]:shadow-md data-[state=active]:bg-violet-50/50 h-auto"
                >
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-pink-100 flex items-center justify-center mb-1 sm:mb-2 group-hover:bg-pink-200 group-data-[state=active]:bg-violet-500 transition-colors">
                    <Wand2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-pink-600 group-data-[state=active]:text-white" />
                  </div>
                  <span className="text-[10px] sm:text-xs font-medium text-slate-600 group-data-[state=active]:text-violet-700">Grammar</span>
                </TabsTrigger>
                )}

                {/* Forms */}
                {hasTabAccess(currentUser, 'forms') && (
                <TabsTrigger 
                  value="forms" 
                  data-testid="forms-tab" 
                  className="group flex flex-col items-center p-2 sm:p-3 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 hover:-translate-y-0.5 transition-all duration-200 data-[state=active]:border-violet-400 data-[state=active]:shadow-md data-[state=active]:bg-violet-50/50 h-auto"
                >
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-indigo-100 flex items-center justify-center mb-1 sm:mb-2 group-hover:bg-indigo-200 group-data-[state=active]:bg-violet-500 transition-colors">
                    <ClipboardList className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-indigo-600 group-data-[state=active]:text-white" />
                  </div>
                  <span className="text-[10px] sm:text-xs font-medium text-slate-600 group-data-[state=active]:text-violet-700">Forms</span>
                </TabsTrigger>
                )}

                {/* Team Reports */}
                {hasTabAccess(currentUser, 'team-reports') && (
                <TabsTrigger 
                  value="team-reports" 
                  data-testid="team-reports-tab" 
                  className="group flex flex-col items-center p-3 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 hover:-translate-y-0.5 transition-all duration-200 data-[state=active]:border-teal-400 data-[state=active]:shadow-md data-[state=active]:bg-teal-50/50 h-auto"
                >
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-teal-100 flex items-center justify-center mb-1 sm:mb-2 group-hover:bg-teal-200 group-data-[state=active]:bg-teal-500 transition-colors">
                    <ClipboardCheck className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-teal-600 group-data-[state=active]:text-white" />
                  </div>
                  <span className="text-[10px] sm:text-xs font-medium text-slate-600 group-data-[state=active]:text-teal-700">Reports</span>
                </TabsTrigger>
                )}

                {/* Emails */}
                {hasTabAccess(currentUser, 'emails') && (
                <TabsTrigger 
                  value="emails" 
                  data-testid="emails-tab" 
                  className="group flex flex-col items-center p-2 sm:p-3 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 hover:-translate-y-0.5 transition-all duration-200 data-[state=active]:border-violet-400 data-[state=active]:shadow-md data-[state=active]:bg-violet-50/50 h-auto"
                >
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-sky-100 flex items-center justify-center mb-1 sm:mb-2 group-hover:bg-sky-200 group-data-[state=active]:bg-violet-500 transition-colors">
                    <Mail className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-sky-600 group-data-[state=active]:text-white" />
                  </div>
                  <span className="text-[10px] sm:text-xs font-medium text-slate-600 group-data-[state=active]:text-violet-700">Emails</span>
                </TabsTrigger>
                )}

                {/* Referrals */}
                {hasTabAccess(currentUser, 'all-referrals') && (
                <TabsTrigger 
                  value="all-referrals" 
                  data-testid="all-referrals-tab" 
                  className="group flex flex-col items-center p-2 sm:p-3 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 hover:-translate-y-0.5 transition-all duration-200 data-[state=active]:border-violet-400 data-[state=active]:shadow-md data-[state=active]:bg-violet-50/50 h-auto"
                >
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-rose-100 flex items-center justify-center mb-1 sm:mb-2 group-hover:bg-rose-200 group-data-[state=active]:bg-violet-500 transition-colors">
                    <Gift className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-rose-600 group-data-[state=active]:text-white" />
                  </div>
                  <span className="text-[10px] sm:text-xs font-medium text-slate-600 group-data-[state=active]:text-violet-700">Referrals</span>
                </TabsTrigger>
                )}

                {/* Assessments */}
                {hasTabAccess(currentUser, 'skill-assessments') && (
                <TabsTrigger 
                  value="skill-assessments" 
                  data-testid="skill-assessments-tab" 
                  className="group flex flex-col items-center p-2 sm:p-3 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 hover:-translate-y-0.5 transition-all duration-200 data-[state=active]:border-violet-400 data-[state=active]:shadow-md data-[state=active]:bg-violet-50/50 h-auto"
                >
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-purple-100 flex items-center justify-center mb-1 sm:mb-2 group-hover:bg-purple-200 group-data-[state=active]:bg-violet-500 transition-colors">
                    <Target className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-purple-600 group-data-[state=active]:text-white" />
                  </div>
                  <span className="text-[10px] sm:text-xs font-medium text-slate-600 group-data-[state=active]:text-violet-700">Assessments</span>
                </TabsTrigger>
                )}

                {/* Careers */}
                {hasTabAccess(currentUser, 'career-paths') && (
                <TabsTrigger 
                  value="career-paths" 
                  data-testid="career-paths-tab" 
                  className="group flex flex-col items-center p-2 sm:p-3 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 hover:-translate-y-0.5 transition-all duration-200 data-[state=active]:border-violet-400 data-[state=active]:shadow-md data-[state=active]:bg-violet-50/50 h-auto"
                >
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-amber-100 flex items-center justify-center mb-1 sm:mb-2 group-hover:bg-amber-200 group-data-[state=active]:bg-violet-500 transition-colors">
                    <Route className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-amber-600 group-data-[state=active]:text-white" />
                  </div>
                  <span className="text-[10px] sm:text-xs font-medium text-slate-600 group-data-[state=active]:text-violet-700">Careers</span>
                </TabsTrigger>
                )}

                {/* App Hub */}
                <TabsTrigger 
                  value="app-hub" 
                  data-testid="app-hub-tab" 
                  className="group flex flex-col items-center p-2 sm:p-3 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 hover:-translate-y-0.5 transition-all duration-200 data-[state=active]:border-violet-400 data-[state=active]:shadow-md data-[state=active]:bg-violet-50/50 h-auto"
                >
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-indigo-100 flex items-center justify-center mb-1 sm:mb-2 group-hover:bg-indigo-200 group-data-[state=active]:bg-violet-500 transition-colors">
                    <Zap className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-indigo-600 group-data-[state=active]:text-white" />
                  </div>
                  <span className="text-[10px] sm:text-xs font-medium text-slate-600 group-data-[state=active]:text-violet-700">App Hub</span>
                </TabsTrigger>

                {/* Activity Feed - Separate tab for admin-controlled access */}
                {hasTabAccess(currentUser, 'activity-feed') && (
                <TabsTrigger 
                  value="activity-feed" 
                  data-testid="activity-feed-tab" 
                  className="group flex flex-col items-center p-2 sm:p-3 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 hover:-translate-y-0.5 transition-all duration-200 data-[state=active]:border-violet-400 data-[state=active]:shadow-md data-[state=active]:bg-violet-50/50 h-auto"
                >
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-violet-100 flex items-center justify-center mb-1 sm:mb-2 group-hover:bg-violet-200 group-data-[state=active]:bg-violet-500 transition-colors">
                    <Activity className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-violet-600 group-data-[state=active]:text-white" />
                  </div>
                  <span className="text-[10px] sm:text-xs font-medium text-slate-600 group-data-[state=active]:text-violet-700">Activity</span>
                </TabsTrigger>
                )}

                {/* Google Sheets */}
                {hasTabAccess(currentUser, 'google-sheets') && (
                <TabsTrigger 
                  value="google-sheets" 
                  data-testid="google-sheets-tab" 
                  className="group flex flex-col items-center p-2 sm:p-3 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 hover:-translate-y-0.5 transition-all duration-200 data-[state=active]:border-violet-400 data-[state=active]:shadow-md data-[state=active]:bg-violet-50/50 h-auto"
                >
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-green-100 flex items-center justify-center mb-1 sm:mb-2 group-hover:bg-green-200 group-data-[state=active]:bg-green-500 transition-colors">
                    <FileSpreadsheet className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-green-600 group-data-[state=active]:text-white" />
                  </div>
                  <span className="text-[10px] sm:text-xs font-medium text-slate-600 group-data-[state=active]:text-green-700">Sheets</span>
                </TabsTrigger>
                )}
              </TabsList>
            </div>

            {/* Admin Management Section */}
            {currentUser?.is_admin && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="h-1 w-1 rounded-full bg-amber-500"></div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400">Admin Management</h3>
              </div>
              <TabsList className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2 sm:gap-3 bg-transparent p-0 h-auto">
                {/* Users */}
                {hasTabAccess(currentUser, 'user-management') && (
                <TabsTrigger 
                  value="user-management" 
                  data-testid="users-tab" 
                  className="group flex flex-col items-center p-2 sm:p-3 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 hover:-translate-y-0.5 transition-all duration-200 data-[state=active]:border-violet-400 data-[state=active]:shadow-md data-[state=active]:bg-violet-50/50 h-auto"
                >
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-gray-100 flex items-center justify-center mb-1 sm:mb-2 group-hover:bg-gray-200 group-data-[state=active]:bg-violet-500 transition-colors">
                    <UserCog className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-600 group-data-[state=active]:text-white" />
                  </div>
                  <span className="text-[10px] sm:text-xs font-medium text-slate-600 group-data-[state=active]:text-violet-700">Users</span>
                </TabsTrigger>
                )}

                {/* Employers */}
                <TabsTrigger 
                  value="pending-employers" 
                  data-testid="pending-employers-tab" 
                  className="group relative flex flex-col items-center p-3 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 hover:-translate-y-0.5 transition-all duration-200 data-[state=active]:border-violet-400 data-[state=active]:shadow-md data-[state=active]:bg-violet-50/50 h-auto"
                >
                  {pendingEmployers.length > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold">{pendingEmployers.length}</span>
                  )}
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-teal-100 flex items-center justify-center mb-1 sm:mb-2 group-hover:bg-teal-200 group-data-[state=active]:bg-violet-500 transition-colors">
                    <Shield className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-teal-600 group-data-[state=active]:text-white" />
                  </div>
                  <span className="text-[10px] sm:text-xs font-medium text-slate-600 group-data-[state=active]:text-violet-700">Employers</span>
                </TabsTrigger>

                {/* Position Requests */}
                <TabsTrigger 
                  value="position-requests" 
                  data-testid="position-requests-tab" 
                  className="group relative flex flex-col items-center p-3 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 hover:-translate-y-0.5 transition-all duration-200 data-[state=active]:border-violet-400 data-[state=active]:shadow-md data-[state=active]:bg-violet-50/50 h-auto"
                >
                  {positionRequests.filter(r => r.status === 'Pending').length > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold animate-pulse">{positionRequests.filter(r => r.status === 'Pending').length}</span>
                  )}
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-amber-100 flex items-center justify-center mb-1 sm:mb-2 group-hover:bg-amber-200 group-data-[state=active]:bg-violet-500 transition-colors">
                    <FileText className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-amber-600 group-data-[state=active]:text-white" />
                  </div>
                  <span className="text-[10px] sm:text-xs font-medium text-slate-600 group-data-[state=active]:text-violet-700">Requests</span>
                </TabsTrigger>

                {/* Banners */}
                <TabsTrigger 
                  value="banners" 
                  data-testid="banners-tab" 
                  className="group flex flex-col items-center p-2 sm:p-3 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 hover:-translate-y-0.5 transition-all duration-200 data-[state=active]:border-violet-400 data-[state=active]:shadow-md data-[state=active]:bg-violet-50/50 h-auto"
                >
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-pink-100 flex items-center justify-center mb-1 sm:mb-2 group-hover:bg-pink-200 group-data-[state=active]:bg-violet-500 transition-colors">
                    <ImageIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-pink-600 group-data-[state=active]:text-white" />
                  </div>
                  <span className="text-[10px] sm:text-xs font-medium text-slate-600 group-data-[state=active]:text-violet-700">Banners</span>
                </TabsTrigger>

                {/* Popups */}
                <TabsTrigger 
                  value="popup-announcements" 
                  data-testid="popup-announcements-tab" 
                  className="group flex flex-col items-center p-2 sm:p-3 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 hover:-translate-y-0.5 transition-all duration-200 data-[state=active]:border-violet-400 data-[state=active]:shadow-md data-[state=active]:bg-violet-50/50 h-auto"
                >
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-orange-100 flex items-center justify-center mb-1 sm:mb-2 group-hover:bg-orange-200 group-data-[state=active]:bg-violet-500 transition-colors">
                    <Bell className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-orange-600 group-data-[state=active]:text-white" />
                  </div>
                  <span className="text-[10px] sm:text-xs font-medium text-slate-600 group-data-[state=active]:text-violet-700">Popups</span>
                </TabsTrigger>

                {/* Courses */}
                <div 
                  className="group flex flex-col items-center p-3 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer h-auto"
                  onClick={() => navigate('/admin/courses')}
                  data-testid="courses-admin-tab"
                >
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-purple-100 flex items-center justify-center mb-1 sm:mb-2 group-hover:bg-purple-200 transition-colors">
                    <GraduationCap className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-purple-600" />
                  </div>
                  <span className="text-[10px] sm:text-xs font-medium text-slate-600">Courses</span>
                </div>

                {/* Partner Dashboard */}
                <TabsTrigger 
                  value="partner-dashboard" 
                  data-testid="partner-dashboard-tab" 
                  className="group flex flex-col items-center p-2 sm:p-3 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 hover:-translate-y-0.5 transition-all duration-200 data-[state=active]:border-violet-400 data-[state=active]:shadow-md data-[state=active]:bg-violet-50/50 h-auto"
                >
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-emerald-100 flex items-center justify-center mb-1 sm:mb-2 group-hover:bg-emerald-200 group-data-[state=active]:bg-violet-500 transition-colors">
                    <TrendingUp className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-emerald-600 group-data-[state=active]:text-white" />
                  </div>
                  <span className="text-[10px] sm:text-xs font-medium text-slate-600 group-data-[state=active]:text-violet-700">Partners</span>
                </TabsTrigger>

                {/* Finance Module */}
                {hasTabAccess(currentUser, 'finance') && (
                <TabsTrigger 
                  value="finance" 
                  data-testid="finance-tab" 
                  className="group flex flex-col items-center p-2 sm:p-3 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 hover:-translate-y-0.5 transition-all duration-200 data-[state=active]:border-violet-400 data-[state=active]:shadow-md data-[state=active]:bg-violet-50/50 h-auto"
                >
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-green-100 flex items-center justify-center mb-1 sm:mb-2 group-hover:bg-green-200 group-data-[state=active]:bg-violet-500 transition-colors">
                    <IndianRupee className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-green-600 group-data-[state=active]:text-white" />
                  </div>
                  <span className="text-[10px] sm:text-xs font-medium text-slate-600 group-data-[state=active]:text-violet-700">Finance</span>
                </TabsTrigger>
                )}

                {/* Integration Settings - Admin Only */}
                {currentUser?.is_admin && (
                <TabsTrigger 
                  value="integration-settings" 
                  data-testid="integration-settings-tab" 
                  className="group flex flex-col items-center p-2 sm:p-3 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 hover:-translate-y-0.5 transition-all duration-200 data-[state=active]:border-violet-400 data-[state=active]:shadow-md data-[state=active]:bg-violet-50/50 h-auto"
                >
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-violet-100 flex items-center justify-center mb-1 sm:mb-2 group-hover:bg-violet-200 group-data-[state=active]:bg-violet-500 transition-colors">
                    <Settings className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-violet-600 group-data-[state=active]:text-white" />
                  </div>
                  <span className="text-[10px] sm:text-xs font-medium text-slate-600 group-data-[state=active]:text-violet-700">Integrations</span>
                </TabsTrigger>
                )}
              </TabsList>
            </div>
            )}
          </div>

          {/* Messages Tab Content */}
          <TabsContent value="messages" className="space-y-4 md:space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
              <div>
                <h2 className="font-heading text-xl md:text-3xl font-bold tracking-tight mb-1 bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                  Messages
                </h2>
                <p className="text-sm text-muted-foreground">Chat with team members and groups</p>
              </div>
              <Button 
                onClick={() => setIsCreateGroupOpen(true)}
                className="rounded-lg font-medium w-full md:w-auto bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 shadow-lg shadow-green-200 transition-all"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                New Group
              </Button>
            </div>
            
            {/* Inline Messaging UI */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border md:rounded-2xl" style={{ height: 'calc(100vh - 320px)', minHeight: '400px' }}>
              <div className="flex h-full">
                {/* Conversations List */}
                <div className={`${selectedChat ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-80 border-r bg-gray-50`}>
                  {/* Search */}
                  <div className="p-3 border-b bg-white">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" />
                      <Input
                        placeholder="Search conversations..."
                        value={messageSearchQuery}
                        onChange={(e) => setMessageSearchQuery(e.target.value)}
                        className="pl-10 rounded-full bg-gray-100 border-0"
                      />
                    </div>
                  </div>
                  
                  {/* Tabs for Groups/DMs/Calls */}
                  <div className="flex border-b">
                    <button 
                      onClick={() => setMessagingActiveTab('chats')}
                      className={`flex-1 py-2 text-sm font-medium transition-colors ${messagingActiveTab === 'chats' ? 'text-green-600 border-b-2 border-green-600' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      Chats
                    </button>
                    <button 
                      onClick={() => setMessagingActiveTab('groups')}
                      className={`flex-1 py-2 text-sm font-medium transition-colors ${messagingActiveTab === 'groups' ? 'text-green-600 border-b-2 border-green-600' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      Groups ({groups.length})
                    </button>
                    <button 
                      onClick={() => { setMessagingActiveTab('calls'); fetchCallHistory(); }}
                      className={`flex-1 py-2 text-sm font-medium transition-colors relative ${messagingActiveTab === 'calls' ? 'text-green-600 border-b-2 border-green-600' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      Calls
                      {missedCallCount > 0 && (
                        <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center">
                          {missedCallCount}
                        </span>
                      )}
                    </button>
                  </div>
                  
                  <ScrollArea className="flex-1">
                    {messagingActiveTab === 'calls' ? (
                      /* Call History */
                      callHistory.length === 0 ? (
                        <div className="p-4 text-center text-gray-500 text-sm">
                          <Phone className="h-10 w-10 mx-auto text-gray-300 mb-2" />
                          <p>No call history yet</p>
                          <p className="text-xs mt-1">Start a call from any chat</p>
                        </div>
                      ) : (
                        <div className="space-y-1 p-2">
                          {callHistory.map((call) => (
                            <div
                              key={call.id}
                              className="p-3 rounded-xl hover:bg-gray-50 transition-all cursor-pointer"
                              onClick={() => {
                                // Find user and open chat
                                const chatUser = conversations.find(c => c.email === call.other_user_email);
                                if (chatUser) {
                                  setSelectedChat({
                                    ...chatUser,
                                    isGroup: false
                                  });
                                }
                              }}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`h-10 w-10 rounded-full flex items-center justify-center font-bold text-white ${
                                  call.status === 'missed' ? 'bg-red-500' : 
                                  call.status === 'completed' ? 'bg-emerald-500' : 'bg-gray-400'
                                }`}>
                                  {call.direction === 'outgoing' ? (
                                    <PhoneOutgoing className="h-5 w-5" />
                                  ) : call.status === 'missed' ? (
                                    <PhoneMissed className="h-5 w-5" />
                                  ) : (
                                    <PhoneIncoming className="h-5 w-5" />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-gray-800 truncate">{call.other_user_name}</p>
                                  <div className="flex items-center gap-2 text-xs text-gray-500">
                                    <span className={call.status === 'missed' ? 'text-red-500' : ''}>
                                      {call.direction === 'outgoing' ? 'Outgoing' : 'Incoming'} • {call.status}
                                    </span>
                                    {call.duration_seconds > 0 && (
                                      <span>• {Math.floor(call.duration_seconds / 60)}:{(call.duration_seconds % 60).toString().padStart(2, '0')}</span>
                                    )}
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="text-xs text-gray-400">{formatISTTime(call.created_at)}</p>
                                  <CallbackButton userEmail={call.other_user_email} userName={call.other_user_name} />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )
                    ) : messagingActiveTab === 'groups' ? (
                      /* Groups List */
                      groups.length === 0 ? (
                        <div className="p-4 text-center text-gray-500 text-sm">
                          <UsersIcon className="h-10 w-10 mx-auto text-gray-300 mb-2" />
                          <p>No groups yet</p>
                          <Button size="sm" variant="outline" className="mt-3" onClick={() => setIsCreateGroupOpen(true)}>
                            <UserPlus className="h-3 w-3 mr-1" />
                            Create Group
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-1 p-2">
                          {groups.map((group) => {
                            // Find unread count from conversations
                            const groupConvo = conversations.find(c => c.group_id === group.id);
                            const groupUnreadCount = groupConvo?.unread_count || 0;
                            
                            return (
                            <div
                              key={group.id}
                              onClick={() => openGroupChat(group)}
                              className={`p-3 rounded-xl cursor-pointer transition-all ${
                                selectedChat?.groupId === group.id 
                                  ? 'bg-green-100 border-l-4 border-green-500' 
                                  : 'hover:bg-gray-100'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                {group.group_image ? (
                                  <img 
                                    src={getImageUrl(group.group_image, API)}
                                    alt={group.name}
                                    className="h-10 w-10 rounded-full object-cover"
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                ) : (
                                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center text-white font-bold">
                                    {group.name?.charAt(0)?.toUpperCase()}
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm truncate">{group.name}</p>
                                  <p className="text-xs text-gray-500">{group.member_emails?.length || 0} members</p>
                                </div>
                                {/* Unread count badge for groups */}
                                {groupUnreadCount > 0 && (
                                  <div className="flex-shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-green-500 text-white text-xs font-bold flex items-center justify-center animate-pulse">
                                    {groupUnreadCount > 99 ? '99+' : groupUnreadCount}
                                  </div>
                                )}
                              </div>
                            </div>
                          )})}
                        </div>
                      )
                    ) : (
                      /* Team Members List */
                      <div className="space-y-1 p-2">
                        <p className="text-xs font-semibold text-gray-500 px-3 py-2 uppercase tracking-wider">Team Members</p>
                        {users.filter(u => u.email !== currentUser?.email).map((user) => {
                          // Find unread count for this user from conversations
                          const userConvo = conversations.find(c => c.partner_email === user.email);
                          const unreadCount = userConvo?.unread_count || 0;
                          
                          return (
                          <div
                            key={user.email}
                            onClick={() => openChat(user)}
                            className={`p-3 rounded-xl cursor-pointer transition-all ${
                              selectedChat?.email === user.email 
                                ? 'bg-green-100 border-l-4 border-green-500' 
                                : 'hover:bg-gray-100'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              {user.profile_picture ? (
                                <img 
                                  src={getImageUrl(user.profile_picture, API)}
                                  alt={user.name}
                                  className="h-10 w-10 rounded-full object-cover"
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              ) : (
                                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center text-white font-bold">
                                  {user.name?.charAt(0)?.toUpperCase()}
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm truncate">{user.name}</p>
                                <p className="text-xs text-gray-500 truncate">{user.role}</p>
                              </div>
                              {/* Unread message count badge */}
                              {unreadCount > 0 && (
                                <div className="flex-shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-green-500 text-white text-xs font-bold flex items-center justify-center animate-pulse">
                                  {unreadCount > 99 ? '99+' : unreadCount}
                                </div>
                              )}
                            </div>
                          </div>
                        )})}
                      </div>
                    )}
                  </ScrollArea>
                </div>
                
                {/* Chat Area */}
                <div className={`flex-1 flex flex-col ${selectedChat ? 'flex' : 'hidden md:flex'} ${selectedChat && isMobileFullscreenChat ? 'fixed inset-0 z-[90] bg-white md:relative md:inset-auto md:z-auto pt-14 md:pt-0' : ''}`}>
                  {!selectedChat ? (
                    <div className="flex-1 flex items-center justify-center bg-gray-50">
                      <div className="text-center">
                        <div className="h-16 w-16 rounded-2xl bg-green-100 flex items-center justify-center mx-auto mb-4">
                          <MessageCircle className="h-8 w-8 text-green-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-700 mb-2">Select a conversation</h3>
                        <p className="text-gray-500 text-sm">Choose from your team members or groups to start chatting</p>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Chat Header */}
                      <div className="p-3 border-b bg-white text-gray-800 flex items-center gap-2 md:gap-3 safe-area-top shadow-sm">
                        <Button variant="ghost" size="icon" className="md:hidden text-gray-600 hover:bg-gray-100 flex-shrink-0" onClick={() => { setSelectedChat(null); setIsMobileFullscreenChat(false); }}>
                          <ArrowLeft className="h-5 w-5" />
                        </Button>
                        {selectedChat.isGroup ? (
                          selectedChat.group_image ? (
                            <img src={getImageUrl(selectedChat.group_image, API)} alt={selectedChat.name} className="h-10 w-10 rounded-full object-cover flex-shrink-0" />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-green-500 text-white flex items-center justify-center font-bold flex-shrink-0">
                              {selectedChat.name?.charAt(0)?.toUpperCase()}
                            </div>
                          )
                        ) : selectedChat.profile_picture ? (
                          <img src={getImageUrl(selectedChat.profile_picture, API)} alt={selectedChat.name} className="h-10 w-10 rounded-full object-cover flex-shrink-0" />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-green-500 text-white flex items-center justify-center font-bold flex-shrink-0">
                            {selectedChat.name?.charAt(0)?.toUpperCase()}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-800 truncate">{selectedChat.name}</p>
                          <p className="text-xs text-gray-500 truncate">{selectedChat.isGroup ? `${selectedChat.memberCount} members` : selectedChat.email}</p>
                        </div>
                        {/* Call button for direct chats (not groups, not candidates) */}
                        {!selectedChat.isGroup && selectedChat.role !== 'candidate' && (
                          <div className="flex-shrink-0">
                            <CallButton 
                              userEmail={selectedChat.email}
                              userName={selectedChat.name}
                              userAvatar={selectedChat.profile_picture ? getImageUrl(selectedChat.profile_picture, API) : null}
                            />
                          </div>
                        )}
                        {/* Group actions */}
                        {selectedChat.isGroup && (
                          <div className="flex items-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => {
                                setEditGroupName(selectedChat.name);
                                setIsEditGroupOpen(true);
                              }}
                              className="text-gray-600 hover:bg-gray-100 h-8 w-8"
                              title="Edit Group"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => setIsAddMemberOpen(true)}
                              className="text-gray-600 hover:bg-gray-100 h-8 w-8"
                              title="Add Members"
                            >
                              <UserPlus className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                      
                      {/* Messages - Lighter background */}
                      <ScrollArea className="flex-1 p-3 bg-gradient-to-b from-gray-50 to-gray-100" onClick={() => setSelectedMessageId(null)}>
                        <div className="space-y-2">
                          {chatMessages.map((msg) => {
                            const isMe = msg.sender_email === currentUser?.email;
                            const isSending = msg.is_sending || msg.status === 'sending';
                            const isFailed = msg.status === 'failed';
                            
                            return (
                              <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                                <div
                                  className={`max-w-[70%] rounded-2xl px-3 py-2 shadow-sm relative cursor-pointer ${
                                    isMe ? 'bg-emerald-50 border border-emerald-100 rounded-tr-sm' : 'bg-white border border-gray-100 rounded-tl-sm'
                                  } ${isSending ? 'opacity-70' : ''} ${isFailed ? 'bg-red-50 border border-red-200' : ''} ${selectedMessageId === msg.id ? 'ring-2 ring-emerald-400' : ''}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (isFailed) { retryFailedMessage(msg); return; }
                                    // Single tap opens fullscreen view
                                    if (!msg.deleted_for_all && !isSending) {
                                      setIsFullscreenMessage(msg);
                                    }
                                  }}
                                  onContextMenu={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    // Right-click / long-press shows action menu
                                    if (!msg.deleted_for_all && !isSending) {
                                      setSelectedMessageId(selectedMessageId === msg.id ? null : msg.id);
                                    }
                                  }}
                                >
                                  {isFailed && (
                                    <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-red-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1 shadow">
                                      <AlertCircle className="h-3 w-3" />
                                      Tap to retry
                                    </div>
                                  )}
                                  {selectedMessageId === msg.id && !msg.deleted_for_all && (
                                    <div className="absolute -bottom-14 md:-bottom-12 left-1/2 -translate-x-1/2 flex justify-center z-20">
                                      <div className="flex items-center gap-0.5 md:gap-1 bg-white rounded-full shadow-lg border px-2 md:px-3 py-1.5 md:py-2">
                                        <button onClick={(e) => { e.stopPropagation(); handleCopyMessage(msg.content); setSelectedMessageId(null); }} className="p-1.5 md:p-2 hover:bg-gray-100 rounded-full" title="Copy">
                                          <Copy className="h-3.5 w-3.5 md:h-4 md:w-4 text-gray-600" />
                                        </button>
                                        {isMe && msg.message_type === 'text' && (
                                          <button onClick={(e) => { e.stopPropagation(); handleEditMessage(msg); setSelectedMessageId(null); }} className="p-1.5 md:p-2 hover:bg-gray-100 rounded-full" title="Edit">
                                            <Pencil className="h-3.5 w-3.5 md:h-4 md:w-4 text-gray-600" />
                                          </button>
                                        )}
                                        <button onClick={(e) => { e.stopPropagation(); handleDeleteMessage(msg.id, 'for_me'); setSelectedMessageId(null); }} className="p-1.5 md:p-2 hover:bg-gray-100 rounded-full" title="Delete">
                                          <Trash2 className="h-3.5 w-3.5 md:h-4 md:w-4 text-gray-600" />
                                        </button>
                                        {isMe && (
                                          <button onClick={(e) => { e.stopPropagation(); handleDeleteMessage(msg.id, 'for_everyone'); setSelectedMessageId(null); }} className="p-1.5 md:p-2 hover:bg-red-100 rounded-full" title="Delete for everyone">
                                            <Trash2 className="h-3.5 w-3.5 md:h-4 md:w-4 text-red-500" />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  {!isMe && selectedChat.isGroup && (
                                    <p className="text-xs font-semibold text-green-600 mb-1">{msg.sender_name || msg.sender_email}</p>
                                  )}
                                  {msg.deleted_for_all ? (
                                    <p className="text-sm text-gray-400 italic">This message was deleted</p>
                                  ) : (
                                    <>
                                      {msg.image_url && <img src={getImageUrl(msg.image_url, API)} alt="Sent" className="max-w-full rounded-lg mb-2" />}
                                      <p className="text-sm whitespace-pre-wrap break-words pr-12" dangerouslySetInnerHTML={{ __html: formatMessageContent(msg.content) }} />
                                    </>
                                  )}
                                  <div className="absolute bottom-1 right-2 flex items-center gap-1">
                                    {msg.edited && <span className="text-[10px] text-gray-400 italic">edited</span>}
                                    <span className="text-[10px] text-gray-500">{isSending ? '' : formatISTTime(msg.created_at)}</span>
                                    {isMe && (
                                      isSending ? (
                                        /* Sending - Clock icon */
                                        <svg className="h-3 w-3 text-gray-400 animate-pulse" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                          <circle cx="12" cy="12" r="10" />
                                          <polyline points="12 6 12 12 16 14" />
                                        </svg>
                                      ) : isFailed ? (
                                        /* Failed - Red exclamation */
                                        <svg className="h-3 w-3 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                                        </svg>
                                      ) : msg.read ? (
                                        /* Read - Double blue ticks with tooltip */
                                        <div className="relative group/tick cursor-pointer">
                                          <svg className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-blue-500" viewBox="0 0 16 15" fill="currentColor">
                                            <path d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.88a.32.32 0 0 1-.484.032l-.358-.325a.32.32 0 0 0-.484.032l-.378.48a.418.418 0 0 0 .036.54l1.32 1.266a.32.32 0 0 0 .484-.034l6.272-8.048a.366.366 0 0 0-.064-.512zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.88a.32.32 0 0 1-.484.032L1.89 7.77a.366.366 0 0 0-.516.005l-.423.433a.364.364 0 0 0 .006.514l3.255 3.185a.32.32 0 0 0 .484-.033l6.272-8.048a.365.365 0 0 0-.063-.51z"/>
                                          </svg>
                                          {msg.read_at && (
                                            <div className="absolute bottom-5 right-0 bg-slate-800 text-white text-[10px] px-2 py-1 rounded shadow-lg whitespace-nowrap opacity-0 group-hover/tick:opacity-100 transition-opacity z-50 pointer-events-none">
                                              Seen {formatIST(msg.read_at, 'MMM d, h:mm a')}
                                            </div>
                                          )}
                                        </div>
                                      ) : msg.delivered !== false ? (
                                        /* Delivered but not read - Double gray ticks */
                                        <svg className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" viewBox="0 0 16 15" fill="currentColor" title="Delivered">
                                          <path d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.88a.32.32 0 0 1-.484.032l-.358-.325a.32.32 0 0 0-.484.032l-.378.48a.418.418 0 0 0 .036.54l1.32 1.266a.32.32 0 0 0 .484-.034l6.272-8.048a.366.366 0 0 0-.064-.512zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.88a.32.32 0 0 1-.484.032L1.89 7.77a.366.366 0 0 0-.516.005l-.423.433a.364.364 0 0 0 .006.514l3.255 3.185a.32.32 0 0 0 .484-.033l6.272-8.048a.365.365 0 0 0-.063-.51z"/>
                                        </svg>
                                      ) : (
                                        /* Sent but not delivered - Single gray tick */
                                        <svg className="h-3 w-3 text-gray-400" viewBox="0 0 16 15" fill="currentColor" title="Sent">
                                          <path d="M10.91 3.316l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.88a.32.32 0 0 1-.484.032L1.89 7.77a.366.366 0 0 0-.516.005l-.423.433a.364.364 0 0 0 .006.514l3.255 3.185a.32.32 0 0 0 .484-.033l6.272-8.048a.365.365 0 0 0-.063-.51z"/>
                                        </svg>
                                      )
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                          <div ref={messagesEndRef} />
                        </div>
                      </ScrollArea>
                      
                      {/* Image Preview */}
                      {imagePreview && (
                        <div className="px-3 py-2 border-t bg-gradient-to-r from-emerald-50 to-teal-50">
                          <div className="relative inline-block">
                            <img src={imagePreview} alt="Preview" className="h-20 rounded-xl shadow-md border-2 border-emerald-200" />
                            <button
                              onClick={() => { setImagePreview(null); setMessageType('text'); }}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-lg hover:bg-red-600 transition-colors"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      )}
                      
                      {/* Reply Preview */}
                      {replyToMessage && (
                        <div className="px-3 py-2 bg-gray-100 border-t flex items-center gap-2">
                          <div className="flex-1 px-3 py-2 bg-white rounded-lg border-l-4 border-emerald-500">
                            <p className="text-xs font-semibold text-emerald-600">{replyToMessage.sender_name}</p>
                            <p className="text-sm text-gray-600 truncate">{replyToMessage.content}</p>
                          </div>
                          <button
                            onClick={() => setReplyToMessage(null)}
                            className="p-1 text-gray-500 hover:text-red-500 rounded-full hover:bg-red-50"
                          >
                            <X className="h-5 w-5" />
                          </button>
                        </div>
                      )}
                      
                      {/* Message Input with formatting toolbar */}
                      <form onSubmit={editingMessage ? (e) => { e.preventDefault(); handleSaveEdit(); } : handleSendMessage} className="p-3 bg-gray-50 border-t">
                        {editingMessage && (
                          <div className="flex items-center justify-between mb-2 px-2 py-1 bg-blue-50 rounded-lg">
                            <div className="flex items-center gap-2 text-blue-600">
                              <Pencil className="h-4 w-4" />
                              <span className="text-sm">Editing message</span>
                            </div>
                            <button type="button" onClick={handleCancelEdit} className="text-gray-500 hover:text-gray-700">
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                        <div className="flex items-center gap-1 mb-2">
                          {!editingMessage && (
                            <>
                              <input type="file" ref={imageInputRef} onChange={handleImageSelect} accept="image/*" className="hidden" />
                              <button 
                                type="button" 
                                onClick={() => imageInputRef.current?.click()}
                                className="p-1.5 rounded hover:bg-gray-200 text-gray-600 transition-colors"
                                title="Attach image"
                              >
                                <LucideImage className="h-4 w-4" />
                              </button>
                              <EmojiPicker onEmojiSelect={(emoji) => setNewMessage(prev => prev + emoji)} disabled={isSendingMessage} />
                            </>
                          )}
                          <span className="text-xs text-gray-400 ml-auto">Select text for formatting options</span>
                        </div>
                        <div className="flex flex-col gap-1">
                          <div className="flex items-start gap-2">
                            <div className="flex-1 message-rich-editor">
                              <RichTextEditor
                                value={newMessage}
                                onChange={(html) => { setNewMessage(html); if (!editingMessage) handleTyping(); }}
                                placeholder={editingMessage ? "Edit your message..." : "Type a message..."}
                                minHeight="44px"
                                className="message-composer"
                                showBottomToolbar={true}
                              />
                            </div>
                            {/* Send button visible on desktop */}
                            <Button 
                              type="submit" 
                              size="icon" 
                              className={`hidden md:flex rounded-full h-10 w-10 shadow-md flex-shrink-0 transition-transform active:scale-95 ${editingMessage ? 'bg-blue-500 hover:bg-blue-600' : 'bg-emerald-500 hover:bg-emerald-600'}`} 
                              disabled={isSendingMessage || (!newMessage.replace(/<[^>]*>/g, '').trim() && !imagePreview)}
                              data-testid="send-message-btn-desktop"
                            >
                              {isSendingMessage ? <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : editingMessage ? <Check className="h-5 w-5" /> : <Send className="h-5 w-5" />}
                            </Button>
                          </div>
                          {/* Send button visible on mobile - full width below toolbar */}
                          <Button 
                            type="submit" 
                            className={`md:hidden w-full rounded-lg shadow-md transition-transform active:scale-95 ${editingMessage ? 'bg-blue-500 hover:bg-blue-600' : 'bg-emerald-500 hover:bg-emerald-600'}`} 
                            disabled={isSendingMessage || (!newMessage.replace(/<[^>]*>/g, '').trim() && !imagePreview)}
                            data-testid="send-message-btn-mobile"
                          >
                            {isSendingMessage ? <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : editingMessage ? <Check className="h-4 w-4 mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                            {editingMessage ? 'Update' : 'Send Message'}
                          </Button>
                        </div>
                      </form>
                    </>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Grammar Check Tab */}
          <TabsContent value="grammar" className="space-y-4 md:space-y-6">
            <div className="max-w-4xl mx-auto">
              {/* Header */}
              <div className="text-center mb-6">
                <h2 className="font-heading text-xl md:text-3xl font-bold tracking-tight mb-2 bg-gradient-to-r from-pink-600 to-rose-600 bg-clip-text text-transparent flex items-center justify-center gap-2" data-testid="grammar-header">
                  <Wand2 className="h-7 w-7 text-pink-500" />
                  Grammar Check
                </h2>
                <p className="text-sm text-muted-foreground">Improve your writing with AI-powered grammar correction</p>
              </div>

              {/* Main Card */}
              <Card className="border-0 shadow-xl bg-white/90 backdrop-blur rounded-2xl overflow-hidden">
                <CardContent className="p-6">
                  {/* Mode Selection */}
                  <div className="mb-6">
                    <Label className="text-sm font-medium text-gray-700 mb-2 block">Correction Mode</Label>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { id: 'basic', label: 'Basic', desc: 'Fix grammar & spelling', icon: CheckCircle2, color: 'emerald' },
                        { id: 'professional', label: 'Professional', desc: 'Polished & refined', icon: Sparkles, color: 'blue' },
                        { id: 'formal', label: 'Formal', desc: 'Business-appropriate', icon: Award, color: 'purple' }
                      ].map((mode) => (
                        <button
                          key={mode.id}
                          onClick={() => setGrammarMode(mode.id)}
                          className={`flex-1 min-w-[140px] p-3 rounded-xl border-2 transition-all ${
                            grammarMode === mode.id
                              ? `border-${mode.color}-500 bg-${mode.color}-50 text-${mode.color}-700`
                              : 'border-gray-200 hover:border-gray-300 text-gray-600'
                          }`}
                          style={{
                            borderColor: grammarMode === mode.id ? (mode.color === 'emerald' ? '#10b981' : mode.color === 'blue' ? '#3b82f6' : '#8b5cf6') : undefined,
                            backgroundColor: grammarMode === mode.id ? (mode.color === 'emerald' ? '#ecfdf5' : mode.color === 'blue' ? '#eff6ff' : '#f5f3ff') : undefined,
                            color: grammarMode === mode.id ? (mode.color === 'emerald' ? '#047857' : mode.color === 'blue' ? '#1d4ed8' : '#6d28d9') : undefined
                          }}
                          data-testid={`grammar-mode-${mode.id}`}
                        >
                          <mode.icon className="h-5 w-5 mx-auto mb-1" />
                          <p className="font-semibold text-sm">{mode.label}</p>
                          <p className="text-xs opacity-70">{mode.desc}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Text Input */}
                  <div className="mb-4">
                    <Label className="text-sm font-medium text-gray-700 mb-2 block">Your Text</Label>
                    <Textarea
                      value={grammarText}
                      onChange={(e) => setGrammarText(e.target.value)}
                      placeholder="Paste or type your text here..."
                      className="min-h-[180px] text-base border-gray-200 focus:border-pink-400 focus:ring-pink-400 rounded-xl resize-none"
                      maxLength={5000}
                      data-testid="grammar-input"
                    />
                    <div className="flex justify-between mt-2 text-xs text-gray-500">
                      <span>{grammarText.split(/\s+/).filter(Boolean).length} words</span>
                      <span>{grammarText.length}/5000 characters</span>
                    </div>
                  </div>

                  {/* Check Button */}
                  <Button
                    onClick={handleGrammarCheck}
                    disabled={grammarLoading || !grammarText.trim()}
                    className="w-full py-6 text-lg font-semibold bg-gradient-to-r from-pink-500 to-rose-600 hover:from-pink-600 hover:to-rose-700 rounded-xl shadow-lg shadow-pink-200 transition-all"
                    data-testid="grammar-check-btn"
                  >
                    {grammarLoading ? (
                      <>
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                        Checking...
                      </>
                    ) : (
                      <>
                        <Wand2 className="h-5 w-5 mr-2" />
                        Check Grammar
                      </>
                    )}
                  </Button>

                  {/* Results */}
                  {grammarResult && (
                    <div className="mt-6 space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
                      {/* Corrected Text */}
                      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 border border-gray-200">
                        <div className="flex items-center justify-between mb-3">
                          <Label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                            <CheckCircle2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-green-500" />
                            Corrected Text
                          </Label>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleCopyGrammarResult}
                              className="h-8 text-xs"
                              data-testid="copy-grammar-result"
                            >
                              <Copy className="h-3 w-3 mr-1" />
                              Copy
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleApplyCorrection}
                              className="h-8 text-xs bg-green-50 text-green-700 border-green-200 hover:bg-green-100"
                              data-testid="apply-grammar-correction"
                            >
                              <Check className="h-3 w-3 mr-1" />
                              Apply
                            </Button>
                          </div>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-gray-200 text-gray-800 whitespace-pre-wrap leading-relaxed">
                          {grammarResult.corrected}
                        </div>
                        <div className="flex justify-between mt-3 text-xs text-gray-500">
                          <span>{grammarResult.word_count} words</span>
                          <span>{grammarResult.character_count} characters</span>
                        </div>
                      </div>

                      {/* Changes Preview */}
                      {grammarResult.original !== grammarResult.corrected && (
                        <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                          <Label className="text-sm font-semibold text-amber-800 flex items-center gap-2 mb-2">
                            <Languages className="h-4 w-4" />
                            Changes Made
                          </Label>
                          <div className="flex flex-wrap gap-2">
                            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 line-through">
                              Original
                            </Badge>
                            <span className="text-gray-400">→</span>
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                              Corrected ({grammarMode})
                            </Badge>
                          </div>
                        </div>
                      )}

                      {/* No changes */}
                      {grammarResult.original === grammarResult.corrected && (
                        <div className="bg-green-50 rounded-xl p-4 border border-green-200 text-center">
                          <CheckCircle2 className="h-8 w-8 text-green-500 mx-auto mb-2" />
                          <p className="text-green-800 font-medium">Your text looks great!</p>
                          <p className="text-green-600 text-sm">No corrections needed.</p>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Tips */}
              <div className="mt-6 text-center text-sm text-gray-500">
                <p>💡 Tip: Use <strong>Professional</strong> mode for emails and <strong>Formal</strong> mode for business documents.</p>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="tasks" className="space-y-6">
            {/* Modern Header Section */}
            <div className="bg-gradient-to-r from-slate-50 via-violet-50/30 to-purple-50/40 rounded-2xl p-6 border border-slate-100/80 shadow-sm">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-200/50">
                    <ClipboardList className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl md:text-2xl font-bold tracking-tight text-slate-800" data-testid="tasks-header">
                      Your Tasks
                    </h2>
                    <p className="text-sm text-slate-500 mt-0.5">Sorted by urgency and deadline</p>
                  </div>
                </div>

                <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="rounded-xl font-medium w-full md:w-auto bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 shadow-lg shadow-violet-200/50 transition-all hover:shadow-xl hover:-translate-y-0.5" data-testid="create-task-button">
                      <Plus className="h-4 w-4 mr-2" />
                      Create Task
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="w-[calc(100vw-2rem)] md:max-w-[500px] rounded-2xl" aria-describedby="task-dialog-description">
                    <DialogHeader>
                      <DialogTitle className="font-heading text-xl">Create New Task</DialogTitle>
                      <p id="task-dialog-description" className="sr-only">Fill in the form to create a new task and assign it to a team member</p>
                    </DialogHeader>
                    <form onSubmit={handleCreateTask} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="title" className="text-slate-700 font-medium">Task Title</Label>
                        <Input
                          id="title"
                          data-testid="task-title-input"
                          required
                          value={newTask.title}
                          onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                          className="rounded-xl border-slate-200 focus:border-violet-400 focus:ring-violet-400/20"
                          placeholder="Enter task title..."
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="description" className="text-slate-700 font-medium">Description</Label>
                        <Textarea
                          id="description"
                          data-testid="task-description-input"
                          value={newTask.description}
                          onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                          className="rounded-xl border-slate-200 focus:border-violet-400 focus:ring-violet-400/20"
                          rows={3}
                          placeholder="Describe the task..."
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="deadline" className="text-slate-700 font-medium">Deadline</Label>
                          <Input
                            id="deadline"
                            data-testid="task-deadline-input"
                            type="datetime-local"
                            required
                            value={newTask.deadline}
                            onChange={(e) => setNewTask({ ...newTask, deadline: e.target.value })}
                            className="rounded-xl border-slate-200 focus:border-violet-400 focus:ring-violet-400/20"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="priority" className="text-slate-700 font-medium">Priority</Label>
                          <Select
                            value={newTask.priority}
                            onValueChange={(value) => setNewTask({ ...newTask, priority: value })}
                          >
                            <SelectTrigger data-testid="task-priority-select" className="rounded-xl border-slate-200">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="z-[200] rounded-xl">
                              <SelectItem value="Urgent">🔴 Urgent</SelectItem>
                              <SelectItem value="High">🟠 High</SelectItem>
                              <SelectItem value="Medium">🔵 Medium</SelectItem>
                              <SelectItem value="Low">⚪ Low</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="assigned_to" className="text-slate-700 font-medium">Assign To</Label>
                        <Select
                          value={newTask.assigned_to_email}
                          onValueChange={(value) => setNewTask({ ...newTask, assigned_to_email: value })}
                        >
                          <SelectTrigger data-testid="task-assignee-select" className="rounded-xl border-slate-200">
                            <SelectValue placeholder="Select team member" />
                          </SelectTrigger>
                          <SelectContent className="z-[200] rounded-xl">
                            <SelectItem value="__ALL_USERS__" className="font-semibold text-violet-600">
                              👥 Assign to ALL Users
                            </SelectItem>
                            <div className="border-t my-1"></div>
                            {users.map((user) => (
                              <SelectItem key={user.id} value={user.email}>
                                {user.name} ({user.email})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <Button type="submit" data-testid="submit-task-button" className="w-full rounded-xl font-medium bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 h-11">
                        Create Task
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            {/* Task Cards Grid */}
            <div className="grid gap-4" data-testid="tasks-list">
              {sortedTasks.length === 0 ? (
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-12 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center">
                    <ClipboardList className="h-8 w-8 text-slate-400" />
                  </div>
                  <p className="text-slate-500 font-medium">No tasks assigned yet.</p>
                  <p className="text-slate-400 text-sm mt-1">Create your first task to get started!</p>
                </div>
              ) : (
                sortedTasks.map((task) => (
                  <div
                    key={task.id}
                    data-testid={`task-card-${task.id}`}
                    className="group relative bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-lg hover:border-slate-200 hover:-translate-y-0.5 transition-all duration-300 overflow-hidden"
                  >
                    {/* Left accent bar based on priority */}
                    <div 
                      className="absolute left-0 top-0 bottom-0 w-1 rounded-l-2xl"
                      style={{ 
                        background: task.priority === 'urgent' ? '#ef4444' :
                                   task.priority === 'high' ? '#f97316' :
                                   task.priority === 'medium' ? '#6366f1' :
                                   '#94a3b8'
                      }}
                    />
                    
                    <div className="p-5 pl-6">
                      {/* Top Row: Title + Badges */}
                      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          {/* Title and Badges */}
                          <div className="flex flex-wrap items-center gap-2.5 mb-3">
                            <h3 className="font-semibold text-lg text-slate-800 truncate" data-testid={`task-title-${task.id}`}>
                              {task.title}
                            </h3>
                            <span
                              data-testid={`task-priority-${task.id}`}
                              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold uppercase tracking-wide ${
                                task.priority === 'urgent' ? 'bg-red-50 text-red-600 ring-1 ring-red-200' :
                                task.priority === 'high' ? 'bg-orange-50 text-orange-600 ring-1 ring-orange-200' :
                                task.priority === 'medium' ? 'bg-indigo-50 text-indigo-600 ring-1 ring-indigo-200' :
                                'bg-slate-50 text-slate-500 ring-1 ring-slate-200'
                              }`}
                            >
                              {task.priority}
                            </span>
                            <span
                              data-testid={`task-status-badge-${task.id}`}
                              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium capitalize ${
                                task.status === 'completed' ? 'bg-emerald-50 text-emerald-600 ring-1 ring-emerald-200' :
                                task.status === 'in_progress' ? 'bg-amber-50 text-amber-600 ring-1 ring-amber-200' :
                                'bg-slate-50 text-slate-500 ring-1 ring-slate-200'
                              }`}
                            >
                              <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                                task.status === 'completed' ? 'bg-emerald-500' :
                                task.status === 'in_progress' ? 'bg-amber-500' :
                                'bg-slate-400'
                              }`}></span>
                              {task.status.replace('_', ' ')}
                            </span>
                          </div>

                          {/* Description */}
                          {task.description && (
                            <p className="text-sm text-slate-500 mb-4 line-clamp-2 whitespace-pre-wrap break-words">{task.description}</p>
                          )}

                          {/* Metadata Row */}
                          <div className="flex flex-wrap items-center gap-3 text-sm">
                            <div className="flex items-center gap-1.5 text-slate-500 bg-slate-50 px-3 py-1.5 rounded-lg">
                              <Calendar className="h-3.5 w-3.5 text-slate-400" />
                              <span className="font-medium" data-testid={`task-deadline-${task.id}`}>
                                {formatIST(task.deadline)}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5 bg-violet-50/70 px-3 py-1.5 rounded-lg">
                              <Mail className="h-3.5 w-3.5 text-violet-400" />
                              <span className="text-violet-600 font-medium truncate max-w-[150px]" data-testid={`task-assigned-to-${task.id}`}>
                                {task.assigned_to}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5 bg-slate-50 px-3 py-1.5 rounded-lg">
                              <User className="h-3.5 w-3.5 text-slate-400" />
                              <span className="text-slate-500">
                                by <span className="font-medium text-slate-600" data-testid={`task-assigned-by-${task.id}`}>{task.assigned_by_name}</span>
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-row md:flex-col items-center gap-2 md:min-w-[140px]">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openTaskReplyDialog(task)}
                            className="flex-1 md:w-full rounded-xl border-slate-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600 transition-all text-slate-600"
                            data-testid={`task-reply-btn-${task.id}`}
                          >
                            <MessageCircle className="h-4 w-4 mr-1.5" />
                            Reply
                          </Button>
                          <Select
                            value={task.status}
                            onValueChange={(value) => handleUpdateStatus(task.id, value)}
                          >
                            <SelectTrigger data-testid={`task-status-select-${task.id}`} className="flex-1 md:w-full rounded-xl border-slate-200 hover:border-violet-300 text-sm h-9">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="rounded-xl">
                              <SelectItem value="pending">⏳ Pending</SelectItem>
                              <SelectItem value="in_progress">🔄 In Progress</SelectItem>
                              <SelectItem value="completed">✅ Completed</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteTask(task.id)}
                            className="rounded-xl text-slate-400 hover:text-red-500 hover:bg-red-50 transition-all px-2.5"
                            data-testid={`task-delete-btn-${task.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          <LazyTabContent value="candidates" activeTab={activeTab} className="space-y-6">
            {/* Professional Header Section */}
            <div className="bg-gradient-to-r from-slate-50 via-emerald-50/30 to-teal-50/40 rounded-2xl p-6 border border-slate-100/80 shadow-sm">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-200/50">
                    <UsersIcon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl md:text-2xl font-bold tracking-tight text-slate-800">
                      Candidate Status Tracker
                    </h2>
                    <p className="text-sm text-slate-500 mt-0.5">Track candidate progress through the hiring process</p>
                  </div>
                </div>

                <Button 
                  onClick={() => handleOpenCandidateDialog()} 
                  className="rounded-xl font-medium w-full md:w-auto bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 shadow-lg shadow-emerald-200/50 transition-all hover:shadow-xl hover:-translate-y-0.5"
                  data-testid="add-candidate-button"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Candidate
                </Button>
              </div>
            </div>

            {/* Search Section */}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 text-slate-400" />
              <Input
                placeholder="Search by name, email, phone, company, process..."
                value={candidateSearch}
                onChange={(e) => setCandidateSearch(e.target.value)}
                className="pl-11 h-12 rounded-xl border-slate-200 bg-white shadow-sm focus:border-emerald-400 focus:ring-emerald-400/20 transition-all"
                data-testid="candidate-search-input"
              />
            </div>

            {/* Candidate Status Table - Desktop: Table View, Mobile: Card View */}
            <Card className="rounded-2xl border-0 shadow-sm overflow-hidden bg-white">
              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white">
                      <th className="px-3 py-3 text-left font-medium whitespace-nowrap">Company</th>
                      <th className="px-3 py-3 text-left font-medium whitespace-nowrap">Candidate</th>
                      <th className="px-3 py-3 text-left font-medium whitespace-nowrap">Contact</th>
                      <th className="px-3 py-3 text-left font-medium whitespace-nowrap">ACN</th>
                      <th className="px-3 py-3 text-left font-medium whitespace-nowrap">Process</th>
                      <th className="px-3 py-3 text-left font-medium whitespace-nowrap">Location</th>
                      <th className="px-3 py-3 text-center font-medium whitespace-nowrap">Payment</th>
                      <th className="px-3 py-3 text-center font-medium whitespace-nowrap">Reg</th>
                      <th className="px-3 py-3 text-center font-medium whitespace-nowrap">Video</th>
                      <th className="px-3 py-3 text-center font-medium whitespace-nowrap">Assess</th>
                      <th className="px-3 py-3 text-center font-medium whitespace-nowrap">PDF</th>
                      <th className="px-3 py-3 text-center font-medium whitespace-nowrap">HR</th>
                      <th className="px-3 py-3 text-center font-medium whitespace-nowrap">Outcome</th>
                      <th className="px-3 py-3 text-center font-medium whitespace-nowrap">Full Pay</th>
                      <th className="px-3 py-3 text-center font-medium whitespace-nowrap">Agree</th>
                      <th className="px-3 py-3 text-center font-medium whitespace-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCandidates.length === 0 ? (
                      <tr>
                        <td colSpan="16" className="px-4 py-12 text-center">
                          <div className="flex flex-col items-center">
                            <div className="h-16 w-16 rounded-2xl bg-emerald-50 flex items-center justify-center mb-4">
                              <UsersIcon className="h-8 w-8 text-emerald-400" />
                            </div>
                            <p className="text-slate-600 font-medium">{candidateSearch ? 'No candidates match your search' : 'No candidate statuses yet'}</p>
                            <p className="text-sm text-slate-400 mt-1">{candidateSearch ? 'Try different search terms' : 'Add your first candidate to get started!'}</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredCandidates.map((candidate, idx) => (
                        <tr 
                          key={candidate.id} 
                          className={`${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'} hover:bg-emerald-50/50 transition-colors border-b border-slate-100`}
                          data-testid={`candidate-row-${candidate.id}`}
                        >
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1">
                              <Building2 className="h-3 w-3 text-blue-500 flex-shrink-0" />
                              <span className="font-medium text-blue-600 text-xs truncate max-w-[100px]" title={candidate.company_name}>
                                {candidate.company_name || '-'}
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <div>
                              <p className="font-medium text-gray-800 text-xs">{candidate.candidate_name}</p>
                              <p className="text-[10px] text-gray-400">
                                {candidate.created_at && formatIST(candidate.created_at)}
                              </p>
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <div className="text-[10px]">
                              <p className="truncate max-w-[120px]" title={candidate.email}>{candidate.email || '-'}</p>
                              <p className="text-gray-500">{candidate.phone || '-'}</p>
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <div className="text-[10px] flex items-center gap-1">
                              <p className="font-medium text-emerald-600">{candidate.actual_contact_number || candidate.acn || '-'}</p>
                              {(candidate.actual_contact_number || candidate.acn) && (
                                <a
                                  href={`https://wa.me/${(candidate.actual_contact_number || candidate.acn).replace(/[^0-9]/g, '')}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-1 rounded hover:bg-green-100 text-green-600 transition-colors"
                                  title="WhatsApp"
                                  data-testid={`whatsapp-btn-${candidate.id}`}
                                >
                                  <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                  </svg>
                                </a>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-xs">{candidate.process || '-'}</td>
                          <td className="px-3 py-2">
                            <div className="text-[10px]">
                              <p>{candidate.original_location || '-'}</p>
                              {candidate.job_location && <p className="text-gray-500">→ {candidate.job_location}</p>}
                            </div>
                          </td>
                          <td className="px-2 py-1 text-center">
                            <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-medium ${getStatusBadgeColor(candidate.initial_payment)} text-white`}>
                              {candidate.initial_payment?.replace('done', '✓').replace('pending', '...')}
                            </span>
                          </td>
                          <td className="px-2 py-1 text-center">
                            <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-medium ${getStatusBadgeColor(candidate.registration_update)} text-white`}>
                              {candidate.registration_update?.replace('done', '✓').replace('pending', '...')}
                            </span>
                          </td>
                          <td className="px-2 py-1 text-center">
                            <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-medium ${getStatusBadgeColor(candidate.video_received)} text-white`}>
                              {candidate.video_received?.replace('received', '✓').replace('not received', '✗')}
                            </span>
                          </td>
                          <td className="px-2 py-1 text-center">
                            <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-medium ${getStatusBadgeColor(candidate.assessment_update)} text-white`}>
                              {candidate.assessment_update?.replace('done', '✓').replace('pending', '...')}
                            </span>
                          </td>
                          <td className="px-2 py-1 text-center">
                            <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-medium ${getStatusBadgeColor(candidate.pdf_sent)} text-white`}>
                              {candidate.pdf_sent?.replace('sent', '✓').replace('pending', '...')}
                            </span>
                          </td>
                          <td className="px-2 py-1 text-center">
                            <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-medium ${getStatusBadgeColor(candidate.demo_hr_round)} text-white`}>
                              {candidate.demo_hr_round?.replace('done', '✓').replace('pending', '...')}
                            </span>
                          </td>
                          <td className="px-2 py-1 text-center">
                            <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-medium ${getStatusBadgeColor(candidate.final_outcome)} text-white`}>
                              {candidate.final_outcome?.replace('pending', '...')}
                            </span>
                          </td>
                          <td className="px-2 py-1 text-center">
                            <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-medium ${getStatusBadgeColor(candidate.full_payment)} text-white`}>
                              {candidate.full_payment?.replace('pending', '...')}
                            </span>
                          </td>
                          <td className="px-2 py-1 text-center">
                            <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-medium ${
                              candidate.agreement_signed === 'yes' ? 'bg-emerald-500' : 
                              candidate.agreement_signed === 'no' ? 'bg-red-500' : 'bg-amber-500'
                            } text-white`}>
                              {candidate.agreement_signed === 'yes' ? '✓' : candidate.agreement_signed === 'no' ? '✗' : '...'}
                            </span>
                          </td>
                          <td className="px-2 py-1 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <button
                                onClick={() => setViewingCandidateActivity(candidate)}
                                className="p-1 rounded hover:bg-violet-100 text-violet-600 transition-colors"
                                title="Activity Feed"
                              >
                                <Activity className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => setSendingSignatureToCandidate(candidate)}
                                className="p-1 rounded hover:bg-orange-100 text-orange-600 transition-colors"
                                title="Send for Signature"
                                data-testid={`signature-btn-${candidate.id}`}
                              >
                                <FileSignature className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => handleOpenCandidateDialog(candidate)}
                                className="p-1 rounded hover:bg-blue-100 text-blue-600 transition-colors"
                                title="Edit"
                              >
                                <Edit className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => handleArchiveCandidate(candidate.id)}
                                className="p-1 rounded hover:bg-amber-100 text-amber-600 transition-colors"
                                title="Archive"
                              >
                                <Archive className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => handleDeleteCandidate(candidate.id)}
                                className="p-1 rounded hover:bg-red-100 text-red-500 transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden divide-y divide-gray-100">
                {filteredCandidates.length === 0 ? (
                  <div className="px-4 py-8 text-center text-gray-500">
                    {candidateSearch ? 'No candidates match your search' : 'No candidate statuses yet. Add your first candidate!'}
                  </div>
                ) : (
                  filteredCandidates.map((candidate) => (
                    <div key={candidate.id} className="p-4 hover:bg-emerald-50 transition-colors" data-testid={`candidate-card-${candidate.id}`}>
                      {/* Header: Name & Company */}
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="font-semibold text-gray-900">{candidate.candidate_name}</h3>
                          <p className="text-sm text-blue-600 flex items-center gap-1">
                            <Building2 className="h-3 w-3" />
                            {candidate.company_name || 'No company'}
                          </p>
                        </div>
                        <div className="flex gap-1">
                          {(candidate.actual_contact_number || candidate.acn) && (
                            <a
                              href={`https://wa.me/${(candidate.actual_contact_number || candidate.acn).replace(/[^0-9]/g, '')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 hover:bg-green-100 rounded-lg text-green-600"
                              title="WhatsApp"
                            >
                              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                              </svg>
                            </a>
                          )}
                          <button
                            onClick={() => setViewingCandidateActivity(candidate)}
                            className="p-2 hover:bg-violet-100 rounded-lg text-violet-600"
                            title="Activity Feed"
                          >
                            <Activity className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => setSendingSignatureToCandidate(candidate)}
                            className="p-2 hover:bg-orange-100 rounded-lg text-orange-600"
                            title="Send for Signature"
                          >
                            <FileSignature className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleOpenCandidateDialog(candidate)}
                            className="p-2 hover:bg-emerald-100 rounded-lg text-emerald-600"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleArchiveCandidate(candidate.id)}
                            className="p-2 hover:bg-amber-100 rounded-lg text-amber-600"
                          >
                            <Archive className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteCandidate(candidate.id)}
                            className="p-2 hover:bg-red-100 rounded-lg text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      
                      {/* Contact Info */}
                      <div className="text-xs text-gray-500 mb-3 space-y-0.5">
                        <p>{candidate.email || 'No email'}</p>
                        <p>{candidate.phone || 'No phone'}</p>
                        <p className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {candidate.city || 'Unknown'}, {candidate.state || ''}
                        </p>
                      </div>
                      
                      {/* Status Pills */}
                      <div className="flex flex-wrap gap-1.5">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${candidate.payment_done ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          Payment {candidate.payment_done ? '✓' : '○'}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${candidate.registration_done ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          Reg {candidate.registration_done ? '✓' : '○'}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${candidate.video_done ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          Video {candidate.video_done ? '✓' : '○'}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${candidate.assessment_done ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          Assess {candidate.assessment_done ? '✓' : '○'}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${candidate.pdf_done ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          PDF {candidate.pdf_done ? '✓' : '○'}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${candidate.hr_done ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          HR {candidate.hr_done ? '✓' : '○'}
                        </span>
                        {candidate.outcome && (
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            candidate.outcome === 'selected' ? 'bg-emerald-100 text-emerald-700' :
                            candidate.outcome === 'rejected' ? 'bg-red-100 text-red-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {candidate.outcome}
                          </span>
                        )}
                      </div>
                      
                      {/* Timestamp */}
                      <p className="text-[10px] text-gray-400 mt-2">
                        Added {candidate.created_at && formatISTFull(candidate.created_at)}
                      </p>
                    </div>
                  ))
                )}
              </div>

              {/* Row count */}
              {filteredCandidates.length > 0 && (
                <div className="px-4 py-2 bg-gray-50 border-t text-xs text-gray-500">
                  Showing {filteredCandidates.length} candidate{filteredCandidates.length !== 1 ? 's' : ''}
                </div>
              )}
            </Card>

            {/* Candidate Dialog */}
            <Dialog open={isCandidateDialogOpen} onOpenChange={setIsCandidateDialogOpen}>
              <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading">
                    {editingCandidate ? 'Edit Candidate Status' : 'Add Candidate Status'}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Company Name *</Label>
                      <Input
                        value={candidateData.company_name}
                        onChange={(e) => setCandidateData({ ...candidateData, company_name: e.target.value })}
                        className="rounded-sm"
                        placeholder="Enter company name"
                        data-testid="company-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Candidate Name *</Label>
                      <Input
                        value={candidateData.candidate_name}
                        onChange={(e) => setCandidateData({ ...candidateData, candidate_name: e.target.value })}
                        className="rounded-sm"
                        placeholder="Enter candidate name"
                        data-testid="candidate-name-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Date</Label>
                      <Input
                        value={format(new Date(), 'yyyy-MM-dd HH:mm')}
                        disabled
                        className="rounded-sm bg-gray-100"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={candidateData.email}
                        onChange={(e) => setCandidateData({ ...candidateData, email: e.target.value })}
                        className="rounded-sm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Phone Number</Label>
                      <Input
                        value={candidateData.phone}
                        onChange={(e) => setCandidateData({ ...candidateData, phone: e.target.value })}
                        className="rounded-sm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Actual Contact Number (ACN)</Label>
                      <Input
                        value={candidateData.actual_contact_number}
                        onChange={(e) => setCandidateData({ ...candidateData, actual_contact_number: e.target.value })}
                        className="rounded-sm"
                        placeholder="Enter actual contact number"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Process</Label>
                      <Input
                        value={candidateData.process}
                        onChange={(e) => setCandidateData({ ...candidateData, process: e.target.value })}
                        className="rounded-sm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Original Location</Label>
                      <Input
                        value={candidateData.original_location}
                        onChange={(e) => setCandidateData({ ...candidateData, original_location: e.target.value })}
                        className="rounded-sm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Job Location</Label>
                      <Input
                        value={candidateData.job_location}
                        onChange={(e) => setCandidateData({ ...candidateData, job_location: e.target.value })}
                        className="rounded-sm"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Initial Payment</Label>
                      <Select
                        value={candidateData.initial_payment}
                        onValueChange={(value) => setCandidateData({ ...candidateData, initial_payment: value })}
                      >
                        <SelectTrigger className="rounded-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="done">Done</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="not-need">Not Needed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Registration Update</Label>
                      <Select
                        value={candidateData.registration_update}
                        onValueChange={(value) => setCandidateData({ ...candidateData, registration_update: value })}
                      >
                        <SelectTrigger className="rounded-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="done">Done</SelectItem>
                          <SelectItem value="not done">Not Done</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Video Received</Label>
                      <Select
                        value={candidateData.video_received}
                        onValueChange={(value) => setCandidateData({ ...candidateData, video_received: value })}
                      >
                        <SelectTrigger className="rounded-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="received">Received</SelectItem>
                          <SelectItem value="not received">Not Received</SelectItem>
                          <SelectItem value="not needed">Not Needed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Assessment Update</Label>
                      <Select
                        value={candidateData.assessment_update}
                        onValueChange={(value) => setCandidateData({ ...candidateData, assessment_update: value })}
                      >
                        <SelectTrigger className="rounded-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="done">Done</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>PDF Sent</Label>
                      <Select
                        value={candidateData.pdf_sent}
                        onValueChange={(value) => setCandidateData({ ...candidateData, pdf_sent: value })}
                      >
                        <SelectTrigger className="rounded-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="sent">Sent</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Demo HR Round</Label>
                      <Select
                        value={candidateData.demo_hr_round}
                        onValueChange={(value) => setCandidateData({ ...candidateData, demo_hr_round: value })}
                      >
                        <SelectTrigger className="rounded-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="done">Done</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Final Outcome</Label>
                      <Select
                        value={candidateData.final_outcome}
                        onValueChange={(value) => setCandidateData({ ...candidateData, final_outcome: value })}
                      >
                        <SelectTrigger className="rounded-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pass">Pass</SelectItem>
                          <SelectItem value="failed">Failed</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Full Payment</Label>
                      <Select
                        value={candidateData.full_payment}
                        onValueChange={(value) => setCandidateData({ ...candidateData, full_payment: value })}
                      >
                        <SelectTrigger className="rounded-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="done">Done</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Agreement Signed</Label>
                      <Select
                        value={candidateData.agreement_signed || 'no'}
                        onValueChange={(value) => setCandidateData({ ...candidateData, agreement_signed: value })}
                      >
                        <SelectTrigger className="rounded-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="no">No</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {candidateData.final_outcome === 'failed' && (
                    <div className="space-y-2">
                      <Label>Failure Reason</Label>
                      <Textarea
                        value={candidateData.failure_reason}
                        onChange={(e) => setCandidateData({ ...candidateData, failure_reason: e.target.value })}
                        className="rounded-sm"
                        placeholder="Enter reason for failure..."
                        rows={3}
                      />
                    </div>
                  )}

                  <Button 
                    onClick={handleSaveCandidate} 
                    className="w-full rounded-sm"
                    data-testid="save-candidate-button"
                  >
                    {editingCandidate ? 'Update Candidate Status' : 'Add Candidate Status'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Candidate Activity Feed Dialog */}
            <Dialog open={!!viewingCandidateActivity} onOpenChange={(open) => !open && setViewingCandidateActivity(null)}>
              <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading flex items-center gap-2">
                    <Activity className="h-5 w-5 text-violet-500" />
                    Universal Activity Feed
                  </DialogTitle>
                </DialogHeader>
                {viewingCandidateActivity && (
                  <CandidateActivityFeed 
                    candidate={viewingCandidateActivity} 
                    onBack={() => setViewingCandidateActivity(null)}
                  />
                )}
              </DialogContent>
            </Dialog>

            {/* RabbitSign E-Signature Dialog */}
            <Dialog open={!!sendingSignatureToCandidate} onOpenChange={(open) => !open && setSendingSignatureToCandidate(null)}>
              <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto p-0">
                {sendingSignatureToCandidate && (
                  <RabbitSignIntegration 
                    candidateId={sendingSignatureToCandidate.id}
                    candidateName={sendingSignatureToCandidate.candidate_name}
                    candidateEmail={sendingSignatureToCandidate.email}
                    onBack={() => setSendingSignatureToCandidate(null)}
                  />
                )}
              </DialogContent>
            </Dialog>
          </LazyTabContent>

          {/* HR Config Tab - Admin Only */}
          {currentUser?.is_admin && (
            <LazyTabContent value="hr-config" activeTab={activeTab} className="space-y-6">
              <div className="bg-gradient-to-r from-slate-50 via-cyan-50/30 to-blue-50/40 rounded-2xl p-6 border border-slate-100/80 shadow-sm">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg shadow-cyan-200/50">
                    <Settings className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl md:text-2xl font-bold tracking-tight text-slate-800">
                      HR Assessment Configuration
                    </h2>
                    <p className="text-sm text-slate-500 mt-0.5">Configure assessment templates per company & position</p>
                  </div>
                </div>
              </div>
              
              <HRAssessmentConfigPanel 
                getAuthToken={getAuthToken}
                companies={companies}
              />
            </LazyTabContent>
          )}

          {/* Interviews Tab - Schedule & Manage */}
          <LazyTabContent value="interviews" activeTab={activeTab} className="space-y-6">
            <InterviewScheduler currentUser={currentUser} />
          </LazyTabContent>

          {/* HR Rounds Tab */}
          <LazyTabContent value="hr-rounds" activeTab={activeTab} className="space-y-6">
            {/* Professional Header Section */}
            <div className="bg-gradient-to-r from-slate-50 via-blue-50/30 to-indigo-50/40 rounded-2xl p-6 border border-slate-100/80 shadow-sm">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-200/50">
                    <Calendar className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl md:text-2xl font-bold tracking-tight text-slate-800">
                      HR Rounds Management
                    </h2>
                    <p className="text-sm text-slate-500 mt-0.5">Track and manage candidate interview rounds</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  {/* New HR Performance Module Button */}
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button className="rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 shadow-lg shadow-blue-200/50 transition-all hover:shadow-xl hover:-translate-y-0.5" data-testid="add-hr-performance-btn">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Performance
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="w-[calc(100vw-1rem)] md:max-w-[500px] max-h-[80vh] overflow-y-auto rounded-2xl">
                      <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                          <UsersIcon className="h-5 w-5 text-blue-600" />
                          Select Candidate
                        </DialogTitle>
                      </DialogHeader>
                      <div className="space-y-2 mt-4">
                        {allCandidates.map(candidate => (
                          <Button
                            key={candidate.id}
                            variant="outline"
                            className="w-full justify-start text-left h-auto py-3 rounded-xl border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 transition-all"
                            onClick={() => {
                              setSelectedCandidateForNewPerformance(candidate);
                              setIsNewPerformanceDialogOpen(true);
                            }}
                          >
                            <div className="flex items-center gap-3 w-full">
                              <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center">
                                <User className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-blue-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-slate-800 truncate">{candidate.candidate_name}</p>
                                <p className="text-xs text-slate-500 truncate">{candidate.company_name} • {candidate.process || 'No process'}</p>
                              </div>
                            </div>
                          </Button>
                        ))}
                        {allCandidates.length === 0 && (
                          <div className="text-center py-8 bg-slate-50 rounded-xl">
                            <UsersIcon className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                            <p className="text-sm text-slate-500">No candidates found</p>
                            <p className="text-xs text-slate-400 mt-1">Add candidates first to create performance records</p>
                          </div>
                        )}
                      </div>
                    </DialogContent>
                  </Dialog>
                  
                  {/* View Reschedule Requests Button */}
                  <Dialog onOpenChange={(open) => open && fetchRescheduleRequests()}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="rounded-xl border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all" data-testid="view-reschedule-requests-btn">
                        <Clock className="h-4 w-4 mr-2 text-amber-600" />
                        Reschedule
                        {rescheduleRequests.filter(r => r.status === 'pending').length > 0 && (
                          <Badge className="ml-2 bg-amber-500 text-white text-xs h-5 min-w-[20px] rounded-full">
                            {rescheduleRequests.filter(r => r.status === 'pending').length}
                          </Badge>
                        )}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="w-[calc(100vw-1rem)] md:max-w-[600px] max-h-[80vh] overflow-y-auto rounded-2xl">
                      <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                          <Clock className="h-5 w-5 text-amber-600" />
                          Reschedule Requests
                        </DialogTitle>
                      </DialogHeader>
                      <div className="space-y-3 mt-4">
                        {rescheduleRequests.length === 0 ? (
                          <div className="text-center py-12 bg-slate-50 rounded-xl">
                            <Clock className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                            <p className="text-sm text-slate-500">No reschedule requests</p>
                          </div>
                        ) : (
                          rescheduleRequests.map(req => (
                            <div key={req.id} className={`p-4 rounded-xl border transition-all ${req.status === 'pending' ? 'border-amber-200 bg-amber-50/50' : req.status === 'approved' ? 'border-emerald-200 bg-emerald-50/50' : 'border-red-200 bg-red-50/50'}`}>
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="font-medium text-slate-800">{req.candidate_name}</p>
                                  <p className="text-sm text-slate-500">{req.candidate_email}</p>
                                  <div className="mt-2">
                                    <Badge className="bg-blue-100 text-blue-700 text-xs rounded-lg">
                                      {req.session_type?.replace('_', ' ').replace('demo hr round', 'Demo HR Round').replace('briefing', 'Briefing')}
                                    </Badge>
                                  </div>
                                  <div className="mt-2 text-xs text-slate-500 space-y-0.5">
                                    <p>Original: {req.original_datetime ? formatISTFull(req.original_datetime) : 'N/A'}</p>
                                    <p className="font-medium text-amber-700">Requested: {formatISTFull(req.requested_datetime)}</p>
                                  </div>
                                  {req.reason && (
                                    <div className="mt-2 p-2 bg-slate-100 rounded-lg">
                                      <p className="text-xs font-semibold text-slate-600 mb-1">Reason for Rescheduling:</p>
                                      <p className="text-sm text-slate-700">{req.reason}</p>
                                    </div>
                                  )}
                                </div>
                                <div>
                                  {req.status === 'pending' ? (
                                    <div className="flex gap-2">
                                      <Button
                                        size="sm"
                                        onClick={() => handleApproveReschedule(req.id)}
                                        className="bg-emerald-500 hover:bg-emerald-600 text-white h-8 rounded-lg"
                                      >
                                        <Check className="h-3 w-3 mr-1" />
                                        Approve
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="destructive"
                                        onClick={() => handleRejectReschedule(req.id)}
                                        className="h-8 rounded-lg"
                                      >
                                        <X className="h-3 w-3 mr-1" />
                                        Reject
                                      </Button>
                                    </div>
                                  ) : (
                                    <Badge className={`${req.status === 'approved' ? 'bg-emerald-500' : 'bg-red-500'} rounded-lg`}>
                                      {req.status}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </div>

            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 text-slate-400" />
              <Input
                placeholder="Search by candidate name or process..."
                value={hrRoundSearch}
                onChange={(e) => setHrRoundSearch(e.target.value)}
                className="pl-11 h-12 rounded-xl border-slate-200 bg-white shadow-sm focus:border-blue-400 focus:ring-blue-400/20 transition-all"
              />
            </div>

            {/* Performance Cards Grid */}
            <div className="grid gap-5">
              {allPerformances.filter(p => 
                p.candidate_name?.toLowerCase().includes(hrRoundSearch.toLowerCase()) ||
                p.process?.toLowerCase().includes(hrRoundSearch.toLowerCase())
              ).length === 0 ? (
                <div className="rounded-2xl p-12 text-center bg-gradient-to-br from-slate-50 to-blue-50/30 border border-slate-100">
                  <div className="h-20 w-20 rounded-2xl bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center mx-auto mb-5 shadow-inner">
                    <ClipboardList className="h-10 w-10 text-blue-500" />
                  </div>
                  <p className="text-lg font-medium text-slate-700">No HR round data yet</p>
                  <p className="text-sm text-slate-400 mt-2 max-w-sm mx-auto">Click "Add Performance" to start tracking candidate interview rounds</p>
                </div>
              ) : (
                allPerformances.filter(p => 
                  p.candidate_name?.toLowerCase().includes(hrRoundSearch.toLowerCase()) ||
                  p.process?.toLowerCase().includes(hrRoundSearch.toLowerCase())
                ).map((perf) => (
                  <div key={perf.id} className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden border border-slate-100">
                    {/* Subtle top accent line */}
                    <div className="h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500" />
                    
                    {/* Clickable Header */}
                    <div 
                      className="p-5 cursor-pointer hover:bg-slate-50/50 transition-colors"
                      onClick={() => setExpandedHrRounds(prev => ({ ...prev, [perf.id]: !prev[perf.id] }))}
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1 flex items-start gap-4">
                          {/* Expand Icon */}
                          <div className={`mt-1 p-1 rounded-lg transition-all ${expandedHrRounds[perf.id] ? 'bg-blue-100' : 'bg-slate-100'}`}>
                            {expandedHrRounds[perf.id] ? (
                              <ChevronDown className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-blue-600" />
                            ) : (
                              <ChevronRight className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-slate-500" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-lg text-slate-800">{perf.candidate_name}</h3>
                            <div className="flex flex-wrap gap-2 mt-2">
                              {perf.company_name && (
                                <Badge variant="outline" className="text-xs border-blue-200 text-blue-700 bg-blue-50/50 rounded-lg">
                                  <Building2 className="h-3 w-3 mr-1.5" />{perf.company_name}
                                </Badge>
                              )}
                              {perf.process && (
                                <Badge className="bg-indigo-100 text-indigo-700 text-xs rounded-lg">{perf.process}</Badge>
                              )}
                              {perf.final_outcome && perf.final_outcome !== 'pending' && (
                                <Badge className={`text-xs rounded-lg ${perf.final_outcome === 'success' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                  {perf.final_outcome === 'success' ? '✓ Success' : '✗ ' + perf.final_outcome}
                                </Badge>
                              )}
                            </div>
                            {/* Contact Info Row */}
                            <div className="flex flex-wrap gap-3 mt-3 text-xs text-slate-500">
                              {perf.email && (
                                <span className="flex items-center gap-1.5 bg-slate-50 px-2.5 py-1 rounded-lg">
                                  <Mail className="h-3 w-3 text-blue-500" />{perf.email}
                                </span>
                              )}
                              {perf.phone && (
                                <span className="flex items-center gap-1.5 bg-slate-50 px-2.5 py-1 rounded-lg">
                                  <Phone className="h-3 w-3 text-emerald-500" />{perf.phone}
                                </span>
                              )}
                              <span className="flex items-center gap-1.5 bg-violet-50 text-violet-700 px-2.5 py-1 rounded-lg font-medium">
                                <Calendar className="h-3 w-3" />
                                {[...(perf.briefing_sessions || []), ...(perf.demo_rounds || [])].filter(s => s.scheduled_datetime).length} sessions scheduled
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => openSessionEditor(perf, { type: 'all' })}
                            data-testid={`update-perf-${perf.id}`}
                            className="rounded-xl bg-blue-600 hover:bg-blue-700 shadow-sm"
                          >
                            <Edit className="h-3.5 w-3.5 mr-1.5" /> Update
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeletePerformance(perf.id, perf.candidate_name)}
                            className="rounded-xl border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300"
                            data-testid={`delete-perf-${perf.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Expandable Sessions Content */}
                    {expandedHrRounds[perf.id] && (
                      <div className="px-5 pb-5 pt-3 border-t border-slate-100 bg-slate-50/30">
                        {/* Sessions Grid - rendered from the configured session arrays */}
                        <div className="space-y-4">
                          {(perf.briefing_sessions?.length > 0 || perf.demo_rounds?.length > 0) ? (
                            <>
                              {perf.briefing_sessions?.length > 0 && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  {perf.briefing_sessions.map((session, idx) => (
                                    <BriefingSessionSummary
                                      key={session.id || idx}
                                      session={session}
                                      index={idx}
                                      formatDate={formatIST}
                                      onEdit={() => openSessionEditor(perf, { type: 'briefing', index: idx })}
                                      onNotify={() => openNotifyDialog(perf.id, `briefing_${idx + 1}`, perf.candidate_name)}
                                    />
                                  ))}
                                </div>
                              )}
                              {perf.demo_rounds?.length > 0 && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  {perf.demo_rounds.map((round, idx) => (
                                    <DemoRoundSummary
                                      key={round.id || idx}
                                      round={round}
                                      index={idx}
                                      formatDate={formatIST}
                                      onEdit={() => openSessionEditor(perf, { type: 'demo', index: idx })}
                                      onNotify={() => openNotifyDialog(perf.id, `demo_hr_round_${idx + 1}`, perf.candidate_name)}
                                    />
                                  ))}
                                </div>
                              )}
                            </>
                          ) : (
                            <p className="text-sm text-slate-400 italic">No sessions configured yet. Click "Update" to set up briefing sessions and demo HR rounds.</p>
                          )}

                          {/* Row 3: Performance Report */}
                          <div className="rounded-xl p-4 bg-white border border-slate-200 shadow-sm">
                            <div className="flex items-center gap-3 mb-3">
                              <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center shadow-sm">
                                <FileText className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" />
                              </div>
                              <h4 className="font-semibold text-sm text-slate-700">Performance Report</h4>
                            </div>
                            {perf.performance_report ? (
                              <p className="text-sm text-slate-600 whitespace-pre-wrap bg-slate-50 p-4 rounded-lg border border-slate-100">{perf.performance_report}</p>
                            ) : <p className="text-xs text-slate-400 italic">No performance report added yet</p>}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Schedule Notification Dialog */}
            <Dialog open={notifyDialogOpen} onOpenChange={setNotifyDialogOpen}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-lg">
                    <Mail className="h-5 w-5 text-blue-600" />
                    Send Schedule Notification
                  </DialogTitle>
                  <DialogDescription>
                    Send an email notification to <strong>{notifyDialogData.candidateName}</strong> about their upcoming session.
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  {/* Session Type Badge */}
                  <div className="flex items-center gap-2">
                    <Badge className="bg-purple-100 text-purple-700">
                      {notifyDialogData.sessionType === 'briefing_1' ? 'Briefing 1' :
                       notifyDialogData.sessionType === 'briefing_2' ? 'Briefing 2' :
                       notifyDialogData.sessionType === 'demo_hr_round_1' ? 'Demo HR Round 1' :
                       notifyDialogData.sessionType === 'demo_hr_round_2' ? 'Demo HR Round 2' : 
                       notifyDialogData.sessionType}
                    </Badge>
                  </div>
                  
                  {/* Meeting Link Input */}
                  <div className="space-y-2">
                    <Label htmlFor="meeting-link" className="flex items-center gap-1">
                      <Link2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-500" />
                      Meeting Link
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        id="meeting-link"
                        type="url"
                        placeholder="https://meet.jit.si/xxx or custom link"
                        value={notifyDialogData.meetingLink}
                        onChange={(e) => setNotifyDialogData(prev => ({...prev, meetingLink: e.target.value}))}
                        className="rounded-lg flex-1"
                        data-testid="meeting-link-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          // Generate Jitsi meeting link
                          const roomId = `officium-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
                          const jitsiLink = `https://meet.jit.si/${roomId}`;
                          setNotifyDialogData(prev => ({...prev, meetingLink: jitsiLink}));
                        }}
                        className="shrink-0 text-xs"
                        data-testid="generate-jitsi-btn"
                      >
                        <Video className="h-3 w-3 mr-1" />
                        Generate Jitsi
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">Add a meeting link or click "Generate Jitsi" to auto-create one</p>
                  </div>
                  
                  {/* Notes Section */}
                  <div className="space-y-2">
                    <Label htmlFor="custom-message" className="flex items-center gap-1">
                      <MessageSquare className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-500" />
                      Notes / Instructions
                    </Label>
                    <Textarea
                      id="custom-message"
                      placeholder="Add any additional instructions or notes for the candidate..."
                      value={notifyDialogData.customMessage}
                      onChange={(e) => setNotifyDialogData(prev => ({...prev, customMessage: e.target.value}))}
                      rows={3}
                      className="rounded-lg"
                      data-testid="custom-message-input"
                    />
                  </div>
                  
                  {/* Preview Section */}
                  {(notifyDialogData.customMessage || notifyDialogData.meetingLink) && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-xs font-semibold text-blue-700 mb-2">Email Preview:</p>
                      <div className="text-sm text-blue-600 space-y-1">
                        {notifyDialogData.customMessage && (
                          <p>{notifyDialogData.customMessage}</p>
                        )}
                        {notifyDialogData.meetingLink && (
                          <p className="flex items-center gap-1">
                            <Link2 className="h-3 w-3" />
                            <a href={notifyDialogData.meetingLink} className="underline" target="_blank" rel="noreferrer">
                              {notifyDialogData.meetingLink.length > 40 ? notifyDialogData.meetingLink.substring(0, 40) + '...' : notifyDialogData.meetingLink}
                            </a>
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                
                <DialogFooter>
                  <Button variant="outline" onClick={() => setNotifyDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleSendScheduleEmail}
                    disabled={sendingScheduleEmail === `${notifyDialogData.performanceId}_${notifyDialogData.sessionType}`}
                    className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
                    data-testid="send-notify-email-btn"
                  >
                    {sendingScheduleEmail === `${notifyDialogData.performanceId}_${notifyDialogData.sessionType}` ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Send Notification
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

          </LazyTabContent>

          {/* Job Updates Tab */}
          <LazyTabContent value="job-updates" activeTab={activeTab} className="space-y-4 md:space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div>
                <h2 className="font-heading text-xl md:text-3xl font-bold tracking-tight mb-1 bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent" data-testid="job-updates-header">
                  Job Updates
                </h2>
                <p className="text-sm text-muted-foreground">
                  {currentUser?.role === 'lead_specialist' || currentUser?.role === 'candidate' 
                    ? 'Latest job opportunities' 
                    : 'Manage and publish job opportunities'}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
                {/* Search Box */}
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" />
                  <Input
                    placeholder="Search jobs..."
                    value={jobSearchQuery}
                    onChange={(e) => setJobSearchQuery(e.target.value)}
                    className="pl-10 rounded-full"
                  />
                </div>
                
                {/* Publish Filter - Only for Admin/User/CRM */}
                {currentUser?.role !== 'lead_specialist' && currentUser?.role !== 'candidate' && (
                  <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
                    <Button
                      size="sm"
                      variant={jobPublishFilter === 'all' ? 'default' : 'ghost'}
                      onClick={() => setJobPublishFilter('all')}
                      className={`h-7 text-xs rounded-md ${jobPublishFilter === 'all' ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white' : 'text-slate-600'}`}
                      data-testid="job-filter-all"
                    >
                      All ({jobUpdates.length})
                    </Button>
                    <Button
                      size="sm"
                      variant={jobPublishFilter === 'published' ? 'default' : 'ghost'}
                      onClick={() => setJobPublishFilter('published')}
                      className={`h-7 text-xs rounded-md ${jobPublishFilter === 'published' ? 'bg-green-500 text-white' : 'text-slate-600'}`}
                      data-testid="job-filter-published"
                    >
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Published ({jobUpdates.filter(j => j.is_published).length})
                    </Button>
                    <Button
                      size="sm"
                      variant={jobPublishFilter === 'unpublished' ? 'default' : 'ghost'}
                      onClick={() => setJobPublishFilter('unpublished')}
                      className={`h-7 text-xs rounded-md ${jobPublishFilter === 'unpublished' ? 'bg-slate-500 text-white' : 'text-slate-600'}`}
                      data-testid="job-filter-unpublished"
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      Unpublished ({jobUpdates.filter(j => !j.is_published).length})
                    </Button>
                  </div>
                )}
                {currentUser?.role !== 'lead_specialist' && currentUser?.role !== 'candidate' && (
                  <Dialog open={isJobFormOpen} onOpenChange={(open) => { setIsJobFormOpen(open); if (!open) { setEditingJob(null); cancelEditJob(); } }}>
                    <DialogTrigger asChild>
                      <Button className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700">
                        <Plus className="h-4 w-4 mr-2" />
                        Post New Job
                      </Button>
                    </DialogTrigger>
                  <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <Briefcase className="h-5 w-5 text-amber-600" />
                        {editingJob ? 'Edit Job Posting' : 'Create New Job'}
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateJob} className="space-y-4 mt-4">
                      {/* Company Logo */}
                      <div>
                        <Label className="text-sm font-medium">Company Logo</Label>
                        <div className="mt-2 flex items-center gap-4">
                          {newJob.company_logo_url ? (
                            <div className="relative">
                              <img 
                                src={`${API.replace('/api', '')}${newJob.company_logo_url}`}
                                alt="Logo preview"
                                className="h-14 w-14 rounded-xl object-cover border-2 border-amber-100"
                              />
                              <button
                                type="button"
                                onClick={() => setNewJob(prev => ({ ...prev, company_logo_url: '' }))}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          ) : (
                            <div className="h-14 w-14 rounded-xl bg-slate-100 flex items-center justify-center border-2 border-dashed border-slate-300">
                              <Building2 className="h-6 w-6 text-muted-foreground" />
                            </div>
                          )}
                          <label className="cursor-pointer">
                            <span className={`inline-flex items-center justify-center px-3 py-1.5 text-sm font-medium rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground ${uploadingLogo ? 'opacity-50 cursor-not-allowed' : ''}`}>
                              {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                            </span>
                            <input 
                              type="file" 
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => e.target.files[0] && handleUploadJobImage(e.target.files[0], 'logo')}
                              disabled={uploadingLogo}
                            />
                          </label>
                        </div>
                      </div>

                      {/* Company Name */}
                      <div>
                        <Label htmlFor="company_name">Company Name *</Label>
                        <Input 
                          id="company_name"
                          value={newJob.company_name}
                          onChange={(e) => setNewJob(prev => ({ ...prev, company_name: e.target.value }))}
                          placeholder="Enter company name"
                          className="mt-1"
                          required
                        />
                      </div>

                      {/* Position Name */}
                      <div>
                        <Label htmlFor="position_name">Position Name *</Label>
                        <Input 
                          id="position_name"
                          value={newJob.position_name || ''}
                          onChange={(e) => setNewJob(prev => ({ ...prev, position_name: e.target.value }))}
                          placeholder="e.g. Software Developer, Sales Manager"
                          className="mt-1"
                          required
                        />
                      </div>

                      {/* Location */}
                      <div>
                        <Label htmlFor="location">Location</Label>
                        <Input 
                          id="location"
                          value={newJob.location}
                          onChange={(e) => setNewJob(prev => ({ ...prev, location: e.target.value }))}
                          placeholder="e.g. Mumbai, Remote, Bangalore"
                          className="mt-1"
                        />
                      </div>

                      {/* Salary with Currency */}
                      <div>
                        <Label htmlFor="salary_range">Salary Range</Label>
                        <div className="flex gap-2 mt-1">
                          <Select
                            value={newJob.salary_currency || '₹'}
                            onValueChange={(value) => setNewJob(prev => ({ ...prev, salary_currency: value }))}
                          >
                            <SelectTrigger className="w-24">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="₹">₹ INR</SelectItem>
                              <SelectItem value="$">$ USD</SelectItem>
                              <SelectItem value="€">€ EUR</SelectItem>
                              <SelectItem value="£">£ GBP</SelectItem>
                              <SelectItem value="AED">AED</SelectItem>
                              <SelectItem value="SAR">SAR</SelectItem>
                              <SelectItem value="QAR">QAR</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input 
                            id="salary_range"
                            value={newJob.salary_range}
                            onChange={(e) => setNewJob(prev => ({ ...prev, salary_range: e.target.value }))}
                            placeholder="e.g. 5-8 LPA or 50,000-80,000"
                            className="flex-1"
                          />
                        </div>
                      </div>

                      {/* Job Description */}
                      <div>
                        <Label htmlFor="job_description">Job Description *</Label>
                        <Textarea 
                          id="job_description"
                          value={newJob.job_description}
                          onChange={(e) => setNewJob(prev => ({ ...prev, job_description: e.target.value }))}
                          placeholder="Describe the job opportunity, roles, responsibilities... (Links, emails, phone numbers will be clickable)"
                          rows={4}
                          className="mt-1"
                          required
                        />
                      </div>

                      {/* Requirements */}
                      <div>
                        <Label htmlFor="requirements">Requirements</Label>
                        <Textarea 
                          id="requirements"
                          value={newJob.requirements || ''}
                          onChange={(e) => setNewJob(prev => ({ ...prev, requirements: e.target.value }))}
                          placeholder="Skills, qualifications, certifications required..."
                          rows={3}
                          className="mt-1"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        {/* Education */}
                        <div>
                          <Label htmlFor="education">Education Required</Label>
                          <Input 
                            id="education"
                            value={newJob.education || ''}
                            onChange={(e) => setNewJob(prev => ({ ...prev, education: e.target.value }))}
                            placeholder="e.g. Bachelor's degree"
                            className="mt-1"
                          />
                        </div>

                        {/* Experience */}
                        <div>
                          <Label htmlFor="experience">Experience Required</Label>
                          <Input 
                            id="experience"
                            value={newJob.experience || ''}
                            onChange={(e) => setNewJob(prev => ({ ...prev, experience: e.target.value }))}
                            placeholder="e.g. 3-5 years"
                            className="mt-1"
                          />
                        </div>
                      </div>

                      {/* Work Type */}
                      <div>
                        <Label htmlFor="work_type">Work Type *</Label>
                        <Select
                          value={newJob.work_type || ''}
                          onValueChange={(value) => setNewJob(prev => ({ ...prev, work_type: value }))}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select work type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Work from Office">Work from Office</SelectItem>
                            <SelectItem value="Work from Home">Work from Home</SelectItem>
                            <SelectItem value="Hybrid">Hybrid</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="pt-2 flex gap-2">
                        {editingJob && (
                          <Button type="button" variant="outline" onClick={() => { cancelEditJob(); setIsJobFormOpen(false); }} className="flex-1">
                            Cancel
                          </Button>
                        )}
                        <Button type="submit" className="flex-1 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700">
                          {editingJob ? 'Update Job' : 'Create Job'}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
              </div>
            </div>

            {jobUpdates.length === 0 ? (
              <div className="border-0 rounded-xl p-8 md:p-12 text-center bg-gradient-to-br from-amber-50 to-orange-50 shadow-inner">
                <div className="h-16 w-16 rounded-full bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center mx-auto mb-4">
                  <Briefcase className="h-8 w-8 text-amber-500" />
                </div>
                <p className="text-gray-600 font-medium">No job updates yet</p>
                <p className="text-sm text-gray-400 mt-1">Job postings will appear here once created</p>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {jobUpdates
                  .filter(job => {
                    // Publish filter (only for non-candidates and non-lead_specialists)
                    if (currentUser?.role !== 'lead_specialist' && currentUser?.role !== 'candidate') {
                      if (jobPublishFilter === 'published' && !job.is_published) return false;
                      if (jobPublishFilter === 'unpublished' && job.is_published) return false;
                    }
                    // Search filter
                    if (!jobSearchQuery) return true;
                    const searchLower = jobSearchQuery.toLowerCase();
                    return (
                      (job.company_name?.toLowerCase().includes(searchLower)) ||
                      (job.position_name?.toLowerCase().includes(searchLower)) ||
                      (job.location?.toLowerCase().includes(searchLower))
                    );
                  })
                  .map((job) => (
                  <Card 
                    key={job.id} 
                    className="border border-gray-100 shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden cursor-pointer bg-white"
                    onClick={() => { setSelectedJob(job); fetchJobApplications(job.id); }}
                    data-testid={`job-update-card-${job.id}`}
                  >
                    {/* Gradient Top Bar - Color based on publish status */}
                    <div className={`h-1.5 bg-gradient-to-r ${
                      !job.is_published ? 'from-gray-300 to-gray-400' :
                      job.work_type === 'Work from Home' ? 'from-green-400 to-emerald-500' :
                      job.work_type === 'Hybrid' ? 'from-purple-400 to-violet-500' :
                      job.work_type === 'Work from Office' ? 'from-blue-400 to-indigo-500' :
                      'from-amber-400 to-orange-500'
                    }`} />
                    
                    <CardContent className="p-4">
                      {/* Header with Logo and Company */}
                      <div className="flex items-start gap-3 mb-3">
                        {job.company_logo_url ? (
                          <img 
                            src={`${API.replace('/api', '')}${job.company_logo_url}`}
                            alt={job.company_name}
                            className="h-12 w-12 rounded-xl object-cover border border-gray-200"
                          />
                        ) : (
                          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white font-bold text-lg shadow-md">
                            {job.company_name.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-bold text-base text-slate-800 truncate">{job.company_name}</h3>
                            {/* Publish Status Badge */}
                            {currentUser?.role !== 'lead_specialist' && currentUser?.role !== 'candidate' && (
                              job.is_published ? (
                                <Badge className="bg-green-100 text-green-700 text-[10px] px-1.5">Live</Badge>
                              ) : (
                                <Badge className="bg-gray-200 text-gray-600 text-[10px] px-1.5">Draft</Badge>
                              )
                            )}
                          </div>
                          <p className="text-sm text-amber-600 font-semibold truncate">
                            {job.position_name || 'Multiple Positions'}
                          </p>
                          {(job.published_at || job.created_at) && (
                            <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {job.published_at ? `Published ${formatISTDate(job.published_at)}` : formatISTDate(job.created_at)}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Info Badges */}
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {job.location && (
                          <Badge variant="secondary" className="bg-slate-50 text-slate-600 text-xs px-2 py-0.5 rounded-md">
                            <MapPin className="h-3 w-3 mr-1" />
                            {job.location}
                          </Badge>
                        )}
                        {job.work_type && (
                          <Badge variant="secondary" className={`text-xs px-2 py-0.5 rounded-md ${
                            job.work_type === 'Work from Home' ? 'bg-green-50 text-green-700' :
                            job.work_type === 'Hybrid' ? 'bg-purple-50 text-purple-700' :
                            'bg-blue-50 text-blue-700'
                          }`}>
                            {job.work_type === 'Work from Home' ? <Home className="h-3 w-3 mr-1" /> :
                             job.work_type === 'Hybrid' ? <Laptop className="h-3 w-3 mr-1" /> :
                             <Building className="h-3 w-3 mr-1" />}
                            {job.work_type}
                          </Badge>
                        )}
                        {job.salary_range && (
                          <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 text-xs px-2 py-0.5 rounded-md font-medium">
                            {job.salary_currency || '₹'} {job.salary_range}
                          </Badge>
                        )}
                      </div>

                      {/* Education & Experience */}
                      {(job.education || job.experience) && (
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {job.education && (
                            <span className="flex items-center gap-1 bg-amber-50 text-amber-700 px-2 py-0.5 rounded-md text-xs">
                              <GraduationCap className="h-3 w-3" />
                              {job.education}
                            </span>
                          )}
                          {job.experience && (
                            <span className="flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-0.5 rounded-md text-xs">
                              <Briefcase className="h-3 w-3" />
                              {job.experience}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Admin/User/CRM Action Buttons */}
                      {currentUser?.role !== 'lead_specialist' && currentUser?.role !== 'candidate' && (
                        <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-gray-100" onClick={(e) => e.stopPropagation()}>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="h-8 px-3 text-xs font-medium rounded-lg"
                            onClick={() => { handleEditJob(job); setIsJobFormOpen(true); }}
                          >
                            <Edit className="h-3.5 w-3.5 mr-1" />
                            Edit
                          </Button>
                          {job.is_published ? (
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="h-8 px-3 text-xs text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded-lg font-medium"
                              onClick={() => handlePublishJob(job.id, false)}
                            >
                              <EyeOff className="h-3.5 w-3.5 mr-1" />
                              Unpublish
                            </Button>
                          ) : (
                            <Button 
                              size="sm"
                              className="h-8 px-3 text-xs font-medium bg-green-600 hover:bg-green-700 text-white rounded-lg"
                              onClick={() => handlePublishJob(job.id, true)}
                            >
                              <Send className="h-3.5 w-3.5 mr-1" />
                              Publish
                            </Button>
                          )}
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="h-8 px-3 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg font-medium ml-auto"
                            onClick={() => handleDeleteJob(job.id)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </LazyTabContent>

          {/* Support Tickets Tab - For Admin, User, CRM */}
          {currentUser?.role !== 'candidate' && (
            <LazyTabContent value="support" activeTab={activeTab} className="space-y-4 md:space-y-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div>
                  <h2 className="font-heading text-xl md:text-3xl font-bold tracking-tight mb-1 bg-gradient-to-r from-cyan-600 to-teal-600 bg-clip-text text-transparent" data-testid="support-header">
                    Support Center
                  </h2>
                  <p className="text-sm text-muted-foreground">Help candidates with their inquiries</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Badge className="bg-blue-100 text-blue-700">{supportTickets?.filter(t => t.status === 'Open' && t.ticket_type !== 'call_request').length || 0} Open Chats</Badge>
                  <Badge className="bg-emerald-100 text-emerald-700">{supportTickets?.filter(t => t.ticket_type === 'call_request' && t.call_status !== 'Completed' && t.call_status !== 'Cancelled').length || 0} Pending Calls</Badge>
                  <Badge className="bg-gray-100 text-gray-700">{supportTickets?.filter(t => t.status === 'Resolved' || t.status === 'Closed' || t.call_status === 'Completed').length || 0} Resolved</Badge>
                </div>
              </div>

              {/* Filter Tabs */}
              <div className="flex flex-wrap gap-2 border-b pb-2">
                <button
                  data-testid="support-chat-tab"
                  onClick={() => { setSupportTabFilter('active'); setSelectedSupportTicket(null); }}
                  className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors flex items-center gap-2 ${
                    supportTabFilter === 'active' 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <MessageCircle className="h-4 w-4" />
                  Chat Tickets
                  <span className="bg-white/20 px-1.5 py-0.5 rounded text-xs">
                    {supportTickets?.filter(t => t.ticket_type !== 'call_request' && t.status !== 'Resolved' && t.status !== 'Closed').length || 0}
                  </span>
                </button>
                <button
                  data-testid="support-calls-tab"
                  onClick={() => { setSupportTabFilter('calls'); setSelectedSupportTicket(null); }}
                  className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors flex items-center gap-2 ${
                    supportTabFilter === 'calls' 
                      ? 'bg-emerald-500 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <Phone className="h-4 w-4" />
                  Call Requests
                  <span className="bg-white/20 px-1.5 py-0.5 rounded text-xs">
                    {supportTickets?.filter(t => t.ticket_type === 'call_request' && t.call_status !== 'Completed' && t.call_status !== 'Cancelled').length || 0}
                  </span>
                </button>
                <button
                  data-testid="support-resolved-tab"
                  onClick={() => { setSupportTabFilter('resolved'); setSelectedSupportTicket(null); }}
                  className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors flex items-center gap-2 ${
                    supportTabFilter === 'resolved' 
                      ? 'bg-gray-500 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <CheckCircle2 className="h-4 w-4" />
                  Resolved
                  <span className="bg-white/20 px-1.5 py-0.5 rounded text-xs">
                    {supportTickets?.filter(t => t.status === 'Resolved' || t.status === 'Closed' || t.call_status === 'Completed' || t.call_status === 'Cancelled').length || 0}
                  </span>
                </button>
                <Button data-testid="support-email-tab" variant={supportTabFilter === 'email' ? 'default' : 'outline'} onClick={() => { setSupportTabFilter('email'); setSelectedSupportTicket(null); }}><Mail className="h-4 w-4 mr-2" />Email Support</Button>
              </div>

              {supportTabFilter === 'email' ? <EmailSupport onTicketsChange={cases => setSupportTickets(prev => [...prev.filter(ticket => ticket.ticket_type !== 'email'), ...cases])} /> : <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Tickets List - Hidden on mobile when ticket is selected */}
                <Card className={`lg:col-span-1 border-0 shadow-lg ${selectedSupportTicket ? 'hidden lg:block' : ''}`}>
                  <CardHeader className={`pb-2 text-white rounded-t-xl ${
                    supportTabFilter === 'active' ? 'bg-gradient-to-r from-blue-500 to-blue-600' :
                    supportTabFilter === 'calls' ? 'bg-gradient-to-r from-emerald-500 to-teal-600' :
                    'bg-gradient-to-r from-gray-500 to-gray-600'
                  }`}>
                    <CardTitle className="text-lg flex items-center gap-2">
                      {supportTabFilter === 'active' && <><MessageCircle className="h-5 w-5" /> Chat Tickets</>}
                      {supportTabFilter === 'calls' && <><Phone className="h-5 w-5" /> Call Requests</>}
                      {supportTabFilter === 'resolved' && <><CheckCircle2 className="h-5 w-5" /> Resolved</>}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    <ScrollArea className="h-[450px]">
                      {(() => {
                        // Filter tickets based on selected tab
                        const filteredTickets = supportTickets?.filter(ticket => {
                          if (supportTabFilter === 'active') {
                            return ticket.ticket_type !== 'call_request' && ticket.ticket_type !== 'email' && ticket.status !== 'Resolved' && ticket.status !== 'Closed';
                          } else if (supportTabFilter === 'calls') {
                            return ticket.ticket_type === 'call_request' && ticket.call_status !== 'Completed' && ticket.call_status !== 'Cancelled';
                          } else {
                            // Resolved tab - show resolved/closed tickets and completed/cancelled calls
                            return ticket.status === 'Resolved' || ticket.status === 'Closed' || ticket.call_status === 'Completed' || ticket.call_status === 'Cancelled';
                          }
                        }) || [];

                        if (filteredTickets.length === 0) {
                          return (
                            <div className="text-center py-8">
                              {supportTabFilter === 'active' && <MessageCircle className="h-10 w-10 mx-auto text-muted-foreground/30 mb-2" />}
                              {supportTabFilter === 'calls' && <Phone className="h-10 w-10 mx-auto text-muted-foreground/30 mb-2" />}
                              {supportTabFilter === 'resolved' && <CheckCircle2 className="h-10 w-10 mx-auto text-muted-foreground/30 mb-2" />}
                              <p className="text-sm text-muted-foreground">
                                {supportTabFilter === 'active' && 'No active chat tickets'}
                                {supportTabFilter === 'calls' && 'No pending call requests'}
                                {supportTabFilter === 'resolved' && 'No resolved tickets'}
                              </p>
                            </div>
                          );
                        }

                        return (
                          <div className="space-y-2">
                            {filteredTickets.map((ticket) => (
                            <div
                              key={ticket.id}
                              data-testid={`support-ticket-${ticket.id}`}
                              onClick={() => handleSelectSupportTicket(ticket)}
                              className={`p-3 rounded-xl cursor-pointer transition-all ${
                                selectedSupportTicket?.id === ticket.id 
                                  ? 'bg-gradient-to-r from-cyan-100 to-teal-100 border-2 border-cyan-400 shadow-md' 
                                  : 'bg-gray-50 hover:bg-gray-100 border border-transparent hover:shadow-sm'
                              }`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2">
                                    {ticket.ticket_type === 'call_request' && (
                                      <Phone className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                                    )}
                                    <p className="font-semibold text-sm truncate">{ticket.subject}</p>
                                    {ticket.has_new_reply && (
                                      <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" title="New reply"></span>
                                    )}
                                  </div>
                                  <p className="text-xs text-muted-foreground truncate">{ticket.candidate_name}</p>
                                </div>
                                <div className="flex flex-col gap-1 shrink-0">
                                  {ticket.ticket_type === 'call_request' ? (
                                    <Badge className={`text-[10px] ${getCallStatusColor(ticket.call_status || 'Pending')}`}>
                                      {ticket.call_status || 'Pending'}
                                    </Badge>
                                  ) : (
                                    <Badge className={`text-[10px] ${getTicketStatusColor(ticket.status)}`}>
                                      {ticket.status}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              <p className="text-[10px] text-muted-foreground mt-1">
                                {new Date(ticket.created_at).toLocaleDateString()} • {ticket.ticket_type === 'call_request' ? 'Call Request' : `${ticket.replies?.length || 0} replies`}
                              </p>
                            </div>
                          ))}
                        </div>
                        );
                      })()}
                    </ScrollArea>
                  </CardContent>
                </Card>

                {/* Ticket Detail - Full width on mobile when selected */}
                <Card className={`lg:col-span-2 border-0 shadow-lg ${!selectedSupportTicket ? 'hidden lg:block' : ''}`}>
                  <CardContent className="p-4 md:p-6">
                    {selectedSupportTicket ? (
                      <div className="flex flex-col min-h-[450px] md:min-h-[500px]">
                        {/* Back button on mobile */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedSupportTicket(null)}
                          className="lg:hidden mb-2 w-fit -ml-2"
                        >
                          <ArrowLeft className="h-4 w-4 mr-1" />
                          Back to tickets
                        </Button>
                        
                        {/* Header */}
                        <div className="border-b pb-3 mb-3">
                          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                {selectedSupportTicket.ticket_type === 'call_request' && (
                                  <Phone className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-emerald-500" />
                                )}
                                <h3 className="font-semibold text-base md:text-lg truncate">{selectedSupportTicket.subject}</h3>
                              </div>
                              <p className="text-xs md:text-sm text-muted-foreground truncate">
                                From: <span className="font-medium">{selectedSupportTicket.candidate_name}</span>
                              </p>
                            </div>
                            {selectedSupportTicket.ticket_type === 'call_request' ? (
                              <Select value={selectedSupportTicket.call_status || 'Pending'} onValueChange={(val) => handleUpdateCallStatus(selectedSupportTicket.id, val)}>
                                <SelectTrigger data-testid="support-call-status" className="w-28 md:w-32 h-8 text-xs md:text-sm shrink-0">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Pending">Pending</SelectItem>
                                  <SelectItem value="Called">Called</SelectItem>
                                  <SelectItem value="Completed">Completed</SelectItem>
                                  <SelectItem value="Cancelled">Cancelled</SelectItem>
                                </SelectContent>
                              </Select>
                            ) : (
                              <Select value={selectedSupportTicket.status} onValueChange={(val) => handleUpdateTicketStatus(selectedSupportTicket.id, val)}>
                                <SelectTrigger data-testid="support-ticket-status" className="w-28 md:w-32 h-8 text-xs md:text-sm shrink-0">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Open">Open</SelectItem>
                                  <SelectItem value="In Progress">In Progress</SelectItem>
                                  <SelectItem value="Resolved">Resolved</SelectItem>
                                  <SelectItem value="Closed">Closed</SelectItem>
                                </SelectContent>
                              </Select>
                            )}
                          </div>
                        </div>

                        <SupportFeedbackPanel key={selectedSupportTicket.id} ticket={selectedSupportTicket} />
                        {selectedSupportTicket.ticket_type === 'email' && <Button data-testid="support-open-email-case" variant="outline" onClick={() => { setSupportTabFilter('email'); setSelectedSupportTicket(null); }}>Open Email Support</Button>}
                        {/* Call Request Details - Special View */}
                        {selectedSupportTicket.ticket_type === 'call_request' ? (
                          <div className="flex-1 overflow-y-auto space-y-4">
                            {/* Call Request Info Card */}
                            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-4">
                              <h4 className="font-semibold text-emerald-800 mb-3 flex items-center gap-2">
                                <Phone className="h-4 w-4" />
                                Call Request Details
                              </h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                <div className="flex items-center gap-2">
                                  <Phone className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-emerald-600" />
                                  <div>
                                    <p className="text-xs text-gray-500">Phone Number</p>
                                    <p className="font-medium">{selectedSupportTicket.phone || 'Not provided'}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-emerald-600" />
                                  <div>
                                    <p className="text-xs text-gray-500">Preferred Date</p>
                                    <p className="font-medium">{selectedSupportTicket.preferred_date || 'Not specified'}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-emerald-600" />
                                  <div>
                                    <p className="text-xs text-gray-500">Preferred Time</p>
                                    <p className="font-medium">{selectedSupportTicket.preferred_time || 'Not specified'}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Mail className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-emerald-600" />
                                  <div>
                                    <p className="text-xs text-gray-500">Email</p>
                                    <p className="font-medium truncate">{selectedSupportTicket.candidate_email}</p>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Click to Call Button */}
                              <div className="mt-4 flex gap-2">
                                <Button
                                  onClick={() => window.location.href = `tel:${selectedSupportTicket.phone}`}
                                  className="bg-emerald-500 hover:bg-emerald-600 text-white gap-2"
                                  disabled={!selectedSupportTicket.phone}
                                >
                                  <Phone className="h-4 w-4" />
                                  Call Now
                                </Button>
                                <Button
                                  variant="outline"
                                  onClick={() => window.location.href = `mailto:${selectedSupportTicket.candidate_email}`}
                                  className="gap-2"
                                >
                                  <Mail className="h-4 w-4" />
                                  Send Email
                                </Button>
                              </div>
                            </div>

                            {/* Reason for Call */}
                            <div className="bg-gray-50 border rounded-xl p-4">
                              <h4 className="font-semibold text-gray-800 mb-2">Reason for Call</h4>
                              <p className="text-sm text-gray-700 whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: formatMessageContent(selectedSupportTicket.message) }} />
                            </div>

                            {/* Admin Notes */}
                            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                              <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                                <Edit className="h-4 w-4" />
                                Admin Notes / Feedback
                              </h4>
                              <Textarea
                                placeholder="Add notes about this call request (e.g., call summary, follow-up actions)..."
                                defaultValue={selectedSupportTicket.admin_notes || ''}
                                className="min-h-[100px] bg-white"
                                onBlur={(e) => {
                                  if (e.target.value !== (selectedSupportTicket.admin_notes || '')) {
                                    handleUpdateAdminNotes(selectedSupportTicket.id, e.target.value);
                                  }
                                }}
                              />
                              <p className="text-xs text-blue-600 mt-2">Notes are saved automatically when you click outside the field</p>
                            </div>

                            {/* Timeline */}
                            <div className="text-xs text-gray-500 flex items-center gap-2">
                              <Clock className="h-3 w-3" />
                              Submitted: {new Date(selectedSupportTicket.created_at).toLocaleString()}
                            </div>
                          </div>
                        ) : (
                        /* Regular Ticket - Messages WhatsApp Style */
                        <>
                        <ScrollArea className="flex-1 pr-2" style={{ background: 'linear-gradient(180deg, #e5ded8 0%, #d9d2c9 100%)' }}>
                          <div className="space-y-2 p-3 min-h-full">
                            {/* Original message from candidate */}
                            <div className="flex justify-start">
                              <div className="max-w-[80%] bg-white rounded-lg rounded-tl-none p-2 px-3 shadow-sm relative">
                                <p className="text-xs font-semibold text-blue-600 mb-1">{selectedSupportTicket.candidate_name}</p>
                                <p className="text-sm text-gray-800 pr-10 whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: formatMessageContent(selectedSupportTicket.message) }} />
                                <span className="absolute bottom-1 right-2 text-[10px] text-gray-500">
                                  {new Date(selectedSupportTicket.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </div>
                            </div>

                            {/* Replies */}
                            {selectedSupportTicket.replies?.map((reply, index) => {
                              const isStaff = reply.sender_role !== 'candidate';
                              const isSending = reply.is_sending || reply.status === 'sending';
                              const isFailed = reply.status === 'failed';
                              
                              return (
                                <div 
                                  key={reply.id}
                                  className={`flex ${isStaff ? 'justify-end' : 'justify-start'} animate-in slide-in-from-bottom-2 duration-200`}
                                  style={{ animationDelay: `${index * 50}ms` }}
                                >
                                  <div 
                                    className={`max-w-[80%] rounded-lg p-2 px-3 shadow-sm relative ${
                                      isStaff 
                                        ? 'bg-[#dcf8c6] rounded-tr-none' 
                                        : 'bg-white rounded-tl-none'
                                    } ${isSending ? 'opacity-70' : ''} ${isFailed ? 'bg-red-100' : ''}`}
                                  >
                                    {!isStaff && (
                                      <p className="text-xs font-semibold text-blue-600 mb-1">{reply.sender_name}</p>
                                    )}
                                    {isStaff && (
                                      <p className="text-xs font-semibold text-emerald-600 mb-1">You (Staff)</p>
                                    )}
                                    <p className="text-sm text-gray-800 pr-12 whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: formatMessageContent(reply.message) }} />
                                    <div className="absolute bottom-1 right-2 flex items-center gap-1">
                                      <span className="text-[10px] text-gray-500">
                                        {isSending ? '' : new Date(reply.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                      </span>
                                      {isStaff && (
                                        isSending ? (
                                          <svg className="w-4 h-4 text-gray-400 animate-pulse" viewBox="0 0 16 15" fill="currentColor">
                                            <path d="M9.75 7.5a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0z"/>
                                          </svg>
                                        ) : isFailed ? (
                                          <svg className="w-4 h-4 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                                          </svg>
                                        ) : (
                                          <svg className="w-4 h-4 text-blue-500" viewBox="0 0 16 15" fill="currentColor">
                                            <path d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.88a.32.32 0 0 1-.484.032l-.358-.325a.32.32 0 0 0-.484.032l-.378.48a.418.418 0 0 0 .036.54l1.32 1.266a.32.32 0 0 0 .484-.034l6.272-8.048a.366.366 0 0 0-.064-.512zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.88a.32.32 0 0 1-.484.032L1.89 7.77a.366.366 0 0 0-.516.005l-.423.433a.364.364 0 0 0 .006.514l3.255 3.185a.32.32 0 0 0 .484-.033l6.272-8.048a.365.365 0 0 0-.063-.51z"/>
                                          </svg>
                                        )
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </ScrollArea>

                        {/* Reply Input - WhatsApp Style */}
                        {selectedSupportTicket.status !== 'Closed' && selectedSupportTicket.ticket_type !== 'email' && (
                          <div className="mt-2 pt-2 border-t bg-gray-100 -mx-6 -mb-6 px-4 py-3 flex gap-2 items-end">
                            <textarea
                              placeholder="Type a message... (Enter for new line, Shift+Enter to send)"
                              value={supportReplyMessage}
                              onChange={(e) => setSupportReplyMessage(e.target.value)}
                              onKeyDown={(e) => {
                                if ((e.shiftKey || e.ctrlKey) && e.key === 'Enter') {
                                  e.preventDefault();
                                  if (!isSendingSupportReply && supportReplyMessage.trim()) {
                                    handleSupportReply();
                                  }
                                }
                              }}
                              disabled={isSendingSupportReply}
                              className="flex-1 rounded-2xl bg-white border-0 shadow-sm focus:outline-none focus:ring-1 focus:ring-emerald-500 px-4 py-2 resize-none min-h-[40px] max-h-[100px]"
                              rows={1}
                              onInput={(e) => {
                                e.target.style.height = 'auto';
                                e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px';
                              }}
                            />
                            <Button 
                              onClick={handleSupportReply} 
                              className="rounded-full w-10 h-10 p-0 bg-emerald-500 hover:bg-emerald-600 shadow-md transition-transform active:scale-95"
                              disabled={isSendingSupportReply || !supportReplyMessage.trim()}
                            >
                              {isSendingSupportReply ? (
                                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <Send className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        )}
                        </>
                        )}
                      </div>
                    ) : (
                      <div className="h-[500px] flex items-center justify-center">
                        <div className="text-center">
                          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-cyan-100 to-teal-100 flex items-center justify-center mx-auto mb-4">
                            <MessageCircle className="h-10 w-10 text-cyan-500" />
                          </div>
                          <p className="text-muted-foreground font-medium">Select a ticket to view details</p>
                          <p className="text-sm text-muted-foreground mt-1">Click on any ticket from the list</p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>}
            </LazyTabContent>
          )}

          {/* Applications Tab Content - CRM Users Only */}
          {currentUser?.role === 'crm_user' && (
            <LazyTabContent value="applications" activeTab={activeTab} className="space-y-6">
              {/* Professional Header Section */}
              <div className="bg-gradient-to-r from-slate-50 via-amber-50/30 to-orange-50/40 rounded-2xl p-6 border border-slate-100/80 shadow-sm">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-200/50">
                      <FileText className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h2 className="font-heading text-xl md:text-2xl font-bold tracking-tight text-slate-800">
                        Job Applications
                      </h2>
                      <p className="text-sm text-slate-500 mt-0.5">Review and manage candidate applications</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Applications List */}
              <div className="grid gap-4">
                {allApplications.length === 0 ? (
                  <div className="rounded-2xl p-12 text-center bg-gradient-to-br from-slate-50 to-amber-50/30 border border-slate-100">
                    <div className="h-20 w-20 rounded-2xl bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center mx-auto mb-5 shadow-inner">
                      <FileText className="h-10 w-10 text-amber-500" />
                    </div>
                    <p className="text-lg font-medium text-slate-700">No job applications yet</p>
                    <p className="text-sm text-slate-400 mt-2 max-w-sm mx-auto">Applications will appear here when candidates apply for jobs</p>
                  </div>
                ) : (
                  allApplications.map((app) => (
                    <Card key={app.id} className="border-0 rounded-2xl shadow-sm hover:shadow-md transition-all bg-white overflow-hidden">
                      <div className="h-1 bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500" />
                      <CardContent className="p-5">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg text-slate-800">{app.full_name}</h3>
                            <p className="text-sm text-slate-500">{app.email} • {app.phone}</p>
                            <div className="flex flex-wrap gap-2 mt-3">
                              <Badge variant="outline" className="text-xs rounded-lg border-amber-200 text-amber-700 bg-amber-50">
                                <Briefcase className="h-3 w-3 mr-1.5" />
                                {app.job_title}
                              </Badge>
                              <Badge variant="outline" className="text-xs rounded-lg border-blue-200 text-blue-700 bg-blue-50">
                                <Building2 className="h-3 w-3 mr-1.5" />
                                {app.company_name}
                              </Badge>
                              {app.experience_years && (
                                <Badge className="text-xs rounded-lg bg-slate-100 text-slate-600">{app.experience_years} exp</Badge>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Select 
                              value={statusOption(app.status)} 
                              onValueChange={(value) => handleApplicationStatusChange(app.id, value)}
                            >
                              <SelectTrigger className="w-36 rounded-xl border-slate-200">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {allApplicationStatuses.map(status => (
                                  <SelectItem key={status} value={status}>{status}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="mt-4 pt-3 border-t border-slate-100 flex items-center text-xs text-slate-500">
                          <Calendar className="h-3.5 w-3.5 mr-1.5" />
                          Applied on {formatISTDate(app.created_at)}
                          {app.resume_url && (
                            <a 
                              href={getImageUrl(app.resume_url, API)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ml-4 text-amber-600 hover:text-amber-700 font-medium hover:underline flex items-center gap-1"
                              download
                            >
                              <FileText className="h-3.5 w-3.5" />
                              View Resume
                            </a>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </LazyTabContent>
          )}

          {/* All Applications Tab Content - Admin/CRM Users */}
          {(currentUser?.is_admin || currentUser?.role === 'user') && (
            <LazyTabContent value="all-applications" activeTab={activeTab} className="space-y-6">
              {/* Professional Header Section */}
              <div className="bg-gradient-to-r from-slate-50 via-amber-50/30 to-orange-50/40 rounded-2xl p-6 border border-slate-100/80 shadow-sm">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-200/50">
                      <FileText className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h2 className="font-heading text-xl md:text-2xl font-bold tracking-tight text-slate-800">
                        All Job Applications
                      </h2>
                      <p className="text-sm text-slate-500 mt-0.5">View and manage all candidate applications across all jobs</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    {/* Position Filter */}
                    <Select value={positionFilter || 'all'} onValueChange={(v) => setPositionFilter(v === 'all' ? '' : v)}>
                      <SelectTrigger className="w-36 md:w-44 h-10 rounded-xl border-slate-200 bg-white" data-testid="position-filter">
                        <Briefcase className="h-4 w-4 mr-2 text-slate-400" />
                        <SelectValue placeholder="All Positions" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Positions</SelectItem>
                        {uniquePositions.map(pos => (
                          <SelectItem key={pos} value={pos}>{pos}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    {/* Company Filter */}
                    <Select value={companyFilter || 'all'} onValueChange={(v) => setCompanyFilter(v === 'all' ? '' : v)}>
                      <SelectTrigger className="w-36 md:w-44 h-10 rounded-xl border-slate-200 bg-white" data-testid="company-filter">
                        <Building2 className="h-4 w-4 mr-2 text-slate-400" />
                        <SelectValue placeholder="All Companies" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Companies</SelectItem>
                        {uniqueCompanies.map(comp => (
                          <SelectItem key={comp} value={comp}>{comp}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    <div className="flex items-center gap-2 px-3 py-2 bg-amber-100 rounded-xl">
                      <FileText className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-amber-600" />
                      <span className="text-sm font-semibold text-amber-700">{filteredApplications.length} / {allApplications.length}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Application Search */}
              <div className="relative" data-testid="application-search">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 text-slate-400" />
                <Input
                  value={applicationSearchQuery}
                  onChange={(e) => setApplicationSearchQuery(e.target.value)}
                  placeholder="Search by name, email, phone, job title, company..."
                  className="pl-11 h-12 rounded-xl border-slate-200 bg-white shadow-sm focus:border-amber-400 focus:ring-amber-400/20 transition-all"
                  data-testid="application-search-input"
                />
                {applicationSearchQuery && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute right-3 top-1/2 -translate-y-1/2 h-7 w-7 p-0 rounded-lg hover:bg-slate-100"
                    onClick={() => setApplicationSearchQuery('')}
                    data-testid="clear-search-btn"
                  >
                    <X className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-slate-400" />
                  </Button>
                )}
              </div>

              {/* Application Filters */}
              <div className="md:overflow-x-auto md:-mx-0 md:px-0">
                <div className="flex flex-wrap gap-2 pb-2 md:flex-nowrap md:min-w-max md:flex-wrap" data-testid="application-filters">
                {/* Count only non-archived applications for accurate tab counts */}
                {(() => {
                  const nonArchivedApps = allApplications.filter(app => !app.is_archived);
                  return (
                    <>
                      <Button
                        variant={applicationFilter === 'all' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setApplicationFilter('all')}
                        className={`text-sm h-9 rounded-xl ${applicationFilter === 'all' ? 'bg-slate-800' : 'border-slate-200'}`}
                        data-testid="filter-all"
                      >
                        All ({nonArchivedApps.length})
                      </Button>
                      {allApplicationStatuses.map(status => {
                        const statusColors = {
                          'Applied': 'bg-blue-600 hover:bg-blue-700',
                          'Called': 'bg-amber-600 hover:bg-amber-700',
                          'Shortlisted': 'bg-violet-600 hover:bg-violet-700',
                          'Emails Sent': 'bg-emerald-600 hover:bg-emerald-700',
                          'Rejected': 'bg-red-600 hover:bg-red-700',
                          'Did Not Pick Up': 'bg-orange-600 hover:bg-orange-700',
                          'Follow-Up': 'bg-cyan-600 hover:bg-cyan-700',
                          'Not Reachable': 'bg-slate-600 hover:bg-slate-700'
                        };
                        const colorClass = statusColors[status] || 'bg-indigo-600 hover:bg-indigo-700';
                        const count = nonArchivedApps.filter(a => sameStatus(a.status, status)).length;
                        
                        return (
                          <Button
                            key={status}
                            variant={applicationFilter === status ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setApplicationFilter(status)}
                            className={`text-sm h-9 rounded-xl whitespace-nowrap ${applicationFilter === status ? colorClass : 'border-slate-200'}`}
                            data-testid={`filter-${status.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            {status} ({count})
                          </Button>
                        );
                      })}
                    </>
                  );
                })()}
                </div>
              </div>

              {/* Group by Candidate Toggle */}
              <div className="flex items-center gap-3 py-2 flex-wrap">
                <button
                  onClick={() => setGroupByCandidate(!groupByCandidate)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    groupByCandidate 
                      ? 'bg-violet-600 text-white shadow-md' 
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid="group-by-candidate-toggle"
                >
                  <UsersIcon className="h-4 w-4" />
                  Group by Candidate
                </button>
                {groupByCandidate && groupedApplications && (
                  <span className="text-xs text-slate-500 bg-slate-100 px-2.5 py-1 rounded-lg">
                    {groupedApplications.length} unique candidates
                  </span>
                )}
                
                {/* Bulk Email Toggle */}
                <button
                  onClick={() => {
                    setIsBulkEmailMode(!isBulkEmailMode);
                    if (isBulkEmailMode) setSelectedForBulkEmail([]);
                  }}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    isBulkEmailMode 
                      ? 'bg-blue-600 text-white shadow-md' 
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid="bulk-email-toggle"
                >
                  <Mail className="h-4 w-4" />
                  Bulk Email
                </button>
                
                {/* Bulk Email Actions */}
                {isBulkEmailMode && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={selectAllForBulkEmail}
                      className="px-3 py-1.5 text-xs bg-slate-200 hover:bg-slate-300 rounded-lg transition-colors"
                    >
                      {selectedForBulkEmail.length === filteredApplications.filter(a => !a.is_archived).length 
                        ? 'Deselect All' 
                        : 'Select All'}
                    </button>
                    <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-lg">
                      {selectedForBulkEmail.length} selected
                    </span>
                    {selectedForBulkEmail.length > 0 && (
                      <Button
                        size="sm"
                        onClick={() => setIsBulkEmailDialogOpen(true)}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 rounded-xl"
                        data-testid="send-bulk-email-btn"
                      >
                        <Send className="h-3 w-3 mr-1.5" />
                        Send Email ({selectedForBulkEmail.length})
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {/* Applications List */}
              <div className="grid gap-4">
                {filteredApplications.length === 0 ? (
                  <div className="rounded-2xl p-12 text-center bg-gradient-to-br from-slate-50 to-amber-50/30 border border-slate-100">
                    <div className="h-20 w-20 rounded-2xl bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center mx-auto mb-5 shadow-inner">
                      <FileText className="h-10 w-10 text-amber-500" />
                    </div>
                    <p className="text-lg font-medium text-slate-700">
                      {applicationSearchQuery 
                        ? `No applications found for "${applicationSearchQuery}"`
                        : applicationFilter === 'all' 
                          ? 'No job applications yet' 
                          : `No ${applicationFilter} applications`}
                    </p>
                    <p className="text-sm text-slate-400 mt-2 max-w-sm mx-auto">
                      {applicationSearchQuery 
                        ? 'Try different search terms or clear the search'
                        : applicationFilter === 'all' 
                          ? 'Applications will appear here when candidates apply for jobs'
                          : 'Try selecting a different filter'}
                    </p>
                  </div>
                ) : groupByCandidate && groupedApplications ? (
                  /* Grouped View - Consolidate by Candidate Email */
                  groupedApplications.map((candidate) => (
                    <Card key={candidate.email} className="border-0 shadow-lg overflow-hidden" data-testid={`grouped-candidate-${candidate.email}`}>
                      <CardContent className="p-0">
                        {/* Candidate Header - Always Visible */}
                        <div 
                          className="p-4 bg-gradient-to-r from-violet-50 to-purple-50 border-b cursor-pointer hover:bg-violet-100 transition-colors"
                          onClick={() => toggleCandidateExpansion(candidate.email)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white text-lg font-bold shadow-lg">
                                {(candidate.full_name || 'U').charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <h3 className="font-bold text-lg text-gray-900">{candidate.full_name}</h3>
                                <div className="flex items-center gap-3 text-sm text-gray-600">
                                  <span className="flex items-center gap-1">
                                    <Mail className="h-3 w-3" /> {candidate.email}
                                  </span>
                                  {candidate.phone && (
                                    <span className="flex items-center gap-1">
                                      <Phone className="h-3 w-3" /> {candidate.phone}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <Badge className="bg-violet-600 text-white text-sm px-3 py-1">
                                {candidate.applications.length} Application{candidate.applications.length !== 1 ? 's' : ''}
                              </Badge>
                              {expandedCandidates[candidate.email] ? (
                                <ChevronUp className="h-5 w-5 text-violet-600" />
                              ) : (
                                <ChevronDown className="h-5 w-5 text-violet-600" />
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Expanded Applications List */}
                        {expandedCandidates[candidate.email] && (
                          <div className="p-4 space-y-3 bg-gray-50">
                            {candidate.applications.map((app, idx) => (
                              <div 
                                key={app.id}
                                className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                      <Briefcase className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-violet-600" />
                                      <span className="font-semibold text-gray-900">{app.job_title}</span>
                                      <Badge className={`text-xs ${
                                        app.status === 'Applied' ? 'bg-blue-100 text-blue-700' :
                                        app.status === 'Called' ? 'bg-amber-100 text-amber-700' :
                                        app.status === 'Shortlisted' ? 'bg-violet-100 text-violet-700' :
                                        app.status === 'Emails Sent' ? 'bg-green-100 text-green-700' :
                                        app.status === 'Rejected' ? 'bg-red-100 text-red-700' :
                                        'bg-gray-100 text-gray-700'
                                      }`}>
                                        {app.status}
                                      </Badge>
                                    </div>
                                    <div className="flex items-center gap-4 text-xs text-gray-500">
                                      <span className="flex items-center gap-1">
                                        <Building className="h-3 w-3" /> {app.company_name}
                                      </span>
                                      <span className="flex items-center gap-1">
                                        <Clock className="h-3 w-3" /> {app.experience_years || 'N/A'} exp
                                      </span>
                                      {app.applied_at && (
                                        <span className="flex items-center gap-1">
                                          <Calendar className="h-3 w-3" /> {formatISTDate(app.applied_at)}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Select
                                      value={statusOption(app.status)}
                                      onValueChange={(newStatus) => handleApplicationStatusChange(app.id, newStatus)}
                                    >
                                      <SelectTrigger className="w-36 h-8 text-xs">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {allApplicationStatuses.map(status => (
                                          <SelectItem key={status} value={status}>{status}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    {currentUser?.is_admin && (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleOpenEditApplication(app)}
                                        className="h-8 w-8 p-0 text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                                        title="Edit Application Details"
                                        data-testid={`edit-application-btn-${app.id}`}
                                      >
                                        <Edit3 className="h-4 w-4" />
                                      </Button>
                                    )}
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleArchiveApplication(app.id)}
                                      className="h-8 w-8 p-0 text-amber-500 hover:text-amber-700 hover:bg-amber-50"
                                      title="Archive"
                                    >
                                      <Archive className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => {
                                        setSelectedAppForScreeningHistory(app);
                                        setScreeningHistoryOpen(true);
                                      }}
                                      className="h-8 w-8 p-0 text-indigo-500 hover:text-indigo-700 hover:bg-indigo-50"
                                      title="View Screening"
                                      aria-label="View Screening"
                                      data-testid={`view-screening-row-${app.id}`}
                                    >
                                      <ScreeningHistoryIcon className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => {
                                        setSelectedAppForScreening(app);
                                        setIsTelephonicScreeningOpen(true);
                                      }}
                                      className="h-8 w-8 p-0 text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50"
                                      title="Start Telephonic Screening"
                                      data-testid={`start-screening-btn-${app.id}`}
                                    >
                                      <Phone className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => setExpandedCandidates(prev => ({
                                        ...prev,
                                        [`details_${app.id}`]: !prev[`details_${app.id}`]
                                      }))}
                                      className="h-8 px-2 text-violet-600 border-violet-300 hover:bg-violet-50"
                                      title="View Full Details"
                                      data-testid={`view-details-btn-${app.id}`}
                                    >
                                      <Eye className="h-4 w-4 mr-1" />
                                      <span className="text-xs">Details</span>
                                    </Button>
                                  </div>
                                </div>
                                
                                {/* Expanded Full Details Section */}
                                {expandedCandidates[`details_${app.id}`] && (
                                  <div className="mt-4 pt-4 border-t-2 border-violet-200 bg-gradient-to-br from-violet-50/50 to-purple-50/50 rounded-lg p-4">
                                    <h5 className="font-bold text-violet-800 mb-3 flex items-center gap-2 text-sm">
                                      <FileText className="h-4 w-4" />
                                      Complete Candidate Profile
                                    </h5>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                      {/* Personal Information */}
                                      <div className="bg-white rounded-lg p-3 shadow-sm border border-violet-100">
                                        <h6 className="font-semibold text-slate-700 mb-2 text-xs flex items-center gap-1.5 border-b pb-1.5">
                                          <User className="h-3.5 w-3.5 text-violet-500" />
                                          Personal Info
                                        </h6>
                                        <div className="space-y-1.5 text-xs">
                                          <div className="flex justify-between">
                                            <span className="text-gray-500">Full Name:</span>
                                            <span className="font-medium text-gray-800">{app.full_name || candidate.name || '-'}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-500">Email:</span>
                                            <span className="font-medium text-gray-800 truncate max-w-[120px]">{app.email || candidate.email || '-'}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-500">Phone:</span>
                                            <span className="font-medium text-gray-800">{app.phone || candidate.phone || '-'}</span>
                                          </div>
                                          {app.actual_contact_number && (
                                            <div className="flex justify-between">
                                              <span className="text-gray-500">ACN:</span>
                                              <span className="font-medium text-gray-800">{app.actual_contact_number}</span>
                                            </div>
                                          )}
                                          {app.current_city_state && (
                                            <div className="flex justify-between">
                                              <span className="text-gray-500">City:</span>
                                              <span className="font-medium text-gray-800">{app.current_city_state}</span>
                                            </div>
                                          )}
                                          {app.aadhar_address && (
                                            <div className="flex justify-between">
                                              <span className="text-gray-500">Aadhar Address:</span>
                                              <span className="font-medium text-gray-800 text-right max-w-[120px] truncate">{app.aadhar_address}</span>
                                            </div>
                                          )}
                                          {app.aadhar_phone_linked && (
                                            <div className="flex justify-between">
                                              <span className="text-gray-500">Aadhar Linked:</span>
                                              <span className={`font-medium ${app.aadhar_phone_linked === 'Yes' ? 'text-green-600' : 'text-amber-600'}`}>
                                                {app.aadhar_phone_linked}
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      
                                      {/* Education */}
                                      <div className="bg-white rounded-lg p-3 shadow-sm border border-violet-100">
                                        <h6 className="font-semibold text-slate-700 mb-2 text-xs flex items-center gap-1.5 border-b pb-1.5">
                                          <GraduationCap className="h-3.5 w-3.5 text-violet-500" />
                                          Education
                                        </h6>
                                        <div className="space-y-1.5 text-xs">
                                          <div className="flex justify-between">
                                            <span className="text-gray-500">Qualification:</span>
                                            <span className="font-medium text-gray-800">
                                              {app.education === 'Others' ? app.education_other : app.education || '-'}
                                            </span>
                                          </div>
                                          {app.university_name && (
                                            <div className="flex justify-between">
                                              <span className="text-gray-500">University:</span>
                                              <span className="font-medium text-gray-800 text-right max-w-[120px] truncate">{app.university_name}</span>
                                            </div>
                                          )}
                                          {app.college_name && (
                                            <div className="flex justify-between">
                                              <span className="text-gray-500">College:</span>
                                              <span className="font-medium text-gray-800 text-right max-w-[120px] truncate">{app.college_name}</span>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      
                                      {/* Experience */}
                                      <div className="bg-white rounded-lg p-3 shadow-sm border border-violet-100">
                                        <h6 className="font-semibold text-slate-700 mb-2 text-xs flex items-center gap-1.5 border-b pb-1.5">
                                          <Briefcase className="h-3.5 w-3.5 text-violet-500" />
                                          Experience
                                        </h6>
                                        <div className="space-y-1.5 text-xs">
                                          <div className="flex justify-between">
                                            <span className="text-gray-500">Experience:</span>
                                            <span className="font-medium text-gray-800">{app.total_experience || app.experience_years || 'Not provided'}</span>
                                          </div>
                                          {app.previous_company_name && (
                                            <div className="flex justify-between">
                                              <span className="text-gray-500">Previous Co:</span>
                                              <span className="font-medium text-gray-800 text-right max-w-[120px] truncate">{app.previous_company_name}</span>
                                            </div>
                                          )}
                                          {app.previous_lpa && (
                                            <div className="flex justify-between">
                                              <span className="text-gray-500">Previous LPA:</span>
                                              <span className="font-medium text-gray-800">{app.previous_lpa}</span>
                                            </div>
                                          )}
                                          {app.expected_salary && (
                                            <div className="flex justify-between">
                                              <span className="text-gray-500">Expected:</span>
                                              <span className="font-medium text-gray-800">{app.expected_salary}</span>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      
                                      {/* Source & Referral */}
                                      <div className="bg-white rounded-lg p-3 shadow-sm border border-violet-100">
                                        <h6 className="font-semibold text-slate-700 mb-2 text-xs flex items-center gap-1.5 border-b pb-1.5">
                                          <Share2 className="h-3.5 w-3.5 text-violet-500" />
                                          Source
                                        </h6>
                                        <div className="space-y-1.5 text-xs">
                                          <div className="flex justify-between">
                                            <span className="text-gray-500">Heard About:</span>
                                            <span className="font-medium text-gray-800">{app.hear_about_us || 'Not provided'}</span>
                                          </div>
                                          {app.friend_name && (
                                            <div className="flex justify-between">
                                              <span className="text-gray-500">Friend:</span>
                                              <span className="font-medium text-gray-800">{app.friend_name}</span>
                                            </div>
                                          )}
                                          {app.referred_by_name && (
                                            <div className="flex justify-between">
                                              <span className="text-gray-500">Lead Specialist:</span>
                                              <span className="font-medium text-purple-600">{app.referred_by_name}</span>
                                            </div>
                                          )}
                                          {app.source && (
                                            <div className="flex justify-between">
                                              <span className="text-gray-500">Source:</span>
                                              <Badge className="bg-purple-100 text-purple-700 text-[10px]">{app.source}</Badge>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      
                                      {/* Links & Documents */}
                                      <div className="bg-white rounded-lg p-3 shadow-sm border border-violet-100 lg:col-span-2">
                                        <h6 className="font-semibold text-slate-700 mb-2 text-xs flex items-center gap-1.5 border-b pb-1.5">
                                          <ExternalLink className="h-3.5 w-3.5 text-violet-500" />
                                          Documents & Links
                                        </h6>
                                        <div className="flex flex-wrap gap-2 text-xs">
                                          {app.resume_url && (
                                            <a 
                                              href={getImageUrl(app.resume_url, API)}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="flex items-center gap-1 text-violet-600 hover:text-violet-700 hover:underline bg-violet-50 px-2 py-1 rounded"
                                            >
                                              <FileText className="h-3 w-3" />
                                              Resume
                                            </a>
                                          )}
                                          {app.linkedin_url && (
                                            <a 
                                              href={app.linkedin_url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="flex items-center gap-1 text-blue-600 hover:text-blue-700 hover:underline bg-blue-50 px-2 py-1 rounded"
                                            >
                                              <Linkedin className="h-3 w-3" />
                                              LinkedIn
                                            </a>
                                          )}
                                          {!app.resume_url && !app.linkedin_url && (
                                            <span className="text-gray-400">No documents uploaded</span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  filteredApplications.map((app) => (
                    <Card key={app.id} className={`border-0 shadow-lg hover:shadow-xl transition-shadow relative overflow-hidden ${isBulkEmailMode && selectedForBulkEmail.some(a => a.id === app.id) ? 'ring-2 ring-blue-500' : ''}`} data-testid={`application-card-${app.id}`}>
                      {/* Bulk Email Checkbox */}
                      {isBulkEmailMode && !app.is_archived && (
                        <div 
                          className="absolute top-3 left-3 z-20"
                          onClick={(e) => { e.stopPropagation(); toggleSelectForBulkEmail(app); }}
                        >
                          <div className={`h-6 w-6 rounded-md border-2 flex items-center justify-center cursor-pointer transition-all ${
                            selectedForBulkEmail.some(a => a.id === app.id)
                              ? 'bg-blue-600 border-blue-600 text-white'
                              : 'bg-white border-gray-300 hover:border-blue-400'
                          }`}>
                            {selectedForBulkEmail.some(a => a.id === app.id) && (
                              <CheckCircle2 className="h-4 w-4" />
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Delete Button - Top Right Corner */}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteApplication(app)}
                        className="absolute top-3 right-3 h-8 w-8 p-0 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-full z-10"
                        title="Delete Application"
                        data-testid="delete-application-btn"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>

                      <CardContent className="p-0">
                        <div className="flex flex-col lg:flex-row">
                          {/* Left Side - CV/Profile Info */}
                          <div className="lg:w-2/5 p-5 bg-gradient-to-br from-slate-50 to-gray-100 lg:border-r-2">
                            {/* Profile Header */}
                            <div className="flex items-start gap-3 mb-4">
                              <div className="h-14 w-14 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white text-xl font-bold flex-shrink-0 shadow-lg">
                                {(app.full_name || 'U').charAt(0).toUpperCase()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="font-bold text-lg text-gray-900 truncate">{app.full_name}</h3>
                                <Badge className={`mt-1 ${
                                  app.status === 'Applied' ? 'bg-blue-100 text-blue-700' :
                                  app.status === 'Called' ? 'bg-amber-100 text-amber-700' :
                                  app.status === 'Shortlisted' ? 'bg-violet-100 text-violet-700' :
                                  app.status === 'Emails Sent' ? 'bg-green-100 text-green-700' :
                                  app.status === 'Rejected' ? 'bg-red-100 text-red-700' :
                                  app.status === 'Did Not Pick Up' ? 'bg-orange-100 text-orange-700' :
                                  app.status === 'Follow-Up' ? 'bg-cyan-100 text-cyan-700' :
                                  app.status === 'Not Reachable' ? 'bg-gray-100 text-gray-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {app.status}
                                </Badge>
                                {/* Cooling Period Badge */}
                                {app.cooling_period_end && app.status === 'Rejected' && (
                                  (() => {
                                    const coolingEnd = new Date(app.cooling_period_end);
                                    const now = new Date();
                                    const isActive = coolingEnd > now;
                                    const daysLeft = Math.ceil((coolingEnd - now) / (1000 * 60 * 60 * 24));
                                    return isActive ? (
                                      <Badge className="ml-1 bg-amber-100 text-amber-700 text-[10px] px-1.5" title={`Cannot reapply until ${coolingEnd.toLocaleDateString('en-IN')}`}>
                                        <Clock className="h-3 w-3 mr-1" />
                                        {daysLeft}d cooling
                                      </Badge>
                                    ) : null;
                                  })()
                                )}
                                {/* Lead Specialist Referral Badge */}
                                {app.source === 'lead_specialist_referral' && (
                                  <Badge className="ml-1 bg-gradient-to-r from-purple-500 to-violet-500 text-white text-[10px] px-1.5">
                                    LS Referral
                                  </Badge>
                                )}
                              </div>
                            </div>

                            {/* Referred By - For LS referrals */}
                            {app.referred_by_name && (
                              <div className="mb-3 flex items-center gap-2 text-xs bg-purple-50 text-purple-700 px-2 py-1 rounded-lg">
                                <UserPlus className="h-3 w-3" />
                                <span>Referred by: <strong>{app.referred_by_name}</strong></span>
                              </div>
                            )}

                            {/* Contact & Position Details */}
                            <div className="space-y-2.5 text-sm">
                              <div className="flex items-center gap-2 text-gray-600">
                                <Mail className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-blue-400 flex-shrink-0" />
                                <span className="truncate">{app.email}</span>
                              </div>
                              <div className="flex items-center gap-2 text-gray-600">
                                <Phone className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-green-400 flex-shrink-0" />
                                <span>{app.phone}</span>
                              </div>
                              <div className="flex items-center gap-2 text-gray-600">
                                <Briefcase className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-violet-400 flex-shrink-0" />
                                <span className="truncate font-medium">{app.job_title}</span>
                              </div>
                              <div className="flex items-center gap-2 text-gray-600">
                                <Building2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-indigo-400 flex-shrink-0" />
                                <span className="truncate">{app.company_name}</span>
                              </div>
                              {app.experience_years && (
                                <div className="flex items-center gap-2 text-gray-600">
                                  <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-amber-400 flex-shrink-0" />
                                  <span>{app.experience_years} experience</span>
                                </div>
                              )}
                            </div>

                            {/* Resume Link */}
                            {app.resume_url && (
                              <a 
                                href={getImageUrl(app.resume_url, API)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-4 inline-flex items-center gap-2 text-sm text-violet-600 hover:text-violet-700 hover:underline bg-violet-50 px-3 py-1.5 rounded-lg"
                                download
                              >
                                <FileText className="h-4 w-4" />
                                View Resume/CV
                              </a>
                            )}

                            {/* Applied Date */}
                            <p className="mt-4 text-xs text-gray-400 flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              Applied: {formatISTDate(app.created_at)}
                            </p>
                            
                            {/* View Full Details Button */}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setExpandedCandidates(prev => ({
                                ...prev,
                                [app.id]: !prev[app.id]
                              }))}
                              className="mt-3 w-full gap-2 border-violet-300 text-violet-600 hover:bg-violet-50"
                              data-testid="view-full-details-btn"
                            >
                              <Eye className="h-4 w-4" />
                              {expandedCandidates[app.id] ? 'Hide Details' : 'View Full Details'}
                              {expandedCandidates[app.id] ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                            </Button>
                          </div>

                          {/* Right Side - Actions */}
                          <div className="lg:w-3/5 p-5 flex flex-col">
                            {/* Click-to-Call and Click-to-Email - Always Visible */}
                            <div className="flex flex-wrap gap-3 mb-5">
                              <Button
                                onClick={() => handleClickToCall(app.phone)}
                                className="bg-emerald-500 hover:bg-emerald-600 text-white gap-2 shadow-md"
                                data-testid="click-to-call-btn"
                              >
                                <Phone className="h-4 w-4" />
                                Call Now
                              </Button>
                              <Button
                                onClick={() => {
                                  window.location.href = `mailto:${app.email}?subject=Regarding your application for ${encodeURIComponent(app.job_title)}`;
                                }}
                                className="bg-blue-500 hover:bg-blue-600 text-white gap-2 shadow-md"
                                data-testid="click-to-email-btn"
                              >
                                <Mail className="h-4 w-4" />
                                Email Now
                              </Button>
                            </div>

                            {/* Quick Actions - Simplified (Removed duplicate Actions dropdown) */}
                            <div className="flex flex-wrap items-center gap-2 mb-4">
                              {/* Revert Action Button - Shows when status is not Applied */}
                              {app.status !== 'Applied' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleApplicationStatusChange(app.id, 'Applied')}
                                  className="gap-2 border-amber-300 text-amber-600 hover:bg-amber-50"
                                  data-testid="revert-action-btn"
                                >
                                  <RotateCcw className="h-4 w-4" />
                                  Revert to Applied
                                </Button>
                              )}
                              
                              {/* Send Email Button */}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleQuickEmail(app)}
                                className="gap-2 border-green-300 text-green-600 hover:bg-green-50"
                              >
                                <Mail className="h-4 w-4" />
                                Send Email
                              </Button>
                              
                              {/* Add Feedback Button */}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleOpenFeedback(app)}
                                className="gap-2 border-purple-300 text-purple-600 hover:bg-purple-50"
                              >
                                <MessageSquare className="h-4 w-4" />
                                Feedback
                              </Button>
                              
                              {/* Archive Button */}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleArchiveApplication(app.id)}
                                className="gap-2 border-amber-300 text-amber-600 hover:bg-amber-50"
                              >
                                <Archive className="h-4 w-4" />
                                Archive
                              </Button>
                              
                              {/* Edit Application Button (Admin Only) */}
                              {currentUser?.is_admin && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleOpenEditApplication(app)}
                                  className="gap-2 border-blue-300 text-blue-600 hover:bg-blue-50"
                                  data-testid={`edit-app-btn-${app.id}`}
                                >
                                  <Edit3 className="h-4 w-4" />
                                  Edit Details
                                </Button>
                              )}
                              
                              {/* Read-only saved screening details for this application */}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedAppForScreeningHistory(app);
                                  setScreeningHistoryOpen(true);
                                }}
                                className="gap-2 border-indigo-300 text-indigo-600 hover:bg-indigo-50"
                                data-testid={`view-screening-${app.id}`}
                              >
                                <ScreeningHistoryIcon className="h-4 w-4" />
                                View Screening
                              </Button>
                              
                              {/* Start Telephonic Screening Button */}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedAppForScreening(app);
                                  setIsTelephonicScreeningOpen(true);
                                }}
                                className="gap-2 border-emerald-300 text-emerald-600 hover:bg-emerald-50"
                                data-testid="start-screening-btn"
                              >
                                <Phone className="h-4 w-4" />
                                Start Screening
                              </Button>
                            </div>

                            {/* Status Dropdown */}
                            <div data-testid={`application-status-controls-${app.id}`} className="flex flex-wrap items-center gap-3 p-3 bg-gray-50 rounded-lg">
                              <span className="text-sm text-gray-600 font-medium">Change Status:</span>
                              <Select 
                                value={statusOption(app.status)} 
                                onValueChange={(value) => {
                                  if (value === 'Rejected') {
                                    // Open rejection dialog with cooling period options
                                    setRejectingApplication(app);
                                    setRejectionReason('');
                                    setCoolingPeriodDays(0);
                                    setIsRejectDialogOpen(true);
                                  } else {
                                    handleApplicationStatusChange(app.id, value);
                                  }
                                }}
                              >
                                <SelectTrigger className="w-48 bg-white" data-testid="status-select">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {allApplicationStatuses.map(status => {
                                    const statusColorMap = {
                                      'Applied': 'bg-blue-500',
                                      'Called': 'bg-amber-500',
                                      'Shortlisted': 'bg-violet-500',
                                      'Emails Sent': 'bg-green-500',
                                      'Did Not Pick Up': 'bg-orange-500',
                                      'Follow-Up': 'bg-cyan-500',
                                      'Not Reachable': 'bg-gray-500',
                                      'Rejected': 'bg-red-500'
                                    };
                                    const colorClass = statusColorMap[status] || 'bg-indigo-500';
                                    const isCurrentStatus = sameStatus(app.status, status);
                                    return (
                                      <SelectItem 
                                        key={status} 
                                        value={status}
                                        disabled={isCurrentStatus}
                                        className={isCurrentStatus ? 'opacity-50 bg-gray-100' : ''}
                                      >
                                        <span className="flex items-center gap-2">
                                          <span className={`w-2 h-2 rounded-full ${colorClass}`}></span>
                                          {status}
                                          {isCurrentStatus && <span className="text-xs text-gray-400 ml-1">(current)</span>}
                                        </span>
                                      </SelectItem>
                                    );
                                  })}
                                </SelectContent>
                              </Select>
                              
                              {/* Blacklist Button */}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleQuickBlacklist(app)}
                                data-testid={`application-blacklist-${app.id}`}
                                className="gap-2 border-red-300 text-red-600 hover:bg-red-50"
                                title="Add candidate to blacklist"
                              >
                                <Ban className="h-4 w-4" />
                                Blacklist
                              </Button>
                            </div>

                            {/* Follow-up Email Button */}
                            {(app.status === 'Did Not Pick Up' || app.status === 'Not Reachable' || app.status === 'Follow-Up') && (
                              <div className="mt-4">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleFollowUpEmail(app)}
                                  className="gap-2 text-indigo-600 border-indigo-300 hover:bg-indigo-50"
                                >
                                  <Mail className="h-4 w-4" />
                                  Send Follow-up Email
                                </Button>
                              </div>
                            )}

                            {/* Feedback Display with History */}
                            {app.feedback && (
                              <div className="mt-4 p-3 bg-purple-50 rounded-lg">
                                <div className="flex items-center justify-between mb-1">
                                  <p className="text-xs font-medium text-purple-700 flex items-center gap-1">
                                    <MessageSquare className="h-3 w-3" /> Feedback
                                  </p>
                                  {app.rating > 0 && (
                                    <div className="flex items-center gap-0.5">
                                      {[1, 2, 3, 4, 5].map((star) => (
                                        <Star key={star} className={`h-3.5 w-3.5 ${star <= app.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} />
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <p className="text-sm text-gray-700">{app.feedback}</p>
                                {(app.feedback_by || app.feedback_at) && (
                                  <div className="mt-2 pt-2 border-t border-purple-200 flex items-center gap-2 text-xs text-purple-600">
                                    <Clock className="h-3 w-3" />
                                    <span>
                                      {app.feedback_by && `By ${app.feedback_by}`}
                                      {app.feedback_by && app.feedback_at && ' • '}
                                      {app.feedback_at && new Date(app.feedback_at).toLocaleDateString('en-US', { 
                                        month: 'short', 
                                        day: 'numeric', 
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Expanded Full Details Section */}
                        {expandedCandidates[app.id] && (
                          <div className="border-t-2 border-violet-200 bg-gradient-to-br from-violet-50 to-purple-50 p-5">
                            <h4 className="font-bold text-violet-800 mb-4 flex items-center gap-2">
                              <FileText className="h-5 w-5" />
                              Complete Candidate Profile
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                              {/* Personal Information */}
                              <div className="bg-white rounded-xl p-4 shadow-sm border border-violet-100">
                                <h5 className="font-semibold text-slate-700 mb-3 text-sm flex items-center gap-2 border-b pb-2">
                                  <User className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-violet-500" />
                                  Personal Information
                                </h5>
                                <div className="space-y-2 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">Full Name:</span>
                                    <span className="font-medium text-gray-800">{app.full_name || '-'}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">Email:</span>
                                    <span className="font-medium text-gray-800 truncate max-w-[150px]">{app.email || '-'}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">Phone:</span>
                                    <span className="font-medium text-gray-800">{app.phone || '-'}</span>
                                  </div>
                                  {app.actual_contact_number && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-500">ACN:</span>
                                      <span className="font-medium text-gray-800">{app.actual_contact_number}</span>
                                    </div>
                                  )}
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">Current City:</span>
                                    <span className="font-medium text-gray-800">{app.current_city_state || 'Not provided'}</span>
                                  </div>
                                  {app.aadhar_address && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-500">Aadhar Address:</span>
                                      <span className="font-medium text-gray-800 text-right max-w-[150px]">{app.aadhar_address}</span>
                                    </div>
                                  )}
                                  {app.aadhar_phone_linked && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-500">Aadhar Phone Linked:</span>
                                      <span className={`font-medium ${app.aadhar_phone_linked === 'Yes' ? 'text-green-600' : 'text-amber-600'}`}>
                                        {app.aadhar_phone_linked}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              {/* Education */}
                              <div className="bg-white rounded-xl p-4 shadow-sm border border-violet-100">
                                <h5 className="font-semibold text-slate-700 mb-3 text-sm flex items-center gap-2 border-b pb-2">
                                  <GraduationCap className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-violet-500" />
                                  Education
                                </h5>
                                <div className="space-y-2 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">Qualification:</span>
                                    <span className="font-medium text-gray-800">
                                      {app.education === 'Others' ? app.education_other : app.education || '-'}
                                    </span>
                                  </div>
                                  {app.university_name && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-500">University:</span>
                                      <span className="font-medium text-gray-800 text-right max-w-[150px]">{app.university_name}</span>
                                    </div>
                                  )}
                                  {app.college_name && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-500">College:</span>
                                      <span className="font-medium text-gray-800 text-right max-w-[150px]">{app.college_name}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              {/* Experience */}
                              <div className="bg-white rounded-xl p-4 shadow-sm border border-violet-100">
                                <h5 className="font-semibold text-slate-700 mb-3 text-sm flex items-center gap-2 border-b pb-2">
                                  <Briefcase className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-violet-500" />
                                  Experience
                                </h5>
                                <div className="space-y-2 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">Total Experience:</span>
                                    <span className="font-medium text-gray-800">{app.total_experience || app.experience_years || 'Not provided'}</span>
                                  </div>
                                  {app.previous_company_name && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-500">Previous Company:</span>
                                      <span className="font-medium text-gray-800 text-right max-w-[150px]">{app.previous_company_name}</span>
                                    </div>
                                  )}
                                  {app.previous_lpa && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-500">Previous LPA:</span>
                                      <span className="font-medium text-gray-800">{app.previous_lpa}</span>
                                    </div>
                                  )}
                                  {app.current_company && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-500">Current Company:</span>
                                      <span className="font-medium text-gray-800 text-right max-w-[150px]">{app.current_company}</span>
                                    </div>
                                  )}
                                  {app.current_position && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-500">Current Position:</span>
                                      <span className="font-medium text-gray-800 text-right max-w-[150px]">{app.current_position}</span>
                                    </div>
                                  )}
                                  {app.expected_salary && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-500">Expected Salary:</span>
                                      <span className="font-medium text-gray-800">{app.expected_salary}</span>
                                    </div>
                                  )}
                                  {app.notice_period && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-500">Notice Period:</span>
                                      <span className="font-medium text-gray-800">{app.notice_period}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              {/* Application Info */}
                              <div className="bg-white rounded-xl p-4 shadow-sm border border-violet-100">
                                <h5 className="font-semibold text-slate-700 mb-3 text-sm flex items-center gap-2 border-b pb-2">
                                  <FileText className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-violet-500" />
                                  Application Details
                                </h5>
                                <div className="space-y-2 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">Job Position:</span>
                                    <span className="font-medium text-gray-800 text-right max-w-[150px]">{app.job_title || '-'}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">Company:</span>
                                    <span className="font-medium text-gray-800">{app.company_name || '-'}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">Status:</span>
                                    <Badge className={`text-xs ${
                                      app.status === 'Applied' ? 'bg-blue-100 text-blue-700' :
                                      app.status === 'Shortlisted' ? 'bg-violet-100 text-violet-700' :
                                      app.status === 'Rejected' ? 'bg-red-100 text-red-700' :
                                      'bg-gray-100 text-gray-700'
                                    }`}>
                                      {app.status}
                                    </Badge>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">Applied On:</span>
                                    <span className="font-medium text-gray-800">{formatISTDate(app.created_at)}</span>
                                  </div>
                                  {app.agrees_to_charges && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-500">Processing Consent:</span>
                                      <span className="font-medium text-green-600">Agreed</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              {/* Source & Referral */}
                              <div className="bg-white rounded-xl p-4 shadow-sm border border-violet-100">
                                <h5 className="font-semibold text-slate-700 mb-3 text-sm flex items-center gap-2 border-b pb-2">
                                  <Share2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-violet-500" />
                                  Source & Referral
                                </h5>
                                <div className="space-y-2 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">Heard About Us:</span>
                                    <span className="font-medium text-gray-800">{app.hear_about_us || 'Not provided'}</span>
                                  </div>
                                  {app.friend_name && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-500">Referred By (Friend):</span>
                                      <span className="font-medium text-gray-800">{app.friend_name}</span>
                                    </div>
                                  )}
                                  {app.referred_by_name && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-500">Lead Specialist:</span>
                                      <span className="font-medium text-purple-600">{app.referred_by_name}</span>
                                    </div>
                                  )}
                                  {app.followed_instagram && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-500">Followed Instagram:</span>
                                      <span className={`font-medium ${app.followed_instagram === 'Yes' ? 'text-pink-600' : 'text-gray-600'}`}>
                                        {app.followed_instagram}
                                      </span>
                                    </div>
                                  )}
                                  {app.source && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-500">Source:</span>
                                      <Badge className="bg-purple-100 text-purple-700 text-xs">{app.source}</Badge>
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              {/* Links & Documents */}
                              <div className="bg-white rounded-xl p-4 shadow-sm border border-violet-100">
                                <h5 className="font-semibold text-slate-700 mb-3 text-sm flex items-center gap-2 border-b pb-2">
                                  <ExternalLink className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-violet-500" />
                                  Links & Documents
                                </h5>
                                <div className="space-y-2 text-sm">
                                  {app.resume_url && (
                                    <a 
                                      href={getImageUrl(app.resume_url, API)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-2 text-violet-600 hover:text-violet-700 hover:underline"
                                    >
                                      <FileText className="h-4 w-4" />
                                      View Resume/CV
                                    </a>
                                  )}
                                  {app.linkedin_url && (
                                    <a 
                                      href={app.linkedin_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-2 text-blue-600 hover:text-blue-700 hover:underline"
                                    >
                                      <Linkedin className="h-4 w-4" />
                                      LinkedIn Profile
                                    </a>
                                  )}
                                  {app.portfolio_url && (
                                    <a 
                                      href={app.portfolio_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700 hover:underline"
                                    >
                                      <Globe className="h-4 w-4" />
                                      Portfolio
                                    </a>
                                  )}
                                  {!app.resume_url && !app.linkedin_url && !app.portfolio_url && (
                                    <span className="text-gray-400">No documents uploaded</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            {/* Cover Letter / Skills Section */}
                            {(app.cover_letter || app.skills) && (
                              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                                {app.skills && (
                                  <div className="bg-white rounded-xl p-4 shadow-sm border border-violet-100">
                                    <h5 className="font-semibold text-slate-700 mb-2 text-sm">Skills</h5>
                                    <p className="text-sm text-gray-700">{app.skills}</p>
                                  </div>
                                )}
                                {app.cover_letter && (
                                  <div className="bg-white rounded-xl p-4 shadow-sm border border-violet-100">
                                    <h5 className="font-semibold text-slate-700 mb-2 text-sm">Cover Letter</h5>
                                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{app.cover_letter}</p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>

              {/* Archived Applications Section */}
              {archivedApplications.length > 0 && (
                <Card className="border-0 shadow-lg mt-6">
                  <CardContent className="p-4">
                    <div 
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => setShowArchivedApplications(!showArchivedApplications)}
                    >
                      <h3 className="font-semibold text-amber-700 flex items-center gap-2">
                        <Archive className="h-5 w-5" />
                        Archived Applications ({archivedApplications.length})
                      </h3>
                      {showArchivedApplications ? (
                        <ChevronUp className="h-5 w-5 text-amber-600" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-amber-600" />
                      )}
                    </div>
                    
                    {showArchivedApplications && (
                      <div className="mt-4 space-y-3">
                        {archivedApplications.map((app) => (
                          <div 
                            key={app.id}
                            className="p-4 bg-amber-50 rounded-xl border border-amber-200 flex items-center justify-between"
                          >
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 rounded-full bg-amber-200 flex items-center justify-center text-amber-700 font-bold">
                                {app.full_name?.charAt(0)?.toUpperCase()}
                              </div>
                              <div>
                                <p className="font-medium text-slate-800">{app.full_name}</p>
                                <p className="text-xs text-gray-500">{app.job_title} • {app.company_name}</p>
                                <p className="text-xs text-gray-400">{app.email}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className={getStatusBadgeColor(app.status)}>{app.status}</Badge>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleRestoreApplication(app.id)}
                                className="border-green-300 text-green-600 hover:bg-green-50"
                              >
                                <RotateCcw className="h-4 w-4 mr-1" />
                                Restore
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </LazyTabContent>
          )}

          {/* Companies Tab Content */}
          {currentUser?.role !== 'candidate' && currentUser?.role !== 'lead_specialist' && (
            <LazyTabContent value="companies" activeTab={activeTab} className="space-y-4 md:space-y-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div>
                  <h2 className="font-heading text-xl md:text-3xl font-bold tracking-tight mb-1 bg-gradient-to-r from-indigo-600 to-blue-600 bg-clip-text text-transparent">
                    Companies
                  </h2>
                  <p className="text-sm text-muted-foreground">Manage companies providing job vacancies</p>
                </div>
                <Button 
                  onClick={() => { setEditingCompany(null); setNewCompany({ name: '', description: '', website: '', logo_url: '' }); setIsCompanyDialogOpen(true); }}
                  className="rounded-xl bg-gradient-to-r from-indigo-500 to-blue-600 hover:from-indigo-600 hover:to-blue-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Company
                </Button>
              </div>

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {companies.length === 0 ? (
                  <div className="col-span-full border border-border rounded-xl p-12 text-center bg-white">
                    <Building2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                    <p className="text-muted-foreground">No companies added yet</p>
                    <p className="text-sm text-muted-foreground mt-1">Click "Add Company" to get started</p>
                  </div>
                ) : (
                  companies.map((company) => (
                    <Card key={company.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex flex-col items-center text-center">
                          {company.logo_url ? (
                            <img 
                              src={`${API.replace('/api', '')}${company.logo_url}`} 
                              alt={company.name}
                              className="h-20 w-20 rounded-xl object-cover border mb-3"
                            />
                          ) : (
                            <div className="h-20 w-20 rounded-xl bg-gradient-to-br from-indigo-400 to-blue-500 flex items-center justify-center mb-3">
                              <Building2 className="h-10 w-10 text-white" />
                            </div>
                          )}
                          <h3 className="font-semibold text-lg">{company.name}</h3>
                          {company.description && (
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{company.description}</p>
                          )}
                          <div className="flex gap-2 mt-3">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => { setEditingCompany(company); setNewCompany(company); setIsCompanyDialogOpen(true); }}
                            >
                              Edit
                            </Button>
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={() => handleDeleteCompany(company.id)}
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>

              {/* Company Dialog */}
              <Dialog open={isCompanyDialogOpen} onOpenChange={setIsCompanyDialogOpen}>
                <DialogContent className="sm:max-w-[500px]">
                  <DialogHeader>
                    <DialogTitle className="font-heading">{editingCompany ? 'Edit Company' : 'Add New Company'}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="flex justify-center">
                      {newCompany.logo_url ? (
                        <img 
                          src={`${API.replace('/api', '')}${newCompany.logo_url}`} 
                          alt="Company logo"
                          className="h-24 w-24 rounded-xl object-cover border"
                        />
                      ) : (
                        <div className="h-24 w-24 rounded-xl bg-gradient-to-br from-indigo-100 to-blue-100 flex items-center justify-center border-2 border-dashed">
                          <Building2 className="h-10 w-10 text-indigo-400" />
                        </div>
                      )}
                    </div>
                    <div className="flex justify-center">
                      <label className="cursor-pointer">
                        <input type="file" accept="image/*" onChange={handleCompanyLogoUpload} className="hidden" />
                        <span className={`inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground ${uploadingCompanyLogo ? 'opacity-50 cursor-not-allowed' : ''}`}>
                          {uploadingCompanyLogo ? 'Uploading...' : 'Upload Logo'}
                        </span>
                      </label>
                    </div>
                    <div className="space-y-2">
                      <Label>Company Name *</Label>
                      <Input
                        value={newCompany.name}
                        onChange={(e) => setNewCompany({...newCompany, name: e.target.value})}
                        placeholder="Enter company name"
                        className="rounded-lg"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea
                        value={newCompany.description || ''}
                        onChange={(e) => setNewCompany({...newCompany, description: e.target.value})}
                        placeholder="Brief description"
                        className="rounded-lg"
                        rows={3}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Website</Label>
                      <Input
                        value={newCompany.website || ''}
                        onChange={(e) => setNewCompany({...newCompany, website: e.target.value})}
                        placeholder="https://example.com"
                        className="rounded-lg"
                      />
                    </div>
                    <Button onClick={handleSaveCompany} className="w-full bg-gradient-to-r from-indigo-500 to-blue-600 rounded-lg">
                      {editingCompany ? 'Update Company' : 'Add Company'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </LazyTabContent>
          )}

          {/* Referral Management Tab - Consolidated Settings */}
          {currentUser?.is_admin && (
            <LazyTabContent value="referral-management" activeTab={activeTab} className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent flex items-center gap-2">
                    <Gift className="h-7 w-7 text-rose-500" />
                    Referral Management
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1">Configure rewards, policies, and manage all referral program settings</p>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid gap-4 md:grid-cols-4">
                <Card className="bg-gradient-to-br from-rose-500 to-pink-600 text-white border-0">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-rose-100 text-sm">Total Referrals</p>
                        <p className="text-3xl font-bold">{allReferrals.length}</p>
                      </div>
                      <UsersIcon className="h-10 w-10 text-rose-200" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white border-0">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-emerald-100 text-sm">Successful</p>
                        <p className="text-3xl font-bold">{allReferrals.filter(r => r.status === 'Registration Done' || r.status === 'Hired').length}</p>
                      </div>
                      <CheckCircle2 className="h-10 w-10 text-emerald-200" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-amber-500 to-orange-600 text-white border-0">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-amber-100 text-sm">Pending</p>
                        <p className="text-3xl font-bold">{allReferrals.filter(r => r.status === 'Pending' || r.status === 'Contacted' || r.status === 'Applied').length}</p>
                      </div>
                      <Clock className="h-10 w-10 text-amber-200" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white border-0">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-blue-100 text-sm">Active Popups</p>
                        <p className="text-3xl font-bold">{popupAnnouncements.filter(p => p.is_active).length}</p>
                      </div>
                      <Bell className="h-10 w-10 text-blue-200" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Quick Actions */}
              <Card className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                    <Activity className="h-5 w-5 text-rose-500" />
                    Quick Actions
                  </h3>
                  <div className="grid gap-3 md:grid-cols-4">
                    <Button 
                      onClick={() => setActiveTab('all-referrals')}
                      className="h-auto py-4 flex flex-col items-center gap-2 bg-gradient-to-r from-rose-500 to-pink-600"
                    >
                      <UsersIcon className="h-6 w-6" />
                      <span>View All Referrals</span>
                    </Button>
                    <Button 
                      onClick={() => setIsPopupAnnouncementDialogOpen(true)}
                      variant="outline"
                      className="h-auto py-4 flex flex-col items-center gap-2 border-2 border-orange-300 text-orange-600 hover:bg-orange-50"
                    >
                      <Bell className="h-6 w-6" />
                      <span>Create Announcement</span>
                    </Button>
                    <Button 
                      onClick={() => setActiveTab('popup-announcements')}
                      variant="outline"
                      className="h-auto py-4 flex flex-col items-center gap-2 border-2 border-blue-300 text-blue-600 hover:bg-blue-50"
                    >
                      <ImageIcon className="h-6 w-6" />
                      <span>Manage Popups</span>
                    </Button>
                    <Button 
                      onClick={() => navigate('/admin')}
                      variant="outline"
                      className="h-auto py-4 flex flex-col items-center gap-2 border-2 border-purple-300 text-purple-600 hover:bg-purple-50"
                    >
                      <Settings className="h-6 w-6" />
                      <span>Referral Settings</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Recent Referrals Preview */}
              <Card className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-lg flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-emerald-500" />
                      Recent Referrals
                    </h3>
                    <Button variant="ghost" size="sm" onClick={() => setActiveTab('all-referrals')}>
                      View All <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {allReferrals.slice(0, 5).map((referral) => (
                      <div key={referral.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-rose-400 to-pink-500 flex items-center justify-center text-white font-bold">
                            {referral.referred_name?.charAt(0) || referral.name?.charAt(0) || '?'}
                          </div>
                          <div>
                            <p className="font-medium text-sm">{referral.referred_name || referral.name}</p>
                            <p className="text-xs text-gray-500">{referral.job_title || 'Position not specified'}</p>
                          </div>
                        </div>
                        <Badge className={
                          referral.status === 'Hired' || referral.status === 'Registration Done' ? 'bg-green-100 text-green-700' :
                          referral.status === 'Pending' ? 'bg-yellow-100 text-yellow-700' :
                          referral.status === 'Telephonic Cleared' ? 'bg-teal-100 text-teal-700' :
                          referral.status === 'Applied' ? 'bg-blue-100 text-blue-700' :
                          referral.status === 'Rejected' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-700'
                        }>
                          {referral.status}
                        </Badge>
                      </div>
                    ))}
                    {allReferrals.length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <Gift className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                        <p>No referrals yet</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Active Announcements Preview */}
              <Card className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-lg flex items-center gap-2">
                      <Bell className="h-5 w-5 text-orange-500" />
                      Active Announcements
                    </h3>
                    <Button variant="ghost" size="sm" onClick={() => setActiveTab('popup-announcements')}>
                      Manage <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {popupAnnouncements.filter(p => p.is_active).map((popup) => (
                      <div key={popup.id} className="flex items-center justify-between p-3 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border border-orange-100">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center text-white">
                            <Bell className="h-5 w-5" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{popup.title}</p>
                            <p className="text-xs text-gray-500">{popup.priority} priority</p>
                          </div>
                        </div>
                        <Badge className="bg-green-100 text-green-700">Active</Badge>
                      </div>
                    ))}
                    {popupAnnouncements.filter(p => p.is_active).length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <Bell className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                        <p>No active announcements</p>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="mt-3"
                          onClick={() => setIsPopupAnnouncementDialogOpen(true)}
                        >
                          Create One
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </LazyTabContent>
          )}

          {/* All Referrals Tab Content (From Lead Specialists + Candidates) */}
          {currentUser?.role !== 'candidate' && currentUser?.role !== 'lead_specialist' && (
            <LazyTabContent value="all-referrals" activeTab={activeTab} className="space-y-4 md:space-y-6">
              <div>
                <h2 className="font-heading text-xl md:text-3xl font-bold tracking-tight mb-1 bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent">
                  Referred Candidates
                </h2>
                <p className="text-sm text-muted-foreground">All candidates referred by Lead Specialists and Candidates</p>
              </div>

              {/* Stats - 2x2 grid on mobile, 5 columns on desktop */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 md:gap-4">
                <Card className="rounded-xl">
                  <CardHeader className="p-3 pb-1">
                    <CardTitle className="text-xs font-medium text-muted-foreground">Total Referrals</CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 pt-0">
                    <div className="text-2xl md:text-3xl font-heading font-bold">{allReferrals.length}</div>
                  </CardContent>
                </Card>
                <Card className="rounded-xl">
                  <CardHeader className="p-3 pb-1">
                    <CardTitle className="text-xs font-medium text-blue-600">Lead Specialists</CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 pt-0">
                    <div className="text-2xl md:text-3xl font-heading font-bold text-blue-600">
                      {allReferrals.filter(r => r.source === 'Lead Specialist').length}
                    </div>
                  </CardContent>
                </Card>
                <Card className="rounded-xl">
                  <CardHeader className="p-3 pb-1">
                    <CardTitle className="text-xs font-medium text-green-600">Candidates</CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 pt-0">
                    <div className="text-2xl md:text-3xl font-heading font-bold text-green-600">
                      {allReferrals.filter(r => r.source === 'Candidate').length}
                    </div>
                  </CardContent>
                </Card>
                <Card className="rounded-xl">
                  <CardHeader className="p-3 pb-1">
                    <CardTitle className="text-xs font-medium text-yellow-600">Pending</CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 pt-0">
                    <div className="text-2xl md:text-3xl font-heading font-bold text-yellow-600">
                      {allReferrals.filter(r => r.status === 'Pending').length}
                    </div>
                  </CardContent>
                </Card>
                <Card className="rounded-xl border-green-200 col-span-2 sm:col-span-1">
                  <CardHeader className="p-3 pb-1">
                    <CardTitle className="text-xs font-medium text-emerald-600">Hired</CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 pt-0">
                    <div className="text-2xl md:text-3xl font-heading font-bold text-emerald-600">
                      {allReferrals.filter(r => r.status === 'Hired').length}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* How to Refer Guide - Collapsible */}
              <Card className="rounded-xl bg-gradient-to-r from-rose-50 to-pink-50 border-rose-200">
                <Collapsible>
                  <CollapsibleTrigger className="w-full">
                    <CardHeader className="pb-2 flex flex-row items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-rose-500 to-pink-500 flex items-center justify-center">
                          <HelpCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" />
                        </div>
                        <CardTitle className="text-base font-semibold text-rose-700">How to Refer?</CardTitle>
                      </div>
                      <ChevronDown className="h-5 w-5 text-rose-500 transition-transform" />
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="pt-0 pb-4">
                      <div className="space-y-3">
                        <div className="flex items-start gap-3">
                          <div className="h-6 w-6 rounded-full bg-rose-500 text-white flex items-center justify-center text-xs font-bold shrink-0">1</div>
                          <div>
                            <p className="font-medium text-sm text-gray-800">Share the Application</p>
                            <p className="text-xs text-muted-foreground">Refer this application to your friends and family</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="h-6 w-6 rounded-full bg-rose-500 text-white flex items-center justify-center text-xs font-bold shrink-0">2</div>
                          <div>
                            <p className="font-medium text-sm text-gray-800">They Download & Apply</p>
                            <p className="text-xs text-muted-foreground">Your referral downloads the app and applies for a job</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="h-6 w-6 rounded-full bg-rose-500 text-white flex items-center justify-center text-xs font-bold shrink-0">3</div>
                          <div>
                            <p className="font-medium text-sm text-gray-800">Clear Telephonic Round</p>
                            <p className="text-xs text-muted-foreground">They pass the initial telephonic screening</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="h-6 w-6 rounded-full bg-emerald-500 text-white flex items-center justify-center text-xs font-bold shrink-0">4</div>
                          <div>
                            <p className="font-medium text-sm text-gray-800">Registration Complete</p>
                            <p className="text-xs text-muted-foreground">Once their registration is done, you earn rewards!</p>
                          </div>
                        </div>
                        
                        {/* Referral Form Link - Admin configurable */}
                        {referralFormLink && (
                          <div className="mt-4 p-3 bg-white rounded-lg border border-rose-200">
                            <p className="text-sm font-medium text-rose-700 mb-2">Fill the Referral Form:</p>
                            <a 
                              href={referralFormLink} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm underline"
                            >
                              <Link2 className="h-4 w-4" />
                              Click here to submit referral details
                            </a>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Collapsible>
              </Card>

              {/* Admin: Configure Referral Form Link */}
              {currentUser?.is_admin && (
                <Card className="rounded-xl border-dashed border-2 border-gray-300">
                  <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <Settings className="h-5 w-5 text-gray-500" />
                        <span className="font-medium text-sm">Referral Form Link (Admin)</span>
                      </div>
                      {editingReferralFormLink ? (
                        <div className="flex gap-2 flex-1 sm:max-w-md">
                          <Input
                            type="url"
                            placeholder="https://forms.google.com/..."
                            value={referralFormLink}
                            onChange={(e) => setReferralFormLink(e.target.value)}
                            className="flex-1 text-sm"
                          />
                          <Button
                            size="sm"
                            onClick={async () => {
                              try {
                                const token = getAuthToken();
                                await axios.post(`${API}/settings/referral-form-link`, 
                                  { link: referralFormLink },
                                  { headers: { Authorization: `Bearer ${token}` } }
                                );
                                toast.success('Referral form link saved!');
                                setEditingReferralFormLink(false);
                              } catch (err) {
                                toast.error('Failed to save link');
                              }
                            }}
                          >
                            Save
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingReferralFormLink(false)}>
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          {referralFormLink ? (
                            <span className="text-sm text-blue-600 truncate max-w-[200px]">{referralFormLink}</span>
                          ) : (
                            <span className="text-sm text-muted-foreground">No link configured</span>
                          )}
                          <Button size="sm" variant="outline" onClick={() => setEditingReferralFormLink(true)}>
                            <Edit className="h-3 w-3 mr-1" />
                            Edit
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {allReferrals.length === 0 ? (
                <div className="border border-border rounded-xl p-12 text-center bg-white">
                  <UsersIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                  <p className="text-muted-foreground">No referrals yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {allReferrals.map((referral) => (
                    <Card key={referral.id} className="overflow-hidden">
                      <CardContent className="p-4">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <h3 className="font-semibold">{referral.candidate_name}</h3>
                              <Badge className={
                                referral.source === 'Lead Specialist' 
                                  ? 'bg-blue-100 text-blue-700' 
                                  : 'bg-green-100 text-green-700'
                              }>
                                {referral.source === 'Lead Specialist' ? '👔 Lead Specialist' : '👤 Candidate'}
                              </Badge>
                              <Badge className={`${
                                referral.status === 'Pending' ? 'bg-yellow-500' :
                                referral.status === 'Contacted' ? 'bg-blue-500' :
                                referral.status === 'Applied' ? 'bg-purple-500' :
                                referral.status === 'Hired' ? 'bg-green-500' :
                                referral.status === 'Rejected' ? 'bg-red-500' :
                                'bg-gray-500'
                              }`}>{referral.status}</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mb-2">
                              {referral.position && <span className="font-medium">{referral.position}</span>}
                              {referral.position && referral.company && ' at '}
                              {referral.company}
                            </p>
                            <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                              {referral.candidate_phone && (
                                <span className="flex items-center gap-1">
                                  <Phone className="h-3 w-3" />
                                  {referral.candidate_phone}
                                </span>
                              )}
                              {referral.candidate_email && (
                                <span className="flex items-center gap-1">
                                  <Mail className="h-3 w-3" />
                                  {referral.candidate_email}
                                </span>
                              )}
                              {referral.relationship && (
                                <span>Relationship: {referral.relationship}</span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-2">
                              Referred by: <span className="font-medium">{referral.referrer_name}</span> • {referral.created_at && formatISTDate(referral.created_at)}
                            </p>
                            {referral.notes && (
                              <p className="text-xs text-gray-500 mt-1 italic">"{referral.notes}"</p>
                            )}
                          </div>
                          <div className="flex flex-col gap-2">
                            <Select 
                              value={referral.status}
                              onValueChange={(value) => {
                                // Handle status update based on source
                                if (referral.source === 'Lead Specialist') {
                                  handleUpdateReferralStatus(referral.id, value);
                                } else {
                                  // Update candidate portal referral
                                  const updateCandidateReferralStatus = async () => {
                                    try {
                                      const token = getAuthToken();
                                      await axios.patch(`${API}/admin/candidate-referrals/${referral.id}?status=${value}`, {}, {
                                        headers: { Authorization: `Bearer ${token}` }
                                      });
                                      toast.success('Referral status updated');
                                      fetchData();
                                    } catch (error) {
                                      toast.error('Failed to update status');
                                    }
                                  };
                                  updateCandidateReferralStatus();
                                }
                              }}
                            >
                              <SelectTrigger className="w-[140px] rounded-lg">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Pending">Pending</SelectItem>
                                <SelectItem value="Contacted">Contacted</SelectItem>
                                <SelectItem value="Applied">Applied</SelectItem>
                                <SelectItem value="Telephonic Cleared">Telephonic Cleared</SelectItem>
                                <SelectItem value="Registration Done">Registration Done ✨</SelectItem>
                                <SelectItem value="Hired">Hired</SelectItem>
                                <SelectItem value="Rejected">Rejected</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </LazyTabContent>
          )}

          {/* Forms Tab Content */}
          {(currentUser?.is_admin || currentUser?.role === 'crm_user' || currentUser?.role === 'user') && (
            <LazyTabContent value="forms" activeTab={activeTab} className="space-y-4 md:space-y-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div>
                  <h2 className="font-heading text-xl md:text-3xl font-bold tracking-tight mb-1 bg-gradient-to-r from-indigo-600 to-blue-600 bg-clip-text text-transparent">
                    Custom Forms
                  </h2>
                  <p className="text-sm text-muted-foreground">Create and manage custom forms with submissions</p>
                </div>
                <Button 
                  onClick={() => {
                    setEditingForm(null);
                    setNewForm({
                      title: '',
                      description: '',
                      header_image_id: null,
                      pipeline_name: '',
                      thank_you_message: 'Thank you for your submission!',
                      fields: []
                    });
                    setIsFormBuilderOpen(true);
                  }}
                  className="rounded-lg font-medium bg-gradient-to-r from-indigo-500 to-blue-600 hover:from-indigo-600 hover:to-blue-700"
                  data-testid="create-form-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create New Form
                </Button>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">Total Forms</p>
                    <p className="text-2xl font-bold text-indigo-600">{customForms.length}</p>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">Total Submissions</p>
                    <p className="text-2xl font-bold text-blue-600">{formSubmissions.length}</p>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">New</p>
                    <p className="text-2xl font-bold text-green-600">{formSubmissions.filter(s => s.status === 'New').length}</p>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">Selected</p>
                    <p className="text-2xl font-bold text-violet-600">{formSubmissions.filter(s => s.status === 'Selected').length}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Forms List */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {customForms.map((form) => {
                  const isFormLockedForUser = !currentUser?.is_admin && form.is_locked_for_user === true;
                  const lockedEmailsCount = form.locked_emails?.length || 0;
                  
                  return (
                  <Card 
                    key={form.id} 
                    className={`border-0 shadow-lg transition-shadow ${
                      isFormLockedForUser 
                        ? 'bg-gray-100 opacity-60 cursor-not-allowed' 
                        : 'hover:shadow-xl'
                    }`}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 flex items-center gap-2">
                          {isFormLockedForUser && (
                            <Lock className="h-5 w-5 text-orange-500" />
                          )}
                          <div>
                            <h3 className={`font-semibold text-lg ${isFormLockedForUser ? 'text-gray-500' : ''}`}>
                              {form.title}
                              {isFormLockedForUser && <span className="ml-2 text-xs text-orange-500">(Locked)</span>}
                            </h3>
                            {form.pipeline_name && (
                              <Badge variant="outline" className="mt-1 text-xs">{form.pipeline_name}</Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Lock Badge for Admin */}
                          {currentUser?.is_admin && lockedEmailsCount > 0 && (
                            <Badge className="bg-orange-100 text-orange-700 border-0">
                              <Lock className="h-3 w-3 mr-1" />
                              {lockedEmailsCount}
                            </Badge>
                          )}
                          <Badge className={form.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                            {form.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                      </div>
                      
                      {/* Locked Message for Non-Admin */}
                      {isFormLockedForUser && (
                        <div className="flex items-center gap-2 p-2 mb-3 bg-orange-50 rounded-lg border border-orange-200">
                          <Lock className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-orange-500" />
                          <span className="text-sm text-orange-600">This form is locked. Contact admin for access.</span>
                        </div>
                      )}
                      
                      {!isFormLockedForUser && (
                        <>
                          {form.description && (
                            <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{form.description}</p>
                          )}
                          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
                            <span>{form.fields?.length || 0} fields</span>
                            <span>•</span>
                            <span>{form.submission_count || 0} submissions</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="flex-1"
                              onClick={() => {
                                const shareUrl = `${window.location.origin}/form/${form.share_link}`;
                                const textArea = document.createElement('textarea');
                                textArea.value = shareUrl;
                                textArea.style.position = 'fixed';
                                textArea.style.left = '-999999px';
                                textArea.style.top = '0';
                                document.body.appendChild(textArea);
                                textArea.focus();
                                textArea.select();
                                try {
                                  document.execCommand('copy');
                                  toast.success('Share link copied to clipboard!');
                                } catch (e) {
                                  toast.error('Could not copy link. URL: ' + shareUrl);
                                }
                                document.body.removeChild(textArea);
                              }}
                            >
                              <Link2 className="h-3 w-3 mr-1" />
                              Copy Link
                            </Button>
                            <Button
                              size="sm"
                              variant="default"
                              className="bg-blue-500 hover:bg-blue-600 text-white"
                              onClick={() => {
                                setSelectedFormForResponses(form);
                                setIsFormResponsesDialogOpen(true);
                              }}
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              Responses ({formSubmissions.filter(s => s.form_id === form.id).length})
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingForm(form);
                                setNewForm({
                                  title: form.title,
                                  description: form.description || '',
                                  header_image_id: form.header_image_id,
                                  pipeline_name: form.pipeline_name || '',
                                  thank_you_message: form.thank_you_message || 'Thank you for your submission!',
                                  fields: form.fields || []
                                });
                                setIsFormBuilderOpen(true);
                              }}
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                            
                            {/* Admin Lock/Unlock Dropdown */}
                            {currentUser?.is_admin && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button size="sm" variant="outline">
                                    <MoreVertical className="h-3 w-3" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setFormLockData({
                                        forms: [form],
                                        mode: 'lock'
                                      });
                                      setFormLockEmail('');
                                      fetchAllUsers(); // Load users for dropdown
                                      // Small delay to allow dropdown to close before dialog opens
                                      setTimeout(() => setIsFormLockDialogOpen(true), 150);
                                    }}
                                    className="text-orange-600"
                                  >
                                    <Lock className="h-4 w-4 mr-2" />
                                    Lock for User
                                  </DropdownMenuItem>
                                  {lockedEmailsCount > 0 && (
                                    <DropdownMenuItem
                                      onClick={() => {
                                        setFormLockData({
                                          forms: [form],
                                          mode: 'unlock'
                                        });
                                        setFormLockEmail('');
                                        setTimeout(() => setIsFormLockDialogOpen(true), 150);
                                      }}
                                      className="text-green-600"
                                    >
                                      <Unlock className="h-4 w-4 mr-2" />
                                      Unlock for User
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={async () => {
                                      if (window.confirm('Delete this form and all its submissions?')) {
                                        try {
                                          const token = getAuthToken();
                                          await axios.delete(`${API}/custom-forms/${form.id}`, {
                                            headers: { Authorization: `Bearer ${token}` }
                                          });
                                          setCustomForms(prev => prev.filter(f => f.id !== form.id));
                                          setFormSubmissions(prev => prev.filter(s => s.form_id !== form.id));
                                          toast.success('Form deleted');
                                        } catch (error) {
                                          console.error('Delete error:', error);
                                          toast.error('Failed to delete form');
                                        }
                                      }
                                    }}
                                    className="text-red-600"
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete Form
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                            
                            {/* Non-admin delete button */}
                            {!currentUser?.is_admin && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-500 hover:text-red-700"
                                data-testid={`delete-form-${form.id}`}
                                onClick={async () => {
                                  if (window.confirm('Delete this form and all its submissions?')) {
                                    try {
                                      const token = getAuthToken();
                                      await axios.delete(`${API}/custom-forms/${form.id}`, {
                                        headers: { Authorization: `Bearer ${token}` }
                                      });
                                      setCustomForms(prev => prev.filter(f => f.id !== form.id));
                                      setFormSubmissions(prev => prev.filter(s => s.form_id !== form.id));
                                      toast.success('Form deleted');
                                    } catch (error) {
                                      console.error('Delete error:', error);
                                      toast.error('Failed to delete form');
                                    }
                                  }
                                }}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>
                  );
                })}
              </div>

              {/* Archived/Orphaned Responses Section */}
              {deletedFormSubmissions.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                    <FileText className="h-5 w-5 text-amber-500" />
                    Archived Responses ({deletedFormSubmissions.length})
                    <span className="text-sm font-normal text-gray-500">- From deleted forms</span>
                  </h3>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {deletedFormSubmissions.map((submission) => (
                      <Card key={submission.id} className="border-amber-200 bg-amber-50/50">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <Badge className="bg-amber-100 text-amber-700 mb-2">
                                {submission.original_form_title || 'Deleted Form'}
                              </Badge>
                              <p className="text-xs text-gray-500">
                                Submitted: {formatISTDate(submission.submitted_at)}
                              </p>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0 text-red-400 hover:text-red-600"
                              onClick={async () => {
                                if (window.confirm('Permanently delete this response?')) {
                                  try {
                                    const token = getAuthToken();
                                    await axios.delete(`${API}/form-submissions/${submission.id}`, {
                                      headers: { Authorization: `Bearer ${token}` }
                                    });
                                    setDeletedFormSubmissions(prev => prev.filter(s => s.id !== submission.id));
                                    toast.success('Response deleted');
                                  } catch (error) {
                                    toast.error('Failed to delete');
                                  }
                                }
                              }}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                          <div className="space-y-2">
                            {(submission.original_form_fields || []).slice(0, 3).map((field) => (
                              <div key={field.id} className="text-sm">
                                <span className="text-gray-500">{field.label}:</span>
                                <span className="ml-1 text-gray-700 break-words">
                                  {Array.isArray(submission.responses?.[field.id])
                                    ? submission.responses[field.id].join(', ')
                                    : submission.responses?.[field.id] || '-'}
                                </span>
                              </div>
                            ))}
                            {(submission.original_form_fields || []).length > 3 && (
                              <p className="text-xs text-gray-400">+{(submission.original_form_fields || []).length - 3} more fields</p>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Form-Specific Responses Dialog */}
              <Dialog open={isFormResponsesDialogOpen} onOpenChange={setIsFormResponsesDialogOpen}>
                <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col z-[100]">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <ClipboardList className="h-5 w-5" />
                      Responses - {selectedFormForResponses?.title}
                    </DialogTitle>
                    {selectedFormForResponses && (
                      <p className="text-sm text-muted-foreground">
                        {formSubmissions.filter(s => s.form_id === selectedFormForResponses.id).length} submission(s)
                      </p>
                    )}
                  </DialogHeader>
                  
                  {/* Search Box */}
                  <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" />
                    <Input
                      placeholder="Search responses by name, email, phone..."
                      value={responseSearchQuery}
                      onChange={(e) => setResponseSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>

                  <div className="flex-1 overflow-auto">
                    {selectedFormForResponses && (() => {
                      const filteredResponses = formSubmissions
                        .filter(s => s.form_id === selectedFormForResponses.id)
                        .filter(s => {
                          if (!responseSearchQuery.trim()) return true;
                          const searchLower = responseSearchQuery.toLowerCase();
                          const responseValues = Object.values(s.responses || {}).join(' ').toLowerCase();
                          return responseValues.includes(searchLower);
                        });
                      
                      return filteredResponses.length === 0 ? (
                        <div className="text-center py-12">
                          <FileText className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                          <p className="text-gray-600">{responseSearchQuery ? 'No matching responses found' : 'No submissions for this form yet'}</p>
                          <p className="text-sm text-gray-400 mt-1">
                            {responseSearchQuery ? 'Try different search terms' : 'Share the form link to collect responses'}
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {filteredResponses.map((submission, idx) => {
                            const isExpanded = expandedResponses[submission.id];
                            // Get first 2 fields for preview
                            const previewFields = (selectedFormForResponses?.fields || []).slice(0, 2);
                            
                            return (
                              <Card key={submission.id} className={`border shadow-sm transition-all ${isExpanded ? 'ring-2 ring-violet-200' : ''}`}>
                                <CardContent className="p-3">
                                  {/* Compact Header Row */}
                                  <div className="flex items-center gap-3">
                                    <div className="h-8 w-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                                      {idx + 1}
                                    </div>
                                    
                                    {/* Preview Info */}
                                    <div className="flex-1 min-w-0 flex items-center gap-4 flex-wrap">
                                      {previewFields.map((field) => {
                                        const value = submission.responses?.[field.id];
                                        const displayValue = Array.isArray(value) ? value.join(', ') : (value || '-');
                                        return (
                                          <div key={field.id} className="text-sm">
                                            <span className="text-gray-400 text-xs">{field.label}: </span>
                                            <span className="text-gray-700 font-medium truncate">{displayValue.substring(0, 30)}{displayValue.length > 30 ? '...' : ''}</span>
                                          </div>
                                        );
                                      })}
                                    </div>

                                    {/* Status Badge */}
                                    <Badge className={`flex-shrink-0 ${
                                      submission.status === 'Done' ? 'bg-green-100 text-green-700' :
                                      submission.status === 'Not Completed' ? 'bg-red-100 text-red-700' :
                                      submission.status === 'Pending' ? 'bg-amber-100 text-amber-700' :
                                      'bg-amber-100 text-amber-700'
                                    }`}>
                                      {submission.status || 'Pending'}
                                    </Badge>

                                    {/* Date */}
                                    <span className="text-xs text-gray-400 flex-shrink-0 hidden sm:block">
                                      {formatIST(submission.submitted_at, 'MMM dd')}
                                    </span>

                                    {/* Expand/Collapse Button */}
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-8 px-2 text-violet-600 hover:bg-violet-50"
                                      onClick={() => setExpandedResponses(prev => ({
                                        ...prev,
                                        [submission.id]: !prev[submission.id]
                                      }))}
                                    >
                                      {isExpanded ? (
                                        <>
                                          <ChevronUp className="h-4 w-4 mr-1" />
                                          <span className="text-xs">Collapse</span>
                                        </>
                                      ) : (
                                        <>
                                          <ChevronDown className="h-4 w-4 mr-1" />
                                          <span className="text-xs">Details</span>
                                        </>
                                      )}
                                    </Button>

                                    {/* Delete Button */}
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-8 w-8 p-0 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-full flex-shrink-0"
                                      onClick={async () => {
                                        if (window.confirm('Delete this submission?')) {
                                          try {
                                            const token = getAuthToken();
                                            await axios.delete(`${API}/form-submissions/${submission.id}`, {
                                              headers: { Authorization: `Bearer ${token}` }
                                            });
                                            setFormSubmissions(prev => prev.filter(s => s.id !== submission.id));
                                            toast.success('Submission deleted');
                                          } catch (error) {
                                            toast.error('Failed to delete');
                                          }
                                        }
                                      }}
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                  </div>

                                  {/* Expanded Details */}
                                  {isExpanded && (
                                    <div className="mt-4 pt-4 border-t">
                                      {/* All Response Fields */}
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                                        {selectedFormForResponses?.fields?.map((field) => {
                                          const value = submission.responses?.[field.id];
                                          const isFileField = field.field_type === 'file' || field.label?.toLowerCase().includes('cv') || field.label?.toLowerCase().includes('resume') || field.label?.toLowerCase().includes('photo');
                                          
                                          return (
                                            <div key={field.id} className="bg-gray-50 rounded-lg p-2.5">
                                              <p className="text-xs font-medium text-gray-500 mb-1">{field.label}</p>
                                              {isFileField && value ? (
                                                <button 
                                                  data-testid="download-file-btn"
                                                  className="text-green-600 hover:underline text-sm flex items-center gap-1 bg-transparent border-none cursor-pointer p-0"
                                                  onClick={async () => {
                                                    try {
                                                      const url = `${API}/forms/files/${value}`;
                                                      const filename = value.split('/').pop() || `download_${Date.now()}`;
                                                      
                                                      // Use fetch with proper headers and blob type detection
                                                      const response = await fetch(url, {
                                                        method: 'GET',
                                                        mode: 'cors',
                                                        credentials: 'same-origin'
                                                      });
                                                      
                                                      if (!response.ok) {
                                                        throw new Error('Download failed');
                                                      }
                                                      
                                                      // Get the content type from response headers
                                                      const contentType = response.headers.get('content-type') || 'application/octet-stream';
                                                      const arrayBuffer = await response.arrayBuffer();
                                                      const blob = new Blob([arrayBuffer], { type: contentType });
                                                      
                                                      const blobUrl = window.URL.createObjectURL(blob);
                                                      const link = document.createElement('a');
                                                      link.href = blobUrl;
                                                      link.download = filename;
                                                      link.style.display = 'none';
                                                      document.body.appendChild(link);
                                                      link.click();
                                                      
                                                      // Cleanup
                                                      setTimeout(() => {
                                                        document.body.removeChild(link);
                                                        window.URL.revokeObjectURL(blobUrl);
                                                      }, 100);
                                                      
                                                      toast.success('File downloaded');
                                                    } catch (error) {
                                                      console.error('Download error:', error);
                                                      toast.error('Failed to download file');
                                                    }
                                                  }}
                                                >
                                                  <Download className="h-3 w-3" />
                                                  Download
                                                </button>
                                              ) : Array.isArray(value) ? (
                                                <p className="text-sm text-gray-700">{value.join(', ')}</p>
                                              ) : (
                                                <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">{value || '-'}</p>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>

                                      {/* Status & Actions Row */}
                                      <div className="flex flex-wrap items-center gap-3 p-2 bg-gray-50 rounded-lg">
                                        <span className="text-sm text-gray-600 font-medium">Status:</span>
                                        <Select
                                          value={['Pending', 'Not Completed', 'Done'].includes(submission.status) ? submission.status : 'Pending'}
                                          onValueChange={(value) => {
                                            if (value === 'Done') {
                                              // Done doesn't need reason
                                              handleSubmissionStatusUpdate(submission.id, value, '');
                                            } else {
                                              // Pending and Not Completed need reason
                                              setStatusReasonDialog({ open: true, submissionId: submission.id, newStatus: value });
                                              setStatusReason('');
                                            }
                                          }}
                                        >
                                          <SelectTrigger className="w-40 h-8 text-sm">
                                            <SelectValue placeholder={submission.status || 'Pending'} />
                                          </SelectTrigger>
                                          <SelectContent className="z-[300]">
                                            <SelectItem value="Pending">
                                              <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-amber-500"></span>Pending</span>
                                            </SelectItem>
                                            <SelectItem value="Not Completed">
                                              <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-red-500"></span>Not Completed</span>
                                            </SelectItem>
                                            <SelectItem value="Done">
                                              <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-green-500"></span>Done</span>
                                            </SelectItem>
                                          </SelectContent>
                                        </Select>
                                        <span className="text-xs text-gray-400 ml-auto">
                                          Submitted: {formatISTFull(submission.submitted_at)}
                                        </span>
                                      </div>

                                      {/* Display Reason if exists */}
                                      {submission.status_reason && (
                                        <div className="mt-3 p-2.5 bg-amber-50 rounded-lg border border-amber-200">
                                          <p className="text-xs font-medium text-amber-700 mb-1">Reason:</p>
                                          <p className="text-sm text-amber-900 whitespace-pre-wrap break-words">{submission.status_reason}</p>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </CardContent>
                              </Card>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                </DialogContent>
              </Dialog>

              {/* Status Reason Dialog */}
              <Dialog open={statusReasonDialog.open} onOpenChange={(open) => setStatusReasonDialog({ ...statusReasonDialog, open })}>
                <DialogContent className="max-w-md z-[350]">
                  <DialogHeader>
                    <DialogTitle>Provide Reason</DialogTitle>
                    <p className="text-sm text-muted-foreground">
                      Please provide a reason for marking this as "{statusReasonDialog.newStatus}"
                    </p>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <Textarea
                      value={statusReason}
                      onChange={(e) => setStatusReason(e.target.value)}
                      placeholder="Enter reason..."
                      rows={3}
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setStatusReasonDialog({ open: false, submissionId: null, newStatus: null })}>
                      Cancel
                    </Button>
                    <Button 
                      onClick={() => {
                        if (!statusReason.trim()) {
                          toast.error('Please provide a reason');
                          return;
                        }
                        handleSubmissionStatusUpdate(statusReasonDialog.submissionId, statusReasonDialog.newStatus, statusReason);
                        setStatusReasonDialog({ open: false, submissionId: null, newStatus: null });
                        setStatusReason('');
                      }}
                    >
                      Confirm
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Form Submissions Section */}
              <div className="mt-8">
                <h3 className="font-heading text-lg font-semibold mb-4">Recent Submissions</h3>
                {formSubmissions.length === 0 ? (
                  <Card className="border-0 shadow-lg">
                    <CardContent className="p-12 text-center">
                      <ClipboardList className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                      <p className="text-gray-600">No form submissions yet</p>
                      <p className="text-sm text-gray-400 mt-1">Submissions will appear here when people fill out your forms</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {formSubmissions.slice(0, 10).map((submission) => (
                      <Card key={submission.id} className="border-0 shadow-lg">
                        <CardContent className="p-4">
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-semibold">{submission.form_title}</h4>
                                {submission.pipeline_name && (
                                  <Badge variant="outline" className="text-xs">{submission.pipeline_name}</Badge>
                                )}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {Object.entries(submission.responses || {}).slice(0, 3).map(([key, value], idx) => (
                                  <span key={key}>
                                    {typeof value === 'string' && value.length < 50 ? value : '...'}
                                    {idx < 2 && Object.keys(submission.responses).length > idx + 1 ? ' • ' : ''}
                                  </span>
                                ))}
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                Submitted {formatISTFull(submission.submitted_at)}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Select 
                                value={['Pending', 'Not Completed', 'Done'].includes(submission.status) ? submission.status : 'Pending'}
                                onValueChange={(value) => {
                                  if (value === 'Done') {
                                    // Done doesn't need reason - update directly
                                    handleSubmissionStatusUpdate(submission.id, value, '');
                                  } else {
                                    // Pending and Not Completed need reason
                                    setStatusReasonDialog({ open: true, submissionId: submission.id, newStatus: value });
                                    setStatusReason('');
                                  }
                                }}
                              >
                                <SelectTrigger className="w-[140px]">
                                  <SelectValue placeholder={submission.status || 'Pending'} />
                                </SelectTrigger>
                                <SelectContent className="z-[200]">
                                  <SelectItem value="Pending">Pending</SelectItem>
                                  <SelectItem value="Not Completed">Not Completed</SelectItem>
                                  <SelectItem value="Done">Done</SelectItem>
                                </SelectContent>
                              </Select>
                              <Dialog>
                                <DialogTrigger asChild>
                                  <Button size="sm" variant="outline">
                                    <Eye className="h-3 w-3 mr-1" />
                                    View
                                  </Button>
                                </DialogTrigger>
                                <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                                  <DialogHeader>
                                    <DialogTitle>{submission.form_title}</DialogTitle>
                                  </DialogHeader>
                                  <div className="space-y-4 mt-4">
                                    {Object.entries(submission.responses || {}).map(([fieldId, value]) => {
                                      const form = customForms.find(f => f.id === submission.form_id);
                                      const field = form?.fields?.find(f => f.id === fieldId);
                                      return (
                                        <div key={fieldId} className="border-b pb-3">
                                          <p className="text-sm font-medium text-gray-600">{field?.label || fieldId}</p>
                                          {field?.field_type === 'file' && value ? (
                                            <a 
                                              href={`${API}/forms/files/${value}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-blue-600 hover:underline text-sm flex items-center gap-1"
                                            >
                                              <Download className="h-3 w-3" />
                                              Download File
                                            </a>
                                          ) : Array.isArray(value) ? (
                                            <p className="text-sm whitespace-pre-wrap break-words">{value.join(', ')}</p>
                                          ) : (
                                            <p className="text-sm whitespace-pre-wrap break-words">{value || '-'}</p>
                                          )}
                                        </div>
                                      );
                                    })}
                                    <div className="border-t pt-3">
                                      <p className="text-xs text-muted-foreground">
                                        Submitted: {formatISTFull(submission.submitted_at)}
                                      </p>
                                    </div>
                                  </div>
                                </DialogContent>
                              </Dialog>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Form Lock/Unlock Dialog */}
              <Dialog open={isFormLockDialogOpen} onOpenChange={setIsFormLockDialogOpen}>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      {formLockData?.mode === 'lock' ? (
                        <>
                          <Lock className="h-5 w-5 text-orange-600" />
                          Lock Form
                        </>
                      ) : (
                        <>
                          <Unlock className="h-5 w-5 text-green-600" />
                          Unlock Form
                        </>
                      )}
                    </DialogTitle>
                  </DialogHeader>
                  
                  {formLockData && (
                    <div className="space-y-4">
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="font-medium text-slate-800">{formLockData.forms.map(f => f.title).join(', ')}</p>
                        <p className="text-sm text-slate-500">{formLockData.forms.length} form(s)</p>
                      </div>
                      
                      {/* Show user selection for lock mode */}
                      {formLockData.mode === 'lock' && (
                        <div className="space-y-2">
                          <Label>Select User or CRM Member to Lock</Label>
                          <div className="space-y-2 max-h-48 overflow-y-auto p-2 bg-gray-50 rounded-lg">
                            {/* Search Input */}
                            <Input
                              placeholder="Search by name or email..."
                              value={formLockEmail}
                              onChange={(e) => setFormLockEmail(e.target.value)}
                              className="mb-2"
                            />
                            {/* User List */}
                            {loadingUsers ? (
                              <div className="flex items-center justify-center py-4">
                                <div className="h-5 w-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mr-2" />
                                <span className="text-sm text-gray-500">Loading users...</span>
                              </div>
                            ) : (allUsers || [])
                              .filter(u => 
                                u.email && 
                                ((u.name || u.full_name || '')?.toLowerCase().includes(formLockEmail.toLowerCase()) ||
                                 u.email?.toLowerCase().includes(formLockEmail.toLowerCase()) ||
                                 formLockEmail === '')
                              )
                              .slice(0, 20)
                              .map((user, idx) => (
                                <div 
                                  key={idx} 
                                  className={`flex items-center justify-between p-2 rounded border cursor-pointer transition-colors ${
                                    formLockEmail.toLowerCase() === user.email?.toLowerCase()
                                      ? 'bg-orange-100 border-orange-300'
                                      : 'bg-white border-gray-200 hover:bg-gray-50'
                                  }`}
                                  onClick={() => setFormLockEmail(user.email)}
                                >
                                  <div>
                                    <p className="text-sm font-medium text-slate-700">{user.name || user.full_name || user.email?.split('@')[0] || 'Unknown'}</p>
                                    <p className="text-xs text-slate-500">{user.email}</p>
                                  </div>
                                  <Badge variant="outline" className="text-xs">
                                    {user.is_admin ? 'Admin' : (user.role || 'User')}
                                  </Badge>
                                </div>
                              ))}
                            {!loadingUsers && (allUsers || []).filter(u => 
                              u.email && 
                              ((u.name || u.full_name || '')?.toLowerCase().includes(formLockEmail.toLowerCase()) ||
                               u.email?.toLowerCase().includes(formLockEmail.toLowerCase()) ||
                               formLockEmail === '')
                            ).length === 0 && (
                              <p className="text-sm text-gray-500 text-center py-2">No users found</p>
                            )}
                          </div>
                          {formLockEmail && (
                            <p className="text-sm text-orange-600">
                              Selected: <strong>{formLockEmail}</strong>
                            </p>
                          )}
                        </div>
                      )}
                      
                      {/* Show currently locked users for unlock mode */}
                      {formLockData.mode === 'unlock' && (
                        <div className="space-y-2">
                          <Label>Currently Locked Users</Label>
                          <div className="space-y-2 max-h-40 overflow-y-auto p-2 bg-gray-50 rounded-lg">
                            {(() => {
                              const lockedUsersMap = {};
                              formLockData.forms.forEach(f => {
                                (f.locked_users_info || []).forEach(info => {
                                  const email = (info.email || '').toLowerCase();
                                  if (email && !lockedUsersMap[email]) {
                                    lockedUsersMap[email] = info;
                                  }
                                });
                                (f.locked_emails || []).forEach(e => {
                                  const email = e.toLowerCase();
                                  if (!lockedUsersMap[email]) {
                                    lockedUsersMap[email] = { email, locked_by_name: 'Unknown', locked_at: null };
                                  }
                                });
                              });
                              const lockedUsers = Object.values(lockedUsersMap);
                              
                              return lockedUsers.length > 0 ? lockedUsers.map((user, idx) => (
                                <div 
                                  key={idx} 
                                  className="flex items-center justify-between p-2 bg-white rounded border border-orange-200 cursor-pointer hover:bg-orange-50"
                                  onClick={() => setFormLockEmail(user.email)}
                                >
                                  <div>
                                    <p className="text-sm font-medium text-slate-700">{user.email}</p>
                                    <p className="text-xs text-slate-500">
                                      Locked by: <span className="font-medium text-orange-600">{user.locked_by_name || 'Unknown'}</span>
                                    </p>
                                  </div>
                                  <Lock className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-orange-500" />
                                </div>
                              )) : (
                                <span className="text-sm text-gray-500">No users locked</span>
                              );
                            })()}
                          </div>
                        </div>
                      )}
                      
                      {/* Email input only shown for unlock mode (lock mode uses user list above) */}
                      {formLockData.mode === 'unlock' && (
                        <div className="space-y-2">
                          <Label htmlFor="form-lock-email">Email to Unlock</Label>
                          <Input
                            id="form-lock-email"
                            type="email"
                            placeholder="Select from list above or type email"
                            value={formLockEmail}
                            onChange={(e) => setFormLockEmail(e.target.value)}
                            disabled={isLockingForm}
                          />
                        </div>
                      )}
                      
                      <div className="flex justify-end gap-2 pt-2">
                        <Button
                          variant="outline"
                          onClick={() => setIsFormLockDialogOpen(false)}
                          disabled={isLockingForm}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleLockUnlockForms}
                          disabled={!formLockEmail.trim() || isLockingForm}
                          className={formLockData.mode === 'lock' 
                            ? "bg-orange-600 hover:bg-orange-700" 
                            : "bg-green-600 hover:bg-green-700"
                          }
                        >
                          {isLockingForm ? (
                            <>
                              <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                              {formLockData.mode === 'lock' ? 'Locking...' : 'Unlocking...'}
                            </>
                          ) : (
                            <>
                              {formLockData.mode === 'lock' ? (
                                <><Lock className="h-4 w-4 mr-2" /> Lock User</>
                              ) : (
                                <><Unlock className="h-4 w-4 mr-2" /> Unlock User</>
                              )}
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </LazyTabContent>
          )}

          {/* Skill Assessments Tab */}
          <LazyTabContent value="skill-assessments" activeTab={activeTab} className="space-y-4 md:space-y-6">
            <div className="bg-gradient-to-r from-slate-50 via-purple-50/30 to-indigo-50/40 rounded-2xl p-6 border border-slate-100/80 shadow-sm">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-purple-200/50">
                    <Target className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl md:text-2xl font-bold tracking-tight text-slate-800">
                      Skill Assessments
                    </h2>
                    <p className="text-sm text-slate-500 mt-0.5">Create and manage candidate skill assessments</p>
                  </div>
                </div>
                <Button onClick={() => { setAssessmentDialogOpen(true); fetchSkillAssessments(); }} className="bg-purple-600 hover:bg-purple-700">
                  <PlusCircle className="h-4 w-4 mr-2" />
                  Create Assessment
                </Button>
              </div>
            </div>

            {/* Assessment List */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {skillAssessments.map(assessment => (
                <Card key={assessment.id} className={`hover:shadow-lg transition-shadow relative ${assessment.is_globally_locked ? 'border-red-300 bg-red-50/30' : ''}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center justify-between text-lg">
                      <span className="flex items-center gap-2">
                        <Brain className="h-5 w-5 text-purple-600" />
                        {assessment.skill_name}
                        {assessment.is_globally_locked && (
                          <Badge className="bg-red-500 text-white text-[10px] px-1.5">
                            <Lock className="h-3 w-3 mr-1" />
                            Locked for All
                          </Badge>
                        )}
                      </span>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="bg-purple-50 text-purple-600">
                          {assessment.questions?.length || 0} Q
                        </Badge>
                        {/* Lock Menu */}
                        {currentUser?.is_admin && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {/* Edit Assessment */}
                              <DropdownMenuItem
                                onClick={() => handleEditAssessment(assessment)}
                                className="text-blue-600"
                              >
                                <Pencil className="h-4 w-4 mr-2" />
                                Edit Assessment
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => {
                                  setLockAssessmentData({ assessment, mode: 'lock' });
                                  setLockAssessmentEmail('');
                                  fetchAllUsers();
                                  setTimeout(() => setIsLockAssessmentDialogOpen(true), 150);
                                }}
                                className="text-orange-600"
                              >
                                <Lock className="h-4 w-4 mr-2" />
                                Lock for User
                              </DropdownMenuItem>
                              {assessment.locked_emails?.length > 0 && (
                                <DropdownMenuItem
                                  onClick={() => {
                                    setLockAssessmentData({ assessment, mode: 'unlock' });
                                    setLockAssessmentEmail('');
                                    setTimeout(() => setIsLockAssessmentDialogOpen(true), 150);
                                  }}
                                  className="text-green-600"
                                >
                                  <Unlock className="h-4 w-4 mr-2" />
                                  Unlock for User ({assessment.locked_emails.length})
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              {assessment.is_globally_locked ? (
                                <DropdownMenuItem
                                  onClick={() => handleUnlockAssessmentForAll(assessment.id, assessment.skill_name)}
                                  className="text-green-600"
                                >
                                  <Unlock className="h-4 w-4 mr-2" />
                                  Unlock for ALL Candidates
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem
                                  onClick={() => handleLockAssessmentForAll(assessment.id, assessment.skill_name)}
                                  className="text-red-600"
                                >
                                  <Lock className="h-4 w-4 mr-2" />
                                  Lock for ALL Candidates
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              {/* Delete Assessment */}
                              <DropdownMenuItem
                                onClick={() => setDeleteAssessmentConfirm(assessment)}
                                className="text-red-600"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete Assessment
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-slate-500 mb-3">{assessment.description || 'No description'}</p>
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {assessment.time_limit_minutes} min
                      </span>
                      <span className="flex items-center gap-1">
                        <Target className="h-3 w-3" />
                        Pass: {assessment.passing_score}%
                      </span>
                    </div>
                    {/* Show locked users count */}
                    {assessment.locked_emails?.length > 0 && (
                      <div className="mt-2 flex items-center gap-1 text-xs text-orange-600">
                        <Lock className="h-3 w-3" />
                        Locked for {assessment.locked_emails.length} user(s)
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
              {skillAssessments.length === 0 && (
                <div className="col-span-full text-center py-8 text-slate-500">
                  No assessments created yet. Click "Create Assessment" to get started.
                </div>
              )}
            </div>

            {/* Create Assessment Dialog */}
            <Dialog open={assessmentDialogOpen} onOpenChange={(open) => {
              setAssessmentDialogOpen(open);
              if (!open) {
                setEditingAssessmentId(null);
                setNewAssessment({ skill_name: '', description: '', passing_score: 70, time_limit_minutes: 15, questions: [] });
              }
            }}>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5 text-purple-600" />
                    {editingAssessmentId ? 'Edit Assessment' : 'Create New Assessment'}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Skill Name *</Label>
                      <Input
                        value={newAssessment.skill_name}
                        onChange={(e) => setNewAssessment(prev => ({ ...prev, skill_name: e.target.value }))}
                        placeholder="e.g., JavaScript, Python, React..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Time Limit (minutes)</Label>
                      <Input
                        type="number"
                        value={newAssessment.time_limit_minutes}
                        onChange={(e) => setNewAssessment(prev => ({ ...prev, time_limit_minutes: parseInt(e.target.value) || 15 }))}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={newAssessment.description}
                      onChange={(e) => setNewAssessment(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Brief description of the assessment..."
                      rows={2}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Passing Score (%)</Label>
                    <Input
                      type="number"
                      value={newAssessment.passing_score}
                      onChange={(e) => setNewAssessment(prev => ({ ...prev, passing_score: parseInt(e.target.value) || 70 }))}
                    />
                  </div>

                  {/* Questions */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label className="text-base">Questions ({newAssessment.questions.length})</Label>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => addQuestionToAssessment('mcq')} className="gap-1">
                          <Plus className="h-4 w-4" /> MCQ
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => addQuestionToAssessment('theoretical')} className="gap-1 border-amber-300 text-amber-700 hover:bg-amber-50">
                          <Plus className="h-4 w-4" /> Theoretical
                        </Button>
                      </div>
                    </div>
                    {newAssessment.questions.map((q, qIdx) => (
                      <Card key={qIdx} className={`p-4 ${q.question_type === 'theoretical' ? 'bg-amber-50 border-amber-200' : 'bg-slate-50'}`}>
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-700">Question {qIdx + 1}</span>
                            <Badge variant="outline" className={q.question_type === 'theoretical' ? 'bg-amber-100 text-amber-700' : 'bg-purple-100 text-purple-700'}>
                              {q.question_type === 'theoretical' ? 'Theoretical' : 'MCQ'}
                            </Badge>
                          </div>
                          <Button size="sm" variant="ghost" onClick={() => removeQuestionFromAssessment(qIdx)} className="text-red-500 h-8 w-8 p-0">
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        <Input
                          value={q.question}
                          onChange={(e) => {
                            const updated = [...newAssessment.questions];
                            updated[qIdx].question = e.target.value;
                            setNewAssessment(prev => ({ ...prev, questions: updated }));
                          }}
                          placeholder="Enter question..."
                          className="mb-3"
                        />
                        
                        {/* MCQ Options */}
                        {q.question_type === 'mcq' && (
                          <div className="space-y-2">
                            {(q.options || ['', '', '', '']).map((opt, optIdx) => (
                              <div key={optIdx} className="flex items-center gap-2">
                                <input
                                  type="radio"
                                  name={`correct-${qIdx}`}
                                  checked={q.correct_answer === optIdx}
                                  onChange={() => {
                                    const updated = [...newAssessment.questions];
                                    updated[qIdx].correct_answer = optIdx;
                                    setNewAssessment(prev => ({ ...prev, questions: updated }));
                                  }}
                                  className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-purple-600"
                                />
                                <Input
                                  value={opt}
                                  onChange={(e) => {
                                    const updated = [...newAssessment.questions];
                                    updated[qIdx].options[optIdx] = e.target.value;
                                    setNewAssessment(prev => ({ ...prev, questions: updated }));
                                  }}
                                  placeholder={`Option ${String.fromCharCode(65 + optIdx)}`}
                                  className="flex-1"
                                />
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Theoretical Answer Field */}
                        {q.question_type === 'theoretical' && (
                          <div className="space-y-3">
                            <div>
                              <Label className="text-xs text-slate-500">Model Answer (for admin reference)</Label>
                              <Textarea
                                value={q.model_answer || ''}
                                onChange={(e) => {
                                  const updated = [...newAssessment.questions];
                                  updated[qIdx].model_answer = e.target.value;
                                  setNewAssessment(prev => ({ ...prev, questions: updated }));
                                }}
                                placeholder="Enter the expected/model answer..."
                                rows={3}
                                className="mt-1"
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <Label className="text-xs text-slate-500">Max Marks:</Label>
                              <Input
                                type="number"
                                value={q.max_marks || 5}
                                onChange={(e) => {
                                  const updated = [...newAssessment.questions];
                                  updated[qIdx].max_marks = parseInt(e.target.value) || 5;
                                  setNewAssessment(prev => ({ ...prev, questions: updated }));
                                }}
                                className="w-20"
                                min={1}
                                max={20}
                              />
                            </div>
                          </div>
                        )}
                      </Card>
                    ))}
                  </div>

                  <Button onClick={handleCreateAssessment} className="w-full bg-purple-600 hover:bg-purple-700">
                    {editingAssessmentId ? 'Update Assessment' : 'Create Assessment'} ({newAssessment.questions.length} questions)
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Delete Assessment Confirmation Dialog */}
            <Dialog open={!!deleteAssessmentConfirm} onOpenChange={(open) => !open && setDeleteAssessmentConfirm(null)}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-red-600">
                    <AlertTriangle className="h-5 w-5" />
                    Delete Assessment
                  </DialogTitle>
                </DialogHeader>
                <div className="py-4">
                  <p className="text-slate-600">
                    Are you sure you want to delete the assessment <strong>"{deleteAssessmentConfirm?.skill_name}"</strong>?
                  </p>
                  <p className="text-sm text-slate-500 mt-2">
                    This will also delete all associated results. This action cannot be undone.
                  </p>
                </div>
                <div className="flex gap-3 justify-end">
                  <Button variant="outline" onClick={() => setDeleteAssessmentConfirm(null)}>
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => handleDeleteAssessment(deleteAssessmentConfirm?.id)}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Assessment
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Candidate Assessment Results Section (Admin Only) */}
            {currentUser?.is_admin && (
              <div className="mt-8">
                <div className="bg-gradient-to-r from-slate-50 via-emerald-50/30 to-teal-50/40 rounded-2xl p-6 border border-slate-100/80 shadow-sm mb-4">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-200/50">
                        <Award className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h2 className="font-heading text-xl md:text-2xl font-bold tracking-tight text-slate-800">
                          Candidate Results
                        </h2>
                        <p className="text-sm text-slate-500 mt-0.5">View all candidate assessment scores and allow retakes</p>
                      </div>
                    </div>
                    <Button variant="outline" onClick={fetchAssessmentResults} disabled={loadingAssessmentResults}>
                      {loadingAssessmentResults ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RotateCcw className="h-4 w-4 mr-2" />}
                      Refresh
                    </Button>
                  </div>
                </div>

                {/* Results Table */}
                <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white">
                          <th className="px-4 py-3 text-left font-medium">Candidate</th>
                          <th className="px-4 py-3 text-left font-medium">Assessment</th>
                          <th className="px-4 py-3 text-center font-medium">Score</th>
                          <th className="px-4 py-3 text-center font-medium">Status</th>
                          <th className="px-4 py-3 text-center font-medium">Time</th>
                          <th className="px-4 py-3 text-center font-medium">Date</th>
                          <th className="px-4 py-3 text-center font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loadingAssessmentResults ? (
                          <tr>
                            <td colSpan="7" className="px-4 py-8 text-center">
                              <Loader2 className="h-6 w-6 animate-spin mx-auto text-emerald-500" />
                              <p className="text-sm text-slate-500 mt-2">Loading results...</p>
                            </td>
                          </tr>
                        ) : assessmentResults.length === 0 ? (
                          <tr>
                            <td colSpan="7" className="px-4 py-8 text-center">
                              <div className="flex flex-col items-center">
                                <Award className="h-12 w-12 text-slate-300 mb-2" />
                                <p className="text-slate-500">No assessment results yet</p>
                                <p className="text-xs text-slate-400 mt-1">Results will appear here when candidates complete assessments</p>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          assessmentResults.map((result, idx) => (
                            <tr key={result.id || idx} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'} hover:bg-emerald-50/50 transition-colors border-b border-slate-100`}>
                              <td className="px-4 py-3">
                                <div>
                                  <p className="font-medium text-slate-700">{result.candidate_name || 'Unknown'}</p>
                                  <p className="text-xs text-slate-500">{result.candidate_email}</p>
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <span className="font-medium text-purple-600">{result.skill_name}</span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className={`inline-block px-2 py-1 rounded-full text-sm font-bold ${
                                  result.score >= 80 ? 'bg-emerald-100 text-emerald-700' :
                                  result.score >= 60 ? 'bg-amber-100 text-amber-700' :
                                  'bg-red-100 text-red-700'
                                }`}>
                                  {result.score}%
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                {result.passed ? (
                                  <Badge className="bg-emerald-500 text-white">Passed</Badge>
                                ) : (
                                  <Badge className="bg-red-500 text-white">Failed</Badge>
                                )}
                              </td>
                              <td className="px-4 py-3 text-center text-slate-500">
                                {result.time_taken_seconds ? `${Math.floor(result.time_taken_seconds / 60)}m ${result.time_taken_seconds % 60}s` : '-'}
                              </td>
                              <td className="px-4 py-3 text-center text-slate-500 text-xs">
                                {result.completed_at ? new Date(result.completed_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' }) : '-'}
                              </td>
                              <td className="px-4 py-3 text-center">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleReassignAssessment(result.candidate_email, result.assessment_id, result.skill_name)}
                                  className="text-orange-600 border-orange-200 hover:bg-orange-50"
                                  title="Allow candidate to retake this assessment"
                                >
                                  <RotateCcw className="h-3.5 w-3.5 mr-1" />
                                  Reassign
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </LazyTabContent>

          {/* Career Paths Tab */}
          <LazyTabContent value="career-paths" activeTab={activeTab} className="space-y-4 md:space-y-6">
            <div className="bg-gradient-to-r from-slate-50 via-amber-50/30 to-orange-50/40 rounded-2xl p-6 border border-slate-100/80 shadow-sm">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-200/50">
                    <Route className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl md:text-2xl font-bold tracking-tight text-slate-800">
                      Career Paths
                    </h2>
                    <p className="text-sm text-slate-500 mt-0.5">Define career progression roadmaps for different domains</p>
                  </div>
                </div>
                <Button onClick={() => { setCareerPathDialogOpen(true); fetchCareerPaths(); }} className="bg-amber-600 hover:bg-amber-700">
                  <PlusCircle className="h-4 w-4 mr-2" />
                  Create Career Path
                </Button>
              </div>
            </div>

            {/* Career Paths List */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {careerPaths.map(path => (
                <Card key={path.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center justify-between text-lg">
                      <span className="flex items-center gap-2">
                        <Route className="h-5 w-5 text-amber-600" />
                        {path.domain}
                      </span>
                      <Badge variant="outline" className="bg-amber-50 text-amber-600">
                        {path.steps?.length || 0} stages
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-slate-500 mb-3">{path.description || 'No description'}</p>
                    <div className="flex flex-wrap gap-1">
                      {path.steps?.slice(0, 3).map((step, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          {step.title}
                        </Badge>
                      ))}
                      {path.steps?.length > 3 && (
                        <Badge variant="secondary" className="text-xs">+{path.steps.length - 3} more</Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
              {careerPaths.length === 0 && (
                <div className="col-span-full text-center py-8 text-slate-500">
                  No career paths created yet. Click "Create Career Path" to get started.
                </div>
              )}
            </div>

            {/* Create Career Path Dialog */}
            <Dialog open={careerPathDialogOpen} onOpenChange={setCareerPathDialogOpen}>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Route className="h-5 w-5 text-amber-600" />
                    Create Career Path
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Domain/Field *</Label>
                    <Input
                      value={newCareerPath.domain}
                      onChange={(e) => setNewCareerPath(prev => ({ ...prev, domain: e.target.value }))}
                      placeholder="e.g., Software Engineering, Data Science..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={newCareerPath.description}
                      onChange={(e) => setNewCareerPath(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Brief description of this career path..."
                      rows={2}
                    />
                  </div>

                  {/* Career Stages */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label className="text-base">Career Stages ({newCareerPath.steps.length})</Label>
                      <Button size="sm" variant="outline" onClick={addStepToCareerPath}>
                        <Plus className="h-4 w-4 mr-1" /> Add Stage
                      </Button>
                    </div>
                    {newCareerPath.steps.map((step, sIdx) => (
                      <Card key={sIdx} className="p-4 bg-slate-50">
                        <div className="flex justify-between items-start mb-3">
                          <span className="font-medium text-slate-700">Stage {sIdx + 1}</span>
                          <Button size="sm" variant="ghost" onClick={() => removeStepFromCareerPath(sIdx)} className="text-red-500 h-8 w-8 p-0">
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <Input
                            value={step.title}
                            onChange={(e) => {
                              const updated = [...newCareerPath.steps];
                              updated[sIdx].title = e.target.value;
                              setNewCareerPath(prev => ({ ...prev, steps: updated }));
                            }}
                            placeholder="Job Title (e.g., Junior Developer)"
                          />
                          <Input
                            value={step.years_experience}
                            onChange={(e) => {
                              const updated = [...newCareerPath.steps];
                              updated[sIdx].years_experience = e.target.value;
                              setNewCareerPath(prev => ({ ...prev, steps: updated }));
                            }}
                            placeholder="Experience (e.g., 0-2 years)"
                          />
                          <Input
                            value={step.salary_range}
                            onChange={(e) => {
                              const updated = [...newCareerPath.steps];
                              updated[sIdx].salary_range = e.target.value;
                              setNewCareerPath(prev => ({ ...prev, steps: updated }));
                            }}
                            placeholder="Salary Range (e.g., ₹3-6 LPA)"
                          />
                          <Input
                            value={step.skills?.join(', ') || ''}
                            onChange={(e) => {
                              const updated = [...newCareerPath.steps];
                              updated[sIdx].skills = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
                              setNewCareerPath(prev => ({ ...prev, steps: updated }));
                            }}
                            placeholder="Skills (comma-separated)"
                          />
                        </div>
                      </Card>
                    ))}
                  </div>

                  <Button onClick={handleCreateCareerPath} className="w-full bg-amber-600 hover:bg-amber-700">
                    Create Career Path ({newCareerPath.steps.length} stages)
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </LazyTabContent>

          {/* Banners Tab Content (Admin only) */}
          {currentUser?.is_admin && (
            <LazyTabContent value="banners" activeTab={activeTab} className="space-y-4 md:space-y-6">
              <div>
                <h2 className="font-heading text-xl md:text-3xl font-bold tracking-tight mb-1 bg-gradient-to-r from-pink-600 to-rose-600 bg-clip-text text-transparent">
                  Banner Announcements
                </h2>
                <p className="text-sm text-muted-foreground">Manage sliding banners for Candidate and Lead Specialist portals</p>
              </div>

              {/* Add New Banner */}
              <Card className="rounded-xl">
                <CardHeader>
                  <CardTitle className="text-lg">Add New Banner</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-4">
                    <Button
                      onClick={async () => {
                        try {
                          setUploadingBanner(true);
                          const token = getAuthToken();
                          const res = await axios.post(`${API}/banners`, {}, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          setBanners(prev => [...prev, res.data]);
                          toast.success('Banner created! Now upload an image.');
                        } catch (error) {
                          toast.error('Failed to create banner');
                        } finally {
                          setUploadingBanner(false);
                        }
                      }}
                      disabled={uploadingBanner}
                      className="bg-gradient-to-r from-pink-500 to-rose-600"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Create New Banner
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Banner List */}
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {banners.map((banner) => (
                  <Card key={banner.id} className="rounded-xl overflow-hidden">
                    <div className="aspect-[16/6] bg-gray-100 relative">
                      {banner.image_id ? (
                        <img 
                          src={`${API}/banners/image/${banner.image_id}`}
                          alt={banner.title || 'Banner'}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-200 to-gray-300">
                          <ImageIcon className="h-12 w-12 text-gray-400" />
                        </div>
                      )}
                      <Badge 
                        className={`absolute top-2 right-2 ${banner.is_active ? 'bg-green-500' : 'bg-gray-500'}`}
                      >
                        {banner.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                      <Badge 
                        className={`absolute top-2 left-2 ${
                          banner.target_portal === 'candidate' ? 'bg-blue-500' :
                          banner.target_portal === 'lead_specialist' ? 'bg-purple-500' :
                          'bg-orange-500'
                        }`}
                      >
                        {banner.target_portal === 'candidate' ? 'Candidates' :
                         banner.target_portal === 'lead_specialist' ? 'Lead Specialists' :
                         'Both'}
                      </Badge>
                    </div>
                    <CardContent className="p-4 space-y-3">
                      <Input
                        placeholder="Banner title (optional)"
                        value={banner.title || ''}
                        onChange={async (e) => {
                          const newTitle = e.target.value;
                          setBanners(prev => prev.map(b => b.id === banner.id ? {...b, title: newTitle} : b));
                        }}
                        onBlur={async (e) => {
                          try {
                            const token = getAuthToken();
                            await axios.patch(`${API}/banners/${banner.id}?title=${encodeURIComponent(e.target.value)}`, {}, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                          } catch (error) {
                            toast.error('Failed to update title');
                          }
                        }}
                        className="text-sm"
                      />
                      
                      <Input
                        placeholder="Link URL (optional)"
                        value={banner.link_url || ''}
                        onChange={(e) => {
                          const newUrl = e.target.value;
                          setBanners(prev => prev.map(b => b.id === banner.id ? {...b, link_url: newUrl} : b));
                        }}
                        onBlur={async (e) => {
                          try {
                            const token = getAuthToken();
                            await axios.patch(`${API}/banners/${banner.id}?link_url=${encodeURIComponent(e.target.value)}`, {}, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                          } catch (error) {
                            toast.error('Failed to update link');
                          }
                        }}
                        className="text-sm"
                      />

                      <Select
                        value={banner.target_portal}
                        onValueChange={async (value) => {
                          try {
                            const token = getAuthToken();
                            await axios.patch(`${API}/banners/${banner.id}?target_portal=${value}`, {}, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            setBanners(prev => prev.map(b => b.id === banner.id ? {...b, target_portal: value} : b));
                            toast.success('Target updated');
                          } catch (error) {
                            toast.error('Failed to update target');
                          }
                        }}
                      >
                        <SelectTrigger className="text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="candidate">Candidate Portal</SelectItem>
                          <SelectItem value="lead_specialist">Lead Specialist Portal</SelectItem>
                          <SelectItem value="both">Both Portals</SelectItem>
                        </SelectContent>
                      </Select>

                      <div className="flex gap-2">
                        <label className="flex-1">
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              
                              try {
                                const token = getAuthToken();
                                const formData = new FormData();
                                formData.append('file', file);
                                
                                await axios.post(`${API}/banners/${banner.id}/image`, formData, {
                                  headers: { 
                                    Authorization: `Bearer ${token}`,
                                    'Content-Type': 'multipart/form-data'
                                  }
                                });
                                
                                toast.success('Image uploaded!');
                                fetchData();
                              } catch (error) {
                                toast.error('Failed to upload image');
                              }
                            }}
                          />
                          <Button variant="outline" size="sm" className="w-full" asChild>
                            <span>
                              <Upload className="h-3 w-3 mr-1" />
                              Upload Image
                            </span>
                          </Button>
                        </label>
                        
                        <Button
                          variant={banner.is_active ? "outline" : "secondary"}
                          size="sm"
                          className={banner.is_active ? "text-green-600 border-green-300" : "text-gray-600"}
                          onClick={async () => {
                            try {
                              const token = getAuthToken();
                              await axios.patch(`${API}/banners/${banner.id}?is_active=${!banner.is_active}`, {}, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              setBanners(prev => prev.map(b => b.id === banner.id ? {...b, is_active: !b.is_active} : b));
                              toast.success(banner.is_active ? 'Banner unpublished' : 'Banner published');
                            } catch (error) {
                              toast.error('Failed to toggle status');
                            }
                          }}
                        >
                          {banner.is_active ? (
                            <>
                              <Eye className="h-3 w-3 mr-1" />
                              Published
                            </>
                          ) : (
                            <>
                              <EyeOff className="h-3 w-3 mr-1" />
                              Unpublished
                            </>
                          )}
                        </Button>
                        
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={async () => {
                            if (!window.confirm('Delete this banner?')) return;
                            try {
                              const token = getAuthToken();
                              await axios.delete(`${API}/banners/${banner.id}`, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              setBanners(prev => prev.filter(b => b.id !== banner.id));
                              toast.success('Banner deleted');
                            } catch (error) {
                              toast.error('Failed to delete banner');
                            }
                          }}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {banners.length === 0 && (
                <div className="border border-border rounded-xl p-12 text-center bg-white">
                  <ImageIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                  <p className="text-muted-foreground">No banners yet. Create one to get started.</p>
                </div>
              )}

              {/* Success Stories Section */}
              <div className="mt-8 pt-8 border-t">
                <h2 className="font-heading text-xl md:text-2xl font-bold tracking-tight mb-1 bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                  Success Stories / Hired Candidates
                </h2>
                <p className="text-sm text-muted-foreground mb-4">Showcase candidates who got hired through Officium Connect</p>

                {/* Add New Story Form */}
                <Card className="rounded-xl mb-6">
                  <CardHeader>
                    <CardTitle className="text-lg">Add New Success Story</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label>Candidate Name *</Label>
                        <Input
                          placeholder="e.g., John Doe"
                          value={newStory.candidate_name}
                          onChange={(e) => setNewStory(prev => ({...prev, candidate_name: e.target.value}))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Position/Company *</Label>
                        <Input
                          placeholder="e.g., Software Engineer at Google"
                          value={newStory.position}
                          onChange={(e) => setNewStory(prev => ({...prev, position: e.target.value}))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Category</Label>
                        <Select
                          value={newStory.category}
                          onValueChange={(value) => setNewStory(prev => ({...prev, category: value}))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="IT">IT / Technology</SelectItem>
                            <SelectItem value="Banking">Banking / Finance</SelectItem>
                            <SelectItem value="Healthcare">Healthcare</SelectItem>
                            <SelectItem value="Education">Education</SelectItem>
                            <SelectItem value="Marketing">Marketing / Sales</SelectItem>
                            <SelectItem value="HR">HR / Admin</SelectItem>
                            <SelectItem value="Engineering">Engineering</SelectItem>
                            <SelectItem value="Other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label>Testimonial *</Label>
                        <Textarea
                          placeholder="What did they say about their experience?"
                          value={newStory.testimonial}
                          onChange={(e) => setNewStory(prev => ({...prev, testimonial: e.target.value}))}
                          rows={3}
                        />
                      </div>
                    </div>
                    <Button
                      className="mt-4 bg-gradient-to-r from-emerald-500 to-teal-600"
                      onClick={async () => {
                        if (!newStory.candidate_name || !newStory.position || !newStory.testimonial) {
                          toast.error('Please fill all required fields');
                          return;
                        }
                        try {
                          const token = getAuthToken();
                          const res = await axios.post(`${API}/success-stories`, newStory, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          setSuccessStories(prev => [...prev, res.data]);
                          setNewStory({ candidate_name: '', testimonial: '', position: '', category: '' });
                          toast.success('Success story added! Now upload a photo.');
                        } catch (error) {
                          toast.error('Failed to add story');
                        }
                      }}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Success Story
                    </Button>
                  </CardContent>
                </Card>

                {/* Success Stories List */}
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {successStories.map((story) => (
                    <Card key={story.id} className="rounded-xl overflow-hidden">
                      <div className="relative">
                        <div className="aspect-square bg-gray-100 flex items-center justify-center">
                          {story.photo_id ? (
                            <img 
                              src={`${API}/success-stories/photo/${story.photo_id}`}
                              alt={story.candidate_name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-emerald-100 to-teal-100">
                              <User className="h-16 w-16 text-emerald-400" />
                            </div>
                          )}
                        </div>
                        <Badge 
                          className={`absolute top-2 right-2 ${story.is_active ? 'bg-green-500' : 'bg-gray-500'}`}
                        >
                          {story.is_active ? 'Active' : 'Hidden'}
                        </Badge>
                        {story.category && (
                          <Badge className="absolute top-2 left-2 bg-blue-500">
                            {story.category}
                          </Badge>
                        )}
                      </div>
                      <CardContent className="p-4 space-y-3">
                        <div>
                          <h3 className="font-semibold text-lg">{story.candidate_name}</h3>
                          <p className="text-sm text-emerald-600 font-medium">{story.position}</p>
                        </div>
                        <p className="text-sm text-gray-600 italic">"{story.testimonial}"</p>
                        
                        <div className="flex gap-2 pt-2">
                          <label className="flex-1">
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                
                                try {
                                  const token = getAuthToken();
                                  const formData = new FormData();
                                  formData.append('file', file);
                                  
                                  await axios.post(`${API}/success-stories/${story.id}/photo`, formData, {
                                    headers: { 
                                      Authorization: `Bearer ${token}`,
                                      'Content-Type': 'multipart/form-data'
                                    }
                                  });
                                  
                                  toast.success('Photo uploaded!');
                                  fetchData();
                                } catch (error) {
                                  toast.error('Failed to upload photo');
                                }
                              }}
                            />
                            <Button variant="outline" size="sm" className="w-full" asChild>
                              <span>
                                <Upload className="h-3 w-3 mr-1" />
                                {story.photo_id ? 'Change Photo' : 'Upload Photo'}
                              </span>
                            </Button>
                          </label>
                          
                          <Button
                            variant={story.is_active ? "outline" : "secondary"}
                            size="sm"
                            className={story.is_active ? "text-green-600 border-green-300" : "text-gray-600"}
                            onClick={async () => {
                              try {
                                const token = getAuthToken();
                                await axios.patch(`${API}/success-stories/${story.id}?is_active=${!story.is_active}`, {}, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                setSuccessStories(prev => prev.map(s => s.id === story.id ? {...s, is_active: !s.is_active} : s));
                                toast.success(story.is_active ? 'Story unpublished' : 'Story published');
                              } catch (error) {
                                toast.error('Failed to toggle status');
                              }
                            }}
                          >
                            {story.is_active ? (
                              <>
                                <Eye className="h-3 w-3 mr-1" />
                                Published
                              </>
                            ) : (
                              <>
                                <EyeOff className="h-3 w-3 mr-1" />
                                Unpublished
                              </>
                            )}
                          </Button>
                          
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={async () => {
                              if (!window.confirm('Delete this success story?')) return;
                              try {
                                const token = getAuthToken();
                                await axios.delete(`${API}/success-stories/${story.id}`, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                setSuccessStories(prev => prev.filter(s => s.id !== story.id));
                                toast.success('Story deleted');
                              } catch (error) {
                                toast.error('Failed to delete story');
                              }
                            }}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {successStories.length === 0 && (
                  <div className="border border-border rounded-xl p-12 text-center bg-white">
                    <Award className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                    <p className="text-muted-foreground">No success stories yet. Add your first hired candidate!</p>
                  </div>
                )}
              </div>
            </LazyTabContent>
          )}

          {/* Email Templates Tab Content */}
          {(currentUser?.role === 'admin' || currentUser?.role === 'user' || currentUser?.role === 'crm_user') && (
            <LazyTabContent value="emails" activeTab={activeTab} className="min-h-[500px]">
              <div className="flex gap-4" style={{ minHeight: '500px' }}>
                {/* Left Sidebar Navigation */}
                <div className="w-48 flex-shrink-0 bg-white border border-slate-200 rounded-lg p-2 space-y-1">
                  <button
                    onClick={() => {
                      setManualEmailData({ to: '', subject: '', body: '', applicantName: '', isHtml: false });
                      setEmailActiveSection('compose');
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      emailActiveSection === 'compose' 
                        ? 'bg-blue-50 text-blue-700' 
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <Send className="h-4 w-4" />
                    Compose
                  </button>
                  <button
                    onClick={() => setEmailActiveSection('inbox')}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      emailActiveSection === 'inbox' 
                        ? 'bg-blue-50 text-blue-700' 
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                    data-testid="email-inbox-nav"
                  >
                    <Inbox className="h-4 w-4" />
                    Inbox
                  </button>
                  <button
                    onClick={() => setEmailActiveSection('templates')}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      emailActiveSection === 'templates' 
                        ? 'bg-blue-50 text-blue-700' 
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <FileText className="h-4 w-4" />
                    Templates
                    <span className="ml-auto text-xs bg-slate-200 px-1.5 py-0.5 rounded">{emailTemplates.length}</span>
                  </button>
                  <button
                    onClick={() => setEmailActiveSection('sent')}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      emailActiveSection === 'sent' 
                        ? 'bg-blue-50 text-blue-700' 
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <Inbox className="h-4 w-4" />
                    Sent
                    <span className="ml-auto text-xs bg-slate-200 px-1.5 py-0.5 rounded">{emailLogs.length}</span>
                  </button>
                  <button
                    onClick={() => setEmailActiveSection('mailboxes')}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      emailActiveSection === 'mailboxes' 
                        ? 'bg-blue-50 text-blue-700' 
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                    data-testid="email-mailboxes-nav"
                  >
                    <Mail className="h-4 w-4" />
                    Mailboxes
                  </button>
                  <button
                    onClick={() => setEmailActiveSection('settings')}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      emailActiveSection === 'settings' 
                        ? 'bg-blue-50 text-blue-700' 
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <Settings className="h-4 w-4" />
                    Settings
                  </button>
                </div>

                {/* Main Content Area */}
                <div className="flex-1 bg-white border border-slate-200 rounded-lg">
                  {/* Compose Section */}
                  {emailActiveSection === 'compose' && (
                    <div className="p-5 space-y-4 pb-8">
                      <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                        <h2 className="text-lg font-semibold text-slate-800">Compose Email</h2>
                      </div>
                      
                      {/* Single/Bulk Email Toggle */}
                      <div className="flex gap-2 border-b border-slate-200 pb-3" data-testid="email-type-toggle">
                        <button
                          onClick={() => setManualEmailData(prev => ({ ...prev, emailType: 'single' }))}
                          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                            (manualEmailData.emailType || 'single') === 'single'
                              ? 'bg-blue-100 text-blue-700 border border-blue-300'
                              : 'text-slate-500 hover:bg-slate-100'
                          }`}
                          data-testid="single-email-tab"
                        >
                          Single Email
                        </button>
                        <button
                          onClick={() => setManualEmailData(prev => ({ ...prev, emailType: 'bulk' }))}
                          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                            manualEmailData.emailType === 'bulk'
                              ? 'bg-blue-100 text-blue-700 border border-blue-300'
                              : 'text-slate-500 hover:bg-slate-100'
                          }`}
                          data-testid="bulk-email-tab"
                        >
                          Bulk Email
                        </button>
                      </div>

                      <div className="max-w-2xl space-y-4">
                        {/* From (default no-reply + connected mailboxes) */}
                        {(manualEmailData.emailType || 'single') === 'single' && (
                          <div>
                            <Label className="text-sm">From</Label>
                            <Select value={selectedEmailSender} onValueChange={setSelectedEmailSender}>
                              <SelectTrigger className="mt-1" data-testid="compose-from-select">
                                <SelectValue placeholder="Officium Connect (no-reply)" />
                              </SelectTrigger>
                              <SelectContent>
                                {(emailSenders.length ? emailSenders : [{ id: 'default', label: 'Officium Connect (no-reply)' }]).map(sender => (
                                  <SelectItem key={sender.id} value={sender.id}>{sender.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-slate-400 mt-1">
                              {selectedEmailSender === 'default'
                                ? 'Uses the existing no-reply sender and templates - unchanged.'
                                : 'Sends through this mailbox\u2019s own SMTP and appends its signature. Replies come back to its inbox.'}
                            </p>
                          </div>
                        )}
                        {/* Single Email Fields */}
                        {(manualEmailData.emailType || 'single') === 'single' ? (
                          <>
                            <div>
                              <Label className="text-sm">To *</Label>
                              <Input
                                placeholder="recipient@example.com"
                                value={manualEmailData.to}
                                onChange={(e) => setManualEmailData(prev => ({...prev, to: e.target.value}))}
                                className="mt-1"
                                data-testid="compose-to-input"
                              />
                            </div>
                            <div>
                              <Label className="text-sm">Recipient Name</Label>
                              <Input
                                placeholder="John Doe"
                                value={manualEmailData.applicantName}
                                onChange={(e) => setManualEmailData(prev => ({...prev, applicantName: e.target.value}))}
                                className="mt-1"
                                data-testid="compose-recipient-name"
                              />
                            </div>
                          </>
                        ) : (
                          /* Bulk Email - Multiple Recipients */
                          <div>
                            <Label className="text-sm">Recipients * (one email per line)</Label>
                            <Textarea 
                              value={manualEmailData.bulkRecipients || ''} 
                              onChange={(e) => setManualEmailData(prev => ({...prev, bulkRecipients: e.target.value}))}
                              placeholder="email1@example.com&#10;email2@example.com&#10;email3@example.com"
                              rows={4}
                              className="mt-1 font-mono text-sm"
                              data-testid="compose-bulk-recipients"
                            />
                            <p className="text-xs text-slate-500 mt-1">
                              {manualEmailData.bulkRecipients 
                                ? `${manualEmailData.bulkRecipients.split('\n').filter(e => e.trim()).length} recipients` 
                                : 'Enter email addresses, one per line'}
                            </p>
                          </div>
                        )}
                        
                        <div>
                          <Label className="text-sm">Subject *</Label>
                          <Input
                            placeholder="Email subject..."
                            value={manualEmailData.subject}
                            onChange={(e) => setManualEmailData(prev => ({...prev, subject: e.target.value}))}
                            className="mt-1"
                            data-testid="compose-subject-input"
                          />
                        </div>
                        <div>
                          <Label className="text-sm">Message *</Label>
                          <Textarea
                            placeholder={manualEmailData.isHtml 
                              ? "Enter HTML content... e.g., <p>Hello,</p><p>Your message here.</p>"
                              : "Write your message..."}
                            value={manualEmailData.body}
                            onChange={(e) => setManualEmailData(prev => ({...prev, body: e.target.value}))}
                            rows={10}
                            className={`mt-1 ${manualEmailData.isHtml ? 'font-mono text-sm' : ''}`}
                            data-testid="compose-body-input"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id="isHtml"
                            checked={manualEmailData.isHtml}
                            onCheckedChange={(checked) => setManualEmailData(prev => ({...prev, isHtml: checked}))}
                            data-testid="compose-html-checkbox"
                          />
                          <Label htmlFor="isHtml" className="text-sm">Send as HTML</Label>
                        </div>
                        
                        {/* Preview Section */}
                        {manualEmailData.body && (
                          <div className="space-y-2">
                            <Label className="text-sm text-slate-500">Preview</Label>
                            <div className="border border-slate-200 rounded-lg p-4 bg-slate-50 max-h-40 overflow-y-auto">
                              <div 
                                className="prose prose-sm max-w-none"
                                dangerouslySetInnerHTML={{ 
                                  __html: manualEmailData.isHtml 
                                    ? manualEmailData.body 
                                    : manualEmailData.body.replace(/\n/g, '<br>') 
                                }}
                              />
                            </div>
                          </div>
                        )}
                        
                        <Button 
                          onClick={handleSendManualEmail}
                          disabled={sendingManualEmail || 
                            ((manualEmailData.emailType || 'single') === 'single' ? !manualEmailData.to : !manualEmailData.bulkRecipients?.trim()) || 
                            !manualEmailData.subject || !manualEmailData.body}
                          className="bg-blue-600 hover:bg-blue-700"
                          data-testid="compose-send-btn"
                        >
                          {sendingManualEmail ? (
                            <><div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" /> Sending...</>
                          ) : (
                            <>
                              <Send className="h-4 w-4 mr-2" />
                              {manualEmailData.emailType === 'bulk' 
                                ? `Send to ${(manualEmailData.bulkRecipients || '').split('\n').filter(e => e.trim()).length} Recipients`
                                : 'Send Email'}
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Inbox (connected mailboxes via IMAP) */}
                  {emailActiveSection === 'inbox' && (
                    <MailboxInbox getAuthToken={getAuthToken} />
                  )}

                  {/* Connected Mailboxes */}
                  {emailActiveSection === 'mailboxes' && (
                    <MailboxManager getAuthToken={getAuthToken} onMailboxesChanged={loadEmailSenders} />
                  )}

                  {/* Templates Section */}
                  {emailActiveSection === 'templates' && (
                    <div className="p-5 space-y-4">
                      <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                        <h2 className="text-lg font-semibold text-slate-800">Email Templates</h2>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline"
                            size="sm"
                            onClick={handleSeedDefaultTemplates}
                            className="text-xs h-8"
                          >
                            Load Defaults
                          </Button>
                          <Dialog open={isTemplateDialogOpen} onOpenChange={setIsTemplateDialogOpen}>
                            <DialogTrigger asChild>
                              <Button 
                                size="sm"
                                className="bg-blue-600 hover:bg-blue-700 h-8"
                                onClick={() => {
                                  setEditingTemplate(null);
                                  setNewTemplate({ status_trigger: '', subject: '', body: '', is_active: true });
                                  setUseCustomStatus(false);
                                  setCustomStatusInput('');
                                }}
                              >
                                <Plus className="h-3.5 w-3.5 mr-1" />
                                New Template
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg">
                              <DialogHeader>
                                <DialogTitle>{editingTemplate ? 'Edit Template' : 'Create Email Template'}</DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4 py-4">
                                <div>
                                  <Label>Status Trigger *</Label>
                                  <div className="space-y-3 mt-1">
                                    {!useCustomStatus && (
                                      <select
                                        className="w-full p-2 border rounded-md text-sm"
                                        value={newTemplate.status_trigger}
                                        onChange={(e) => setNewTemplate(prev => ({...prev, status_trigger: e.target.value}))}
                                        disabled={!!editingTemplate}
                                      >
                                        <option value="">Select status...</option>
                                        {allApplicationStatuses.map(status => (
                                          <option key={status} value={status}>{status}</option>
                                        ))}
                                      </select>
                                    )}
                                    {useCustomStatus && (
                                      <Input
                                        placeholder="Enter custom status name"
                                        value={customStatusInput}
                                        onChange={(e) => {
                                          setCustomStatusInput(e.target.value);
                                          setNewTemplate(prev => ({...prev, status_trigger: e.target.value}));
                                        }}
                                        disabled={!!editingTemplate}
                                      />
                                    )}
                                    {!editingTemplate && (
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                          setUseCustomStatus(!useCustomStatus);
                                          if (!useCustomStatus) {
                                            setNewTemplate(prev => ({...prev, status_trigger: customStatusInput}));
                                          } else {
                                            setNewTemplate(prev => ({...prev, status_trigger: ''}));
                                            setCustomStatusInput('');
                                          }
                                        }}
                                        className="text-xs"
                                      >
                                        {useCustomStatus ? 'Use Existing Status' : '+ Custom Status'}
                                      </Button>
                                    )}
                                  </div>
                                </div>
                                <div>
                                  <Label>Subject *</Label>
                                  <Input
                                    placeholder="Email subject with {{name}}, {{role}}, {{company}}"
                                    value={newTemplate.subject}
                                    onChange={(e) => setNewTemplate(prev => ({...prev, subject: e.target.value}))}
                                    className="mt-1"
                                  />
                                </div>
                                <div>
                                  <Label>Email Body (HTML)</Label>
                                  <Textarea
                                    placeholder="Email body with variables..."
                                    value={newTemplate.body}
                                    onChange={(e) => setNewTemplate(prev => ({...prev, body: e.target.value}))}
                                    rows={10}
                                    className="font-mono text-sm mt-1"
                                  />
                                  <p className="text-xs text-slate-500 mt-1">
                                    Variables: {"{{name}}"}, {"{{email}}"}, {"{{role}}"}, {"{{company}}"}, {"{{status}}"}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Checkbox
                                    id="template-active"
                                    checked={newTemplate.is_active}
                                    onCheckedChange={(checked) => setNewTemplate(prev => ({...prev, is_active: checked}))}
                                  />
                                  <Label htmlFor="template-active">Active</Label>
                                </div>
                              </div>
                              <div className="flex justify-end gap-2">
                                <Button variant="outline" onClick={() => setIsTemplateDialogOpen(false)}>Cancel</Button>
                                <Button onClick={editingTemplate ? handleUpdateTemplate : handleCreateTemplate} className="bg-blue-600 hover:bg-blue-700">
                                  {editingTemplate ? 'Update' : 'Create'}
                                </Button>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </div>
                      </div>
                      
                      {/* Templates List */}
                      {emailTemplates.length === 0 ? (
                        <div className="text-center py-12">
                          <FileText className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                          <p className="text-sm text-slate-500">No templates yet</p>
                          <p className="text-xs text-slate-400 mt-1">Click "Load Defaults" to get started</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {emailTemplates.map((template) => (
                            <div key={template.id} className={`flex items-center justify-between p-3 border rounded-lg ${template.is_active ? 'border-slate-200' : 'border-slate-100 bg-slate-50 opacity-60'}`}>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <Badge className={`text-xs ${
                                    template.status_trigger === 'Applied' ? 'bg-blue-100 text-blue-700' :
                                    template.status_trigger === 'Shortlisted' ? 'bg-violet-100 text-violet-700' :
                                    template.status_trigger === 'Rejected' ? 'bg-red-100 text-red-700' :
                                    'bg-slate-100 text-slate-700'
                                  }`}>
                                    {template.status_trigger}
                                  </Badge>
                                  {template.is_active && <span className="text-xs text-emerald-600">Active</span>}
                                </div>
                                <p className="text-sm font-medium text-slate-800 truncate">{template.subject}</p>
                              </div>
                              <div className="flex gap-1 ml-2">
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handlePreviewTemplate(template.id)}>
                                  <Eye className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-slate-500" />
                                </Button>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => openEditTemplate(template)}>
                                  <Edit className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-slate-500" />
                                </Button>
                                {currentUser?.is_admin && (
                                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleDeleteTemplate(template.id)}>
                                    <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-red-500" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Sent Items Section - Gmail Style */}
                  {emailActiveSection === 'sent' && (
                    <div className="p-5 space-y-4">
                      <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                        <h2 className="text-lg font-semibold text-slate-800">Sent Emails</h2>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-slate-500">{emailLogs.length} emails</span>
                          {emailLogs.length >= 500 && (
                            <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                              Showing latest 500
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {emailLogs.length === 0 ? (
                        <div className="text-center py-12">
                          <Inbox className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                          <p className="text-sm text-slate-500">No emails sent yet</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {[...emailLogs].sort((a, b) => new Date(b.sent_at) - new Date(a.sent_at)).map((log) => (
                            <div 
                              key={log.id} 
                              className={`border rounded-lg hover:shadow-md transition-all cursor-pointer ${
                                log.send_status === 'sent' || log.status === 'sent' 
                                  ? 'border-slate-200 hover:border-blue-300' 
                                  : 'border-red-200 bg-red-50/30'
                              }`}
                              onClick={() => setViewingEmailLog(log)}
                            >
                              {/* Email Header Row */}
                              <div className="p-4">
                                <div className="flex items-start justify-between gap-4">
                                  {/* Left Section - Sender/Recipient Info */}
                                  <div className="flex items-start gap-3 flex-1 min-w-0">
                                    {/* Avatar */}
                                    <div className={`h-10 w-10 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0 ${
                                      log.send_status === 'sent' || log.status === 'sent' 
                                        ? 'bg-gradient-to-br from-blue-500 to-indigo-600' 
                                        : 'bg-gradient-to-br from-red-500 to-rose-600'
                                    }`}>
                                      {(log.sent_by || log.recipient_email || 'U').charAt(0).toUpperCase()}
                                    </div>
                                    
                                    {/* Details */}
                                    <div className="flex-1 min-w-0">
                                      {/* From/To Row */}
                                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                                        <div className="flex items-center gap-1.5">
                                          <span className="text-slate-500 font-medium">From:</span>
                                          <span className="text-slate-800 font-semibold truncate">
                                            {log.sent_by || 'System'}
                                          </span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                          <span className="text-slate-500 font-medium">To:</span>
                                          <span className="text-slate-800 truncate">
                                            {log.recipient_name || log.to_name || 'Unknown'}
                                          </span>
                                          <span className="text-slate-400 text-xs truncate">
                                            &lt;{log.recipient_email || log.to_email}&gt;
                                          </span>
                                        </div>
                                      </div>
                                      
                                      {/* Subject */}
                                      <h4 className="font-medium text-slate-900 mt-1 truncate">
                                        {log.subject}
                                      </h4>
                                      
                                      {/* Preview Text */}
                                      {log.body && (
                                        <p className="text-sm text-slate-500 mt-1 truncate">
                                          {log.body.replace(/<[^>]*>/g, '').substring(0, 100)}...
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {/* Right Section - Time & Status */}
                                  <div className="flex flex-col items-end gap-2 flex-shrink-0">
                                    {/* Timestamp */}
                                    <div className="text-xs text-slate-500 whitespace-nowrap">
                                      {formatISTFull(log.sent_at)}
                                    </div>
                                    
                                    {/* Status Badge */}
                                    {log.send_status === 'sent' || log.status === 'sent' ? (
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                                        <CheckCircle2 className="h-3 w-3" /> Delivered
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                        <X className="h-3 w-3" /> Failed
                                      </span>
                                    )}
                                    
                                    {/* Type Badge */}
                                    {(log.type || log.status_trigger) && (
                                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs ${
                                        log.status_trigger 
                                          ? 'bg-purple-100 text-purple-600' 
                                          : 'bg-slate-100 text-slate-600'
                                      }`}>
                                        {log.status_trigger 
                                          ? `Status: ${log.status_trigger}` 
                                          : log.type === 'manual' 
                                            ? 'Manual' 
                                            : log.type === 'bulk' 
                                              ? 'Bulk' 
                                              : 'Auto'}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Error Message if Failed */}
                                {(log.send_status === 'failed' || log.status === 'failed') && (log.error_message || log.error) && (
                                  <div className="mt-3 p-2 bg-red-50 rounded-lg border border-red-100">
                                    <p className="text-xs text-red-600 flex items-start gap-1.5">
                                      <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                                      <span className="truncate">{log.error_message || log.error}</span>
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Settings Section */}
                  {emailActiveSection === 'settings' && (
                    <div className="p-5 space-y-6">
                      <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                        <h2 className="text-lg font-semibold text-slate-800">Email Settings</h2>
                      </div>
                      
                      {/* Auto-send Toggle */}
                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                        <div>
                          <h3 className="font-medium text-slate-800">Auto-send on status change</h3>
                          <p className="text-sm text-slate-500 mt-0.5">Automatically send emails when application status changes</p>
                        </div>
                        <button
                          onClick={() => setSendEmailOnStatusChange(!sendEmailOnStatusChange)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${sendEmailOnStatusChange ? 'bg-blue-600' : 'bg-slate-300'}`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${sendEmailOnStatusChange ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                      </div>

                      {/* Email Logo */}
                      <div className="space-y-3">
                        <h3 className="font-medium text-slate-800">Email Logo</h3>
                        <p className="text-sm text-slate-500">Appears at the top of all outgoing emails</p>
                        
                        {emailLogoUrl ? (
                          <div className="flex items-center gap-4">
                            <div className="border rounded-lg p-3 bg-slate-50">
                              <img 
                                src={`${API.replace('/api', '')}${emailLogoUrl}`}
                                alt="Email Logo" 
                                className="h-10 max-w-[150px] object-contain"
                              />
                            </div>
                            <div className="flex gap-2">
                              <label className="cursor-pointer">
                                <input type="file" accept="image/*" onChange={handleEmailLogoUpload} className="hidden" />
                                <Button variant="outline" size="sm" asChild disabled={uploadingEmailLogo}>
                                  <span><Upload className="h-3.5 w-3.5 mr-1" /> Change</span>
                                </Button>
                              </label>
                              <Button variant="outline" size="sm" onClick={handleDeleteEmailLogo} className="text-red-600 hover:bg-red-50">
                                <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <label className="cursor-pointer inline-block">
                            <input type="file" accept="image/*" onChange={handleEmailLogoUpload} className="hidden" />
                            <div className="border-2 border-dashed border-slate-300 rounded-lg px-6 py-4 hover:border-blue-400 hover:bg-blue-50/50 transition-colors flex items-center gap-3">
                              {uploadingEmailLogo ? (
                                <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
                              ) : (
                                <>
                                  <Upload className="h-5 w-5 text-slate-400" />
                                  <span className="text-sm text-slate-600">Upload logo</span>
                                </>
                              )}
                            </div>
                          </label>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Email Preview Dialog */}
              <Dialog open={!!previewTemplate} onOpenChange={() => setPreviewTemplate(null)}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto rounded-lg">
                  <DialogHeader>
                    <DialogTitle>Email Preview</DialogTitle>
                  </DialogHeader>
                  {previewTemplate && (
                    <div className="space-y-4">
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-slate-500">Subject:</p>
                        <p className="font-medium">{previewTemplate.subject}</p>
                      </div>
                      <div className="border rounded-lg p-4">
                        <div dangerouslySetInnerHTML={{ __html: previewTemplate.body }} />
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>

              {/* View Email Log Dialog */}
              <Dialog open={!!viewingEmailLog} onOpenChange={() => setViewingEmailLog(null)}>
                <DialogContent className="max-w-2xl rounded-lg max-h-[85vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Mail className="h-5 w-5 text-blue-600" />
                      Email Details
                    </DialogTitle>
                  </DialogHeader>
                  {viewingEmailLog && (
                    <div className="space-y-5">
                      {/* Status Banner */}
                      <div className={`p-3 rounded-lg flex items-center justify-between ${
                        viewingEmailLog.send_status === 'sent' || viewingEmailLog.status === 'sent'
                          ? 'bg-emerald-50 border border-emerald-200'
                          : 'bg-red-50 border border-red-200'
                      }`}>
                        <div className="flex items-center gap-2">
                          {viewingEmailLog.send_status === 'sent' || viewingEmailLog.status === 'sent' ? (
                            <>
                              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                              <span className="font-medium text-emerald-700">Email Delivered Successfully</span>
                            </>
                          ) : (
                            <>
                              <X className="h-5 w-5 text-red-600" />
                              <span className="font-medium text-red-700">Email Delivery Failed</span>
                            </>
                          )}
                        </div>
                        {viewingEmailLog.type && (
                          <span className="text-xs px-2 py-1 bg-white rounded-full border">
                            {viewingEmailLog.type === 'manual' ? 'Manual Email' : viewingEmailLog.type === 'bulk' ? 'Bulk Email' : 'Automated Email'}
                          </span>
                        )}
                      </div>
                      
                      {/* From/To/Date Info */}
                      <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                        <div className="flex items-start gap-3">
                          <span className="text-xs font-medium text-slate-500 w-16 pt-0.5">From:</span>
                          <div>
                            <p className="font-semibold text-slate-800">{viewingEmailLog.sent_by || 'System'}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <span className="text-xs font-medium text-slate-500 w-16 pt-0.5">To:</span>
                          <div>
                            <p className="font-semibold text-slate-800">
                              {viewingEmailLog.recipient_name || viewingEmailLog.to_name || 'Unknown'}
                            </p>
                            <p className="text-sm text-slate-600">
                              {viewingEmailLog.recipient_email || viewingEmailLog.to_email}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <span className="text-xs font-medium text-slate-500 w-16 pt-0.5">Date:</span>
                          <div>
                            <p className="font-medium text-slate-800">{formatISTFull(viewingEmailLog.sent_at)}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <span className="text-xs font-medium text-slate-500 w-16 pt-0.5">Subject:</span>
                          <div>
                            <p className="font-semibold text-slate-900">{viewingEmailLog.subject}</p>
                          </div>
                        </div>
                        {viewingEmailLog.status_trigger && (
                          <div className="flex items-start gap-3">
                            <span className="text-xs font-medium text-slate-500 w-16 pt-0.5">Trigger:</span>
                            <div>
                              <span className="text-sm px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                                {viewingEmailLog.status_trigger}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Email Body */}
                      {viewingEmailLog.body && (
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Email Content</p>
                          <div className="border rounded-lg p-4 bg-white max-h-[300px] overflow-y-auto">
                            <div 
                              className="prose prose-sm max-w-none"
                              dangerouslySetInnerHTML={{ __html: viewingEmailLog.body }}
                            />
                          </div>
                        </div>
                      )}
                      
                      {/* Error Message if Failed */}
                      {(viewingEmailLog.error_message || viewingEmailLog.error) && (
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-red-500 uppercase tracking-wider">Error Details</p>
                          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-sm text-red-700">{viewingEmailLog.error_message || viewingEmailLog.error}</p>
                          </div>
                        </div>
                      )}
                      
                      {/* Additional Info */}
                      {(viewingEmailLog.application_id || viewingEmailLog.resend_id) && (
                        <div className="flex flex-wrap gap-4 pt-3 border-t text-xs text-slate-500">
                          {viewingEmailLog.application_id && (
                            <span>Application ID: {viewingEmailLog.application_id}</span>
                          )}
                          {viewingEmailLog.resend_id && (
                            <span>Email ID: {viewingEmailLog.resend_id}</span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </LazyTabContent>
          )}

          {/* Popup Announcements Tab Content */}
          {currentUser?.is_admin && (
            <LazyTabContent value="popup-announcements" activeTab={activeTab} className="space-y-4 md:space-y-6" data-testid="popup-announcements-content">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div>
                  <h2 className="font-heading text-xl md:text-3xl font-bold tracking-tight mb-1 bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                    Popup Announcements
                  </h2>
                  <p className="text-sm text-muted-foreground">Create popups that appear to candidates and lead specialists</p>
                </div>
                <Button 
                  onClick={() => setIsPopupAnnouncementDialogOpen(true)}
                  className="rounded-xl bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700"
                  data-testid="create-popup-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Popup
                </Button>
              </div>

              <div className="grid gap-4">
                {popupAnnouncements.length === 0 ? (
                  <Card className="border-0 shadow-lg">
                    <CardContent className="p-12 text-center">
                      <Bell className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                      <p className="text-lg text-gray-600">No popup announcements yet</p>
                      <p className="text-sm text-gray-400 mt-1">Create announcements to notify candidates and lead specialists</p>
                    </CardContent>
                  </Card>
                ) : (
                  popupAnnouncements.map((announcement) => (
                    <Card key={announcement.id} className={`border-0 shadow-lg overflow-hidden ${!announcement.is_active ? 'opacity-60' : ''}`} data-testid={`popup-card-${announcement.id}`}>
                      <CardContent className="p-0">
                        <div className="flex flex-col md:flex-row">
                          {/* Image thumbnail */}
                          {announcement.image_url && (
                            <div className="w-full md:w-40 h-32 md:h-auto flex-shrink-0">
                              <img
                                src={announcement.image_url.startsWith('http') ? announcement.image_url : `${API.replace('/api', '')}${announcement.image_url}`}
                                alt={announcement.title}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          )}
                          <div className="flex-1 p-5">
                            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                  <h3 className="font-semibold text-lg">{announcement.title}</h3>
                                  <Badge className={
                                    announcement.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                                    announcement.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                                    'bg-blue-100 text-blue-700'
                                  }>
                                    {announcement.priority}
                                  </Badge>
                                  {announcement.is_active ? (
                                    <Badge className="bg-green-100 text-green-700">Active</Badge>
                                  ) : (
                                    <Badge className="bg-gray-100 text-gray-700">Inactive</Badge>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{announcement.content}</p>
                                <div className="flex flex-wrap items-center gap-2 mt-3">
                                  {announcement.button_link && (
                                    <Badge variant="outline" className="text-xs">
                                      <LinkIcon className="h-3 w-3 mr-1" />
                                      {announcement.button_text || 'Learn More'}
                                    </Badge>
                                  )}
                                  {(announcement.start_date || announcement.end_date) && (
                                    <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700">
                                      <Calendar className="h-3 w-3 mr-1" />
                                      {announcement.start_date && `From ${new Date(announcement.start_date).toLocaleDateString()}`}
                                      {announcement.start_date && announcement.end_date && ' - '}
                                      {announcement.end_date && `Until ${new Date(announcement.end_date).toLocaleDateString()}`}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleToggleAnnouncementStatus(announcement.id, announcement.is_active, announcement)}
                                  className={announcement.is_active ? 'text-red-600 border-red-300' : 'text-green-600 border-green-300'}
                                  data-testid={`toggle-popup-${announcement.id}`}
                                >
                                  {announcement.is_active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDeleteAnnouncement(announcement.id)}
                                  className="text-red-600 border-red-300 hover:bg-red-50"
                                  data-testid={`delete-popup-${announcement.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </LazyTabContent>
          )}

          {/* Pending Employers Tab Content */}
          {currentUser?.is_admin && (
            <LazyTabContent value="pending-employers" activeTab={activeTab} className="space-y-4 md:space-y-6" data-testid="pending-employers-content">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div>
                  <h2 className="font-heading text-xl md:text-3xl font-bold tracking-tight mb-1 bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
                    Pending Employer Approvals
                  </h2>
                  <p className="text-sm text-muted-foreground">Review and approve employer accounts</p>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-teal-100 rounded-full">
                  <Shield className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-teal-600" />
                  <span className="text-sm font-medium text-teal-700">{pendingEmployers.length} Pending</span>
                </div>
              </div>

              <div className="grid gap-4">
                {pendingEmployers.length === 0 ? (
                  <Card className="border-0 shadow-lg">
                    <CardContent className="p-12 text-center">
                      <CheckCircle2 className="h-16 w-16 mx-auto text-green-300 mb-4" />
                      <p className="text-lg text-gray-600">No pending employer approvals</p>
                      <p className="text-sm text-gray-400 mt-1">All employer accounts have been reviewed</p>
                    </CardContent>
                  </Card>
                ) : (
                  pendingEmployers.map((employer) => (
                    <Card key={employer.id} className="border-0 shadow-lg hover:shadow-xl transition-shadow" data-testid={`employer-card-${employer.id}`}>
                      <CardContent className="p-5">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-lg">{employer.name}</h3>
                              <Badge className="bg-amber-100 text-amber-700">Pending Approval</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">{employer.email}</p>
                            <div className="flex flex-wrap gap-2 mt-2">
                              <Badge variant="outline" className="text-xs">
                                <Phone className="h-3 w-3 mr-1" />
                                {employer.phone}
                              </Badge>
                              <Badge variant="outline" className="text-xs">
                                <Calendar className="h-3 w-3 mr-1" />
                                Registered: {new Date(employer.created_at).toLocaleDateString()}
                              </Badge>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              onClick={() => handleApproveEmployer(employer.id)}
                              className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                              data-testid={`approve-employer-${employer.id}`}
                            >
                              <CheckCircle2 className="h-4 w-4 mr-2" />
                              Approve
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => handleRejectEmployer(employer.id)}
                              className="text-red-600 border-red-300 hover:bg-red-50"
                              data-testid={`reject-employer-${employer.id}`}
                            >
                              <X className="h-4 w-4 mr-2" />
                              Reject
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </LazyTabContent>
          )}

          {/* Site Settings and Analytics Tab Content - Admin Only */}
          {currentUser?.is_admin && (
            <>
            {/* User Management Tab */}
            <LazyTabContent value="user-management" activeTab={activeTab} className="space-y-4 md:space-y-6" data-testid="user-management-content">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div>
                  <h2 className="font-heading text-xl md:text-3xl font-bold tracking-tight mb-1 bg-gradient-to-r from-indigo-600 to-blue-600 bg-clip-text text-transparent">
                    User Management
                  </h2>
                  <p className="text-sm text-muted-foreground">Manage all users, roles and permissions</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={fetchAllUsers} data-testid="user-list-refresh">
                    <Search className="h-4 w-4 mr-1" />
                    Refresh
                  </Button>
                </div>
              </div>
              
              {/* Search Bar */}
              <Card className="border shadow-md">
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row gap-3">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search by ID, name, email, phone or role..."
                        value={userSearchQuery}
                        onChange={(e) => setUserSearchQuery(e.target.value)}
                        className="pl-10"
                        data-testid="user-search-input"
                      />
                    </div>
                    <Badge variant="outline" className="self-center px-3 py-1.5" data-testid="user-list-total">
                      {filteredAllUsers.length} of {allUsers.length} users
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Users Table */}
              <Card className="border shadow-lg overflow-hidden">
                <CardContent className="p-0">
                  {loadingUsers ? (
                    <div className="p-8 text-center">
                      <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
                      <p className="text-muted-foreground">Loading users...</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full max-md:block max-md:[&_thead]:hidden max-md:[&_tbody]:block max-md:[&_tr]:grid max-md:[&_tr]:grid-cols-2 max-md:[&_tr]:gap-2 max-md:[&_tr]:p-3 max-md:[&_td]:block max-md:[&_td]:min-w-0 max-md:[&_td]:p-1 max-md:[&_td:nth-child(1)]:col-span-2 max-md:[&_td:nth-child(2)]:col-span-2 max-md:[&_td:nth-child(3)]:hidden max-md:[&_p]:break-all" data-testid="users-table">
                        <thead className="bg-slate-100 dark:bg-slate-800">
                          <tr>
                            <th className="text-left p-4 font-semibold text-sm">Name</th>
                            <th className="text-left p-4 font-semibold text-sm">Email</th>
                            <th className="text-left p-4 font-semibold text-sm hidden md:table-cell">Phone</th>
                            <th className="text-left p-4 font-semibold text-sm">Role</th>
                            <th className="text-center p-4 font-semibold text-sm">Admin</th>
                            <th className="text-center p-4 font-semibold text-sm">Permissions</th>
                            <th className="text-center p-4 font-semibold text-sm">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {filteredAllUsers.slice((userListPage - 1) * 50, userListPage * 50).map((user) => (
                            <tr key={user.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors" data-testid={`user-row-${user.id}`}>
                              <td className="p-4">
                                <div className="flex items-center gap-3">
                                  <div className="h-10 w-10 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                                    {user.name?.charAt(0)?.toUpperCase() || '?'}
                                  </div>
                                  <div>
                                    <p className="font-medium">{user.name || 'N/A'}</p>
                                    <p className="text-xs text-muted-foreground capitalize">{user.approval_status || 'approved'}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="p-4">
                                <p className="text-sm font-mono">{user.email}</p>
                              </td>
                              <td className="p-4 hidden md:table-cell">
                                <p className="text-sm">{user.phone || '-'}</p>
                              </td>
                              <td className="p-4">
                                {user.email !== currentUser?.email ? (
                                  <Select 
                                    value={user.role || 'user'} 
                                    onValueChange={(val) => handleChangeUserRole(user.id, val)}
                                  >
                                    <SelectTrigger className="h-8 w-32 text-xs" data-testid={`role-select-${user.id}`}>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="user">User</SelectItem>
                                      <SelectItem value="candidate">Candidate</SelectItem>
                                      <SelectItem value="crm_user">CRM User</SelectItem>
                                      <SelectItem value="lead_specialist">Lead Specialist</SelectItem>
                                      <SelectItem value="telesales">Telesales</SelectItem>
                                      <SelectItem value="employer">Employer</SelectItem>
                                      <SelectItem value="admin">Admin</SelectItem>
                                    </SelectContent>
                                  </Select>
                                ) : (
                                  <Badge variant="outline" className="capitalize">{user.role || 'user'}</Badge>
                                )}
                              </td>
                              <td className="p-4 text-center">
                                {user.email !== currentUser?.email ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleToggleAdmin(user.id, user.is_admin)}
                                    className={user.is_admin ? 'text-green-600' : 'text-muted-foreground'}
                                    data-testid={`toggle-admin-${user.id}`}
                                  >
                                    {user.is_admin ? <ToggleRight className="h-5 w-5" /> : <ToggleLeft className="h-5 w-5" />}
                                  </Button>
                                ) : (
                                  <Badge className="bg-green-500 text-white">You</Badge>
                                )}
                              </td>
                              <td className="p-4 text-center">
                                {user.email !== currentUser?.email && !user.is_admin ? (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleOpenTabPermissions(user)}
                                    className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 h-8 px-3"
                                    data-testid={`manage-permissions-${user.id}`}
                                  >
                                    <Settings className="h-3.5 w-3.5 mr-1" />
                                    {user.tab_permissions?.length > 0 ? (
                                      <span className="text-xs">{user.tab_permissions.length} tabs</span>
                                    ) : (
                                      <span className="text-xs">Set</span>
                                    )}
                                  </Button>
                                ) : user.is_admin ? (
                                  <Badge className="bg-purple-500 text-white text-xs">All Access</Badge>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </td>
                              <td className="p-4">
                                {user.email !== currentUser?.email && (
                                  <div className="flex items-center justify-center gap-1">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleRevokeAccess(user.id)}
                                      className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                                      title="Revoke Access"
                                      data-testid={`revoke-${user.id}`}
                                    >
                                      <Ban className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDeleteUser(user.id, user.name)}
                                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                      title="Delete User"
                                      data-testid={`delete-${user.id}`}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {filteredAllUsers.length === 0 && (
                        <div className="p-8 text-center text-muted-foreground">
                          No users found matching your search.
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
              <div className="flex flex-wrap items-center justify-between gap-3" data-testid="user-list-pagination">
                <p className="text-sm text-slate-500" data-testid="user-list-page-summary">Page {userListPage} of {Math.max(1, Math.ceil(filteredAllUsers.length / 50))} · {filteredAllUsers.length} users</p>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" disabled={userListPage <= 1} onClick={() => setUserListPage(page => page - 1)} data-testid="user-list-previous">Previous</Button>
                  <Input type="number" aria-label="User list page" min={1} max={Math.max(1, Math.ceil(filteredAllUsers.length / 50))} value={userListPage} onChange={e => setUserListPage(Math.max(1, Math.min(Math.max(1, Math.ceil(filteredAllUsers.length / 50)), Number(e.target.value) || 1)))} className="h-8 w-16" data-testid="user-list-page-input" />
                  <Button variant="outline" size="sm" disabled={userListPage * 50 >= filteredAllUsers.length} onClick={() => setUserListPage(page => page + 1)} data-testid="user-list-next">Next</Button>
                </div>
              </div>
            </LazyTabContent>

            {/* Blacklist & Restrictions Tab */}
            <LazyTabContent value="blacklist-restrictions" activeTab={activeTab} className="space-y-6" data-testid="blacklist-restrictions-content">
              {/* Header Section */}
              <div className="bg-gradient-to-r from-red-50 via-rose-50/30 to-pink-50/40 rounded-2xl p-6 border border-red-100/80 shadow-sm">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center shadow-lg shadow-red-200/50">
                      <Ban className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h2 className="font-heading text-xl md:text-2xl font-bold tracking-tight text-slate-800">
                        Candidate Restrictions
                      </h2>
                      <p className="text-sm text-slate-500 mt-0.5">Manage blacklisted candidates and cooling period applications</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" onClick={() => { fetchBlacklist(); fetchCoolingPeriodApps(); }} disabled={loadingBlacklist || loadingCoolingPeriodApps}>
                      {(loadingBlacklist || loadingCoolingPeriodApps) ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RotateCcw className="h-4 w-4 mr-2" />}
                      Refresh All
                    </Button>
                    <Button onClick={() => { setBlacklistData({ email: '', name: '', reason: '' }); setIsBlacklistDialogOpen(true); }} className="bg-red-600 hover:bg-red-700">
                      <Ban className="h-4 w-4 mr-2" />
                      Add to Blacklist
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-xl bg-red-100 flex items-center justify-center">
                      <Ban className="h-6 w-6 text-red-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-800">{blacklist.filter(b => b.is_active).length}</p>
                      <p className="text-sm text-slate-500">Blacklisted Candidates</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-xl bg-amber-100 flex items-center justify-center">
                      <Clock className="h-6 w-6 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-800">{coolingPeriodApps.filter(a => a.is_active).length}</p>
                      <p className="text-sm text-slate-500">Active Cooling Periods</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-xl bg-green-100 flex items-center justify-center">
                      <CheckCircle2 className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-800">{coolingPeriodApps.filter(a => !a.is_active).length}</p>
                      <p className="text-sm text-slate-500">Expired Cooling Periods</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Blacklisted Candidates Section */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-8 w-8 rounded-lg bg-red-500 flex items-center justify-center">
                    <Ban className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-800">Blacklisted Candidates</h3>
                  <Badge className="bg-red-100 text-red-700">{blacklist.filter(b => b.is_active).length}</Badge>
                </div>
                
                <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gradient-to-r from-red-500 to-rose-600 text-white">
                          <th className="px-4 py-3 text-left font-medium">Candidate</th>
                          <th className="px-4 py-3 text-left font-medium">Reason</th>
                          <th className="px-4 py-3 text-left font-medium">Blacklisted By</th>
                          <th className="px-4 py-3 text-center font-medium">Date</th>
                          <th className="px-4 py-3 text-center font-medium">Status</th>
                          <th className="px-4 py-3 text-center font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loadingBlacklist ? (
                          <tr>
                            <td colSpan="6" className="px-4 py-8 text-center">
                              <Loader2 className="h-6 w-6 animate-spin mx-auto text-red-500" />
                              <p className="text-sm text-slate-500 mt-2">Loading blacklist...</p>
                            </td>
                          </tr>
                        ) : blacklist.filter(b => b.is_active).length === 0 ? (
                          <tr>
                            <td colSpan="6" className="px-4 py-8 text-center">
                              <div className="flex flex-col items-center">
                                <CheckCircle2 className="h-12 w-12 text-green-300 mb-2" />
                                <p className="text-slate-500">No blacklisted candidates</p>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          blacklist.filter(b => b.is_active).map((entry, idx) => (
                            <tr key={entry.id || idx} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'} hover:bg-red-50/50 transition-colors border-b border-slate-100`}>
                              <td className="px-4 py-3">
                                <div>
                                  <p className="font-medium text-slate-700">{entry.candidate_name || 'Unknown'}</p>
                                  <p className="text-xs text-slate-500">{entry.candidate_email}</p>
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <p className="text-sm text-slate-600 max-w-xs truncate" title={entry.reason}>
                                  {entry.reason}
                                </p>
                              </td>
                              <td className="px-4 py-3 text-slate-500">
                                {entry.blacklisted_by_name || entry.blacklisted_by}
                              </td>
                              <td className="px-4 py-3 text-center text-slate-500 text-xs">
                                {entry.created_at ? new Date(entry.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' }) : '-'}
                              </td>
                              <td className="px-4 py-3 text-center">
                                <Badge className="bg-red-500 text-white">Active</Badge>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleRemoveFromBlacklist(entry.id, entry.candidate_email)}
                                  className="text-green-600 border-green-200 hover:bg-green-50"
                                  title="Remove from blacklist"
                                >
                                  <Unlock className="h-3.5 w-3.5 mr-1" />
                                  Remove
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Cooling Period Applications Section */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-8 w-8 rounded-lg bg-amber-500 flex items-center justify-center">
                    <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-800">Cooling Period Applications</h3>
                  <Badge className="bg-amber-100 text-amber-700">{coolingPeriodApps.length}</Badge>
                </div>
                
                <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gradient-to-r from-amber-500 to-orange-600 text-white">
                          <th className="px-4 py-3 text-left font-medium">Candidate</th>
                          <th className="px-4 py-3 text-left font-medium">Position</th>
                          <th className="px-4 py-3 text-left font-medium">Company</th>
                          <th className="px-4 py-3 text-left font-medium">Notes</th>
                          <th className="px-4 py-3 text-center font-medium">Cooling Ends</th>
                          <th className="px-4 py-3 text-center font-medium">Days Left</th>
                          <th className="px-4 py-3 text-center font-medium">Status</th>
                          <th className="px-4 py-3 text-center font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loadingCoolingPeriodApps ? (
                          <tr>
                            <td colSpan="8" className="px-4 py-8 text-center">
                              <Loader2 className="h-6 w-6 animate-spin mx-auto text-amber-500" />
                              <p className="text-sm text-slate-500 mt-2">Loading cooling period applications...</p>
                            </td>
                          </tr>
                        ) : coolingPeriodApps.length === 0 ? (
                          <tr>
                            <td colSpan="8" className="px-4 py-8 text-center">
                              <div className="flex flex-col items-center">
                                <Clock className="h-12 w-12 text-slate-300 mb-2" />
                                <p className="text-slate-500">No applications with cooling periods</p>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          coolingPeriodApps.map((app, idx) => (
                            <tr key={app.id || idx} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'} hover:bg-amber-50/50 transition-colors border-b border-slate-100`}>
                              <td className="px-4 py-3">
                                <div>
                                  <p className="font-medium text-slate-700">{app.full_name || 'Unknown'}</p>
                                  <p className="text-xs text-slate-500">{app.email}</p>
                                  {app.phone && <p className="text-xs text-slate-400">{app.phone}</p>}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <p className="font-medium text-slate-700">{app.job_title || '-'}</p>
                              </td>
                              <td className="px-4 py-3 text-slate-600">
                                {app.company_name || '-'}
                              </td>
                              <td className="px-4 py-3">
                                <p className="text-sm text-slate-600 max-w-xs truncate" title={app.rejection_notes}>
                                  {app.rejection_notes || '-'}
                                </p>
                              </td>
                              <td className="px-4 py-3 text-center text-slate-500 text-xs">
                                {app.cooling_period_end ? new Date(app.cooling_period_end).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' }) : '-'}
                              </td>
                              <td className="px-4 py-3 text-center">
                                {app.is_active ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">
                                    <Clock className="h-3 w-3" />
                                    {app.days_remaining} days
                                  </span>
                                ) : (
                                  <span className="text-slate-400 text-xs">Expired</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-center">
                                {app.is_active ? (
                                  <Badge className="bg-amber-500 text-white">Active</Badge>
                                ) : (
                                  <Badge className="bg-green-100 text-green-700">Can Reapply</Badge>
                                )}
                              </td>
                              <td className="px-4 py-3 text-center">
                                <div className="flex items-center justify-center gap-1">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setEditCoolingPeriodDialog({ open: true, app });
                                      setNewCoolingEndDate(app.cooling_period_end ? app.cooling_period_end.split('T')[0] : '');
                                    }}
                                    className="text-blue-600 border-blue-200 hover:bg-blue-50"
                                    title="Edit cooling period"
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleRemoveCoolingPeriod(app.id, app.full_name)}
                                    className="text-green-600 border-green-200 hover:bg-green-50"
                                    title="Remove cooling period"
                                  >
                                    <Unlock className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </LazyTabContent>

            {/* Position Requests Tab */}
            <LazyTabContent value="position-requests" activeTab={activeTab} className="space-y-4 md:space-y-6" data-testid="position-requests-content">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div>
                  <h2 className="font-heading text-xl md:text-3xl font-bold tracking-tight mb-1 bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                    Position Requests
                  </h2>
                  <p className="text-sm text-muted-foreground">Manage candidate & lead specialist position requests</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={fetchPositionRequests}>
                    <Search className="h-4 w-4 mr-1" />
                    Refresh
                  </Button>
                </div>
              </div>
              
              {/* Search Bar */}
              <Card className="border shadow-md">
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row gap-3">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search by name, company, position or email..."
                        value={positionRequestSearch}
                        onChange={(e) => setPositionRequestSearch(e.target.value)}
                        className="pl-10"
                        data-testid="position-request-search"
                      />
                    </div>
                    <Badge variant="outline" className="self-center px-3 py-1.5">
                      {filteredPositionRequests.length} requests
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Requests List */}
              <Card className="border shadow-lg overflow-hidden">
                <CardContent className="p-0">
                  {loadingPositionRequests ? (
                    <div className="p-8 text-center">
                      <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
                      <p className="text-muted-foreground">Loading requests...</p>
                    </div>
                  ) : filteredPositionRequests.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground">
                      No position requests found.
                    </div>
                  ) : (
                    <div className="divide-y">
                      {filteredPositionRequests.map((req) => (
                        <div key={req.id} className="p-4 hover:bg-slate-50 transition-colors" data-testid={`position-request-${req.id}`}>
                          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h3 className="font-semibold text-lg">{req.position_name}</h3>
                                <Badge className={
                                  req.status === 'Pending' ? 'bg-yellow-100 text-yellow-700' :
                                  req.status === 'In Review' ? 'bg-blue-100 text-blue-700' :
                                  req.status === 'Approved' ? 'bg-green-100 text-green-700' :
                                  req.status === 'Rejected' ? 'bg-red-100 text-red-700' :
                                  req.status === 'Completed' ? 'bg-purple-100 text-purple-700' :
                                  'bg-gray-100 text-gray-700'
                                }>
                                  {req.status}
                                </Badge>
                              </div>
                              <p className="text-muted-foreground mb-1">
                                <Building2 className="h-4 w-4 inline mr-1" />
                                {req.company_name}
                              </p>
                              <div className="grid md:grid-cols-2 gap-2 text-sm">
                                <p><span className="text-muted-foreground">Requested by:</span> {req.candidate_name}</p>
                                <p><span className="text-muted-foreground">Email:</span> {req.requester_email}</p>
                                <p><span className="text-muted-foreground">Role:</span> <Badge variant="outline" className="capitalize">{req.requester_role}</Badge></p>
                                <p><span className="text-muted-foreground">Reason:</span> {req.reason}</p>
                              </div>
                              {req.company_link && (
                                <a href={req.company_link} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline mt-1 inline-block">
                                  <Link2 className="h-3 w-3 inline mr-1" />
                                  View Company/Job Link
                                </a>
                              )}
                              {req.cv_url && (
                                <a 
                                  href={getImageUrl(req.cv_url, API)} 
                                  target="_blank" 
                                  rel="noopener noreferrer" 
                                  className="text-sm text-green-600 hover:underline mt-1 ml-3 inline-block"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    window.open(getImageUrl(req.cv_url, API), '_blank');
                                  }}
                                >
                                  <FileText className="h-3 w-3 inline mr-1" />
                                  View CV
                                </a>
                              )}
                              {req.notes && (
                                <p className="text-sm text-muted-foreground mt-2 bg-slate-50 p-2 rounded">
                                  <strong>Notes:</strong> {req.notes}
                                </p>
                              )}
                              {req.admin_notes && (
                                <p className="text-sm mt-2 bg-amber-50 p-2 rounded text-amber-800">
                                  <strong>Admin Notes:</strong> {req.admin_notes}
                                </p>
                              )}
                              <p className="text-xs text-muted-foreground mt-2">
                                Submitted: {new Date(req.created_at).toLocaleDateString()} at {new Date(req.created_at).toLocaleTimeString()}
                              </p>
                            </div>
                            <div className="flex flex-col gap-2 md:min-w-[150px]">
                              <Select
                                value={req.status}
                                onValueChange={(val) => handleUpdatePositionRequestStatus(req.id, val)}
                              >
                                <SelectTrigger className="h-9" data-testid={`status-select-${req.id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Pending">Pending</SelectItem>
                                  <SelectItem value="In Review">In Review</SelectItem>
                                  <SelectItem value="Approved">Approved</SelectItem>
                                  <SelectItem value="Rejected">Rejected</SelectItem>
                                  <SelectItem value="Completed">Completed</SelectItem>
                                </SelectContent>
                              </Select>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  const note = window.prompt('Add admin notes:');
                                  if (note) {
                                    handleUpdatePositionRequestStatus(req.id, req.status, note);
                                  }
                                }}
                                className="text-amber-600 border-amber-300 hover:bg-amber-50"
                              >
                                <Edit className="h-4 w-4 mr-1" />
                                Add Note
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeletePositionRequest(req.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-request-${req.id}`}
                              >
                                <Trash2 className="h-4 w-4 mr-1" />
                                Delete
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </LazyTabContent>
            </>
          )}

          {/* Officium Pages - Confluence style knowledge base (replaces the old Questions tab) */}
          {hasTabAccess(currentUser, 'officium-pages') && (
            <LazyTabContent value="officium-pages" activeTab={activeTab} className="space-y-4" data-testid="officium-pages-content">
              <OfficiumPages getAuthToken={getAuthToken} currentUser={currentUser} />
            </LazyTabContent>
          )}

          {/* Question Banks Tab - For Telephonic Screening */}
          {(currentUser?.is_admin || currentUser?.role === 'crm_user') && (
            <LazyTabContent value="question-banks" activeTab={activeTab} className="space-y-4" data-testid="question-banks-content">
              <QuestionBankManager 
                getAuthToken={getAuthToken}
                companies={companies}
              />
            </LazyTabContent>
          )}

          {/* Site Settings and Analytics - Admin Only */}
          {currentUser?.is_admin && (
            <>
            <LazyTabContent value="site-settings" activeTab={activeTab} className="space-y-4 md:space-y-6" data-testid="site-settings-content">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div>
                  <h2 className="font-heading text-xl md:text-3xl font-bold tracking-tight mb-1 bg-gradient-to-r from-slate-600 to-gray-700 bg-clip-text text-transparent">
                    Site Settings
                  </h2>
                  <p className="text-sm text-muted-foreground">Configure website footer, links and branding</p>
                </div>
                <Button 
                  onClick={handleSaveFooterConfig}
                  className="bg-gradient-to-r from-slate-600 to-gray-700 hover:from-slate-700 hover:to-gray-800"
                >
                  Save All Settings
                </Button>
              </div>

              {/* Company Info */}
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-slate-600" />
                    Company Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Company Name</Label>
                      <Input
                        value={footerConfig.company_name || ''}
                        onChange={(e) => setFooterConfig({ ...footerConfig, company_name: e.target.value })}
                        placeholder="Your Company Name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Tagline</Label>
                      <Input
                        value={footerConfig.company_tagline || ''}
                        onChange={(e) => setFooterConfig({ ...footerConfig, company_tagline: e.target.value })}
                        placeholder="Company tagline"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Copyright Text</Label>
                      <Input
                        value={footerConfig.copyright_text || ''}
                        onChange={(e) => setFooterConfig({ ...footerConfig, copyright_text: e.target.value })}
                        placeholder="© 2026 Company Name. All rights reserved."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Made In Text</Label>
                      <Input
                        value={footerConfig.made_in_text || ''}
                        onChange={(e) => setFooterConfig({ ...footerConfig, made_in_text: e.target.value })}
                        placeholder="MADE WITH ❤️ IN INDIA"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Social Links */}
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="h-5 w-5 text-slate-600" />
                    Social Media Links
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Instagram</Label>
                      <Input
                        value={footerConfig.social_instagram || ''}
                        onChange={(e) => setFooterConfig({ ...footerConfig, social_instagram: e.target.value })}
                        placeholder="https://instagram.com/..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Facebook</Label>
                      <Input
                        value={footerConfig.social_facebook || ''}
                        onChange={(e) => setFooterConfig({ ...footerConfig, social_facebook: e.target.value })}
                        placeholder="https://facebook.com/..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>LinkedIn</Label>
                      <Input
                        value={footerConfig.social_linkedin || ''}
                        onChange={(e) => setFooterConfig({ ...footerConfig, social_linkedin: e.target.value })}
                        placeholder="https://linkedin.com/company/..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Twitter/X</Label>
                      <Input
                        value={footerConfig.social_twitter || ''}
                        onChange={(e) => setFooterConfig({ ...footerConfig, social_twitter: e.target.value })}
                        placeholder="https://twitter.com/..."
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Company Links */}
              <Card className="border-0 shadow-lg">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Link2 className="h-5 w-5 text-slate-600" />
                    Company Links
                  </CardTitle>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => setFooterConfig({
                      ...footerConfig,
                      company_links: [...(footerConfig.company_links || []), { label: '', url: '', is_active: true }]
                    })}
                  >
                    <Plus className="h-4 w-4 mr-1" /> Add Link
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {(footerConfig.company_links || []).map((link, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1 grid grid-cols-2 gap-3">
                          <Input
                            value={link.label || ''}
                            onChange={(e) => {
                              const updated = [...footerConfig.company_links];
                              updated[index] = { ...updated[index], label: e.target.value };
                              setFooterConfig({ ...footerConfig, company_links: updated });
                            }}
                            placeholder="Link Label"
                          />
                          <Input
                            value={link.url || ''}
                            onChange={(e) => {
                              const updated = [...footerConfig.company_links];
                              updated[index] = { ...updated[index], url: e.target.value };
                              setFooterConfig({ ...footerConfig, company_links: updated });
                            }}
                            placeholder="/page-url"
                          />
                        </div>
                        <Button
                          size="sm"
                          variant={link.is_active ? "default" : "outline"}
                          onClick={() => {
                            const updated = [...footerConfig.company_links];
                            updated[index] = { ...updated[index], is_active: !updated[index].is_active };
                            setFooterConfig({ ...footerConfig, company_links: updated });
                          }}
                          className={link.is_active ? "bg-green-600 hover:bg-green-700" : "text-gray-500"}
                        >
                          {link.is_active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const updated = footerConfig.company_links.filter((_, i) => i !== index);
                            setFooterConfig({ ...footerConfig, company_links: updated });
                          }}
                          className="text-red-600 border-red-300 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Support Links */}
              <Card className="border-0 shadow-lg">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <MessageCircle className="h-5 w-5 text-slate-600" />
                    Support Links
                  </CardTitle>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => setFooterConfig({
                      ...footerConfig,
                      support_links: [...(footerConfig.support_links || []), { label: '', url: '', is_active: true }]
                    })}
                  >
                    <Plus className="h-4 w-4 mr-1" /> Add Link
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {(footerConfig.support_links || []).map((link, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1 grid grid-cols-2 gap-3">
                          <Input
                            value={link.label || ''}
                            onChange={(e) => {
                              const updated = [...footerConfig.support_links];
                              updated[index] = { ...updated[index], label: e.target.value };
                              setFooterConfig({ ...footerConfig, support_links: updated });
                            }}
                            placeholder="Link Label"
                          />
                          <Input
                            value={link.url || ''}
                            onChange={(e) => {
                              const updated = [...footerConfig.support_links];
                              updated[index] = { ...updated[index], url: e.target.value };
                              setFooterConfig({ ...footerConfig, support_links: updated });
                            }}
                            placeholder="/page-url"
                          />
                        </div>
                        <Button
                          size="sm"
                          variant={link.is_active ? "default" : "outline"}
                          onClick={() => {
                            const updated = [...footerConfig.support_links];
                            updated[index] = { ...updated[index], is_active: !updated[index].is_active };
                            setFooterConfig({ ...footerConfig, support_links: updated });
                          }}
                          className={link.is_active ? "bg-green-600 hover:bg-green-700" : "text-gray-500"}
                        >
                          {link.is_active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const updated = footerConfig.support_links.filter((_, i) => i !== index);
                            setFooterConfig({ ...footerConfig, support_links: updated });
                          }}
                          className="text-red-600 border-red-300 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Legal Links */}
              <Card className="border-0 shadow-lg">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5 text-slate-600" />
                    Legal Links
                  </CardTitle>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => setFooterConfig({
                      ...footerConfig,
                      legal_links: [...(footerConfig.legal_links || []), { label: '', url: '', is_active: true }]
                    })}
                  >
                    <Plus className="h-4 w-4 mr-1" /> Add Link
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {(footerConfig.legal_links || []).map((link, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1 grid grid-cols-2 gap-3">
                          <Input
                            value={link.label || ''}
                            onChange={(e) => {
                              const updated = [...footerConfig.legal_links];
                              updated[index] = { ...updated[index], label: e.target.value };
                              setFooterConfig({ ...footerConfig, legal_links: updated });
                            }}
                            placeholder="Link Label"
                          />
                          <Input
                            value={link.url || ''}
                            onChange={(e) => {
                              const updated = [...footerConfig.legal_links];
                              updated[index] = { ...updated[index], url: e.target.value };
                              setFooterConfig({ ...footerConfig, legal_links: updated });
                            }}
                            placeholder="/page-url"
                          />
                        </div>
                        <Button
                          size="sm"
                          variant={link.is_active ? "default" : "outline"}
                          onClick={() => {
                            const updated = [...footerConfig.legal_links];
                            updated[index] = { ...updated[index], is_active: !updated[index].is_active };
                            setFooterConfig({ ...footerConfig, legal_links: updated });
                          }}
                          className={link.is_active ? "bg-green-600 hover:bg-green-700" : "text-gray-500"}
                        >
                          {link.is_active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const updated = footerConfig.legal_links.filter((_, i) => i !== index);
                            setFooterConfig({ ...footerConfig, legal_links: updated });
                          }}
                          className="text-red-600 border-red-300 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Page Content Management */}
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-slate-600" />
                    Page Content (CMS)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-4">
                    Click edit to modify page content. You can publish or unpublish pages.
                  </p>
                  <div className="space-y-2">
                    {[
                      { slug: 'privacy', label: 'Privacy Policy' },
                      { slug: 'terms', label: 'Terms of Service' },
                      { slug: 'our-story', label: 'Our Story' },
                      { slug: 'contact', label: 'Contact' },
                      { slug: 'refund', label: 'Refund Policy' },
                      { slug: 'faq', label: 'FAQ' },
                      { slug: 'pricing', label: 'Pricing' },
                      { slug: 'reviews', label: 'Reviews' }
                    ].map((page) => {
                      const existingPage = cmsPages.find(p => p.slug === page.slug);
                      const isPublished = existingPage?.is_published !== false;
                      return (
                        <div key={page.slug} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-testid={`cms-page-${page.slug}`}>
                          <div className="flex items-center gap-3">
                            <FileText className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-500" />
                            <span className="font-medium">{page.label}</span>
                            <Badge variant={isPublished ? "default" : "secondary"} className={isPublished ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}>
                              {isPublished ? 'Published' : 'Draft'}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => window.open(`/${page.slug}`, '_blank')}
                              className="text-blue-600 border-blue-300 hover:bg-blue-50"
                              data-testid={`preview-${page.slug}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openPageEditor(page.slug)}
                              className="text-violet-600 border-violet-300 hover:bg-violet-50"
                              data-testid={`edit-${page.slug}`}
                            >
                              <Edit className="h-4 w-4 mr-1" />
                              Edit
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Database Backup & Restore */}
              <section className="border-y border-amber-200 bg-amber-50/40 px-5 py-6" data-testid="backup-settings-entry">
                <h2 className="mb-2 flex items-center gap-2 text-lg font-semibold"><Archive className="h-5 w-5 text-amber-700" />Backup & data safety</h2>
                <p className="mb-4 text-sm text-slate-600">Complete CRM backups, isolated preview, storage measurements and confirmed recovery.</p>
                <Button onClick={() => window.open('/data-backup', '_blank', 'noopener,noreferrer')} data-testid="open-data-backup-settings">Open backup workspace<ExternalLink className="ml-2 h-4 w-4" /></Button>
                {(currentUser?.is_admin || currentUser?.role === 'admin') && <div className="mt-4 space-y-3">
                  <Button data-testid="export-data-btn" disabled={isExporting} onClick={handleExportData} className="bg-emerald-700 hover:bg-emerald-800"><Download className="h-4 w-4 mr-2" />{isExporting ? 'Preparing verified backup…' : 'Export All Data'}</Button>
                  <div className="flex flex-wrap gap-3 items-center"><input data-testid="import-file-input" ref={importFileInputRef} type="file" accept=".json,.jsonl" aria-label="Backup to preview" className="text-sm max-w-full min-w-0" onChange={e => setImportFile(e.target.files?.[0] || null)} /><Button data-testid="import-data-btn" variant="outline" disabled={isImporting || !importFile} onClick={handleImportData}>{isImporting ? 'Verifying backup…' : 'Import into CRM preview'}</Button></div>
                </div>}
              </section>
              {false && (
              <Card className="border-0 shadow-lg border-l-4 border-l-amber-500">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-amber-700">
                    <Archive className="h-5 w-5" />
                    Database Backup & Restore
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Export all your data as JSON or restore from a previous backup
                  </p>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Export Section */}
                  <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-green-800 flex items-center gap-2">
                          <Download className="h-4 w-4" />
                          Export Data
                        </h4>
                        <p className="text-sm text-green-600 mt-1">
                          Download all data including users, messages, jobs, applications, and settings
                        </p>
                      </div>
                      <Button
                        onClick={handleExportData}
                        disabled={isExporting}
                        className="bg-green-600 hover:bg-green-700 text-white"
                        data-testid="export-data-btn"
                      >
                        {isExporting ? (
                          <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Exporting...</>
                        ) : (
                          <><Download className="h-4 w-4 mr-2" /> Export All Data</>
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Import Section */}
                  <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                    <div className="space-y-3">
                      <div>
                        <h4 className="font-semibold text-blue-800 flex items-center gap-2">
                          <Upload className="h-4 w-4" />
                          Restore Data
                        </h4>
                        <p className="text-sm text-blue-600 mt-1">
                          Restore data from a previously exported JSON backup file
                        </p>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-3">
                        <input
                          type="file"
                          accept=".json,.jsonl"
                          ref={importFileInputRef}
                          onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                          className="flex-1 text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-100 file:text-blue-700 hover:file:bg-blue-200 cursor-pointer"
                          data-testid="import-file-input"
                        />
                        <Button
                          onClick={handleImportData}
                          disabled={isImporting || !importFile}
                          variant="outline"
                          className="border-blue-300 text-blue-700 hover:bg-blue-100"
                          data-testid="import-data-btn"
                        >
                          {isImporting ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Importing...</>
                          ) : (
                            <><Upload className="h-4 w-4 mr-2" /> Restore Backup</>
                          )}
                        </Button>
                      </div>
                      {importFile && (
                        <p className="text-xs text-blue-600">
                          Selected: {importFile.name} ({(importFile.size / 1024).toFixed(1)} KB)
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Warning Note */}
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-700">
                      <strong>Important:</strong> Regular exports are recommended to prevent data loss. 
                      Importing data will merge with existing records, not replace them entirely.
                    </div>
                  </div>
                </CardContent>
              </Card>

              )}
              {/* Save Button */}
              {/* Hero Banner Management */}
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <LucideImage className="h-5 w-5 text-slate-600" />
                    Hero Banner Management
                  </CardTitle>
                  <p className="text-sm text-slate-500">Manage banners displayed on the Lead Specialist job dashboard</p>
                </CardHeader>
                <CardContent>
                  <BannerManagement />
                </CardContent>
              </Card>

              <div className="flex justify-end pt-4">
                <Button 
                  onClick={handleSaveFooterConfig}
                  size="lg"
                  className="bg-gradient-to-r from-slate-600 to-gray-700 hover:from-slate-700 hover:to-gray-800"
                >
                  Save All Settings
                </Button>
              </div>
            </LazyTabContent>
          </>
          )}

          {/* Team Activity Reports Tab - Available to all users */}
          <LazyTabContent value="team-reports" activeTab={activeTab} className="space-y-4 md:space-y-6" data-testid="team-reports-tab-content">
            <TeamActivityReports currentUser={currentUser} getAuthToken={getAuthToken} />
          </LazyTabContent>

          {/* Analytics Tab - Admin Only */}
          {currentUser?.is_admin && (
            <>
            <LazyTabContent value="analytics" activeTab={activeTab} className="space-y-4 md:space-y-6" data-testid="analytics-content">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div>
                  <h2 className="font-heading text-xl md:text-3xl font-bold tracking-tight mb-1 bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent">
                    Analytics & Tracking
                  </h2>
                  <p className="text-sm text-muted-foreground">Monitor visitor engagement, applications, and search trends</p>
                </div>
                <Button 
                  onClick={fetchVisitorAnalytics} 
                  variant="outline"
                  disabled={loadingAnalytics}
                >
                  {loadingAnalytics ? (
                    <><div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-500 border-t-transparent mr-2" /> Refreshing...</>
                  ) : (
                    <><TrendingUp className="h-4 w-4 mr-2" /> Refresh Data</>
                  )}
                </Button>
              </div>

              {/* App Visits Dashboard Section */}
              <Card className="border-0 shadow-lg bg-white">
                <CardHeader className="pb-2">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <CardTitle className="text-lg font-semibold text-slate-800">App visits</CardTitle>
                    </div>
                    <div className="flex items-center gap-2">
                      <select
                        value={appVisitsPeriod}
                        onChange={(e) => {
                          setAppVisitsPeriod(e.target.value);
                          fetchAppVisitsAnalytics(e.target.value);
                        }}
                        className="text-sm border rounded-lg px-3 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      >
                        <option value="7d">Last 7 days</option>
                        <option value="30d">Last 30 days</option>
                        <option value="90d">Last 90 days</option>
                      </select>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => fetchAppVisitsAnalytics(appVisitsPeriod)}
                        disabled={loadingAppVisits}
                        className="h-8"
                      >
                        {loadingAppVisits ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-400 border-t-transparent" />
                        ) : (
                          <RefreshCw className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  {loadingAppVisits && !appVisitsData ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-10 w-10 border-4 border-emerald-500 border-t-transparent"></div>
                    </div>
                  ) : appVisitsData ? (
                    <div className="space-y-6">
                      {/* Overview Stats Cards */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {/* Unique Visitors */}
                        <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-slate-500 font-medium uppercase tracking-wide">Unique Visitors</span>
                            <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${appVisitsData.overview.unique_visitors_change >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                              {appVisitsData.overview.unique_visitors_change >= 0 ? '+' : ''}{appVisitsData.overview.unique_visitors_change}%
                            </span>
                          </div>
                          <p className="text-3xl font-bold text-slate-800">{appVisitsData.overview.unique_visitors.toLocaleString()}</p>
                        </div>
                        
                        {/* Page Views */}
                        <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-slate-500 font-medium uppercase tracking-wide">Page Views</span>
                            <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${appVisitsData.overview.page_views_change >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                              {appVisitsData.overview.page_views_change >= 0 ? '+' : ''}{appVisitsData.overview.page_views_change}%
                            </span>
                          </div>
                          <p className="text-3xl font-bold text-slate-800">{appVisitsData.overview.page_views.toLocaleString()}</p>
                        </div>
                        
                        {/* Visit Duration */}
                        <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-slate-500 font-medium uppercase tracking-wide">Visit Duration</span>
                            <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${appVisitsData.overview.duration_change >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                              {appVisitsData.overview.duration_change >= 0 ? '+' : ''}{appVisitsData.overview.duration_change}%
                            </span>
                          </div>
                          <p className="text-3xl font-bold text-slate-800">{appVisitsData.overview.avg_duration_formatted}</p>
                        </div>
                      </div>

                      {/* Visitors Chart */}
                      <div className="bg-slate-50 rounded-xl p-4">
                        <div className="flex items-center gap-4 mb-4">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                            <span className="text-xs text-slate-600">this period</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-slate-300"></div>
                            <span className="text-xs text-slate-600">last period</span>
                          </div>
                        </div>
                        <div className="relative h-40">
                          {/* Y-axis labels */}
                          <div className="absolute left-0 top-0 bottom-6 w-8 flex flex-col justify-between text-xs text-slate-400">
                            <span>{Math.max(...appVisitsData.chart_data.current_period.map(d => d.visitors), 1)}</span>
                            <span>{Math.round(Math.max(...appVisitsData.chart_data.current_period.map(d => d.visitors), 1) / 2)}</span>
                            <span>0</span>
                          </div>
                          {/* Chart area */}
                          <div className="ml-10 h-full flex items-end justify-between gap-1">
                            {appVisitsData.chart_data.current_period.map((day, idx) => {
                              const maxVisitors = Math.max(...appVisitsData.chart_data.current_period.map(d => d.visitors), 1);
                              const currentHeight = (day.visitors / maxVisitors) * 100;
                              const prevDay = appVisitsData.chart_data.previous_period[idx];
                              const prevHeight = prevDay ? (prevDay.visitors / maxVisitors) * 100 : 0;
                              
                              return (
                                <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                                  <div className="w-full h-32 relative flex items-end justify-center gap-0.5">
                                    {/* Previous period bar */}
                                    <div 
                                      className="w-2 bg-slate-300 rounded-t transition-all duration-300"
                                      style={{ height: `${Math.max(prevHeight, 2)}%` }}
                                    ></div>
                                    {/* Current period bar */}
                                    <div 
                                      className="w-2 bg-emerald-500 rounded-t transition-all duration-300"
                                      style={{ height: `${Math.max(currentHeight, 2)}%` }}
                                    ></div>
                                  </div>
                                  <span className="text-xs text-slate-500">{day.day_name}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>

                      {/* Bottom Grid - Pages, Sources, Countries, Devices */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {/* Top Pages */}
                        <div className="bg-white border border-slate-200 rounded-xl p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Top Pages</h4>
                            <span className="text-[10px] text-slate-400">by page views</span>
                          </div>
                          <div className="space-y-2">
                            {appVisitsData.top_pages.length === 0 ? (
                              <p className="text-xs text-slate-400 text-center py-2">No data yet</p>
                            ) : (
                              appVisitsData.top_pages.slice(0, 3).map((page, idx) => (
                                <div key={idx} className="flex items-center justify-between">
                                  <span className="text-sm text-slate-700 truncate max-w-[120px]">{page.page}</span>
                                  <span className="text-sm font-semibold text-slate-800">{page.percentage}%</span>
                                </div>
                              ))
                            )}
                          </div>
                        </div>

                        {/* Top Sources */}
                        <div className="bg-white border border-slate-200 rounded-xl p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Sources (Top 3)</h4>
                            <span className="text-[10px] text-slate-400">by visitors</span>
                          </div>
                          <div className="space-y-2">
                            {appVisitsData.top_sources.length === 0 ? (
                              <p className="text-xs text-slate-400 text-center py-2">No data yet</p>
                            ) : (
                              appVisitsData.top_sources.map((source, idx) => (
                                <div key={idx} className="flex items-center justify-between">
                                  <span className="text-sm text-slate-700 truncate max-w-[120px]">{source.source}</span>
                                  <span className="text-sm font-semibold text-slate-800">{source.percentage}%</span>
                                </div>
                              ))
                            )}
                          </div>
                        </div>

                        {/* Countries */}
                        <div className="bg-white border border-slate-200 rounded-xl p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Countries (Top 5)</h4>
                            <span className="text-[10px] text-slate-400">by visitors</span>
                          </div>
                          <div className="space-y-2">
                            {appVisitsData.top_countries.length === 0 ? (
                              <p className="text-xs text-slate-400 text-center py-2">No data yet</p>
                            ) : (
                              appVisitsData.top_countries.slice(0, 5).map((country, idx) => (
                                <div key={idx} className="flex items-center justify-between">
                                  <span className="text-sm text-slate-700 truncate max-w-[120px]">{country.country}</span>
                                  <span className="text-sm font-semibold text-slate-800">{country.percentage < 1 ? '<1' : country.percentage}%</span>
                                </div>
                              ))
                            )}
                          </div>
                        </div>

                        {/* Devices */}
                        <div className="bg-white border border-slate-200 rounded-xl p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Devices</h4>
                            <span className="text-[10px] text-slate-400">by visitors</span>
                          </div>
                          <div className="space-y-2">
                            {appVisitsData.devices.length === 0 ? (
                              <p className="text-xs text-slate-400 text-center py-2">No data yet</p>
                            ) : (
                              appVisitsData.devices.map((device, idx) => (
                                <div key={idx} className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    {device.device === 'Mobile' && <Smartphone className="h-3.5 w-3.5 text-slate-500" />}
                                    {device.device === 'Desktop' && <Monitor className="h-3.5 w-3.5 text-slate-500" />}
                                    {device.device === 'Tablet' && <Tablet className="h-3.5 w-3.5 text-slate-500" />}
                                    <span className="text-sm text-slate-700">{device.device}</span>
                                  </div>
                                  <span className="text-sm font-semibold text-slate-800">{device.percentage < 1 ? '<1' : device.percentage}%</span>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-slate-500">
                      <Globe className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                      <p className="text-sm">No visit data available yet</p>
                      <p className="text-xs text-slate-400 mt-1">Analytics will appear as visitors use the app</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {loadingAnalytics && !visitorAnalytics ? (
                <div className="flex items-center justify-center py-20">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent"></div>
                </div>
              ) : visitorAnalytics ? (
                <div className="grid gap-6">
                  {/* Overview Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-50 to-indigo-50">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center">
                            <UsersIcon className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-blue-700">{visitorAnalytics.overview.total_visits}</p>
                            <p className="text-xs text-blue-600">Total Visits</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border-0 shadow-lg bg-gradient-to-br from-green-50 to-emerald-50">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-green-500 flex items-center justify-center">
                            <TrendingUp className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-green-700">{visitorAnalytics.overview.today_visits}</p>
                            <p className="text-xs text-green-600">Today</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border-0 shadow-lg bg-gradient-to-br from-violet-50 to-purple-50">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-violet-500 flex items-center justify-center">
                            <Eye className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-violet-700">{visitorAnalytics.job_engagement.total_job_views}</p>
                            <p className="text-xs text-violet-600">Job Views</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border-0 shadow-lg bg-gradient-to-br from-amber-50 to-orange-50">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center">
                            <FileText className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-amber-700">{visitorAnalytics.applications.total_applications}</p>
                            <p className="text-xs text-amber-600">Applications</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border-0 shadow-lg bg-gradient-to-br from-rose-50 to-pink-50">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-rose-500 flex items-center justify-center">
                            <PieChart className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-rose-700">{visitorAnalytics.applications.conversion_rate}%</p>
                            <p className="text-xs text-rose-600">Conversion</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* User Registration Analytics */}
                  <Card className="border-0 shadow-lg">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <UserPlus className="h-5 w-5 text-indigo-600" />
                        User Registration & Visitor Analytics
                      </CardTitle>
                      <p className="text-sm text-muted-foreground">Track new user registrations and registered user activity</p>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        {/* Total Users */}
                        <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <UsersIcon className="h-5 w-5 text-indigo-600" />
                            <span className="text-xs text-indigo-600 font-medium">Total Users</span>
                          </div>
                          <p className="text-3xl font-bold text-indigo-700">{visitorAnalytics.user_registrations?.total_users || 0}</p>
                        </div>
                        
                        {/* New Registrations Today */}
                        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <UserPlus className="h-5 w-5 text-green-600" />
                            <span className="text-xs text-green-600 font-medium">New Today</span>
                          </div>
                          <p className="text-3xl font-bold text-green-700">{visitorAnalytics.user_registrations?.today_registrations || 0}</p>
                        </div>
                        
                        {/* Weekly Registrations */}
                        <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <TrendingUp className="h-5 w-5 text-purple-600" />
                            <span className="text-xs text-purple-600 font-medium">This Week</span>
                          </div>
                          <p className="text-3xl font-bold text-purple-700">{visitorAnalytics.user_registrations?.weekly_registrations || 0}</p>
                        </div>
                        
                        {/* Monthly Registrations */}
                        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Calendar className="h-5 w-5 text-amber-600" />
                            <span className="text-xs text-amber-600 font-medium">This Month</span>
                          </div>
                          <p className="text-3xl font-bold text-amber-700">{visitorAnalytics.user_registrations?.monthly_registrations || 0}</p>
                        </div>
                      </div>

                      {/* Registered User Visits */}
                      <div className="border-t pt-4">
                        <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                          <Eye className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-cyan-600" />
                          Registered User Visits (Logins)
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="bg-cyan-50 rounded-lg p-3 text-center">
                            <p className="text-2xl font-bold text-cyan-700">{visitorAnalytics.registered_user_visits?.total_logins || 0}</p>
                            <p className="text-xs text-cyan-600">Total Logins</p>
                          </div>
                          <div className="bg-teal-50 rounded-lg p-3 text-center">
                            <p className="text-2xl font-bold text-teal-700">{visitorAnalytics.registered_user_visits?.today_logins || 0}</p>
                            <p className="text-xs text-teal-600">Today</p>
                          </div>
                          <div className="bg-sky-50 rounded-lg p-3 text-center">
                            <p className="text-2xl font-bold text-sky-700">{visitorAnalytics.registered_user_visits?.weekly_logins || 0}</p>
                            <p className="text-xs text-sky-600">This Week</p>
                          </div>
                          <div className="bg-blue-50 rounded-lg p-3 text-center">
                            <p className="text-2xl font-bold text-blue-700">{visitorAnalytics.registered_user_visits?.unique_registered_visitors_week || 0}</p>
                            <p className="text-xs text-blue-600">Unique Users (Week)</p>
                          </div>
                        </div>
                      </div>

                      {/* Daily Registrations Chart */}
                      {visitorAnalytics.user_registrations?.daily_registrations && (
                        <div className="border-t pt-4 mt-4">
                          <h4 className="font-semibold text-sm mb-3">Daily Registrations (Last 7 Days)</h4>
                          <div className="flex items-end justify-between gap-2 h-32">
                            {visitorAnalytics.user_registrations.daily_registrations.map((day, idx) => {
                              const maxRegs = Math.max(...visitorAnalytics.user_registrations.daily_registrations.map(d => d.registrations), 1);
                              const height = (day.registrations / maxRegs) * 100;
                              return (
                                <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                                  <span className="text-xs font-medium text-indigo-600">{day.registrations}</span>
                                  <div 
                                    className="w-full rounded-t-lg bg-gradient-to-t from-indigo-500 to-purple-400 transition-all duration-500"
                                    style={{ height: `${Math.max(height, 5)}%` }}
                                  ></div>
                                  <span className="text-xs text-gray-500">{new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Daily Visits Chart */}
                  <Card className="border-0 shadow-lg">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <BarChart3 className="h-5 w-5 text-emerald-600" />
                        Daily Visits (Last 7 Days)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-end justify-between gap-2 h-40">
                        {visitorAnalytics.daily_visits.map((day, idx) => {
                          const maxVisits = Math.max(...visitorAnalytics.daily_visits.map(d => d.visits), 1);
                          const height = (day.visits / maxVisits) * 100;
                          return (
                            <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                              <span className="text-xs font-medium text-gray-600">{day.visits}</span>
                              <div 
                                className="w-full rounded-t-lg bg-gradient-to-t from-emerald-500 to-green-400 transition-all duration-500"
                                style={{ height: `${Math.max(height, 5)}%` }}
                              ></div>
                              <span className="text-xs text-gray-500">{new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}</span>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>

                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Top Viewed Jobs */}
                    <Card className="border-0 shadow-lg">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Briefcase className="h-5 w-5 text-violet-600" />
                          Top Viewed Jobs
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {visitorAnalytics.job_engagement.top_viewed_jobs.length === 0 ? (
                          <p className="text-sm text-gray-500 text-center py-4">No job views recorded yet</p>
                        ) : (
                          <div className="space-y-3">
                            {visitorAnalytics.job_engagement.top_viewed_jobs.slice(0, 5).map((job, idx) => (
                              <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                                <div className="flex items-center gap-2">
                                  <span className="w-6 h-6 rounded-full bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center">
                                    {idx + 1}
                                  </span>
                                  <span className="text-sm font-medium truncate max-w-[200px]">{job.title || 'Unknown Job'}</span>
                                </div>
                                <Badge className="bg-violet-100 text-violet-700">{job.views} views</Badge>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Application Status Breakdown */}
                    <Card className="border-0 shadow-lg">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <ClipboardList className="h-5 w-5 text-amber-600" />
                          Application Status
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {Object.entries(visitorAnalytics.applications.status_breakdown || {}).map(([status, count]) => (
                            <div key={status} className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className={`w-3 h-3 rounded-full ${
                                  status === 'Applied' ? 'bg-blue-500' :
                                  status === 'Called' ? 'bg-amber-500' :
                                  status === 'Shortlisted' ? 'bg-violet-500' :
                                  status === 'Emails Sent' ? 'bg-green-500' :
                                  status === 'Rejected' ? 'bg-red-500' :
                                  status === 'Follow-Up' ? 'bg-cyan-500' :
                                  'bg-gray-500'
                                }`}></span>
                                <span className="text-sm">{status}</span>
                              </div>
                              <span className="font-semibold">{count}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Popular Searches */}
                    <Card className="border-0 shadow-lg">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Search className="h-5 w-5 text-blue-600" />
                          Popular Searches
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {visitorAnalytics.search_analytics.popular_searches.length === 0 ? (
                          <p className="text-sm text-gray-500 text-center py-4">No searches recorded yet</p>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {visitorAnalytics.search_analytics.popular_searches.map((search, idx) => (
                              <Badge key={idx} variant="outline" className="px-3 py-1">
                                {search.query} <span className="ml-1 text-gray-400">({search.count})</span>
                              </Badge>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* User Distribution by Role */}
                    <Card className="border-0 shadow-lg">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <UsersIcon className="h-5 w-5 text-green-600" />
                          Users by Role
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {Object.entries(visitorAnalytics.user_by_role || {}).map(([role, count]) => (
                            <div key={role} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                              <span className="text-sm capitalize">{role.replace('_', ' ')}</span>
                              <Badge className="bg-green-100 text-green-700">{count}</Badge>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Page Breakdown */}
                  <Card className="border-0 shadow-lg">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Globe className="h-5 w-5 text-indigo-600" />
                        Page Views
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {visitorAnalytics.page_breakdown.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-4">No page views recorded yet. Tracking will start with visitor activity.</p>
                      ) : (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {visitorAnalytics.page_breakdown.map((page, idx) => (
                            <div key={idx} className="p-3 bg-gray-50 rounded-lg text-center">
                              <p className="text-2xl font-bold text-indigo-600">{page.visits}</p>
                              <p className="text-xs text-gray-600 truncate">{page.page || 'Home'}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              ) : (
                <Card className="border-0 shadow-lg">
                  <CardContent className="py-12 text-center">
                    <BarChart3 className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">No analytics data available yet</p>
                    <p className="text-sm text-gray-400">Data will appear as visitors interact with your site</p>
                  </CardContent>
                </Card>
              )}
            </LazyTabContent>
          </>
          )}

          {/* Partner Dashboard Tab - Admin Only */}
          {currentUser?.is_admin && (
          <TabsContent value="partner-dashboard" className="space-y-6">
            <PartnerDashboard />
          </TabsContent>
          )}

          {/* Finance Module Tab */}
          {currentUser?.is_admin && (
          <TabsContent value="finance" className="space-y-6">
            <FinanceModule />
          </TabsContent>
          )}

          {/* Integration Settings Tab - Admin Only */}
          {currentUser?.is_admin && (
          <TabsContent value="integration-settings" className="space-y-6">
            {currentUser?.is_admin && <Button variant="outline" onClick={() => window.open('/data-backup', '_blank', 'noopener,noreferrer')} data-testid="open-data-backup-integrations"><Archive className="mr-2 h-4 w-4" />Backup & storage</Button>}
            <IntegrationSettingsPanel />
          </TabsContent>
          )}

          {/* Application Hub Tab */}
          <TabsContent value="app-hub" className="space-y-6">
            <ApplicationHub />
          </TabsContent>

          {/* Activity Feed Tab - Separate from App Hub for admin-controlled access */}
          {hasTabAccess(currentUser, 'activity-feed') && (
          <TabsContent value="activity-feed" className="space-y-6">
            <GlobalActivityFeed />
          </TabsContent>
          )}

          {/* Google Sheets Tab */}
          {hasTabAccess(currentUser, 'google-sheets') && (
          <TabsContent value="google-sheets" className="space-y-6">
            <CandidateSpreadsheet />
          </TabsContent>
          )}

        </Tabs>
        )}

        {/* HR Session Editor - shared by the pencil dialogs and the global Update button */}
        <HRSessionEditDialog
          isOpen={sessionEditor.open}
          onClose={() => setSessionEditor(prev => ({ ...prev, open: false }))}
          performance={sessionEditor.performance}
          candidateStatusId={sessionEditor.performance?.candidate_status_id}
          candidateName={sessionEditor.performance?.candidate_name}
          scope={sessionEditor.scope}
          getAuthToken={getAuthToken}
          onSaved={refreshPerformances}
        />

        {/* Messaging Panel (Extracted Component) */}
        <MessagingPanel 
          isOpen={isMessagingOpen}
          onClose={setIsMessagingOpen}
          currentUser={currentUser}
          users={users}
          API={API}
          getAuthToken={getAuthToken}
        />


        {/* Task Reply Dialog */}
        <Dialog open={isReplyDialogOpen} onOpenChange={setIsReplyDialogOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MessageCircle className="h-5 w-5 text-blue-500" />
                Task Replies
              </DialogTitle>
              {selectedTaskForReply && (
                <div className="mt-2 p-3 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold text-sm break-words">{selectedTaskForReply.title}</h4>
                  {selectedTaskForReply.description && (
                    <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap break-words">{selectedTaskForReply.description}</p>
                  )}
                </div>
              )}
            </DialogHeader>
            
            {/* Replies List */}
            <ScrollArea className="flex-1 max-h-[300px] pr-4">
              <div className="space-y-3">
                {taskReplies.length === 0 ? (
                  <p className="text-center text-muted-foreground text-sm py-4">No replies yet. Be the first to reply!</p>
                ) : (
                  taskReplies.map((reply) => (
                    <div key={reply.id} className="p-3 bg-white border border-slate-200 rounded-lg shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-sm text-blue-600">{reply.user_name}</span>
                        <span className="text-xs text-muted-foreground">
                          {formatIST(reply.created_at)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">{reply.comment}</p>
                      {reply.image_url && (
                        <div className="mt-2">
                          <img 
                            src={`${API.replace('/api', '')}${reply.image_url}`}
                            alt="Reply attachment" 
                            className="max-w-full h-auto rounded-lg border border-slate-200 cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={() => window.open(`${API.replace('/api', '')}${reply.image_url}`, '_blank')}
                          />
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
            
            {/* Reply Input */}
            <div className="border-t pt-4 mt-4 space-y-3">
              {replyImagePreview && (
                <div className="relative inline-block">
                  <img 
                    src={replyImagePreview} 
                    alt="Preview" 
                    className="h-20 w-auto rounded-lg border border-slate-200"
                  />
                  <button
                    onClick={() => {
                      setReplyImageFile(null);
                      setReplyImagePreview(null);
                    }}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
              
              <div className="flex gap-2">
                <Textarea
                  value={newReply}
                  onChange={(e) => setNewReply(e.target.value)}
                  placeholder="Write your reply..."
                  className="flex-1 min-h-[60px] resize-none"
                  data-testid="task-reply-input"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <input
                  ref={replyImageInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleReplyImageSelect}
                  className="hidden"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => replyImageInputRef.current?.click()}
                  className="text-slate-600"
                  data-testid="task-reply-attach-btn"
                >
                  <LucideImage className="h-4 w-4 mr-1" />
                  Attach Image
                </Button>
                
                <Button
                  onClick={handleSubmitReply}
                  disabled={uploadingReplyImage || (!newReply.trim() && !replyImageFile)}
                  className="bg-blue-500 hover:bg-blue-600"
                  data-testid="task-reply-submit-btn"
                >
                  {uploadingReplyImage ? (
                    'Uploading...'
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-1" />
                      Send Reply
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Job Updates Side Panel */}
        {isJobPanelOpen && (
          <div className="fixed inset-0 z-50 flex justify-end">
            <div 
              className="absolute inset-0 bg-black/50" 
              onClick={() => setIsJobPanelOpen(false)}
            />
            
            <div className="relative w-full max-w-md bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
              <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-indigo-900 via-blue-900 to-indigo-900 text-white">
                <div className="flex items-center gap-2">
                  <Briefcase className="h-5 w-5" />
                  <h2 className="font-heading font-semibold text-lg">Job Updates</h2>
                  <Badge variant="secondary" className="bg-white/20 text-white">{jobUpdates.length}</Badge>
                </div>
                <Button 
                  size="icon" 
                  variant="ghost" 
                  className="h-8 w-8 text-white hover:bg-white/10"
                  onClick={() => setIsJobPanelOpen(false)}
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              <ScrollArea className="flex-1 p-4">
                {jobUpdates.length === 0 ? (
                  <div className="text-center py-12">
                    <Briefcase className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
                    <p className="text-muted-foreground">No job updates yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {jobUpdates.map((job) => (
                      <div 
                        key={job.id} 
                        className="p-4 border rounded-xl bg-white shadow-sm hover:shadow-md transition-all cursor-pointer group"
                        onClick={() => { setSelectedJob(job); fetchJobApplications(job.id); }}
                      >
                        <div className="flex items-start gap-3">
                          {job.company_logo_url ? (
                            <img 
                              src={`${API.replace('/api', '')}${job.company_logo_url}`}
                              alt={job.company_name}
                              className="h-12 w-12 rounded-lg object-cover border"
                            />
                          ) : (
                            <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-lg">
                              {job.company_name.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-base truncate">{job.position_name}</h3>
                            <p className="text-sm text-muted-foreground flex items-center gap-1">
                              <Building2 className="h-3 w-3" />
                              {job.company_name}
                            </p>
                            {job.location && (
                              <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                                <MapPin className="h-3 w-3" />
                                {job.location}
                              </p>
                            )}
                          </div>
                          <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-blue-500 transition-colors" />
                        </div>
                        {job.salary_range && (
                          <div className="mt-2 flex items-center gap-1 text-xs text-green-600 font-medium">
                            <span>{job.salary_currency || '₹'}</span>
                            {job.salary_range}
                          </div>
                        )}
                        <p className="mt-2 text-xs text-muted-foreground line-clamp-2">{job.job_description}</p>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          </div>
        )}

        {/* Job Detail View Dialog */}
        <Dialog open={!!selectedJob} onOpenChange={() => { setSelectedJob(null); setJobApplications([]); }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            {selectedJob && (
              <>
                <DialogHeader>
                  <div className="flex items-start gap-4">
                    {selectedJob.company_logo_url ? (
                      <img 
                        src={`${API.replace('/api', '')}${selectedJob.company_logo_url}`}
                        alt={selectedJob.company_name}
                        className="h-16 w-16 rounded-xl object-cover border"
                      />
                    ) : (
                      <div className="h-16 w-16 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-2xl">
                        {selectedJob.company_name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <DialogTitle className="text-xl">{selectedJob.position_name}</DialogTitle>
                      <p className="text-muted-foreground flex items-center gap-1 mt-1">
                        <Building2 className="h-4 w-4" />
                        {selectedJob.company_name}
                      </p>
                    </div>
                  </div>
                </DialogHeader>

                {selectedJob.job_image_url && (
                  <div className="my-4">
                    <img 
                      src={`${API.replace('/api', '')}${selectedJob.job_image_url}`}
                      alt="Job"
                      className="w-full h-48 object-cover rounded-lg border"
                    />
                  </div>
                )}

                <div className="space-y-4">
                  <div className="flex flex-wrap gap-4">
                    {selectedJob.location && (
                      <div className="flex items-center gap-2 text-sm">
                        <MapPin className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-muted-foreground" />
                        <span>{selectedJob.location}</span>
                      </div>
                    )}
                    {selectedJob.salary_range && (
                      <div className="flex items-center gap-2 text-sm text-green-600 font-medium">
                        <span>{selectedJob.salary_currency || '₹'}</span>
                        <span>{selectedJob.salary_range}</span>
                      </div>
                    )}
                  </div>

                  <div>
                    <h4 className="font-semibold text-sm mb-2">Job Description</h4>
                    <Linkify componentDecorator={(decoratedHref, decoratedText, key) => (
                      <a href={decoratedHref} key={key} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        {decoratedText}
                      </a>
                    )}>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">{selectedJob.job_description}</p>
                    </Linkify>
                  </div>

                  {selectedJob.requirements && (
                    <div>
                      <h4 className="font-semibold text-sm mb-2">Requirements</h4>
                      <Linkify componentDecorator={(decoratedHref, decoratedText, key) => (
                        <a href={decoratedHref} key={key} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                          {decoratedText}
                        </a>
                      )}>
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">{selectedJob.requirements}</p>
                      </Linkify>
                    </div>
                  )}

                  <div className="pt-4 border-t text-xs text-muted-foreground">
                    Posted by {selectedJob.created_by_name} on {new Date(selectedJob.created_at).toLocaleDateString()}
                  </div>

                  {/* Job Applications Section - Visible to Admin/User only */}
                  {currentUser?.role !== 'lead_specialist' && currentUser?.role !== 'candidate' && (
                    <div className="pt-4 border-t">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-sm flex items-center gap-2">
                          <UsersIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-violet-600" />
                          Applications ({jobApplications.length})
                        </h4>
                        {loadingApplications && (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-violet-600"></div>
                        )}
                      </div>
                      
                      {jobApplications.length === 0 && !loadingApplications ? (
                        <div className="text-center py-6 bg-gray-50 rounded-lg">
                          <UsersIcon className="h-8 w-8 mx-auto text-gray-300 mb-2" />
                          <p className="text-sm text-muted-foreground">No applications yet</p>
                        </div>
                      ) : (
                        <div className="space-y-3 max-h-64 overflow-y-auto">
                          {jobApplications.map((app) => (
                            <div 
                              key={app.id} 
                              className="p-3 bg-gradient-to-r from-violet-50 to-purple-50 rounded-lg border border-violet-100"
                              data-testid={`job-application-${app.id}`}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-slate-800 truncate">{app.full_name}</p>
                                  <p className="text-xs text-muted-foreground">{app.email}</p>
                                  {app.phone && <p className="text-xs text-muted-foreground flex items-center gap-1"><Phone className="h-3 w-3" />{app.phone}</p>}
                                </div>
                                <Select 
                                  value={app.status || 'Applied'} 
                                  onValueChange={(value) => updateApplicationStatus(app.id, value)}
                                >
                                  <SelectTrigger className="w-28 h-7 text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="Applied">Applied</SelectItem>
                                    <SelectItem value="Called">Called</SelectItem>
                                    <SelectItem value="Shortlisted">Shortlisted</SelectItem>
                                    <SelectItem value="Emails Sent">Emails Sent</SelectItem>
                                    <SelectItem value="Rejected">Rejected</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              
                              <div className="mt-2 flex flex-wrap gap-2 text-xs">
                                {app.experience_years && (
                                  <span className="bg-white px-2 py-0.5 rounded-full text-slate-600">{app.experience_years} exp</span>
                                )}
                                {app.current_company && (
                                  <span className="bg-white px-2 py-0.5 rounded-full text-slate-600">{app.current_company}</span>
                                )}
                                {app.expected_salary && (
                                  <span className="bg-green-100 px-2 py-0.5 rounded-full text-green-700">{app.expected_salary}</span>
                                )}
                              </div>

                              {app.skills && (
                                <p className="mt-2 text-xs text-muted-foreground line-clamp-2">{app.skills}</p>
                              )}

                              <div className="mt-2 flex gap-2">
                                {app.resume_url && (
                                  <a 
                                    href={getImageUrl(app.resume_url, API)} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-xs text-violet-600 hover:text-violet-700 font-medium"
                                    download
                                  >
                                    <FileText className="h-3 w-3" />
                                    Resume
                                  </a>
                                )}
                                {app.linkedin_url && (
                                  <a 
                                    href={app.linkedin_url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium"
                                  >
                                    <Link2 className="h-3 w-3" />
                                    LinkedIn
                                  </a>
                                )}
                              </div>

                              {app.agrees_to_charges && (
                                <div className="mt-2 flex items-center gap-1 text-xs text-amber-600">
                                  <CheckCircle2 className="h-3 w-3" />
                                  Agreed to service charges
                                </div>
                              )}

                              <p className="mt-2 text-xs text-muted-foreground">
                                Applied {new Date(app.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Form Builder Dialog */}
        <Dialog open={isFormBuilderOpen} onOpenChange={setIsFormBuilderOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingForm ? 'Edit Form' : 'Create New Form'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-6 mt-4">
              {/* Form Basic Info */}
              <div className="grid gap-4">
                <div>
                  <Label>Form Title *</Label>
                  <Input
                    value={newForm.title}
                    onChange={(e) => setNewForm({ ...newForm, title: e.target.value })}
                    placeholder="Enter form title"
                    data-testid="form-title-input"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={newForm.description}
                    onChange={(e) => setNewForm({ ...newForm, description: e.target.value })}
                    placeholder="Enter form description"
                    rows={2}
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Pipeline/Section Name</Label>
                    <Input
                      value={newForm.pipeline_name}
                      onChange={(e) => setNewForm({ ...newForm, pipeline_name: e.target.value })}
                      placeholder="e.g., Sales Leads, Job Applications"
                    />
                  </div>
                  <div>
                    <Label>Thank You Message</Label>
                    <Input
                      value={newForm.thank_you_message}
                      onChange={(e) => setNewForm({ ...newForm, thank_you_message: e.target.value })}
                      placeholder="Message shown after submission"
                    />
                  </div>
                </div>
                
                {/* Header Image Upload */}
                <div>
                  <Label>Header Image (for branding)</Label>
                  <div className="mt-2">
                    {newForm.header_image_id ? (
                      <div className="relative">
                        <img 
                          src={`${API}/custom-forms/header-image/${newForm.header_image_id}`}
                          alt="Header"
                          className="w-full h-32 object-cover rounded-lg"
                        />
                        <Button
                          size="sm"
                          variant="destructive"
                          className="absolute top-2 right-2"
                          onClick={() => setNewForm({ ...newForm, header_image_id: null })}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed rounded-lg p-4 text-center">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          id="header-image-upload"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            setUploadingFormHeader(true);
                            try {
                              const formData = new FormData();
                              formData.append('file', file);
                              const token = getAuthToken();
                              const res = await axios.post(`${API}/custom-forms/header-image`, formData, {
                                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                              });
                              setNewForm({ ...newForm, header_image_id: res.data.file_id });
                              toast.success('Header image uploaded');
                            } catch (error) {
                              toast.error('Failed to upload image');
                            } finally {
                              setUploadingFormHeader(false);
                            }
                          }}
                        />
                        <label htmlFor="header-image-upload" className="cursor-pointer">
                          <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                          <p className="text-sm text-gray-600">
                            {uploadingFormHeader ? 'Uploading...' : 'Click to upload header image'}
                          </p>
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Form Fields */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-base font-semibold">Form Fields</Label>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const newField = {
                          id: Date.now().toString(),
                          label: '',
                          field_type: 'section',
                          required: false,
                          description: ''
                        };
                        setNewForm({ ...newForm, fields: [...newForm.fields, newField] });
                      }}
                      className="text-white bg-gradient-to-r from-indigo-500 to-blue-500 hover:from-indigo-600 hover:to-blue-600 border-0 shadow-md"
                    >
                      <Type className="h-3 w-3 mr-1" />
                      Section
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const newField = {
                          id: Date.now().toString(),
                          label: 'Image',
                          field_type: 'form_image',
                          required: false,
                          image_id: null
                        };
                        setNewForm({ ...newForm, fields: [...newForm.fields, newField] });
                      }}
                      className="text-green-600 border-green-200 hover:bg-green-50"
                    >
                      <ImageIcon className="h-3 w-3 mr-1" />
                      Image
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const newField = {
                          id: Date.now().toString(),
                          label: 'Yes/No Question',
                          field_type: 'radio',
                          required: false,
                          options: ['Yes', 'No'],
                          placeholder: '',
                          description: ''
                        };
                        setNewForm({ ...newForm, fields: [...newForm.fields, newField] });
                      }}
                      className="text-blue-600 border-blue-200 hover:bg-blue-50"
                    >
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Yes/No
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const newField = {
                          id: Date.now().toString(),
                          label: '',
                          field_type: 'text',
                          required: false,
                          options: [],
                          placeholder: '',
                          description: ''
                        };
                        setNewForm({ ...newForm, fields: [...newForm.fields, newField] });
                      }}
                      data-testid="add-field-btn"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Field
                    </Button>
                  </div>
                </div>
                
                <DragDropContext
                  onDragEnd={(result) => {
                    if (!result.destination) return;
                    const items = Array.from(newForm.fields);
                    const [reorderedItem] = items.splice(result.source.index, 1);
                    items.splice(result.destination.index, 0, reorderedItem);
                    setNewForm({ ...newForm, fields: items });
                  }}
                >
                  <Droppable droppableId="form-fields">
                    {(provided) => (
                      <div 
                        {...provided.droppableProps} 
                        ref={provided.innerRef}
                        className="space-y-3"
                      >
                        {newForm.fields.map((field, index) => (
                          <Draggable key={field.id} draggableId={field.id} index={index}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={`${snapshot.isDragging ? 'shadow-xl ring-2 ring-indigo-400' : ''}`}
                              >
                                {/* Section Header Field */}
                                {field.field_type === 'section' ? (
                                  <Card className="border-2 border-indigo-300 bg-gradient-to-r from-indigo-50 via-blue-50 to-indigo-50 shadow-md">
                                    <CardContent className="p-4">
                                      <div className="flex items-center gap-2 mb-3">
                                        <div {...provided.dragHandleProps} className="cursor-grab hover:bg-indigo-100 p-1.5 rounded-lg bg-white/50">
                                          <GripVertical className="h-5 w-5 text-indigo-400" />
                                        </div>
                                        <div className="flex items-center gap-2 px-3 py-1.5 bg-indigo-500 rounded-lg">
                                          <Type className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" />
                                          <span className="text-xs font-bold text-white uppercase tracking-wide">Section Header</span>
                                        </div>
                                        <div className="flex-1" />
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                          onClick={() => {
                                            const updated = newForm.fields.filter((_, i) => i !== index);
                                            setNewForm({ ...newForm, fields: updated });
                                          }}
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </div>
                                      <div className="space-y-3 pl-10">
                                        <div>
                                          <Label className="text-xs font-semibold text-indigo-700 mb-1 block">Section Title</Label>
                                          <Input
                                            value={field.label}
                                            onChange={(e) => {
                                              const updated = [...newForm.fields];
                                              updated[index].label = e.target.value;
                                              setNewForm({ ...newForm, fields: updated });
                                            }}
                                            placeholder="e.g., Personal Information, Contact Details"
                                            className="border-indigo-200 bg-white font-semibold text-indigo-900 focus:border-indigo-400 focus:ring-indigo-400"
                                          />
                                        </div>
                                        <div>
                                          <Label className="text-xs font-semibold text-indigo-700 mb-1 block">Description (Optional)</Label>
                                          <Input
                                            value={field.description || ''}
                                            onChange={(e) => {
                                              const updated = [...newForm.fields];
                                              updated[index].description = e.target.value;
                                              setNewForm({ ...newForm, fields: updated });
                                            }}
                                            placeholder="Brief description of this section"
                                            className="border-indigo-200 bg-white text-sm focus:border-indigo-400 focus:ring-indigo-400"
                                          />
                                        </div>
                                      </div>
                                    </CardContent>
                                  </Card>
                                ) : field.field_type === 'form_image' ? (
                                  /* In-Form Image Field */
                                  <Card className="border-2 border-green-200 bg-green-50/50">
                                    <CardContent className="p-3">
                                      <div className="flex items-start gap-3">
                                        <div {...provided.dragHandleProps} className="cursor-grab hover:bg-green-100 p-1 rounded mt-1">
                                          <GripVertical className="h-5 w-5 text-green-400" />
                                        </div>
                                        <ImageIcon className="h-5 w-5 text-green-500 mt-1" />
                                        <div className="flex-1">
                                          {field.image_id ? (
                                            <div className="relative">
                                              <img 
                                                src={`${API}/custom-forms/header-image/${field.image_id}`}
                                                alt="Form"
                                                className="max-h-32 rounded-lg object-contain"
                                              />
                                              <Button
                                                size="sm"
                                                variant="destructive"
                                                className="absolute top-1 right-1 h-6 w-6 p-0"
                                                onClick={() => {
                                                  const updated = [...newForm.fields];
                                                  updated[index].image_id = null;
                                                  setNewForm({ ...newForm, fields: updated });
                                                }}
                                              >
                                                <X className="h-3 w-3" />
                                              </Button>
                                            </div>
                                          ) : (
                                            <div className="border-2 border-dashed border-green-300 rounded-lg p-4 text-center bg-white">
                                              <input
                                                type="file"
                                                accept="image/*"
                                                className="hidden"
                                                id={`form-image-${field.id}`}
                                                onChange={async (e) => {
                                                  const file = e.target.files[0];
                                                  if (!file) return;
                                                  try {
                                                    const formData = new FormData();
                                                    formData.append('file', file);
                                                    const token = getAuthToken();
                                                    const res = await axios.post(`${API}/custom-forms/header-image`, formData, {
                                                      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                                                    });
                                                    const updated = [...newForm.fields];
                                                    updated[index].image_id = res.data.file_id;
                                                    setNewForm({ ...newForm, fields: updated });
                                                    toast.success('Image uploaded');
                                                  } catch (error) {
                                                    toast.error('Failed to upload image');
                                                  }
                                                }}
                                              />
                                              <label htmlFor={`form-image-${field.id}`} className="cursor-pointer">
                                                <Upload className="h-6 w-6 mx-auto text-green-400 mb-1" />
                                                <p className="text-xs text-green-600">Click to upload image</p>
                                              </label>
                                            </div>
                                          )}
                                        </div>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="text-red-500 hover:text-red-700"
                                          onClick={() => {
                                            const updated = newForm.fields.filter((_, i) => i !== index);
                                            setNewForm({ ...newForm, fields: updated });
                                          }}
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    </CardContent>
                                  </Card>
                                ) : (
                                  /* Regular Field */
                                  <Card className="border shadow-sm hover:shadow-md transition-shadow">
                                    <CardContent className="p-4">
                                      <div className="flex items-start gap-3">
                                        <div {...provided.dragHandleProps} className="cursor-grab hover:bg-gray-100 p-1 rounded mt-1">
                                          <GripVertical className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <div className="flex-1 grid gap-3">
                                          <div className="grid md:grid-cols-3 gap-3">
                                            <div className="md:col-span-2">
                                              <Label className="text-xs">Field Label *</Label>
                                              <Input
                                                value={field.label}
                                                onChange={(e) => {
                                                  const updated = [...newForm.fields];
                                                  updated[index].label = e.target.value;
                                                  setNewForm({ ...newForm, fields: updated });
                                                }}
                                                placeholder="Enter field label"
                                              />
                                            </div>
                                            <div>
                                              <Label className="text-xs">Field Type</Label>
                                              <Select
                                                value={field.field_type}
                                                onValueChange={(value) => {
                                                  const updated = [...newForm.fields];
                                                  updated[index].field_type = value;
                                                  if (['select', 'multiselect', 'checkbox', 'radio'].includes(value) && !updated[index].options?.length) {
                                                    updated[index].options = ['Option 1'];
                                                  }
                                                  setNewForm({ ...newForm, fields: updated });
                                                }}
                                              >
                                                <SelectTrigger>
                                                  <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                  <SelectItem value="text">Text</SelectItem>
                                                  <SelectItem value="email">Email</SelectItem>
                                                  <SelectItem value="phone">Phone</SelectItem>
                                                  <SelectItem value="number">Number</SelectItem>
                                                  <SelectItem value="textarea">Long Text</SelectItem>
                                                  <SelectItem value="select">Dropdown</SelectItem>
                                                  <SelectItem value="multiselect">Multi-Select</SelectItem>
                                                  <SelectItem value="checkbox">Checkboxes</SelectItem>
                                                  <SelectItem value="radio">Radio Buttons</SelectItem>
                                                  <SelectItem value="date">Date</SelectItem>
                                                  <SelectItem value="file">File Upload</SelectItem>
                                                </SelectContent>
                                              </Select>
                                            </div>
                                          </div>
                                          
                                          <div className="grid md:grid-cols-2 gap-3">
                                            <div>
                                              <Label className="text-xs">Placeholder</Label>
                                              <Input
                                                value={field.placeholder || ''}
                                                onChange={(e) => {
                                                  const updated = [...newForm.fields];
                                                  updated[index].placeholder = e.target.value;
                                                  setNewForm({ ...newForm, fields: updated });
                                                }}
                                                placeholder="Enter placeholder text"
                                              />
                                            </div>
                                            <div>
                                              <Label className="text-xs">Help Text</Label>
                                              <Input
                                                value={field.description || ''}
                                                onChange={(e) => {
                                                  const updated = [...newForm.fields];
                                                  updated[index].description = e.target.value;
                                                  setNewForm({ ...newForm, fields: updated });
                                                }}
                                                placeholder="Additional instructions"
                                              />
                                            </div>
                                          </div>
                                          
                                          {/* Options for select/checkbox/radio fields */}
                                          {['select', 'multiselect', 'checkbox', 'radio'].includes(field.field_type) && (
                                            <div className="space-y-2">
                                              <Label className="text-xs">Options</Label>
                                              <div className="space-y-2">
                                                {(field.options || []).map((option, optIdx) => (
                                                  <div key={optIdx} className="flex items-center gap-2">
                                                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-xs font-medium text-gray-500">
                                                      {optIdx + 1}
                                                    </div>
                                                    <Input
                                                      value={option}
                                                      onChange={(e) => {
                                                        const updated = [...newForm.fields];
                                                        updated[index].options[optIdx] = e.target.value;
                                                        setNewForm({ ...newForm, fields: updated });
                                                      }}
                                                      placeholder={`Option ${optIdx + 1}`}
                                                      className="flex-1"
                                                    />
                                                    <Button
                                                      type="button"
                                                      size="sm"
                                                      variant="ghost"
                                                      className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                                                      onClick={() => {
                                                        const updated = [...newForm.fields];
                                                        updated[index].options = updated[index].options.filter((_, i) => i !== optIdx);
                                                        setNewForm({ ...newForm, fields: updated });
                                                      }}
                                                      disabled={(field.options || []).length <= 1}
                                                    >
                                                      <X className="h-4 w-4" />
                                                    </Button>
                                                  </div>
                                                ))}
                                              </div>
                                              <Button
                                                type="button"
                                                size="sm"
                                                variant="outline"
                                                className="w-full border-dashed"
                                                onClick={() => {
                                                  const updated = [...newForm.fields];
                                                  if (!updated[index].options) updated[index].options = [];
                                                  updated[index].options.push(`Option ${updated[index].options.length + 1}`);
                                                  setNewForm({ ...newForm, fields: updated });
                                                }}
                                              >
                                                <Plus className="h-4 w-4 mr-1" />
                                                Add Option
                                              </Button>
                                              {field.field_type === 'radio' && (
                                                <label className="flex items-center gap-2 text-xs text-gray-600 mt-2">
                                                  <input
                                                    type="checkbox"
                                                    checked={field.allow_other || false}
                                                    onChange={(e) => {
                                                      const updated = [...newForm.fields];
                                                      updated[index].allow_other = e.target.checked;
                                                      setNewForm({ ...newForm, fields: updated });
                                                    }}
                                                    className="rounded"
                                                  />
                                                  Add "Other" option with text input
                                                </label>
                                              )}
                                              {(field.field_type === 'checkbox' || field.field_type === 'multiselect') && (
                                                <div className="flex items-center gap-4 mt-2">
                                                  <label className="flex items-center gap-2 text-xs text-gray-600">
                                                    <input
                                                      type="checkbox"
                                                      checked={field.allow_other || false}
                                                      onChange={(e) => {
                                                        const updated = [...newForm.fields];
                                                        updated[index].allow_other = e.target.checked;
                                                        setNewForm({ ...newForm, fields: updated });
                                                      }}
                                                      className="rounded"
                                                    />
                                                    Add "Other" option
                                                  </label>
                                                  <label className="flex items-center gap-2 text-xs text-gray-600">
                                                    <input
                                                      type="number"
                                                      min="0"
                                                      max={(field.options || []).length}
                                                      value={field.min_selections || 0}
                                                      onChange={(e) => {
                                                        const updated = [...newForm.fields];
                                                        updated[index].min_selections = parseInt(e.target.value) || 0;
                                                        setNewForm({ ...newForm, fields: updated });
                                                      }}
                                                      className="w-14 h-6 text-xs border rounded px-1"
                                                    />
                                                    Min selections
                                                  </label>
                                                  <label className="flex items-center gap-2 text-xs text-gray-600">
                                                    <input
                                                      type="number"
                                                      min="0"
                                                      max={(field.options || []).length}
                                                      value={field.max_selections || (field.options || []).length}
                                                      onChange={(e) => {
                                                        const updated = [...newForm.fields];
                                                        updated[index].max_selections = parseInt(e.target.value) || 0;
                                                        setNewForm({ ...newForm, fields: updated });
                                                      }}
                                                      className="w-14 h-6 text-xs border rounded px-1"
                                                    />
                                                    Max selections
                                                  </label>
                                                </div>
                                              )}
                                            </div>
                                          )}
                                          
                                          <div className="flex items-center justify-between pt-2 border-t">
                                            <label className="flex items-center gap-2 text-sm">
                                              <input
                                                type="checkbox"
                                                checked={field.required}
                                                onChange={(e) => {
                                                  const updated = [...newForm.fields];
                                                  updated[index].required = e.target.checked;
                                                  setNewForm({ ...newForm, fields: updated });
                                                }}
                                                className="rounded"
                                              />
                                              Required field
                                            </label>
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              className="text-red-500 hover:text-red-700"
                                              onClick={() => {
                                                const updated = newForm.fields.filter((_, i) => i !== index);
                                                setNewForm({ ...newForm, fields: updated });
                                              }}
                                            >
                                              <Trash2 className="h-3 w-3 mr-1" />
                                              Remove
                                            </Button>
                                          </div>
                                        </div>
                                      </div>
                                    </CardContent>
                                  </Card>
                                )}
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
                
                {newForm.fields.length === 0 && (
                  <div className="text-center py-8 border-2 border-dashed rounded-lg">
                    <ClipboardList className="h-10 w-10 mx-auto text-gray-300 mb-2" />
                    <p className="text-gray-500">No fields added yet</p>
                    <p className="text-sm text-gray-400">Click "Add Field", "Section", or "Image" to build your form</p>
                  </div>
                )}
              </div>

              {/* Save Button */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setIsFormBuilderOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={async () => {
                    if (!newForm.title.trim()) {
                      toast.error('Please enter a form title');
                      return;
                    }
                    const inputFields = newForm.fields.filter(f => !['section', 'form_image'].includes(f.field_type));
                    if (inputFields.length === 0) {
                      toast.error('Please add at least one input field');
                      return;
                    }
                    if (inputFields.some(f => !f.label.trim())) {
                      toast.error('All input fields must have a label');
                      return;
                    }
                    
                    try {
                      const token = getAuthToken();
                      const config = { headers: { Authorization: `Bearer ${token}` } };
                      
                      if (editingForm) {
                        await axios.patch(`${API}/custom-forms/${editingForm.id}`, newForm, config);
                        setCustomForms(prev => prev.map(f => 
                          f.id === editingForm.id ? { ...f, ...newForm, updated_at: new Date().toISOString() } : f
                        ));
                        toast.success('Form updated successfully');
                      } else {
                        const res = await axios.post(`${API}/custom-forms`, newForm, config);
                        setCustomForms(prev => [res.data.form, ...prev]);
                        toast.success('Form created successfully');
                      }
                      
                      setIsFormBuilderOpen(false);
                      setEditingForm(null);
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Failed to save form');
                    }
                  }}
                  className="bg-gradient-to-r from-indigo-500 to-blue-600"
                  data-testid="save-form-btn"
                >
                  {editingForm ? 'Update Form' : 'Create Form'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Manual Email Dialog */}
        <Dialog open={isManualEmailDialogOpen} onOpenChange={setIsManualEmailDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto z-[100] mt-10" data-testid="manual-email-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-blue-600" />
                Send Email
              </DialogTitle>
            </DialogHeader>
            
            {/* Email Type Tabs */}
            <div className="flex gap-2 mb-4 border-b">
              <button
                onClick={() => setManualEmailData(prev => ({ ...prev, emailType: 'single' }))}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  (manualEmailData.emailType || 'single') === 'single'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Single Email
              </button>
              <button
                onClick={() => setManualEmailData(prev => ({ ...prev, emailType: 'bulk' }))}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  manualEmailData.emailType === 'bulk'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Bulk Email
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Single Email - To Field */}
              {(manualEmailData.emailType || 'single') === 'single' ? (
                <div className="space-y-2">
                  <Label>To *</Label>
                  <Input 
                    value={manualEmailData.to} 
                    onChange={(e) => setManualEmailData({ ...manualEmailData, to: e.target.value })}
                    placeholder="recipient@example.com"
                    className={manualEmailData.applicantName !== 'Recipient' ? 'bg-gray-50' : ''}
                    disabled={manualEmailData.applicantName !== 'Recipient'}
                    data-testid="email-to-input"
                  />
                </div>
              ) : (
                /* Bulk Email - Multiple Recipients */
                <div className="space-y-2">
                  <Label>Recipients * (one email per line)</Label>
                  <Textarea 
                    value={manualEmailData.bulkRecipients || ''} 
                    onChange={(e) => setManualEmailData({ ...manualEmailData, bulkRecipients: e.target.value })}
                    placeholder="email1@example.com&#10;email2@example.com&#10;email3@example.com"
                    rows={4}
                    className="font-mono text-sm"
                    data-testid="bulk-recipients-input"
                  />
                  <p className="text-xs text-gray-500">
                    {manualEmailData.bulkRecipients ? 
                      `${manualEmailData.bulkRecipients.split('\n').filter(e => e.trim()).length} recipients` : 
                      'Enter email addresses, one per line'}
                  </p>
                </div>
              )}
              <div className="space-y-2">
                <Label>Subject *</Label>
                <Input
                  value={manualEmailData.subject}
                  onChange={(e) => setManualEmailData({ ...manualEmailData, subject: e.target.value })}
                  placeholder="Email subject"
                  data-testid="email-subject-input"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Message *</Label>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={insertHyperlink}
                      className="h-8 text-xs"
                      title="Insert Hyperlink"
                    >
                      <LinkIcon className="h-3 w-3 mr-1" />
                      Insert Link
                    </Button>
                    <label className="flex items-center gap-2 text-xs text-gray-500">
                      <input
                        type="checkbox"
                        checked={manualEmailData.isHtml}
                        onChange={(e) => setManualEmailData({ ...manualEmailData, isHtml: e.target.checked })}
                        className="rounded border-gray-300"
                      />
                      HTML Mode
                    </label>
                  </div>
                </div>
                <Textarea
                  value={manualEmailData.body}
                  onChange={(e) => setManualEmailData({ ...manualEmailData, body: e.target.value })}
                  placeholder={manualEmailData.isHtml 
                    ? "Enter HTML content... e.g., <p>Hello,</p><p>Your message here.</p><a href='https://example.com'>Click here</a>"
                    : "Type your message here...\n\nYou can use line breaks for paragraphs."}
                  rows={10}
                  className={manualEmailData.isHtml ? 'font-mono text-sm' : ''}
                  data-testid="email-body-input"
                />
                <p className="text-xs text-gray-500">
                  {manualEmailData.isHtml 
                    ? "HTML mode: You can paste formatted content with images and links. Use <a href='URL'>text</a> for links."
                    : "Plain text mode: Line breaks will be converted to paragraphs. Use 'Insert Link' to add clickable links."}
                </p>
              </div>
              
              {/* Preview Section */}
              {manualEmailData.body && (
                <div className="space-y-2">
                  <Label className="text-gray-500">Preview</Label>
                  <div className="border rounded-lg p-4 bg-gray-50 max-h-40 overflow-y-auto">
                    <div 
                      className="prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ 
                        __html: manualEmailData.isHtml 
                          ? manualEmailData.body 
                          : manualEmailData.body.replace(/\n/g, '<br>') 
                      }}
                    />
                  </div>
                </div>
              )}
              
              <div className="flex justify-end gap-3 pt-2 border-t">
                <Button variant="outline" onClick={() => setIsManualEmailDialogOpen(false)}>Cancel</Button>
                <Button
                  onClick={handleSendManualEmail}
                  disabled={sendingManualEmail || 
                    ((manualEmailData.emailType || 'single') === 'single' ? !manualEmailData.to : !manualEmailData.bulkRecipients?.trim()) || 
                    !manualEmailData.subject || !manualEmailData.body}
                  className="bg-gradient-to-r from-blue-500 to-indigo-600"
                  data-testid="send-email-btn"
                >
                  {sendingManualEmail ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      {manualEmailData.emailType === 'bulk' 
                        ? `Send to ${(manualEmailData.bulkRecipients || '').split('\n').filter(e => e.trim()).length} Recipients`
                        : 'Send Email'}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Feedback Dialog */}
        <Dialog open={isFeedbackDialogOpen} onOpenChange={setIsFeedbackDialogOpen}>
          <DialogContent className="max-w-lg" data-testid="feedback-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-purple-600" />
                Feedback for {feedbackData.applicantName}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Rating</Label>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setFeedbackData({ ...feedbackData, rating: star })}
                      className="p-1 hover:scale-110 transition-transform"
                    >
                      <Star
                        className={`h-6 w-6 ${
                          star <= feedbackData.rating 
                            ? 'text-yellow-500 fill-yellow-500' 
                            : 'text-gray-300 hover:text-yellow-300'
                        }`}
                      />
                    </button>
                  ))}
                  <span className="text-sm text-gray-500 ml-2">
                    {feedbackData.rating > 0 ? `${feedbackData.rating}/5` : 'Not rated'}
                  </span>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Feedback Notes</Label>
                <Textarea
                  value={feedbackData.feedback}
                  onChange={(e) => setFeedbackData({ ...feedbackData, feedback: e.target.value })}
                  placeholder="Enter your feedback about this candidate..."
                  rows={5}
                  data-testid="feedback-textarea"
                />
              </div>
              {/* Feedback History */}
              {feedbackData.feedback_by && (
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <p className="text-xs text-slate-500 flex items-center gap-1.5">
                    <Clock className="h-3 w-3" />
                    Last updated by <span className="font-medium text-slate-700">{feedbackData.feedback_by}</span>
                    {feedbackData.feedback_at && (
                      <>
                        {' on '}
                        <span className="font-medium text-slate-700">
                          {new Date(feedbackData.feedback_at).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric', 
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </>
                    )}
                  </p>
                </div>
              )}
              <div className="flex justify-end gap-3 pt-2 border-t">
                <Button variant="outline" onClick={() => setIsFeedbackDialogOpen(false)}>Cancel</Button>
                <Button
                  onClick={handleSaveFeedback}
                  disabled={savingFeedback || (!feedbackData.feedback && feedbackData.rating === 0)}
                  className="bg-gradient-to-r from-purple-500 to-violet-600"
                  data-testid="save-feedback-btn"
                >
                  {savingFeedback ? 'Saving...' : 'Save Feedback'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Application Dialog (Admin Only) */}
        <Dialog open={isEditApplicationDialogOpen} onOpenChange={setIsEditApplicationDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="edit-application-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit3 className="h-5 w-5 text-blue-600" />
                Edit Application Details
                {editingApplication && (
                  <Badge className="ml-2 bg-blue-100 text-blue-700">
                    {editingApplication.job_title}
                  </Badge>
                )}
              </DialogTitle>
              <DialogDescription>
                Update candidate information. Only admins can modify these details.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6 py-4">
              {/* Personal Information */}
              <div className="space-y-4">
                <h4 className="font-semibold text-slate-800 flex items-center gap-2 border-b pb-2">
                  <User className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-blue-500" />
                  Personal Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Full Name</Label>
                    <Input
                      value={editApplicationForm.full_name}
                      onChange={(e) => setEditApplicationForm({...editApplicationForm, full_name: e.target.value})}
                      placeholder="Enter full name"
                      data-testid="edit-full-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={editApplicationForm.email}
                      onChange={(e) => setEditApplicationForm({...editApplicationForm, email: e.target.value})}
                      placeholder="Enter email"
                      data-testid="edit-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      value={editApplicationForm.phone}
                      onChange={(e) => setEditApplicationForm({...editApplicationForm, phone: e.target.value})}
                      placeholder="Enter phone number"
                      data-testid="edit-phone"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Actual Contact Number (ACN)</Label>
                    <Input
                      value={editApplicationForm.actual_contact_number}
                      onChange={(e) => setEditApplicationForm({...editApplicationForm, actual_contact_number: e.target.value})}
                      placeholder="Enter actual contact number"
                      data-testid="edit-acn"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Current City/State</Label>
                    <Input
                      value={editApplicationForm.current_city_state}
                      onChange={(e) => setEditApplicationForm({...editApplicationForm, current_city_state: e.target.value})}
                      placeholder="Enter city/state"
                      data-testid="edit-city"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Aadhar Address</Label>
                    <Input
                      value={editApplicationForm.aadhar_address}
                      onChange={(e) => setEditApplicationForm({...editApplicationForm, aadhar_address: e.target.value})}
                      placeholder="Enter aadhar address"
                      data-testid="edit-aadhar"
                    />
                  </div>
                </div>
              </div>

              {/* Education */}
              <div className="space-y-4">
                <h4 className="font-semibold text-slate-800 flex items-center gap-2 border-b pb-2">
                  <GraduationCap className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-blue-500" />
                  Education
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Education Level</Label>
                    <Select 
                      value={editApplicationForm.education} 
                      onValueChange={(v) => setEditApplicationForm({...editApplicationForm, education: v})}
                    >
                      <SelectTrigger data-testid="edit-education">
                        <SelectValue placeholder="Select education" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10th">10th</SelectItem>
                        <SelectItem value="12th">12th</SelectItem>
                        <SelectItem value="Diploma">Diploma</SelectItem>
                        <SelectItem value="Graduation">Graduation</SelectItem>
                        <SelectItem value="Post Graduation">Post Graduation</SelectItem>
                        <SelectItem value="Others">Others</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {editApplicationForm.education === 'Others' && (
                    <div className="space-y-2">
                      <Label>Other Education</Label>
                      <Input
                        value={editApplicationForm.education_other}
                        onChange={(e) => setEditApplicationForm({...editApplicationForm, education_other: e.target.value})}
                        placeholder="Specify education"
                        data-testid="edit-education-other"
                      />
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>University Name</Label>
                    <Input
                      value={editApplicationForm.university_name}
                      onChange={(e) => setEditApplicationForm({...editApplicationForm, university_name: e.target.value})}
                      placeholder="Enter university name"
                      data-testid="edit-university"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>College Name</Label>
                    <Input
                      value={editApplicationForm.college_name}
                      onChange={(e) => setEditApplicationForm({...editApplicationForm, college_name: e.target.value})}
                      placeholder="Enter college name"
                      data-testid="edit-college"
                    />
                  </div>
                </div>
              </div>

              {/* Experience */}
              <div className="space-y-4">
                <h4 className="font-semibold text-slate-800 flex items-center gap-2 border-b pb-2">
                  <Briefcase className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-blue-500" />
                  Experience
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Total Experience</Label>
                    <Input
                      value={editApplicationForm.total_experience}
                      onChange={(e) => setEditApplicationForm({...editApplicationForm, total_experience: e.target.value})}
                      placeholder="e.g., 2 years"
                      data-testid="edit-experience"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Previous Company</Label>
                    <Input
                      value={editApplicationForm.previous_company_name}
                      onChange={(e) => setEditApplicationForm({...editApplicationForm, previous_company_name: e.target.value})}
                      placeholder="Enter previous company"
                      data-testid="edit-prev-company"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Previous LPA</Label>
                    <Input
                      value={editApplicationForm.previous_lpa}
                      onChange={(e) => setEditApplicationForm({...editApplicationForm, previous_lpa: e.target.value})}
                      placeholder="Enter previous salary"
                      data-testid="edit-prev-lpa"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Expected Salary</Label>
                    <Input
                      value={editApplicationForm.expected_salary}
                      onChange={(e) => setEditApplicationForm({...editApplicationForm, expected_salary: e.target.value})}
                      placeholder="Enter expected salary"
                      data-testid="edit-expected-salary"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Current Company</Label>
                    <Input
                      value={editApplicationForm.current_company}
                      onChange={(e) => setEditApplicationForm({...editApplicationForm, current_company: e.target.value})}
                      placeholder="Enter current company"
                      data-testid="edit-current-company"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Current Position</Label>
                    <Input
                      value={editApplicationForm.current_position}
                      onChange={(e) => setEditApplicationForm({...editApplicationForm, current_position: e.target.value})}
                      placeholder="Enter current position"
                      data-testid="edit-current-position"
                    />
                  </div>
                </div>
              </div>

              {/* Additional Info */}
              <div className="space-y-4">
                <h4 className="font-semibold text-slate-800 flex items-center gap-2 border-b pb-2">
                  <FileText className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-blue-500" />
                  Additional Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Skills</Label>
                    <Input
                      value={editApplicationForm.skills}
                      onChange={(e) => setEditApplicationForm({...editApplicationForm, skills: e.target.value})}
                      placeholder="Enter skills (comma separated)"
                      data-testid="edit-skills"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>How did they hear about us?</Label>
                    <Input
                      value={editApplicationForm.hear_about_us}
                      onChange={(e) => setEditApplicationForm({...editApplicationForm, hear_about_us: e.target.value})}
                      placeholder="Source"
                      data-testid="edit-source"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={editApplicationForm.notes}
                    onChange={(e) => setEditApplicationForm({...editApplicationForm, notes: e.target.value})}
                    placeholder="Add any additional notes..."
                    rows={3}
                    data-testid="edit-notes"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button 
                variant="outline" 
                onClick={() => {
                  setIsEditApplicationDialogOpen(false);
                  setEditingApplication(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveEditedApplication}
                disabled={savingApplicationEdit}
                className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
                data-testid="save-edit-btn"
              >
                {savingApplicationEdit ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Popup Announcement Dialog */}
        <Dialog open={isPopupAnnouncementDialogOpen} onOpenChange={(open) => {
          setIsPopupAnnouncementDialogOpen(open);
          if (!open) {
            setPopupImagePreview(null);
          }
        }}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="popup-announcement-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5 text-orange-600" />
                Create Promotional Popup
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Image Upload */}
              <div className="space-y-2">
                <Label>Popup Image (Optional)</Label>
                <div className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center hover:border-orange-300 transition-colors">
                  {popupImagePreview ? (
                    <div className="relative inline-block">
                      <img src={popupImagePreview} alt="Preview" className="max-h-32 rounded-lg mx-auto" />
                      <button
                        type="button"
                        onClick={() => setPopupImagePreview(null)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-lg hover:bg-red-600"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ) : (
                    <label className="cursor-pointer block">
                      <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                      <p className="text-sm text-gray-500">Click to upload banner image</p>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handlePopupImageUpload}
                        data-testid="popup-image-input"
                      />
                    </label>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  value={newPopupAnnouncement.title}
                  onChange={(e) => setNewPopupAnnouncement({ ...newPopupAnnouncement, title: e.target.value })}
                  placeholder="Special Offer: Get 20% Off!"
                  data-testid="popup-title-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Content *</Label>
                <Textarea
                  value={newPopupAnnouncement.content}
                  onChange={(e) => setNewPopupAnnouncement({ ...newPopupAnnouncement, content: e.target.value })}
                  placeholder="Describe your offer or announcement..."
                  rows={4}
                  data-testid="popup-content-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Button Text</Label>
                  <Input
                    value={newPopupAnnouncement.button_text}
                    onChange={(e) => setNewPopupAnnouncement({ ...newPopupAnnouncement, button_text: e.target.value })}
                    placeholder="Learn More"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Button Link</Label>
                  <Input
                    value={newPopupAnnouncement.button_link}
                    onChange={(e) => setNewPopupAnnouncement({ ...newPopupAnnouncement, button_link: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Priority</Label>
                <Select 
                  value={newPopupAnnouncement.priority} 
                  onValueChange={(value) => setNewPopupAnnouncement({ ...newPopupAnnouncement, priority: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High - Limited Time</SelectItem>
                    <SelectItem value="urgent">Urgent - Special Offer</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Scheduling Section */}
              <div className="border-t pt-4">
                <Label className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
                  <Calendar className="h-4 w-4" />
                  Schedule Popup (Optional)
                </Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs text-gray-500">Start Date & Time</Label>
                    <Input
                      type="datetime-local"
                      value={newPopupAnnouncement.start_date}
                      onChange={(e) => setNewPopupAnnouncement({ ...newPopupAnnouncement, start_date: e.target.value })}
                      data-testid="popup-start-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-gray-500">End Date & Time</Label>
                    <Input
                      type="datetime-local"
                      value={newPopupAnnouncement.end_date}
                      onChange={(e) => setNewPopupAnnouncement({ ...newPopupAnnouncement, end_date: e.target.value })}
                      data-testid="popup-end-date"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  Leave empty to show immediately with no end date
                </p>
              </div>

              {/* Mascot Selector Section */}
              <div className="border-t pt-4">
                <Label className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
                  🎭 Choose Mascot Character
                </Label>
                <div className="grid grid-cols-5 gap-2 max-h-48 overflow-y-auto p-2 bg-gray-50 rounded-xl">
                  {MASCOT_OPTIONS.map((mascot) => {
                    const MascotComponent = mascot.component;
                    const isSelected = newPopupAnnouncement.mascot_type === mascot.id;
                    return (
                      <button
                        key={mascot.id}
                        type="button"
                        onClick={() => setNewPopupAnnouncement({ ...newPopupAnnouncement, mascot_type: mascot.id })}
                        className={`flex flex-col items-center p-2 rounded-xl transition-all ${
                          isSelected 
                            ? 'bg-gradient-to-br from-orange-100 to-amber-100 ring-2 ring-orange-400 shadow-md' 
                            : 'bg-white hover:bg-gray-100 border border-gray-200'
                        }`}
                        data-testid={`mascot-option-${mascot.id}`}
                      >
                        <div className="w-12 h-12 flex items-center justify-center transform scale-[0.35]">
                          <MascotComponent />
                        </div>
                        <span className={`text-xs mt-1 font-medium ${isSelected ? 'text-orange-700' : 'text-gray-600'}`}>
                          {mascot.name}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  This mascot will appear on the popup announcement card
                </p>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="popup-active"
                  checked={newPopupAnnouncement.is_active}
                  onCheckedChange={(checked) => setNewPopupAnnouncement({ ...newPopupAnnouncement, is_active: checked })}
                />
                <label htmlFor="popup-active" className="text-sm text-gray-700">
                  Activate immediately (will deactivate other popups)
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-2 border-t">
                <Button variant="outline" onClick={() => setIsPopupAnnouncementDialogOpen(false)}>Cancel</Button>
                <Button
                  onClick={handleCreatePopupAnnouncement}
                  disabled={!newPopupAnnouncement.title || !newPopupAnnouncement.content}
                  className="bg-gradient-to-r from-orange-500 to-red-600"
                  data-testid="create-popup-btn"
                >
                  Create Popup
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* CMS Page Editor Dialog */}
        <Dialog open={isPageEditorOpen} onOpenChange={setIsPageEditorOpen}>
          <DialogContent className="w-[calc(100vw-2rem)] md:max-w-[800px] max-h-[90vh] overflow-y-auto" data-testid="page-editor-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="h-5 w-5 text-violet-600" />
                Edit Page: {editingPage?.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              {/* Title */}
              <div className="space-y-2">
                <Label>Page Title</Label>
                <Input
                  value={pageEditorData.title}
                  onChange={(e) => setPageEditorData({ ...pageEditorData, title: e.target.value })}
                  placeholder="Page title"
                  data-testid="page-title-input"
                />
              </div>
              
              {/* Publish Toggle */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Label className="mb-0">Page Status</Label>
                  <Badge variant={pageEditorData.is_published ? "default" : "secondary"} className={pageEditorData.is_published ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-600"}>
                    {pageEditorData.is_published ? 'Published' : 'Draft'}
                  </Badge>
                </div>
                <Button
                  size="sm"
                  variant={pageEditorData.is_published ? "default" : "outline"}
                  onClick={() => setPageEditorData({ ...pageEditorData, is_published: !pageEditorData.is_published })}
                  className={pageEditorData.is_published ? "bg-green-600 hover:bg-green-700" : "text-gray-500"}
                  data-testid="toggle-publish-btn"
                >
                  {pageEditorData.is_published ? (
                    <>
                      <Eye className="h-4 w-4 mr-1" />
                      Published
                    </>
                  ) : (
                    <>
                      <EyeOff className="h-4 w-4 mr-1" />
                      Unpublished
                    </>
                  )}
                </Button>
              </div>

              {/* HTML Content Editor */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Page Content (HTML)</Label>
                  <span className="text-xs text-gray-500">Supports HTML formatting</span>
                </div>
                <Textarea
                  value={pageEditorData.content}
                  onChange={(e) => setPageEditorData({ ...pageEditorData, content: e.target.value })}
                  placeholder="<p>Enter your page content here...</p>&#10;<h2>Section Title</h2>&#10;<p>More content...</p>"
                  className="min-h-[300px] font-mono text-sm"
                  data-testid="page-content-textarea"
                />
                <p className="text-xs text-gray-500">
                  Tip: Use HTML tags like &lt;h1&gt;, &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;strong&gt;, &lt;a href="..."&gt; to format content.
                </p>
              </div>

              {/* Preview */}
              {pageEditorData.content && (
                <div className="space-y-2">
                  <Label>Preview</Label>
                  <div className="border rounded-lg p-4 bg-white max-h-[200px] overflow-y-auto">
                    <div 
                      className="prose prose-sm max-w-none prose-headings:text-slate-800 prose-p:text-gray-600"
                      dangerouslySetInnerHTML={{ __html: pageEditorData.content }}
                    />
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setIsPageEditorOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSavePage}
                  disabled={savingPage || !pageEditorData.title}
                  className="bg-gradient-to-r from-violet-500 to-purple-600"
                  data-testid="save-page-btn"
                >
                  {savingPage ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-1" />
                      Save Page
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Profile Dialog */}
        <Dialog open={isProfileDialogOpen} onOpenChange={setIsProfileDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold flex items-center gap-2">
                <User className="h-5 w-5 text-emerald-600" />
                Edit Profile
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-6 py-4">
              {/* Profile Photo */}
              <div className="flex flex-col items-center gap-3">
                <ProfilePhotoUpload
                  currentPhoto={profileData.profile_picture}
                  onPhotoChange={(photo) => setProfileData(prev => ({ ...prev, profile_picture: photo }))}
                  name={profileData.name}
                  size="lg"
                />
                <p className="text-sm text-gray-500">Click to change photo</p>
              </div>

              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="profile-name">Display Name</Label>
                <Input
                  id="profile-name"
                  value={profileData.name}
                  onChange={(e) => setProfileData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter your name"
                  className="focus-visible:ring-emerald-500"
                />
              </div>

              {/* Email (Read-only) */}
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  value={currentUser?.email || ''}
                  disabled
                  className="bg-gray-50"
                />
                <p className="text-xs text-gray-500">Email cannot be changed</p>
              </div>

              {/* Change Password Link */}
              <button
                type="button"
                onClick={() => { setIsProfileDialogOpen(false); setIsPasswordDialogOpen(true); }}
                className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
              >
                Change Password →
              </button>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsProfileDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateProfile} className="bg-emerald-500 hover:bg-emerald-600">
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Email Dialog */}
        <Dialog open={isBulkEmailDialogOpen} onOpenChange={setIsBulkEmailDialogOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold flex items-center gap-2">
                <Mail className="h-5 w-5 text-blue-600" />
                Send Bulk Email
              </DialogTitle>
              <DialogDescription>
                Send email to {selectedForBulkEmail.length} selected candidate{selectedForBulkEmail.length !== 1 ? 's' : ''}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Selected Recipients Preview */}
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm font-medium text-gray-700 mb-2">Recipients:</p>
                <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto">
                  {selectedForBulkEmail.map((app) => (
                    <Badge key={app.id} variant="secondary" className="text-xs">
                      {app.full_name} ({app.email})
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Use Template Toggle */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="use-template"
                  checked={bulkEmailData.useTemplate}
                  onChange={(e) => setBulkEmailData(prev => ({ ...prev, useTemplate: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="use-template">Use email template</Label>
              </div>

              {bulkEmailData.useTemplate && (
                <div className="space-y-2">
                  <Label>Select Template</Label>
                  <Select
                    value={bulkEmailData.templateId}
                    onValueChange={(val) => setBulkEmailData(prev => ({ ...prev, templateId: val }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a template..." />
                    </SelectTrigger>
                    <SelectContent>
                      {emailTemplates.map((template) => (
                        <SelectItem key={template.id} value={template.id}>
                          {template.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Subject */}
              <div className="space-y-2">
                <Label htmlFor="bulk-subject">Subject *</Label>
                <Input
                  id="bulk-subject"
                  value={bulkEmailData.subject}
                  onChange={(e) => setBulkEmailData(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="Enter email subject..."
                  className="focus-visible:ring-blue-500"
                />
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label htmlFor="bulk-message">Message *</Label>
                <Textarea
                  id="bulk-message"
                  value={bulkEmailData.message}
                  onChange={(e) => setBulkEmailData(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Enter your message...

You can use placeholders:
{{name}} - Candidate's name
{{job_title}} - Job title they applied for
{{company}} - Company name"
                  rows={8}
                  className="focus-visible:ring-blue-500"
                />
                <p className="text-xs text-gray-500">
                  Tip: Use {'{{name}}'}, {'{{job_title}}'}, {'{{company}}'} as placeholders
                </p>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setIsBulkEmailDialogOpen(false);
                  setBulkEmailData({ subject: '', message: '', useTemplate: false, templateId: '' });
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSendBulkEmail}
                disabled={sendingBulkEmail || !bulkEmailData.subject.trim() || !bulkEmailData.message.trim()}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {sendingBulkEmail ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send to {selectedForBulkEmail.length} Recipients
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Fullscreen Message View */}
        {isFullscreenMessage && (
          <div className="fixed inset-0 z-[200] bg-black/90 flex items-center justify-center p-4" onClick={() => setIsFullscreenMessage(null)}>
            <div className="relative max-w-2xl w-full bg-white rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center text-white font-bold">
                    {isFullscreenMessage.sender_name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <p className="font-semibold text-white">{isFullscreenMessage.sender_name || 'Unknown'}</p>
                    <p className="text-xs text-white/70">{formatISTFull(isFullscreenMessage.created_at)}</p>
                  </div>
                </div>
                <button onClick={() => setIsFullscreenMessage(null)} className="p-2 rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors">
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <div className="p-6 max-h-[70vh] overflow-y-auto">
                {isFullscreenMessage.reply_to && (
                  <div className="mb-4 px-3 py-2 rounded-lg bg-gray-100 border-l-4 border-emerald-500">
                    <p className="text-xs font-semibold text-gray-600">{isFullscreenMessage.reply_to.sender_name}</p>
                    <p className="text-sm text-gray-500">{isFullscreenMessage.reply_to.content}</p>
                  </div>
                )}
                
                {isFullscreenMessage.message_type === 'image' && isFullscreenMessage.image_url && (
                  <div className="relative mb-4">
                    <img src={getImageUrl(isFullscreenMessage.image_url, API)} alt="Shared" className="max-w-full rounded-lg shadow-md" />
                    <a
                      href={getImageUrl(isFullscreenMessage.image_url, API)}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      className="absolute bottom-3 right-3 px-3 py-2 bg-black/60 rounded-lg text-white hover:bg-black/80 transition-colors flex items-center gap-2 text-sm"
                    >
                      <Download className="h-4 w-4" />
                      Download
                    </a>
                  </div>
                )}
                
                {isFullscreenMessage.deleted_for_all ? (
                  <p className="text-gray-400 italic flex items-center gap-2">
                    <Ban className="h-5 w-5" />
                    This message was deleted
                  </p>
                ) : (
                  <p className="text-lg text-gray-800 whitespace-pre-wrap leading-relaxed">
                    {isFullscreenMessage.content}
                  </p>
                )}
                
                {isFullscreenMessage.edited && (
                  <p className="text-xs text-gray-400 italic mt-4">Edited</p>
                )}
              </div>
              
              <div className="p-4 border-t bg-gray-50 flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  {!isFullscreenMessage.deleted_for_all && (
                    <>
                      <button onClick={() => { handleCopyMessage(isFullscreenMessage.content); toast.success('Copied!'); }} className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm flex items-center gap-2 transition-colors">
                        <Copy className="h-4 w-4" />
                        Copy
                      </button>
                      <button onClick={() => { setReplyToMessage(isFullscreenMessage); setIsFullscreenMessage(null); }} className="px-3 py-2 rounded-lg bg-emerald-100 hover:bg-emerald-200 text-emerald-700 text-sm flex items-center gap-2 transition-colors">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                        </svg>
                        Reply
                      </button>
                      {isFullscreenMessage.sender_email === currentUser?.email && isFullscreenMessage.message_type === 'text' && (
                        <button onClick={() => { handleEditMessage(isFullscreenMessage); setIsFullscreenMessage(null); }} className="px-3 py-2 rounded-lg bg-blue-100 hover:bg-blue-200 text-blue-700 text-sm flex items-center gap-2 transition-colors">
                          <Pencil className="h-4 w-4" />
                          Edit
                        </button>
                      )}
                      <button onClick={() => { handleDeleteMessage(isFullscreenMessage.id, 'for_me'); setIsFullscreenMessage(null); }} className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm flex items-center gap-2 transition-colors">
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </button>
                      {isFullscreenMessage.sender_email === currentUser?.email && (
                        <button onClick={() => { handleDeleteMessage(isFullscreenMessage.id, 'for_everyone'); setIsFullscreenMessage(null); }} className="px-3 py-2 rounded-lg bg-red-100 hover:bg-red-200 text-red-700 text-sm flex items-center gap-2 transition-colors">
                          <Trash2 className="h-4 w-4" />
                          Delete All
                        </button>
                      )}
                    </>
                  )}
                </div>
                <button onClick={() => setIsFullscreenMessage(null)} className="px-4 py-2 rounded-lg bg-gray-800 hover:bg-gray-900 text-white text-sm transition-colors">
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Fullscreen Messages Page */}
        {isMessagesFullscreen && (
          <div className="fixed inset-0 z-[150] bg-gray-100 flex flex-col overflow-hidden">
            {/* Header - White */}
            <div className="bg-white p-3 md:p-4 flex items-center justify-between shadow-md border-b safe-area-top">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center">
                  <MessageCircle className="h-5 w-5 md:h-6 md:w-6 text-white" />
                </div>
                <div>
                  <h2 className="font-heading text-lg md:text-xl font-bold text-gray-800">Messages</h2>
                  <p className="text-xs text-gray-500">Chat with team members and groups</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  onClick={() => setIsCreateGroupOpen(true)}
                  size="sm"
                  className="rounded-full bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700 border-0 h-8 md:h-10 px-3 md:px-4"
                >
                  <UserPlus className="h-4 w-4 mr-1 md:mr-2" />
                  <span className="hidden md:inline">New Group</span>
                </Button>
                <button 
                  onClick={() => {
                    setIsMessagesFullscreen(false);
                    setSelectedChat(null);
                    if (previousTabBeforeChat) {
                      setActiveTab(previousTabBeforeChat);
                    }
                  }}
                  className="p-2 md:p-3 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                  data-testid="close-messages-fullscreen"
                >
                  <X className="h-5 w-5 md:h-6 md:w-6" />
                </button>
              </div>
            </div>

            {/* Messages Content */}
            <div className="flex-1 flex overflow-hidden">
              {/* Conversations List */}
              <div className={`${selectedChat ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-80 lg:w-96 border-r bg-white`}>
                {/* Search */}
                <div className="p-3 border-b bg-gray-50">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" />
                    <Input
                      placeholder="Search conversations..."
                      value={messageSearchQuery}
                      onChange={(e) => setMessageSearchQuery(e.target.value)}
                      className="pl-10 rounded-full bg-white border-gray-200"
                    />
                  </div>
                </div>
                
                {/* Tabs for Groups/DMs/Calls */}
                <div className="flex border-b">
                  <button 
                    onClick={() => setMessagingActiveTab('chats')}
                    className={`flex-1 py-3 text-sm font-medium transition-colors ${messagingActiveTab === 'chats' ? 'text-green-600 border-b-2 border-green-600 bg-green-50' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
                  >
                    Chats
                  </button>
                  <button 
                    onClick={() => setMessagingActiveTab('groups')}
                    className={`flex-1 py-3 text-sm font-medium transition-colors ${messagingActiveTab === 'groups' ? 'text-green-600 border-b-2 border-green-600 bg-green-50' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
                  >
                    Groups ({groups.length})
                  </button>
                  <button 
                    onClick={() => { setMessagingActiveTab('calls'); fetchCallHistory(); }}
                    className={`flex-1 py-3 text-sm font-medium transition-colors relative ${messagingActiveTab === 'calls' ? 'text-green-600 border-b-2 border-green-600 bg-green-50' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
                  >
                    Calls
                    {missedCallCount > 0 && (
                      <span className="absolute top-1 right-2 h-5 w-5 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center">
                        {missedCallCount}
                      </span>
                    )}
                  </button>
                </div>
                
                <ScrollArea className="flex-1">
                  {messagingActiveTab === 'calls' ? (
                    /* Call History */
                    callHistory.length === 0 ? (
                      <div className="p-6 text-center text-gray-500">
                        <Phone className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                        <p className="font-medium">No call history</p>
                        <p className="text-sm text-gray-400">Start a call from any chat</p>
                      </div>
                    ) : (
                      <div className="p-2">
                        {callHistory.map((call) => (
                          <div
                            key={call.id}
                            className="p-3 rounded-xl hover:bg-gray-50 transition-all cursor-pointer mb-1"
                            onClick={() => {
                              const chatUser = conversations.find(c => c.email === call.other_user_email);
                              if (chatUser) {
                                setSelectedChat({ ...chatUser, isGroup: false });
                              }
                            }}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`h-10 w-10 rounded-full flex items-center justify-center font-bold text-white ${
                                call.status === 'missed' ? 'bg-red-500' : 
                                call.status === 'completed' ? 'bg-emerald-500' : 'bg-gray-400'
                              }`}>
                                {call.direction === 'outgoing' ? (
                                  <PhoneOutgoing className="h-5 w-5" />
                                ) : call.status === 'missed' ? (
                                  <PhoneMissed className="h-5 w-5" />
                                ) : (
                                  <PhoneIncoming className="h-5 w-5" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-800 truncate">{call.other_user_name}</p>
                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                  <span className={call.status === 'missed' ? 'text-red-500' : ''}>
                                    {call.direction === 'outgoing' ? 'Outgoing' : 'Incoming'} • {call.status}
                                  </span>
                                  {call.duration_seconds > 0 && (
                                    <span>• {Math.floor(call.duration_seconds / 60)}:{(call.duration_seconds % 60).toString().padStart(2, '0')}</span>
                                  )}
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-xs text-gray-400">{formatISTTime(call.created_at)}</p>
                                <CallbackButton userEmail={call.other_user_email} userName={call.other_user_name} />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )
                  ) : messagingActiveTab === 'groups' ? (
                    /* Groups List */
                    groups.length === 0 ? (
                      <div className="p-6 text-center text-gray-500">
                        <UsersIcon className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                        <p className="font-medium">No groups yet</p>
                        <p className="text-sm text-gray-400 mb-4">Create a group to start chatting</p>
                        <Button size="sm" variant="outline" onClick={() => setIsCreateGroupOpen(true)}>
                          <UserPlus className="h-4 w-4 mr-2" />
                          Create Group
                        </Button>
                      </div>
                    ) : (
                      <div className="p-2">
                        {groups.map((group) => {
                          const groupConvo = conversations.find(c => c.group_id === group.id);
                          const groupUnreadCount = groupConvo?.unread_count || 0;
                          
                          return (
                          <div
                            key={group.id}
                            onClick={() => openGroupChat(group)}
                            className={`p-3 rounded-xl cursor-pointer transition-all mb-1 ${
                              selectedChat?.groupId === group.id 
                                ? 'bg-green-100 border-l-4 border-green-500' 
                                : 'hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              {group.group_image ? (
                                <img 
                                  src={getImageUrl(group.group_image, API)}
                                  alt={group.name}
                                  className="h-12 w-12 rounded-full object-cover border-2 border-gray-100"
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              ) : (
                                <div className="h-12 w-12 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center text-white font-bold text-lg">
                                  {group.name?.charAt(0)?.toUpperCase()}
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-gray-800 truncate">{group.name}</p>
                                <p className="text-xs text-gray-500">{group.member_emails?.length || 0} members</p>
                              </div>
                              {/* Unread count badge for groups */}
                              {groupUnreadCount > 0 && (
                                <div className="flex-shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-green-500 text-white text-xs font-bold flex items-center justify-center animate-pulse">
                                  {groupUnreadCount > 99 ? '99+' : groupUnreadCount}
                                </div>
                              )}
                            </div>
                          </div>
                        )})}
                      </div>
                    )
                  ) : (
                    /* Team Members List - sorted by last message time */
                    <div className="p-2">
                      <p className="text-xs font-semibold text-gray-500 px-3 py-2 uppercase tracking-wider">Team Members</p>
                      {users
                        .filter(u => u.email !== currentUser?.email)
                        .map(user => {
                          const userConvo = conversations.find(c => c.partner_email === user.email);
                          return {
                            ...user,
                            lastMessageTime: userConvo?.last_message_time || '1970-01-01',
                            unreadCount: userConvo?.unread_count || 0
                          };
                        })
                        .sort((a, b) => new Date(b.lastMessageTime) - new Date(a.lastMessageTime))
                        .map((user) => (
                          <div
                            key={user.email}
                            onClick={() => openChat(user)}
                            className={`p-3 rounded-xl cursor-pointer transition-all mb-1 ${
                              selectedChat?.email === user.email 
                                ? 'bg-green-100 border-l-4 border-green-500' 
                                : 'hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              {user.profile_picture ? (
                                <img 
                                  src={getImageUrl(user.profile_picture, API)}
                                  alt={user.name}
                                  className="h-12 w-12 rounded-full object-cover border-2 border-gray-100"
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              ) : (
                                <div className="h-12 w-12 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center text-white font-bold text-lg">
                                  {user.name?.charAt(0)?.toUpperCase()}
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-gray-800 truncate">{user.name}</p>
                                <p className="text-xs text-gray-500 truncate">{user.role}</p>
                              </div>
                              {user.unreadCount > 0 && (
                                <div className="flex-shrink-0 min-w-[24px] h-6 px-2 rounded-full bg-green-500 text-white text-xs font-bold flex items-center justify-center animate-pulse">
                                  {user.unreadCount > 99 ? '99+' : user.unreadCount}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </ScrollArea>
              </div>
              
              {/* Chat Area */}
              <div className={`flex-1 flex flex-col ${selectedChat ? 'flex' : 'hidden md:flex'} bg-gray-50`}>
                {!selectedChat ? (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                      <div className="h-20 w-20 rounded-2xl bg-green-100 flex items-center justify-center mx-auto mb-4">
                        <MessageCircle className="h-10 w-10 text-green-600" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-700 mb-2">Select a conversation</h3>
                      <p className="text-gray-500">Choose from your team members or groups to start chatting</p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Chat Header */}
                    <div className="p-3 md:p-4 border-b bg-white text-gray-800 flex items-center gap-2 md:gap-3 shadow-sm">
                      <Button variant="ghost" size="icon" className="md:hidden text-gray-600 hover:bg-gray-100 flex-shrink-0" onClick={() => setSelectedChat(null)}>
                        <ArrowLeft className="h-5 w-5" />
                      </Button>
                      {selectedChat.isGroup ? (
                        selectedChat.group_image ? (
                          <img src={getImageUrl(selectedChat.group_image, API)} alt={selectedChat.name} className="h-10 w-10 md:h-12 md:w-12 rounded-full object-cover border-2 border-gray-100 flex-shrink-0" />
                        ) : (
                          <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 text-white flex items-center justify-center font-bold text-lg flex-shrink-0">
                            {selectedChat.name?.charAt(0)?.toUpperCase()}
                          </div>
                        )
                      ) : selectedChat.profile_picture ? (
                        <img src={getImageUrl(selectedChat.profile_picture, API)} alt={selectedChat.name} className="h-10 w-10 md:h-12 md:w-12 rounded-full object-cover border-2 border-gray-100 flex-shrink-0" />
                      ) : (
                        <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 text-white flex items-center justify-center font-bold text-lg flex-shrink-0">
                          {selectedChat.name?.charAt(0)?.toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-800 truncate">{selectedChat.name}</p>
                        <p className="text-xs text-gray-500 truncate">{selectedChat.isGroup ? `${selectedChat.memberCount} members` : selectedChat.email}</p>
                      </div>
                      {/* Call button for direct chats (not groups, not candidates) */}
                      {!selectedChat.isGroup && selectedChat.role !== 'candidate' && (
                        <div className="flex-shrink-0">
                          <CallButton 
                            userEmail={selectedChat.email}
                            userName={selectedChat.name}
                            userAvatar={selectedChat.profile_picture ? getImageUrl(selectedChat.profile_picture, API) : null}
                          />
                        </div>
                      )}
                      {selectedChat.isGroup && (
                        <div className="flex items-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => { setEditGroupName(selectedChat.name); setIsEditGroupOpen(true); }}
                            className="text-gray-600 hover:bg-gray-100 h-9 w-9"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => setIsAddMemberOpen(true)}
                            className="text-gray-600 hover:bg-gray-100 h-9 w-9"
                          >
                            <UserPlus className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                    
                    {/* Messages */}
                    <ScrollArea className="flex-1 p-3 md:p-4" onClick={() => setSelectedMessageId(null)}>
                      <div className="space-y-2">
                        {chatMessages.map((msg) => {
                          const isMe = msg.sender_email === currentUser?.email;
                          const isSending = msg.is_sending || msg.status === 'sending';
                          const isFailed = msg.status === 'failed';
                          
                          return (
                            <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'} group`}>
                              <div
                                className={`max-w-[75%] md:max-w-[60%] rounded-2xl px-4 py-2 shadow-sm relative cursor-pointer ${
                                  isMe ? 'bg-emerald-50 border border-emerald-100 rounded-tr-sm' : 'bg-white border border-gray-100 rounded-tl-sm'
                                } ${isSending ? 'opacity-70' : ''} ${isFailed ? 'bg-red-50 border border-red-200' : ''} ${selectedMessageId === msg.id ? 'ring-2 ring-emerald-400' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (isFailed) { retryFailedMessage(msg); return; }
                                  // Single tap opens fullscreen view
                                  if (!msg.deleted_for_all && !isSending) {
                                    setIsFullscreenMessage(msg);
                                  }
                                }}
                                onContextMenu={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  // Right-click / long-press shows action menu
                                  if (!msg.deleted_for_all && !isSending) {
                                    setSelectedMessageId(selectedMessageId === msg.id ? null : msg.id);
                                  }
                                }}
                              >
                                {/* Action Menu on long press */}
                                {selectedMessageId === msg.id && !msg.deleted_for_all && (
                                  <div className="absolute -bottom-14 left-1/2 -translate-x-1/2 flex justify-center z-20">
                                    <div className="flex items-center gap-1 bg-white rounded-full shadow-lg border px-3 py-2">
                                      <button onClick={(e) => { e.stopPropagation(); handleCopyMessage(msg.content); setSelectedMessageId(null); }} className="p-2 hover:bg-gray-100 rounded-full" title="Copy">
                                        <Copy className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-600" />
                                      </button>
                                      {isMe && msg.message_type === 'text' && (
                                        <button onClick={(e) => { e.stopPropagation(); handleEditMessage(msg); setSelectedMessageId(null); }} className="p-2 hover:bg-gray-100 rounded-full" title="Edit">
                                          <Pencil className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-600" />
                                        </button>
                                      )}
                                      <button onClick={(e) => { e.stopPropagation(); handleDeleteMessage(msg.id, 'for_me'); setSelectedMessageId(null); }} className="p-2 hover:bg-gray-100 rounded-full" title="Delete">
                                        <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-600" />
                                      </button>
                                      {isMe && (
                                        <button onClick={(e) => { e.stopPropagation(); handleDeleteMessage(msg.id, 'for_everyone'); setSelectedMessageId(null); }} className="p-2 hover:bg-red-100 rounded-full" title="Delete for everyone">
                                          <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-red-500" />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                )}
                                {!isMe && selectedChat.isGroup && (
                                  <p className="text-xs font-semibold text-green-600 mb-1">{msg.sender_name || msg.sender_email}</p>
                                )}
                                {msg.deleted_for_all ? (
                                  <p className="text-sm text-gray-400 italic">This message was deleted</p>
                                ) : (
                                  <>
                                    {msg.image_url && (
                                      <div className="relative group/img mb-2">
                                        <img src={getImageUrl(msg.image_url, API)} alt="Sent" className="max-w-full rounded-lg" />
                                        {/* Download button for images */}
                                        <a
                                          href={getImageUrl(msg.image_url, API)}
                                          download
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          onClick={(e) => e.stopPropagation()}
                                          className="absolute bottom-2 right-2 p-2 bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors opacity-0 group-hover/img:opacity-100"
                                          title="Download image"
                                        >
                                          <Download className="h-4 w-4" />
                                        </a>
                                      </div>
                                    )}
                                    <p className="text-sm whitespace-pre-wrap break-words pr-12" dangerouslySetInnerHTML={{ __html: formatMessageContent(msg.content) }} />
                                  </>
                                )}
                                <div className="absolute bottom-1 right-2 flex items-center gap-1">
                                  {msg.edited && <span className="text-[10px] text-gray-400 italic">edited</span>}
                                  <span className="text-[10px] text-gray-500">{isSending ? '' : formatISTTime(msg.created_at)}</span>
                                  {isMe && (
                                    msg.read ? (
                                      <svg className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-blue-500" viewBox="0 0 16 15" fill="currentColor">
                                        <path d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.88a.32.32 0 0 1-.484.032l-.358-.325a.32.32 0 0 0-.484.032l-.378.48a.418.418 0 0 0 .036.54l1.32 1.266a.32.32 0 0 0 .484-.034l6.272-8.048a.366.366 0 0 0-.064-.512zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.88a.32.32 0 0 1-.484.032L1.89 7.77a.366.366 0 0 0-.516.005l-.423.433a.364.364 0 0 0 .006.514l3.255 3.185a.32.32 0 0 0 .484-.033l6.272-8.048a.365.365 0 0 0-.063-.51z"/>
                                      </svg>
                                    ) : (
                                      <svg className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" viewBox="0 0 16 15" fill="currentColor">
                                        <path d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.88a.32.32 0 0 1-.484.032l-.358-.325a.32.32 0 0 0-.484.032l-.378.48a.418.418 0 0 0 .036.54l1.32 1.266a.32.32 0 0 0 .484-.034l6.272-8.048a.366.366 0 0 0-.064-.512zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.88a.32.32 0 0 1-.484.032L1.89 7.77a.366.366 0 0 0-.516.005l-.423.433a.364.364 0 0 0 .006.514l3.255 3.185a.32.32 0 0 0 .484-.033l6.272-8.048a.365.365 0 0 0-.063-.51z"/>
                                      </svg>
                                    )
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        <div ref={messagesEndRef} />
                      </div>
                    </ScrollArea>
                    
                    {/* Image Preview */}
                    {imagePreview && (
                      <div className="px-4 py-3 border-t bg-gradient-to-r from-emerald-50 to-teal-50">
                        <div className="relative inline-block">
                          <img src={imagePreview} alt="Preview" className="h-20 rounded-xl shadow-md border-2 border-emerald-200" />
                          <button onClick={() => { setImagePreview(null); setMessageType('text'); }} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-lg hover:bg-red-600 transition-colors">
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {/* Reply Preview */}
                    {replyToMessage && (
                      <div className="px-4 py-2 bg-gray-100 border-t flex items-center gap-2">
                        <div className="flex-1 px-3 py-2 bg-white rounded-lg border-l-4 border-emerald-500">
                          <p className="text-xs font-semibold text-emerald-600">{replyToMessage.sender_name}</p>
                          <p className="text-sm text-gray-600 truncate">{replyToMessage.content}</p>
                        </div>
                        <button onClick={() => setReplyToMessage(null)} className="p-1 text-gray-500 hover:text-red-500 rounded-full hover:bg-red-50">
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                    )}
                    
                    {/* Message Input */}
                    <form onSubmit={editingMessage ? (e) => { e.preventDefault(); handleSaveEdit(); } : handleSendMessage} className="p-3 md:p-4 bg-white border-t">
                      {editingMessage && (
                        <div className="flex items-center justify-between mb-2 px-3 py-2 bg-blue-50 rounded-lg">
                          <div className="flex items-center gap-2 text-blue-600">
                            <Pencil className="h-4 w-4" />
                            <span className="text-sm">Editing message</span>
                          </div>
                          <button type="button" onClick={handleCancelEdit} className="text-gray-500 hover:text-gray-700">
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                      {/* Formatting buttons */}
                      <div className="flex items-center gap-1 mb-2">
                        <input
                          ref={imageInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleImageSelect}
                          className="hidden"
                        />
                        <button type="button" onClick={() => imageInputRef.current?.click()} className="p-1.5 rounded hover:bg-gray-200 text-gray-600 transition-colors">
                          <LucideImage className="h-4 w-4" />
                        </button>
                        <span className="text-xs text-gray-400 ml-auto">Select text for formatting options</span>
                      </div>
                      <div className="flex flex-col gap-1">
                        <div className="flex items-start gap-2">
                          <div className="flex-1 message-rich-editor">
                            <RichTextEditor
                              value={newMessage}
                              onChange={(html) => { setNewMessage(html); handleTyping(); }}
                              placeholder="Type a message..."
                              minHeight="42px"
                              className="message-composer"
                              showBottomToolbar={true}
                            />
                          </div>
                          {/* Send button visible on desktop */}
                          <Button type="submit" size="icon" disabled={!newMessage.replace(/<[^>]*>/g, '').trim() && !imagePreview} className="hidden md:flex rounded-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 h-10 w-10" data-testid="send-message-btn-fullscreen-desktop">
                            <Send className="h-5 w-5" />
                          </Button>
                        </div>
                        {/* Send button visible on mobile */}
                        <Button type="submit" disabled={!newMessage.replace(/<[^>]*>/g, '').trim() && !imagePreview} className="md:hidden w-full rounded-lg bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700" data-testid="send-message-btn-fullscreen-mobile">
                          <Send className="h-4 w-4 mr-2" />
                          Send Message
                        </Button>
                      </div>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Edit Group Dialog */}
        <Dialog open={isEditGroupOpen} onOpenChange={setIsEditGroupOpen}>
          <DialogContent className="sm:max-w-md z-[200]">
            <DialogHeader>
              <DialogTitle>Edit Group</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Group Name</label>
                <Input
                  value={editGroupName}
                  onChange={(e) => setEditGroupName(e.target.value)}
                  placeholder="Enter group name"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Group Image (Optional)</label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleEditGroupImageUpload}
                  disabled={isGroupImageUploading}
                />
                {isGroupImageUploading && <p className="text-sm text-muted-foreground">Uploading...</p>}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEditGroupOpen(false)}>Cancel</Button>
              <Button onClick={handleUpdateGroup} disabled={!editGroupName.trim()}>Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Group Dialog */}
        <Dialog open={isCreateGroupOpen} onOpenChange={setIsCreateGroupOpen}>
          <DialogContent className="sm:max-w-md z-[200]">
            <DialogHeader>
              <DialogTitle>Create New Group</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Group Name *</label>
                <Input
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  placeholder="Enter group name"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Select Members *</label>
                <div className="max-h-60 overflow-y-auto space-y-2 border rounded-lg p-2">
                  {users.filter(u => u.email !== currentUser?.email).map((user) => (
                    <div 
                      key={user.email}
                      onClick={() => {
                        setSelectedGroupMembers(prev => 
                          prev.includes(user.email) 
                            ? prev.filter(e => e !== user.email)
                            : [...prev, user.email]
                        );
                      }}
                      className={`p-2 rounded-lg cursor-pointer flex items-center gap-2 transition-colors ${
                        selectedGroupMembers.includes(user.email) ? 'bg-green-100 border border-green-300' : 'hover:bg-gray-100'
                      }`}
                    >
                      <div className="h-8 w-8 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center text-white text-sm font-bold">
                        {user.name?.charAt(0)?.toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{user.name}</p>
                        <p className="text-xs text-gray-500">{user.role}</p>
                      </div>
                      {selectedGroupMembers.includes(user.email) && (
                        <CheckCircle2 className="h-5 w-5 text-green-500" />
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500">{selectedGroupMembers.length} member(s) selected</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setIsCreateGroupOpen(false); setNewGroupName(''); setSelectedGroupMembers([]); }}>Cancel</Button>
              <Button onClick={handleCreateGroup} disabled={!newGroupName.trim() || selectedGroupMembers.length === 0}>
                Create Group
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Member Dialog */}
        <Dialog open={isAddMemberOpen} onOpenChange={setIsAddMemberOpen}>
          <DialogContent className="sm:max-w-md z-[200]">
            <DialogHeader>
              <DialogTitle>Add Members to Group</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Select Members</label>
                <div className="max-h-60 overflow-y-auto space-y-2 border rounded-lg p-2">
                  {users.filter(u => 
                    u.email !== currentUser?.email && 
                    !selectedChat?.member_emails?.includes(u.email)
                  ).map((user) => (
                    <div 
                      key={user.email}
                      onClick={() => {
                        setSelectedMembersToAdd(prev => 
                          prev.includes(user.email) 
                            ? prev.filter(e => e !== user.email)
                            : [...prev, user.email]
                        );
                      }}
                      className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                        selectedMembersToAdd.includes(user.email) ? 'bg-green-100 border border-green-500' : 'hover:bg-gray-100'
                      }`}
                    >
                      <div className="h-8 w-8 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center text-white font-bold text-sm">
                        {user.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">{user.name}</p>
                        <p className="text-xs text-gray-500">{user.email}</p>
                      </div>
                      {selectedMembersToAdd.includes(user.email) && (
                        <CheckCircle2 className="h-5 w-5 text-green-500" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
              {selectedMembersToAdd.length > 0 && (
                <p className="text-sm text-muted-foreground">{selectedMembersToAdd.length} member(s) selected</p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setIsAddMemberOpen(false); setSelectedMembersToAdd([]); }}>Cancel</Button>
              <Button onClick={handleAddMemberToGroup} disabled={selectedMembersToAdd.length === 0}>Add Members</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Tab Permissions Dialog */}
        <Dialog open={tabPermissionsDialogOpen} onOpenChange={(open) => {
          if (!open) {
            setTabPermissionsDialogOpen(false);
            setEditingUserPermissions(null);
          }
        }}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="tab-permissions-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5 text-purple-600" />
                Manage Tab Permissions
              </DialogTitle>
              {editingUserPermissions && (
                <p className="text-sm text-muted-foreground">
                  Configure which tabs <span className="font-medium">{editingUserPermissions.name}</span> ({editingUserPermissions.email}) can access
                </p>
              )}
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Select All / Deselect All */}
              <div className="flex items-center justify-between border-b pb-3">
                <span className="text-sm font-medium text-gray-700">Quick Actions</span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSelectAllTabs(true)}
                    className="text-xs h-7"
                  >
                    Select All
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSelectAllTabs(false)}
                    className="text-xs h-7"
                  >
                    Deselect All
                  </Button>
                </div>
              </div>

              {/* Tabs Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {ALL_TABS.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => handleToggleTabPermission(tab.id)}
                    className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                      selectedTabPermissions.includes(tab.id)
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-gray-200 hover:border-gray-300 text-gray-600'
                    }`}
                    data-testid={`tab-permission-${tab.id}`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      selectedTabPermissions.includes(tab.id)
                        ? 'border-purple-500 bg-purple-500'
                        : 'border-gray-300'
                    }`}>
                      {selectedTabPermissions.includes(tab.id) && (
                        <Check className="h-3 w-3 text-white" />
                      )}
                    </div>
                    <span className="text-sm font-medium">{tab.label}</span>
                  </button>
                ))}
              </div>

              {/* Selected Count */}
              <div className="text-center text-sm text-muted-foreground pt-2 border-t">
                {selectedTabPermissions.length} of {ALL_TABS.length} tabs selected
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setTabPermissionsDialogOpen(false);
                  setEditingUserPermissions(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveTabPermissions}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="save-permissions-btn"
              >
                Save Permissions
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Rejection Dialog with Cooling Period */}
        <Dialog open={isRejectDialogOpen} onOpenChange={setIsRejectDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <Ban className="h-5 w-5" />
                Reject Application
              </DialogTitle>
              <DialogDescription>
                {rejectingApplication && (
                  <span>Rejecting application from <strong>{rejectingApplication.full_name || rejectingApplication.referred_name}</strong> for <strong>{rejectingApplication.job_title}</strong></span>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Rejection Reason (Optional)</Label>
                <Textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Brief reason for rejection (visible to candidate)..."
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label>Cooling Period</Label>
                <p className="text-xs text-slate-500 mb-2">
                  Prevent this candidate from applying to this <strong>company</strong> for a specified period
                </p>
                <Select
                  value={coolingPeriodDays.toString()}
                  onValueChange={(v) => setCoolingPeriodDays(parseInt(v))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select cooling period" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">No cooling period</SelectItem>
                    <SelectItem value="7">7 days</SelectItem>
                    <SelectItem value="15">15 days</SelectItem>
                    <SelectItem value="30">30 days</SelectItem>
                    <SelectItem value="60">60 days (2 months)</SelectItem>
                    <SelectItem value="90">90 days (3 months)</SelectItem>
                    <SelectItem value="180">180 days (6 months)</SelectItem>
                    <SelectItem value="365">1 year</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg">
                <AlertCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-amber-600 flex-shrink-0" />
                <p className="text-xs text-amber-700">
                  {coolingPeriodDays > 0 
                    ? `Candidate won't be able to apply to this company for ${coolingPeriodDays} days`
                    : 'Candidate can reapply immediately if no cooling period is set'
                  }
                </p>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setIsRejectDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleRejectWithCoolingPeriod} className="bg-red-600 hover:bg-red-700 text-white">
                Reject Application
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Blacklist Candidate Dialog */}
        <Dialog open={isBlacklistDialogOpen} onOpenChange={setIsBlacklistDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <Ban className="h-5 w-5" />
                Blacklist Candidate
              </DialogTitle>
              <DialogDescription>
                Blacklisted candidates cannot apply to any position
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Candidate Email *</Label>
                <Input
                  value={blacklistData.email}
                  onChange={(e) => setBlacklistData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="candidate@email.com"
                  type="email"
                />
              </div>
              <div className="space-y-2">
                <Label>Candidate Name (Optional)</Label>
                <Input
                  value={blacklistData.name}
                  onChange={(e) => setBlacklistData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Full name"
                />
              </div>
              <div className="space-y-2">
                <Label>Reason for Blacklisting *</Label>
                <Textarea
                  value={blacklistData.reason}
                  onChange={(e) => setBlacklistData(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="Reason for blacklisting this candidate..."
                  rows={3}
                />
              </div>
              <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg">
                <AlertCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-red-600 flex-shrink-0" />
                <p className="text-xs text-red-700">
                  This action will prevent the candidate from applying to any future positions.
                </p>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setIsBlacklistDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleBlacklistCandidate} 
                className="bg-red-600 hover:bg-red-700 text-white"
                disabled={!blacklistData.email || !blacklistData.reason}
              >
                Add to Blacklist
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Cooling Period Dialog */}
        <Dialog open={editCoolingPeriodDialog.open} onOpenChange={(open) => setEditCoolingPeriodDialog({ open, app: open ? editCoolingPeriodDialog.app : null })}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Pencil className="h-5 w-5 text-blue-600" />
                Edit Cooling Period
              </DialogTitle>
              <DialogDescription>
                {editCoolingPeriodDialog.app && (
                  <span>
                    Update cooling period for <strong>{editCoolingPeriodDialog.app.full_name}</strong> at <strong>{editCoolingPeriodDialog.app.company_name}</strong>
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Current End Date</Label>
                <p className="text-sm text-slate-600">
                  {editCoolingPeriodDialog.app?.cooling_period_end 
                    ? new Date(editCoolingPeriodDialog.app.cooling_period_end).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
                    : 'Not set'}
                </p>
              </div>
              <div className="space-y-2">
                <Label>New End Date</Label>
                <Input
                  type="date"
                  value={newCoolingEndDate}
                  onChange={(e) => setNewCoolingEndDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
                <AlertCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-blue-600 flex-shrink-0" />
                <p className="text-xs text-blue-700">
                  The candidate will not be able to apply to this company until this date.
                </p>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setEditCoolingPeriodDialog({ open: false, app: null })}>
                Cancel
              </Button>
              <Button 
                onClick={handleEditCoolingPeriod} 
                className="bg-blue-600 hover:bg-blue-700 text-white"
                disabled={!newCoolingEndDate}
              >
                Update Cooling Period
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Assessment Lock Dialog */}
        <Dialog open={isLockAssessmentDialogOpen} onOpenChange={setIsLockAssessmentDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {lockAssessmentData?.mode === 'lock' ? (
                  <>
                    <Lock className="h-5 w-5 text-orange-600" />
                    Lock Assessment
                  </>
                ) : (
                  <>
                    <Unlock className="h-5 w-5 text-green-600" />
                    Unlock Assessment
                  </>
                )}
              </DialogTitle>
              <DialogDescription>
                {lockAssessmentData?.assessment && (
                  <span>
                    {lockAssessmentData.mode === 'lock' 
                      ? `Lock "${lockAssessmentData.assessment.skill_name}" assessment for a user`
                      : `Unlock "${lockAssessmentData.assessment.skill_name}" assessment for a user`
                    }
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {lockAssessmentData?.mode === 'lock' ? (
                <div className="space-y-2">
                  <Label>Select User to Lock</Label>
                  <div className="space-y-2 max-h-48 overflow-y-auto p-2 bg-gray-50 rounded-lg">
                    <Input
                      placeholder="Search by name or email..."
                      value={lockAssessmentEmail}
                      onChange={(e) => setLockAssessmentEmail(e.target.value)}
                      className="mb-2"
                    />
                    {loadingUsers ? (
                      <div className="flex items-center justify-center py-4">
                        <div className="h-5 w-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mr-2" />
                        <span className="text-sm text-gray-500">Loading users...</span>
                      </div>
                    ) : (allUsers || [])
                      .filter(u => 
                        u.email && 
                        ((u.name || u.full_name || '')?.toLowerCase().includes(lockAssessmentEmail.toLowerCase()) ||
                         u.email?.toLowerCase().includes(lockAssessmentEmail.toLowerCase()) ||
                         lockAssessmentEmail === '')
                      )
                      .filter(u => !lockAssessmentData?.assessment?.locked_emails?.includes(u.email))
                      .slice(0, 15)
                      .map((user, idx) => (
                        <div 
                          key={idx} 
                          className={`flex items-center justify-between p-2 rounded border cursor-pointer transition-colors ${
                            lockAssessmentEmail.toLowerCase() === user.email?.toLowerCase()
                              ? 'bg-orange-100 border-orange-300'
                              : 'bg-white border-gray-200 hover:bg-gray-50'
                          }`}
                          onClick={() => setLockAssessmentEmail(user.email)}
                        >
                          <div>
                            <p className="text-sm font-medium text-slate-700">{user.name || user.full_name || user.email?.split('@')[0]}</p>
                            <p className="text-xs text-slate-500">{user.email}</p>
                          </div>
                          <Badge variant="outline" className="text-xs">
                            {user.is_admin ? 'Admin' : (user.role || 'User')}
                          </Badge>
                        </div>
                      ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Select User to Unlock</Label>
                  <div className="space-y-2 max-h-48 overflow-y-auto p-2 bg-gray-50 rounded-lg">
                    {lockAssessmentData?.assessment?.locked_users_info?.map((info, idx) => (
                      <div 
                        key={idx} 
                        className={`flex items-center justify-between p-2 rounded border cursor-pointer transition-colors ${
                          lockAssessmentEmail === info.email
                            ? 'bg-green-100 border-green-300'
                            : 'bg-white border-gray-200 hover:bg-gray-50'
                        }`}
                        onClick={() => setLockAssessmentEmail(info.email)}
                      >
                        <div>
                          <p className="text-sm font-medium text-slate-700">{info.email}</p>
                          <p className="text-xs text-slate-500">Locked by {info.locked_by_name}</p>
                        </div>
                      </div>
                    ))}
                    {(!lockAssessmentData?.assessment?.locked_users_info || lockAssessmentData.assessment.locked_users_info.length === 0) && (
                      <p className="text-sm text-gray-500 text-center py-2">No locked users</p>
                    )}
                  </div>
                </div>
              )}
              {lockAssessmentEmail && (
                <p className={`text-sm ${lockAssessmentData?.mode === 'lock' ? 'text-orange-600' : 'text-green-600'}`}>
                  Selected: <strong>{lockAssessmentEmail}</strong>
                </p>
              )}
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setIsLockAssessmentDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (lockAssessmentData?.mode === 'lock') {
                    handleLockAssessment(lockAssessmentData.assessment.id, lockAssessmentEmail, lockAssessmentData.assessment.skill_name);
                  } else {
                    handleUnlockAssessment(lockAssessmentData.assessment.id, lockAssessmentEmail, lockAssessmentData.assessment.skill_name);
                  }
                  setIsLockAssessmentDialogOpen(false);
                }}
                className={lockAssessmentData?.mode === 'lock' ? 'bg-orange-600 hover:bg-orange-700' : 'bg-green-600 hover:bg-green-700'}
                disabled={!lockAssessmentEmail}
              >
                {lockAssessmentData?.mode === 'lock' ? 'Lock Assessment' : 'Unlock Assessment'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Styles for message composer rich text editor */}
        <style>{`
          .message-rich-editor [contenteditable] {
            border-radius: 1rem !important;
            background: white;
            border: 1px solid #e5e7eb;
            box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
            padding: 0.625rem 1rem !important;
            min-height: 44px !important;
            max-height: 150px;
            overflow-y: auto;
            font-size: 0.875rem;
            line-height: 1.5;
          }
          .message-rich-editor [contenteditable]:focus {
            outline: none;
            ring: 1px;
            border-color: #34d399;
            box-shadow: 0 0 0 1px #34d399;
          }
          .message-rich-editor [contenteditable]:empty:before {
            color: #9ca3af;
          }
        `}</style>
        
        {/* AI Assistant */}
        <div className={['user-management', 'support', 'interviews', 'google-sheets'].includes(activeTab) && !isAIAssistantOpen ? 'hidden' : undefined}>
        <AIAssistant 
          currentModule={activeTab}
          isOpen={isAIAssistantOpen}
          onClose={() => setIsAIAssistantOpen(false)}
          onOpen={() => setIsAIAssistantOpen(true)}
          onNavigate={(tab) => {
            // Navigate to the specified tab
            setActiveTab(tab);
            setIsAIAssistantOpen(false);
          }}
          onOpenImageGenerator={() => {
            setIsImageGeneratorOpen(true);
          }}
        />
        </div>
        {/* AI Image Generator */}
        <AIImageGenerator
          isOpen={isImageGeneratorOpen}
          onClose={() => setIsImageGeneratorOpen(false)}
        />
        
        {/* Telephonic Screening Dialog */}
        {screeningHistoryOpen && selectedAppForScreeningHistory && <ScreeningHistoryDialog
          isOpen={screeningHistoryOpen}
          onClose={() => { setScreeningHistoryOpen(false); setSelectedAppForScreeningHistory(null); }}
          application={selectedAppForScreeningHistory}
          getAuthToken={getAuthToken}
        />}
        <TelephonicScreeningDialog
          isOpen={isTelephonicScreeningOpen}
          onClose={() => {
            setIsTelephonicScreeningOpen(false);
            setSelectedAppForScreening(null);
          }}
          application={selectedAppForScreening}
          getAuthToken={getAuthToken}
          onComplete={(screeningData) => {
            toast.success('Telephonic screening completed successfully!');
            // Refresh applications list if needed
            if (currentUser?.is_admin || currentUser?.role === 'crm_user') {
              fetchData();
            }
          }}
        />
        
        {/* New HR Performance Dialog */}
        <AddPerformanceDialog
          isOpen={isNewPerformanceDialogOpen}
          onClose={() => {
            setIsNewPerformanceDialogOpen(false);
            setSelectedCandidateForNewPerformance(null);
          }}
          candidate={selectedCandidateForNewPerformance}
          getAuthToken={getAuthToken}
          companies={companies}
          onSave={() => {
            // Refresh HR performances list
            const fetchPerformances = async () => {
              try {
                const token = getAuthToken();
                const config = { headers: { Authorization: `Bearer ${token}` } };
                const perfRes = await axios.get(`${API}/candidate-performance`, config);
                const enrichedPerf = perfRes.data.map(perf => {
                  const candidate = allCandidates.find(c => c.id === perf.candidate_status_id);
                  return { 
                    ...perf, 
                    candidate_name: candidate?.candidate_name || 'Unknown', 
                    company_name: candidate?.company_name || '',
                    email: candidate?.email || '',
                    phone: candidate?.phone || '',
                    process: candidate?.process || '',
                  };
                });
                setAllPerformances(enrichedPerf);
              } catch (error) {
                console.error('Failed to fetch performances:', error);
              }
            };
            fetchPerformances();
          }}
        />
      </main>
      </div>
    </div>
  );
};

// Wrapper component that provides call functionality
const DashboardWithCall = () => {
  const token = getAuthToken();
  const [currentUserForCall, setCurrentUserForCall] = React.useState(null);

  React.useEffect(() => {
    const fetchUser = async () => {
      try {
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const res = await axios.get(`${API}/auth/me`, config);
        setCurrentUserForCall(res.data);
      } catch (error) {
        console.error('Failed to fetch user for call context');
      }
    };
    if (token) fetchUser();
  }, [token]);

  return (
    <CallProvider token={token} currentUser={currentUserForCall} API={API}>
      <Dashboard />
      <CallUI />
    </CallProvider>
  );
};

export default DashboardWithCall;